// Editor entry: wires renderer, document state, tools, panels, file I/O.

import { DeleteLayerCommand } from "./core/commands/DeleteLayerCommand";
import { DuplicateLayerCommand } from "./core/commands/DuplicateLayerCommand";
import { ConvertToSmartObjectCommand, RasterizeSmartObjectCommand } from "./core/commands/ConvertToSmartObjectCommand";
import { ApplySmartFilterCommand, RemoveSmartFilterCommand, ToggleSmartFilterCommand, UpdateSmartFilterCommand } from "./core/commands/SmartFilterCommands";
import { GroupLayersCommand } from "./core/commands/GroupLayersCommand";
import { LoadDocumentCommand } from "./core/commands/LoadDocumentCommand";
import { RenameDocumentCommand } from "./core/commands/RenameDocumentCommand";
import { TrimRevealCommand } from "./core/commands/TrimRevealCommand";
import { MergeDownCommand } from "./core/commands/MergeDownCommand";
import { MergeVisibleCommand } from "./core/commands/MergeVisibleCommand";
import { StampVisibleCommand } from "./core/commands/StampVisibleCommand";
import { MoveLayerCommand } from "./core/commands/MoveLayerCommand";
import { NewLayerCommand } from "./core/commands/NewLayerCommand";
import { PaintStrokeCommand, snapshotRegion } from "./core/commands/PaintStrokeCommand";
import { SetLayerPropertyCommand } from "./core/commands/SetLayerPropertyCommand";
import { UngroupCommand } from "./core/commands/UngroupCommand";
import { ReorderLayerCommand } from "./core/commands/ReorderLayerCommand";
import { ReorderLayersCommand } from "./core/commands/ReorderLayersCommand";
import { CompositeCommand } from "./core/commands/CompositeCommand";
import { RenameLayerCommand } from "./core/commands/RenameLayerCommand";
import { FlattenCommand } from "./core/commands/FlattenCommand";
import { AddLayerMaskCommand, DeleteLayerMaskCommand, ApplyLayerMaskCommand, EnableLayerMaskCommand } from "./core/commands/LayerMaskCommands";
import type { Document } from "./core/document/Document";
import type { BlendMode, GroupLayer, Layer, RasterLayer, AdjustmentLayer, AdjustmentKind, SmartObjectLayer, LayerColorLabel, SolidFillLayer, GradientFillLayer, PatternFillLayer, BackgroundLayer, ArtboardLayer, FillLayer } from "./core/document/Layer";
import type { PathModel } from "./core/document/PathModel";
import { createAdjustmentLayer, createSolidFillLayer, createGradientFillLayer, createPatternFillLayer, createBackgroundLayer, createArtboardLayer } from "./core/document/Layer";
import { findRaster, findLayer } from "./core/document/LayerUtils";
import { PixelBuffer } from "./core/pixel/PixelBuffer";
import { rgbToGrayscale, rgbToIndexed, rgbToLab, rgbToCmyk, expand8to16, quantize16to8 } from "./core/pixel/modeConversions";
import { ModeCommand, collectRasterLayerIds } from "./core/commands/ModeCommand";
import { nativeCompositeOp, compositeLayer } from "./core/pixel/blend";
import { ColorPicker, hexToRgb, rgbToHex } from "./app/ui/ColorPicker";
import { openLayerStyleDialog } from "./app/ui/LayerStyleDialog";
import { openWarpTextDialogForActiveLayer, getActiveTypeLayerWarp } from "./app/ui/WarpTextDialog";
import { ColorPanel } from "./app/ui/ColorPanel";
import { NewDocumentDialog } from "./app/ui/NewDocumentDialog";
import { PreferencesDialog, load as loadPreferences, type RulerUnit } from "./app/ui/PreferencesDialog";
import { FILE_INPUT_ACCEPT, extOf, isPlaceholderExtension, unimplementedMessage } from "./app/io/acceptedFormats";
import { Canvas2DRenderer } from "./core/render/Canvas2DRenderer";
import { fitToScreen, zoomAt, screenToDoc, docToScreen } from "./core/render/Viewport";
import { BrushTool } from "./core/tools/BrushTool";
import { CropTool } from "./core/tools/CropTool";
import { EyedropperTool } from "./core/tools/EyedropperTool";
import { HandTool } from "./core/tools/HandTool";
import { LassoTool } from "./core/tools/LassoTool";
import { MagicWandTool } from "./core/tools/MagicWandTool";
import { MarqueeTool } from "./core/tools/MarqueeTool";
import { MoveTool } from "./core/tools/MoveTool";
import { PolygonalLassoTool } from "./core/tools/PolygonalLassoTool";
import { MagneticLassoTool } from "./core/tools/MagneticLassoTool";
import { QuickSelectionTool } from "./core/tools/QuickSelectionTool";
import type { RGB, Tool, ToolContext, PointerSample } from "./core/tools/Tool";
import { ZoomTool } from "./core/tools/ZoomTool";
import { TypeTool } from "./core/tools/TypeTool";
import { createTypeLayer, isTypeLayer, applyFontSizeToRuns } from "./core/document/TypeLayer";
import { createShapeLayer, isShapeLayer } from "./core/document/Layer";
import type { TypeLayer, AntiAliasMode } from "./core/document/TypeLayer";
import { TextEditorOverlay } from "./app/ui/TextEditorOverlay";
import { PenTool } from "./core/tools/PenTool";
import { ShapeTool } from "./core/tools/ShapeTool";
import { CustomShapeTool } from "./core/tools/CustomShapeTool";
import { DirectSelectionTool } from "./core/tools/DirectSelectionTool";
import { PathSelectionTool } from "./core/tools/PathSelectionTool";
import { VerticalTypeTool } from "./core/tools/VerticalTypeTool";
import { HorizontalTypeMaskTool } from "./core/tools/HorizontalTypeMaskTool";
import { VerticalTypeMaskTool } from "./core/tools/VerticalTypeMaskTool";
import { ObjectSelectionTool } from "./core/tools/ObjectSelectionTool";
import { CloneStampTool } from "./core/tools/CloneStampTool";
import { DodgeBurnTool } from "./core/tools/DodgeBurnTool";
import { FreeTransformTool } from "./core/tools/FreeTransformTool";
import { GradientTool } from "./core/tools/GradientTool";
import { PaintBucketTool } from "./core/tools/PaintBucketTool";
import { RetouchTool } from "./core/tools/RetouchTool";
import { HistoryBrushTool } from "./core/tools/HistoryBrushTool";
import { HealingBrushTool } from "./core/tools/HealingBrushTool";
import { SpotHealingTool } from "./core/tools/SpotHealingTool";
import { PatchTool } from "./core/tools/PatchTool";
import { PencilTool } from "./core/tools/PencilTool";
import { PatternStampTool } from "./core/tools/PatternStampTool";
import { ColorSamplerTool } from "./core/tools/ColorSamplerTool";
import { RulerTool } from "./core/tools/RulerTool";
import { NoteTool } from "./core/tools/NoteTool";
import { CountTool } from "./core/tools/CountTool";
import { RedEyeTool } from "./core/tools/RedEyeTool";
import { BackgroundEraserTool } from "./core/tools/BackgroundEraserTool";
import { MagicEraserTool } from "./core/tools/MagicEraserTool";
import { MixerBrushTool } from "./core/tools/MixerBrushTool";
import { ColorReplacementTool } from "./core/tools/ColorReplacementTool";
import { ArtHistoryBrushTool } from "./core/tools/ArtHistoryBrushTool";
import { SliceTool } from "./core/tools/SliceTool";
import { SliceSelectTool } from "./core/tools/SliceSelectTool";
import { FrameTool } from "./core/tools/FrameTool";
import { PerspectiveCropTool } from "./core/tools/PerspectiveCropTool";
import { ContentAwareMoveTool } from "./core/tools/ContentAwareMoveTool";
import { RemoveTool } from "./core/tools/RemoveTool";
import { EraseToHistoryTool } from "./core/tools/EraseToHistoryTool";
import { RotateViewTool } from "./core/tools/RotateViewTool";
import type { PenMode } from "./core/tools/PenTool";
import type { ShapeMode } from "./core/tools/ShapeTool";
import { decodeImage, decodeTiff, encodeTiff } from "./app/io/decodeImage";
import { decodeLayer, decodeLayerMask, decodeMergedComposite } from "./core/io/psd/decodeChannels";
import { parsePsd } from "./core/io/psd/parsePsd";
import { parseLayerEffects } from "./core/io/psd/parseLayerEffects";
import { parseTySh } from "./core/io/psd/parseTySh";
import { parseVmsk, parseGuideResource } from "./core/io/psd/parseVmsk";
import {
  parseAdjustmentFromInfo,
  parseSoCoDescriptor,
  parseGdFlDescriptor,
  parsePtFlDescriptor,
} from "./core/io/psd/parseAdjustmentFill";
import { ApplyFilterCommand, setFilterProgressCallback } from "./core/commands/ApplyFilterCommand";
import { SetSelectionCommand } from "./core/commands/SetSelectionCommand";
import { FillCommand, type FillOptions } from "./core/commands/FillCommand";
import { StrokeCommand, type StrokeOptions } from "./core/commands/StrokeCommand";
import { ResizeImageCommand, type ResizeOptions } from "./core/commands/ResizeImageCommand";
import { ResizeCanvasCommand, type CanvasSizeOptions } from "./core/commands/ResizeCanvasCommand";
import { RotateImageCommand, type RotateOptions } from "./core/commands/RotateImageCommand";
import {
  TransformLayerOrientationCommand,
  planTransformAgain,
  type LastTransformRecord,
} from "./core/commands/TransformLayerOrientationCommand";
import type { OrientationOp } from "./core/pixel/orientationTransform";
import { collectLayerTargetIds, planAlignLayers, type AlignOp } from "./core/commands/AlignLayersCommand";
import { planDistributeLayers, type DistributeOp } from "./core/commands/DistributeLayersCommand";
import { SetWorkPathCommand } from "./core/commands/SetWorkPathCommand";
import type { Command } from "./core/commands/Command";
import { Selection, type AlphaChannel } from "./core/selection/Selection";
import { floodFillFromSeed, selectByColor } from "./core/pixel/floodFill";
import { contentAwareFill } from "./core/pixel/contentAwareFill";
import type { Filter } from "./core/filters/Filter";
import { GaussianBlur } from "./core/filters/GaussianBlur";
import { MotionBlur } from "./core/filters/MotionBlur";
import { BoxBlur } from "./core/filters/BoxBlur";
import { SharpenSuite } from "./core/filters/SharpenSuite";
import { NoiseSuite } from "./core/filters/NoiseSuite";
import { PixelateSuite } from "./core/filters/PixelateSuite";
import { DistortSuite } from "./core/filters/DistortSuite";
import { StylizeSuite } from "./core/filters/StylizeSuite";
import { UtilitySuite } from "./core/filters/UtilitySuite";
import { HueSaturation } from "./core/filters/HueSaturation";
import { ColorBalance } from "./core/filters/ColorBalance";
import { Levels } from "./core/filters/Levels";
import { Curves } from "./core/filters/Curves";
import { BlackWhite } from "./core/filters/BlackWhite";
import { Vibrance } from "./core/filters/Vibrance";
import { Exposure } from "./core/filters/Exposure";
import { PhotoFilter } from "./core/filters/PhotoFilter";
import { ChannelMixer } from "./core/filters/ChannelMixer";
import { Invert } from "./core/filters/Invert";
import { Posterize } from "./core/filters/Posterize";
import { Threshold } from "./core/filters/Threshold";
import { GradientMap } from "./core/filters/GradientMap";
import { ColorLookup } from "./core/filters/ColorLookup";
import { SelectiveColor } from "./core/filters/SelectiveColor";
import { ShadowsHighlights } from "./core/filters/ShadowsHighlights";
import { AutoTone } from "./core/filters/AutoTone";
import { AutoContrast } from "./core/filters/AutoContrast";
import { AutoColor } from "./core/filters/AutoColor";
import { AverageBlur } from "./core/filters/AverageBlur";
import { SurfaceBlur } from "./core/filters/SurfaceBlur";
import { SmartBlur } from "./core/filters/SmartBlur";
import { LensBlur } from "./core/filters/LensBlur";
import { RadialBlur } from "./core/filters/RadialBlur";
import { ShapeBlur } from "./core/filters/ShapeBlur";
import { FieldBlur } from "./core/filters/FieldBlur";
import { IrisBlur } from "./core/filters/IrisBlur";
import { TiltShift } from "./core/filters/TiltShift";
import { Displace } from "./core/filters/Displace";
import { Ripple } from "./core/filters/Ripple";
import { Shear } from "./core/filters/Shear";
import { Wave } from "./core/filters/Wave";
import { ZigZag } from "./core/filters/ZigZag";
import { PolarCoordinates } from "./core/filters/PolarCoordinates";
import { Crystallize } from "./core/filters/Crystallize";
import { Facet } from "./core/filters/Facet";
import { Fragment } from "./core/filters/Fragment";
import { Mezzotint } from "./core/filters/Mezzotint";
import { Pointillize } from "./core/filters/Pointillize";
import { Diffuse } from "./core/filters/Diffuse";
import { Extrude } from "./core/filters/Extrude";
import { Solarize } from "./core/filters/Solarize";
import { Tiles } from "./core/filters/Tiles";
import { TraceContour } from "./core/filters/TraceContour";
import { Wind } from "./core/filters/Wind";
import { GlowingEdges } from "./core/filters/GlowingEdges";
import { Fibers } from "./core/filters/Fibers";
import { LensFlare } from "./core/filters/LensFlare";
import { LightingEffects } from "./core/filters/LightingEffects";
import { PictureFrame } from "./core/filters/PictureFrame";
import { OilPaint } from "./core/filters/OilPaint";
import { SmartSharpen } from "./core/filters/SmartSharpen";
import { downloadBlob, exportDocument, exportBmp, exportGif, exportIco, exportPdf, encodePsd, exportSvg, collectExportLayers, type ExportFormat } from "./app/io/encodeImage";
import { BrightnessContrast } from "./core/filters/BrightnessContrast";
import { SaveAsDialog } from "./app/ui/SaveAsDialog";
import { SelectAndMaskDialog, applyEdgeAdjustments } from "./app/ui/SelectAndMaskDialog";
import { FilterDialog } from "./app/ui/FilterDialog";
import { FilterGalleryDialog } from "./app/ui/FilterGalleryDialog";
import { CharacterPanel } from "./app/ui/CharacterPanel";
import { ParagraphPanel } from "./app/ui/ParagraphPanel";
import { PathsPanel } from "./app/ui/PathsPanel";
import { NavigatorPanel } from "./app/ui/NavigatorPanel";
import { InfoPanel } from "./app/ui/InfoPanel";
import { HistogramPanel } from "./app/ui/HistogramPanel";
import { SwatchesPanel } from "./app/ui/SwatchesPanel";
import { BrushSettingsPanel, type BrushSettings } from "./app/ui/BrushSettingsPanel";
import { GradientEditor } from "./app/ui/GradientEditor";
import { PatternEditor } from "./app/ui/PatternEditor";
import { EditorState } from "./app/state/EditorState";
import { ToolUX } from "./app/ui/ToolUX";
import { StylesPanel } from "./app/ui/StylesPanel";
import { PropertiesPanel } from "./app/ui/PropertiesPanel";
import { AdjustmentsPanel } from "./app/ui/AdjustmentsPanel";
import { ShapesPanel } from "./app/ui/ShapesPanel";
import { CloneSourcePanel } from "./app/ui/CloneSourcePanel";
// Decision (#177): the inline keydown handler in this file IS the single runtime
// keymap. keymap.ts is kept as a reference/constants module (PHOTOSHOP_TOOL_SHORTCUTS,
// KeymapDeps) and for sharing the documented modifier conventions. initKeymap()
// is NOT called â€” calling it would double-register every shortcut and cause
// each keypress to fire twice.  The two files must stay logically in sync; the
// inline handler wins on any divergence.
import { PHOTOSHOP_TOOL_SHORTCUTS, type KeymapDeps } from "./app/keymap";
import { installAutosaveWithDefaults, type AutosaveSnapshot } from "./app/autosave";
export type { KeymapDeps };
import { wireMenus, type MenuDeps } from "./app/menus";
import { toolCtx as modToolCtx, sampleFromEvent as modSampleFromEvent, highlightToolByShortcut as modHighlightTool, initRailButtons as modInitRailButtons, wireToolRailClicks as modWireToolRailClicks } from "./app/tools";
import { updateStatusBar as modUpdateStatusBar, updateCursor as modUpdateCursor, updateSwatches as modUpdateSwatches, showError as modShowError, showTip as modShowTip, updateDropHint as modUpdateDropHint, rgbToHex as modRgbToHex, setCapsLockFromEvent as modSetCapsLockFromEvent } from "./app/cursor";
// NOTE (#179): modRenderLayersPanel (src/app/layersPanel.ts) has a different
// call signature (requires LayersPanelDeps injection) than the inline
// renderLayersPanel() below, which closes over many local variables. Swapping
// them would require threading all those locals through LayersPanelDeps â€” a
// large-surface refactor with non-trivial regression risk. The swap is deferred;
// BLEND_OPTIONS and populateBlendSelect are still consumed from the module.
import { populateBlendSelect as modPopulateBlendSelect, BLEND_OPTIONS, flattenLayerRows, type LayerRowEntry } from "./app/layersPanel";
import { renderHistoryPanel as modRenderHistoryPanel, createHistorySnapshot as modCreateHistorySnapshot, ensureHistorySourceDefault as modEnsureHistorySourceDefault } from "./app/ui/HistoryPanel";
import { renderChannelsPanel as modRenderChannelsPanel, type ChannelColorMode } from "./app/ui/ChannelsPanel";
import { scheduleFrame as modScheduleFrame, resize as modResize, zoomCentered as modZoomCentered, fitDoc as modFitDoc, resetTo100 as modResetTo100, updateDocTab as modUpdateDocTab } from "./app/render";
import { routePointerDown, wantsHoverMove } from "./app/pointerRouting";
import { getRecentFiles as modGetRecentFiles, addRecentFile as modAddRecentFile, savePngSilent as modSavePngSilent, exportAs as modExportAs, placeEmbedded as modPlaceEmbedded } from "./app/fileOps";
import { promptNewLayer as modPromptNewLayer, promptPreferences as modPromptPreferences, promptImageSize as modPromptImageSize, promptCanvasSize as modPromptCanvasSize, promptHueSaturation as modPromptHueSaturation, promptLevels as modPromptLevels, promptCurves as modPromptCurves, promptColorBalance as modPromptColorBalance, promptGaussianBlur as modPromptGaussianBlur, promptFill as modPromptFill } from "./app/dialogs";
import * as FD from "./app/filterDialogs";
import * as AD from "./app/adjustmentDialogs";

const state = new EditorState();
// Set a visible default foreground so brush strokes are immediately obvious
state.setForegroundColor({ r: 30, g: 150, b: 255 }); // bright blue

// Filter processing progress indicator.
// The element starts empty and aria-hidden=true. We only fill it (and remove
// aria-hidden) while a filter is actively running, then strip it back to empty
// so screen readers and inspectors don't see stale "Processing filterâ€¦" text.
// Closes #133 (status-bar text leak).
const filterStatusEl = document.getElementById("filter-status");
setFilterProgressCallback((message: string) => {
  if (filterStatusEl) {
    if (message) {
      filterStatusEl.innerHTML = '<span class="spinner"></span><span class="filter-status-text"></span>';
      const textEl = filterStatusEl.querySelector(".filter-status-text");
      if (textEl) textEl.textContent = message;
      filterStatusEl.style.display = "";
      filterStatusEl.setAttribute("aria-hidden", "false");
    } else {
      filterStatusEl.innerHTML = "";
      filterStatusEl.style.display = "none";
      filterStatusEl.setAttribute("aria-hidden", "true");
    }
  }
  // Disable UI interactions during filter processing
  const canvasArea = document.querySelector(".canvas-area") as HTMLElement;
  if (canvasArea) {
    canvasArea.style.pointerEvents = message ? "none" : "";
    canvasArea.style.opacity = message ? "0.7" : "";
  }
});

// Alpha channels stored per-document
let docAlphaChannels: Map<string, AlphaChannel[]> = new Map();

// Channel viewing state
let activeChannelView: { r: boolean; g: boolean; b: boolean; alphaId: string | null } = {
  r: true, g: true, b: true, alphaId: null,
};

const moveTool = new MoveTool();
const brushTool = new BrushTool({ id: "brush", shortcut: "B", mode: "paint" });
const eraserTool = new BrushTool({ id: "eraser", shortcut: "E", mode: "erase" });
const eyedropperTool = new EyedropperTool();
const handTool = new HandTool();
const zoomTool = new ZoomTool();
const marqueeRectTool = new MarqueeTool({ id: "marquee-rect", shortcut: "M", shape: "rect" });
const marqueeEllipseTool = new MarqueeTool({ id: "marquee-ellipse", shortcut: "M", shape: "ellipse" });
const marqueeSingleRowTool = new MarqueeTool({ id: "marquee-single-row", shortcut: "M", shape: "single-row" });
const marqueeSingleColumnTool = new MarqueeTool({ id: "marquee-single-column", shortcut: "M", shape: "single-column" });
const lassoTool = new LassoTool({ id: "lasso", shortcut: "L" });
const polyLassoTool = new PolygonalLassoTool({ id: "lasso-poly", shortcut: "L" });
const magneticLassoTool = new MagneticLassoTool({ id: "magnetic-lasso", shortcut: "L" });
const magicWandTool = new MagicWandTool();
const quickSelectionTool = new QuickSelectionTool();
const cropTool = new CropTool({ id: "crop", shortcut: "C" });
const typeTool = new TypeTool();
const textEditor = new TextEditorOverlay();

// Wire TypeTool to use the in-canvas text editor overlay.
typeTool.setCallbacks({
  requestNewText: (x: number, y: number) => {
    return new Promise((resolve) => {
      // i94: seed the draft layer (used only to style the text-editor
      // overlay while typing) from the options bar's current Font/Size/
      // Anti-alias/Alignment/Faux Bold/Faux Italic — TypeToolOptions
      // (ToolUX.ts) stashes these on typeTool itself. The layer actually
      // committed on resolve() is built by TypeTool.createNewTypeLayer/
      // createParagraphText, which now read the same fields (TypeTool.ts).
      const layer = createTypeLayer({
        text: "",
        x,
        y,
        fontFamily: typeTool.fontFamily,
        fontSize: typeTool.fontSize,
        antiAlias: typeTool.antiAlias,
        textAlign: typeTool.textAlign,
        fauxBold: typeTool.fauxBold,
        fauxItalic: typeTool.fauxItalic,
      });
      textEditor.open({
        viewport: state.viewport,
        layer,
        canvasRect: canvas.getBoundingClientRect(),
        initialText: "",
        commit: (text: string) => resolve(text || null),
        cancel: () => resolve(null),
      });
    });
  },
  requestEditText: (layer) => {
    return new Promise((resolve) => {
      textEditor.open({
        viewport: state.viewport,
        layer,
        canvasRect: canvas.getBoundingClientRect(),
        initialText: layer.text,
        commit: (text: string) => resolve(text || null),
        cancel: () => resolve(null),
      });
    });
  },
});
const penTool = new PenTool();
const directSelectionTool = new DirectSelectionTool();
const rectShapeTool = new ShapeTool({ id: "shape-rect", mode: "rect" });
const ellipseShapeTool = new ShapeTool({ id: "shape-ellipse", mode: "ellipse" });
const cloneStampTool = new CloneStampTool();
const dodgeBurnTool = new DodgeBurnTool();
const freeTransformTool = new FreeTransformTool();
const gradientTool = new GradientTool();
const paintBucketTool = new PaintBucketTool();
const retouchTool = new RetouchTool();
const historyBrushTool = new HistoryBrushTool();
const healingBrushTool = new HealingBrushTool();
const spotHealingTool = new SpotHealingTool();
const patchTool = new PatchTool();

// New tools (BATCH I)
const pencilTool = new PencilTool();
// i172: SpongeTool (the standalone class) is dead — DodgeBurnTool's own
// "sponge" mode (see the O-key cycle and the Dodge/Burn flyout above) is the
// sole real implementation actually reachable from the UI. Removed the
// duplicate instance + its `tools` id-map registration to avoid future
// divergence between the two.
const patternStampTool = new PatternStampTool();
const colorSamplerTool = new ColorSamplerTool();
const rulerTool = new RulerTool();
const noteTool = new NoteTool();
const countTool = new CountTool();
const redEyeTool = new RedEyeTool();

// New tools (BATCH BB)
const backgroundEraserTool = new BackgroundEraserTool();
const magicEraserTool = new MagicEraserTool();
const mixerBrushTool = new MixerBrushTool();
const colorReplacementTool = new ColorReplacementTool();

// New tools (BATCH CC)
const artHistoryBrushTool = new ArtHistoryBrushTool();
const sliceTool = new SliceTool();
const sliceSelectTool = new SliceSelectTool();
const frameTool = new FrameTool();

// Remaining tools (BATCH DD)
const perspectiveCropTool = new PerspectiveCropTool();
const contentAwareMoveTool = new ContentAwareMoveTool();
const removeTool = new RemoveTool();
const eraseToHistoryTool = new EraseToHistoryTool();
const rotateViewTool = new RotateViewTool();

// Shape tool variants (BATCH J)
const polygonShapeTool = new ShapeTool({ id: "shape-polygon", mode: "polygon" });
const roundedRectShapeTool = new ShapeTool({ id: "shape-rounded-rect", mode: "rect" });
roundedRectShapeTool.cornerRadius = 10;

// Previously-missing tools (issue #187)
const pathSelectionTool = new PathSelectionTool();
const verticalTypeTool = new VerticalTypeTool();
const horizontalTypeMaskTool = new HorizontalTypeMaskTool();
const verticalTypeMaskTool = new VerticalTypeMaskTool();
// Shape flyout additions: line, triangle (3-polygon), custom shape.
const lineShapeTool = new ShapeTool({ id: "shape-line", mode: "line" });
lineShapeTool.createVector = true;
const triangleShapeTool = new ShapeTool({ id: "shape-triangle", mode: "polygon" });
triangleShapeTool.polygonSides = 3;
triangleShapeTool.createVector = true;
const customShapeTool2 = new CustomShapeTool({ id: "shape-custom" });

// i188: several ShapeTool instances intentionally share the same `mode`
// (roundedRectShapeTool/rectShapeTool both use "rect"; triangleShapeTool/
// polygonShapeTool both use "polygon", distinguished only by polygonSides),
// so deriving the new layer's name purely from `tool.mode` collapsed them
// into identical names ("Rect Shape" / "Polygon Shape"). ShapeTool.ts itself
// has no per-instance display-name field, so this is tracked here instead of
// adding one to the tool class.
const shapeToolDisplayNames = new Map<ShapeTool | CustomShapeTool, string>([
  [rectShapeTool, "Rectangle"],
  [ellipseShapeTool, "Ellipse"],
  [polygonShapeTool, "Polygon"],
  [roundedRectShapeTool, "Rounded Rectangle"],
  [lineShapeTool, "Line"],
  [triangleShapeTool, "Triangle"],
  [customShapeTool2, "Custom Shape"],
]);
// Object Selection tool.
const objectSelectionTool = new ObjectSelectionTool();

// i69: remember the last-used Lasso / Wand-group sub-tool so a bare L/W press
// after switching away to another tool restores that variant instead of
// always resetting to index 0 (Lasso / Object Selection). Tracked via a
// state.subscribe hook (setActiveTool() notifies) rather than only from the
// L/W keydown handlers, so EVERY activation path is captured — rail-icon click,
// right-click flyout menu, and keyboard alike — not just the keyboard.
let lastLassoVariant: Tool = lassoTool;
let lastWandVariant: Tool = objectSelectionTool;
state.subscribe(() => {
  const t = state.activeTool;
  if (t === lassoTool || t === polyLassoTool || t === magneticLassoTool) lastLassoVariant = t;
  else if (t === objectSelectionTool || t === quickSelectionTool || t === magicWandTool) lastWandVariant = t;
});

// i137 follow-up: Color Sampler points are scoped per-document (Photoshop). The
// samplers live on the single ColorSamplerTool instance in raw doc-space coords,
// and updateOverlay() now draws them persistently — so on a document switch/open
// they must be cleared, otherwise the outgoing doc's crosshairs would render at
// meaningless coordinates on the incoming doc. (Full per-document persistence
// would require storing samplers on the Document; clearing on change is the
// correct, non-corrupting minimum until then.)
let _lastDocForSamplers: Document | null = state.doc ?? null;
state.subscribe(() => {
  const doc = state.doc ?? null;
  if (doc !== _lastDocForSamplers) {
    _lastDocForSamplers = doc;
    if (colorSamplerTool.samplers.length > 0) colorSamplerTool.clearAll();
  }
});

// Wire VerticalTypeTool callbacks (same text editor as TypeTool).
// Note: textEditor is declared below the tool block, so this is deferred to
// a function called after all declarations â€” see lateInitTypeTools() below.
let lateTypeToolsWired = false;
function lateInitTypeTools(): void {
  if (lateTypeToolsWired) return;
  lateTypeToolsWired = true;
  verticalTypeTool.setCallbacks({
    requestNewText: (x: number, y: number) => {
      return new Promise((resolve) => {
        // i94 (partial — VerticalTypeTool.ts is out of this fix's scope):
        // VerticalTypeTool instances also get fontFamily/fontSize/antiAlias/
        // textAlign/fauxBold/fauxItalic stashed on them by the shared
        // TypeToolOptions
        // (ToolUX.ts), same as typeTool, so read them the same duck-typed
        // way for the draft layer's editor styling. VerticalTypeTool.ts
        // itself (its createNewTypeLayer) is out of this fix's file scope,
        // so the layer actually committed still won't carry these — only
        // the in-progress editor's look is fixed here.
        const vt = verticalTypeTool as unknown as {
          fontFamily?: string; fontSize?: number; antiAlias?: string;
          textAlign?: string; fauxBold?: boolean; fauxItalic?: boolean;
        };
        // exactOptionalPropertyTypes is on, so `?? <default>` (collapsing to
        // a concrete value) is required here rather than passing through a
        // possibly-`undefined` duck-typed read directly.
        const layer = createTypeLayer({
          text: "", x, y,
          fontFamily: vt.fontFamily ?? "Arial",
          fontSize: vt.fontSize ?? 36,
          antiAlias: (vt.antiAlias as AntiAliasMode | undefined) ?? "smooth",
          textAlign: (vt.textAlign as "left" | "center" | "right" | "justify" | undefined) ?? "left",
          fauxBold: vt.fauxBold ?? false,
          fauxItalic: vt.fauxItalic ?? false,
        });
        textEditor.open({
          viewport: state.viewport,
          layer,
          canvasRect: canvas.getBoundingClientRect(),
          initialText: "",
          commit: (text: string) => resolve(text || null),
          cancel: () => resolve(null),
        });
      });
    },
    requestEditText: (layer) => {
      return new Promise((resolve) => {
        textEditor.open({
          viewport: state.viewport,
          layer,
          canvasRect: canvas.getBoundingClientRect(),
          initialText: layer.text,
          commit: (text: string) => resolve(text || null),
          cancel: () => resolve(null),
        });
      });
    },
  });
  horizontalTypeMaskTool.setCallbacks({
    requestText: (x: number, y: number) => {
      return new Promise((resolve) => {
        const layer = createTypeLayer({
          text: "", x, y,
          fontFamily: horizontalTypeMaskTool.fontFamily,
          fontSize: horizontalTypeMaskTool.fontSize,
        });
        const restore = beginTypeMaskQuickMask();
        textEditor.open({
          viewport: state.viewport,
          layer,
          canvasRect: canvas.getBoundingClientRect(),
          initialText: "",
          commit: (text: string) => { restore(); resolve(text || null); },
          cancel: () => { restore(); resolve(null); },
        });
        styleTypeMaskEditorRed();
      });
    },
  });
  verticalTypeMaskTool.setCallbacks({
    requestText: (x: number, y: number) => {
      return new Promise((resolve) => {
        const layer = createTypeLayer({
          text: "", x, y,
          fontFamily: verticalTypeMaskTool.fontFamily,
          fontSize: verticalTypeMaskTool.fontSize,
        });
        const restore = beginTypeMaskQuickMask();
        textEditor.open({
          viewport: state.viewport,
          layer,
          canvasRect: canvas.getBoundingClientRect(),
          initialText: "",
          commit: (text: string) => { restore(); resolve(text || null); },
          cancel: () => { restore(); resolve(null); },
        });
        styleTypeMaskEditorRed();
      });
    },
  });
}

// i103: Type Mask tools (horizontal + vertical) show Photoshop's
// translucent-red Quick Mask styling while the user is typing, instead of
// the plain black text editor the ordinary Type tool uses — the in-progress
// text is a selection-to-be, not painted pixels. HorizontalTypeMaskTool.ts/
// VerticalTypeMaskTool.ts/TextEditorOverlay.ts are out of this fix's file
// scope (owned by a parallel agent), so this works entirely from the caller
// side: toggle the existing Quick Mask overlay for the duration of the
// editing session (restored on commit/cancel either way, since these tools
// always convert to a real selection rather than staying in Quick Mask),
// and directly restyle the DOM node TextEditorOverlay.open() just
// synchronously created/appended (querying it right after open() returns —
// open() always calls close() first, so at most one `.text-editor-overlay`
// exists at a time).
let _typeMaskRestore: (() => void) | null = null;
function beginTypeMaskQuickMask(): () => void {
  const prevQuickMask = state.quickMask;
  state.setQuickMask(true);
  renderer.setQuickMask(true);
  state.requestRender();
  const restore = (): void => {
    _typeMaskRestore = null;
    state.setQuickMask(prevQuickMask);
    renderer.setQuickMask(prevQuickMask);
    state.requestRender();
  };
  _typeMaskRestore = restore;
  return restore;
}
// i103 safety net: a type-mask text session forces Quick Mask on and is
// normally restored by the editor's commit/cancel. But if the user abandons it
// via a tool switch that fires neither (e.g. Tab out of the textarea, then a
// tool shortcut), Quick Mask would stay stuck on with an orphaned editor. When
// the active tool is no longer a type-mask tool but a session is still pending,
// cancel the editor (its cancel callback runs restore()), or restore directly.
state.subscribe(() => {
  if (!_typeMaskRestore) return;
  if (state.activeTool === horizontalTypeMaskTool || state.activeTool === verticalTypeMaskTool) return;
  if (textEditor.isActive()) {
    (textEditor as unknown as { doCancel: () => void }).doCancel();
  } else {
    _typeMaskRestore();
  }
});

function styleTypeMaskEditorRed(): void {
  const overlayEl = document.querySelector<HTMLDivElement>(".text-editor-overlay");
  if (overlayEl) overlayEl.style.background = "rgba(255, 0, 0, 0.15)";
  const textarea = overlayEl?.querySelector<HTMLTextAreaElement>(".text-editor-overlay__textarea");
  if (textarea) {
    textarea.style.color = "rgba(255, 0, 0, 0.55)";
    textarea.style.caretColor = "rgba(255, 60, 60, 0.9)";
  }
}

// Enable vector mode by default for all shape tools
rectShapeTool.createVector = true;
ellipseShapeTool.createVector = true;
polygonShapeTool.createVector = true;
roundedRectShapeTool.createVector = true;
// NOTE: customShapeTool2 is a CustomShapeTool (separate class, no createVector),
// so its Tool Mode defaults to "path" via the derive-default in the shape
// pointerup routing — which now matches what its options bar displays (i85
// verification fix: previously the bar showed "Path" but drawing rasterized).

// Pen tool variants (BATCH K) â€” same instance, mode switched

// Map shortcut â†’ real Tool instance. Only contains tools that actually do
// something. Pressing other shortcut keys highlights the rail button
// (muscle-memory feedback) but does NOT change the active tool â€” that
// avoids polluting state with placeholder objects.
const tools: Record<string, Tool> = {
  V: moveTool,
  A: pathSelectionTool, // PS: A = Path Selection (black arrow); Shift+A cycles to Direct Selection
  M: marqueeRectTool,
  L: lassoTool,
  W: objectSelectionTool, // PS: W = Object Selection; Shift+W cycles to Quick Selection / Magic Wand
  C: cropTool,
  T: typeTool, // PS: bare T = Type tool; Ctrl+T = Free Transform (fixes #171)
  P: penTool,
  U: rectShapeTool,
  S: cloneStampTool,
  O: dodgeBurnTool,
  I: eyedropperTool,
  B: brushTool,
  E: eraserTool,
  H: handTool,
  Z: zoomTool,
  R: rotateViewTool,
  Y: historyBrushTool,
  K: frameTool,
  // Additional tools (accessible via UI, share shortcuts with grouped tools).
  // Issue #130: every Photoshop-roster tool reachable by id.
  "perspective-crop": perspectiveCropTool,
  "content-aware-move": contentAwareMoveTool,
  remove: removeTool,
  "erase-to-history": eraseToHistoryTool,
  // Selection flyouts
  "marquee-ellipse": marqueeEllipseTool,
  "lasso-poly": polyLassoTool,
  "magnetic-lasso": magneticLassoTool,
  "quick-selection": quickSelectionTool,
  "object-selection": objectSelectionTool,
  "path-select": pathSelectionTool,     // actual tool id
  "path-selection": pathSelectionTool,  // PS-style alias (A key = Path Selection)
  "direct-selection": directSelectionTool, // PS: Shift+A = Direct Selection
  // Brush flyouts (B + Shift)
  pencil: pencilTool,
  "mixer-brush": mixerBrushTool,
  "color-replacement": colorReplacementTool,
  // Eraser flyouts (E + Shift)
  "background-eraser": backgroundEraserTool,
  "magic-eraser": magicEraserTool,
  // Clone Stamp flyouts (S + Shift)
  "pattern-stamp": patternStampTool,
  // History Brush flyouts (Y + Shift)
  "history-brush": historyBrushTool,
  "art-history-brush": artHistoryBrushTool,
  // Spot Healing flyouts (J + Shift)
  "spot-healing": spotHealingTool,
  "healing-brush": healingBrushTool,
  patch: patchTool,
  "red-eye": redEyeTool,
  // Eyedropper flyouts (I + Shift)
  "color-sampler": colorSamplerTool,
  ruler: rulerTool,
  note: noteTool,
  count: countTool,
  // Shape flyouts (U + Shift)
  "shape-ellipse": ellipseShapeTool,
  "shape-polygon": polygonShapeTool,
  "shape-rounded-rect": roundedRectShapeTool,
  "shape-line": lineShapeTool,
  "shape-triangle": triangleShapeTool,
  "shape-custom": customShapeTool2,
  // Dodge/Burn flyouts (O + Shift): sponge mode lives on dodgeBurnTool itself
  // (see the O-key cycle) — the standalone SpongeTool class was dead (i172).
  // Retouch (Blur/Sharpen/Smudge)
  retouch: retouchTool,
  // Crop flyouts (C + Shift)
  slice: sliceTool,
  "slice-select": sliceSelectTool,
  // Type flyouts (T + Shift)
  "type-vertical": verticalTypeTool,
  "type-mask-horizontal": horizontalTypeMaskTool,
  "type-mask-vertical": verticalTypeMaskTool,
  // Type / Gradient / Paint Bucket / Pen â€” id routes for flyout discovery.
  type: typeTool,
  gradient: gradientTool,
  "paint-bucket": paintBucketTool,
  pen: penTool,
  frame: frameTool,
};

// Shortcut â†’ tool-rail button (for highlight feedback, includes stubs).
const railButtons = new Map<string, HTMLButtonElement>();
document.querySelectorAll<HTMLButtonElement>(".tool").forEach((btn) => {
  const key = btn.getAttribute("data-shortcut");
  if (key) railButtons.set(key, btn);
});

state.setActiveTool(moveTool);

// Canvas mount
const canvas = document.getElementById("stage") as HTMLCanvasElement;
const viewportEl = document.querySelector(".canvas-viewport") as HTMLElement;
const renderer = new Canvas2DRenderer(canvas);

// Wire the Eyedropper "Sample All Layers" composite reader. EyedropperTool
// stays DOM-free; the actual canvas read happens here. Issue #14.
eyedropperTool.compositeReader = (docPt, n) => {
  const vp = state.viewport;
  // doc→screen(CSS) must be docPt*zoom + pan (docToScreen), NOT (docPt-pan)*zoom.
  // The old formula produced negative coords for a centered doc, so the read
  // always missed the canvas and Sample-All-Layers returned null. (audit i36)
  const s = docToScreen(vp, docPt.x, docPt.y);
  const cssX = s.x;
  const cssY = s.y;
  const cctx = canvas.getContext("2d");
  if (!cctx) return null;
  const dpr = window.devicePixelRatio || 1;
  if (n === 0) {
    const devX = Math.floor(cssX * dpr);
    const devY = Math.floor(cssY * dpr);
    if (devX < 0 || devY < 0 || devX >= canvas.width || devY >= canvas.height) return null;
    try {
      const pixel = cctx.getImageData(devX, devY, 1, 1).data;
      if (pixel[3] === 0) return null;
      return { r: pixel[0]!, g: pixel[1]!, b: pixel[2]! };
    } catch { return null; }
  }
  // NxN average
  const size = n * 2 + 1;
  const devX = Math.floor(cssX * dpr) - n;
  const devY = Math.floor(cssY * dpr) - n;
  try {
    const imgData = cctx.getImageData(devX, devY, size, size);
    let rSum = 0, gSum = 0, bSum = 0, count = 0;
    for (let i = 0; i < imgData.data.length; i += 4) {
      if (imgData.data[i + 3] === 0) continue;
      rSum += imgData.data[i]!;
      gSum += imgData.data[i + 1]!;
      bSum += imgData.data[i + 2]!;
      count++;
    }
    if (count === 0) return null;
    return {
      r: Math.round(rSum / count),
      g: Math.round(gSum / count),
      b: Math.round(bSum / count),
    };
  } catch { return null; }
};

// Wire ZoomTool viewport-size provider (issue #196).
zoomTool.viewportSizeProvider = () => ({
  width: canvas.clientWidth || canvas.offsetWidth || 800,
  height: canvas.clientHeight || canvas.offsetHeight || 600,
});

// Wire TypeTool / VerticalTypeTool text measurer (issue #196).
{
  const _measureCanvas = document.createElement("canvas");
  const _measureCtx = _measureCanvas.getContext("2d")!;
  const measureTextFn = (text: string, fontCss: string): number => {
    _measureCtx.font = fontCss;
    return _measureCtx.measureText(text).width;
  };
  typeTool.measureText = measureTextFn;
  verticalTypeTool.measureText = measureTextFn;
}

// Wire HorizontalTypeMaskTool / VerticalTypeMaskTool text rasterizer (issue #196).
{
  const textRasterizerFn = (
    docW: number, docH: number, text: string, x: number, y: number,
    fontCss: string, vertical: boolean, charSpacing: number,
  ): Uint8Array => {
    const off = document.createElement("canvas");
    off.width = docW;
    off.height = docH;
    const ctx2d = off.getContext("2d")!;
    ctx2d.fillStyle = "#fff";
    ctx2d.font = fontCss;
    if (!vertical) {
      ctx2d.fillText(text, x, y);
    } else {
      let currentY = y;
      for (const char of text) {
        if (char === "\n") { currentY += charSpacing * 1.25; continue; }
        ctx2d.fillText(char, x, currentY);
        currentY += charSpacing;
      }
    }
    const imageData = ctx2d.getImageData(0, 0, docW, docH);
    const mask = new Uint8Array(docW * docH);
    for (let i = 0; i < docW * docH; i++) {
      mask[i] = (imageData.data[i * 4 + 3] ?? 0) > 128 ? 255 : 0;
    }
    return mask;
  };
  horizontalTypeMaskTool.textRasterizer = textRasterizerFn;
  verticalTypeMaskTool.textRasterizer = textRasterizerFn;
}

// Issue #42 â€” observe Caps Lock state on every keyboard event.
document.addEventListener("keydown", (e) => modSetCapsLockFromEvent(e), { capture: true });
document.addEventListener("keyup", (e) => modSetCapsLockFromEvent(e), { capture: true });

function resize(): void {
  modResize({ viewportEl, renderer, cursorCanvas, state });
}

new ResizeObserver(resize).observe(viewportEl);
window.addEventListener("resize", resize);

// Cursor overlay canvas (must be created BEFORE resize() is called)
const cursorCanvas = document.createElement("canvas");
cursorCanvas.style.position = "absolute";
cursorCanvas.style.top = "0";
cursorCanvas.style.left = "0";
cursorCanvas.style.pointerEvents = "none";
cursorCanvas.style.zIndex = "10";
canvas.parentElement?.appendChild(cursorCanvas);
const optionsBarEl = document.querySelector<HTMLElement>(".optionsbar");
if (!optionsBarEl) throw new Error("Options bar element not found");
const toolUx = new ToolUX(optionsBarEl, cursorCanvas, state);
let lastToolId: string | null = null;
resize(); // Initial size now that cursorCanvas exists

// ----- Character Panel -----
const charPanelContainer = document.getElementById("character-panel-container");
const charPanelBody = document.getElementById("character-panel-body");
let charPanel: CharacterPanel | null = null;
let paraPanel: ParagraphPanel | null = null;

function showCharPanel(): void {
  if (charPanelContainer) charPanelContainer.style.display = "";
  if (!charPanel && charPanelBody) {
    charPanel = new CharacterPanel((props) => {
      const doc = state.doc;
      if (!doc?.activeLayerId) return;
      const layer = findLayer(doc.layers, doc.activeLayerId);
      if (!isTypeLayer(layer)) return;
      const patches: Record<string, unknown> = {};
      if (props.fontFamily !== layer.fontFamily) patches.fontFamily = props.fontFamily;
      // Per-character font-size overrides (audit i17): when the in-canvas
      // text editor is open on this exact layer with a non-empty selection,
      // apply the new size to just that character range (via TypeLayer's
      // applyFontSizeToRuns) instead of the whole layer. There's no
      // equivalent per-run helper yet for fontFamily/color, so those still
      // apply layer-wide below.
      if (props.fontSize !== layer.fontSize) {
        const sel = textEditor.getEditingLayerId() === layer.id ? textEditor.getSelection() : null;
        const runs = sel && sel.start !== sel.end
          ? applyFontSizeToRuns(layer.text, layer.textRuns, sel.start, sel.end, props.fontSize)
          : null;
        if (runs) {
          patches.textRuns = runs;
        } else {
          patches.fontSize = props.fontSize;
        }
      }
      if (props.bold !== (layer.fontWeight > 400 && !layer.fauxBold)) patches.fontWeight = props.bold ? 700 : 400;
      if (props.italic !== (layer.fontStyle === "italic" && !layer.fauxItalic)) patches.fontStyle = props.italic ? "italic" : "normal";
      if (props.underline !== layer.underline) patches.underline = props.underline;
      if (props.strikethrough !== layer.strikethrough) patches.strikethrough = props.strikethrough;
      if (props.align !== layer.textAlign) patches.textAlign = props.align;
      if (props.fauxBold !== layer.fauxBold) patches.fauxBold = props.fauxBold;
      if (props.fauxItalic !== layer.fauxItalic) patches.fauxItalic = props.fauxItalic;
      if (props.allCaps !== layer.allCaps) patches.allCaps = props.allCaps;
      if (props.smallCaps !== layer.smallCaps) patches.smallCaps = props.smallCaps;
      if (props.superscript !== layer.superscript) patches.superscript = props.superscript;
      if (props.subscript !== layer.subscript) patches.subscript = props.subscript;
      if (props.antiAlias !== layer.antiAlias) patches.antiAlias = props.antiAlias;
      const c = props.color;
      if (layer.color.r !== c.r || layer.color.g !== c.g || layer.color.b !== c.b) {
        patches.color = { r: c.r, g: c.g, b: c.b, a: 1 };
      }
      if (props.leading !== layer.leading) patches.leading = props.leading;
      if (props.tracking !== layer.tracking) patches.tracking = props.tracking;
      if (props.baselineShift !== (layer as any).baselineShift) patches.baselineShift = props.baselineShift;
      if (Object.keys(patches).length > 0) {
        state.execute(new SetLayerPropertyCommand(layer.id, patches));
      }
    });
    charPanel.mount(charPanelBody);
  }
}

function showParaPanel(): void {
  if (charPanelContainer) charPanelContainer.style.display = "";
  if (!paraPanel && charPanelBody) {
    paraPanel = new ParagraphPanel((props) => {
      const doc = state.doc;
      if (!doc?.activeLayerId) return;
      const layer = findLayer(doc.layers, doc.activeLayerId);
      if (!isTypeLayer(layer)) return;
      const patches: Record<string, unknown> = {};
      if (props.align !== layer.textAlign) patches.textAlign = props.align;
      // i100: Paragraph panel's Left/Right/First-Line Indent + Space
      // Before/After — these were dropped entirely before, only `align` made
      // it into the patch.
      if (props.leftIndent !== (layer.leftIndent ?? 0)) patches.leftIndent = props.leftIndent;
      if (props.rightIndent !== (layer.rightIndent ?? 0)) patches.rightIndent = props.rightIndent;
      if (props.firstLineIndent !== (layer.firstLineIndent ?? 0)) patches.firstLineIndent = props.firstLineIndent;
      if (props.spaceBefore !== (layer.spaceBefore ?? 0)) patches.spaceBefore = props.spaceBefore;
      if (props.spaceAfter !== (layer.spaceAfter ?? 0)) patches.spaceAfter = props.spaceAfter;
      if (Object.keys(patches).length > 0) {
        state.execute(new SetLayerPropertyCommand(layer.id, patches));
      }
    });
    paraPanel.mount(charPanelBody);
  }
}

function hideCharPanel(): void {
  if (charPanelContainer) charPanelContainer.style.display = "none";
}

function syncCharPanel(): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) { hideCharPanel(); return; }
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (!isTypeLayer(layer)) { hideCharPanel(); return; }
  showCharPanel();
  if (charPanel) {
    charPanel.setProps({
      fontFamily: layer.fontFamily,
      fontStyle: layer.fontStyle === "italic" ? "Italic" : "Regular",
      fontSize: layer.fontSize,
      leading: layer.leading,
      tracking: layer.tracking,
      baselineShift: (layer as any).baselineShift ?? 0,
      color: { r: layer.color.r, g: layer.color.g, b: layer.color.b },
      bold: layer.fontWeight > 400 && !layer.fauxBold,
      italic: layer.fontStyle === "italic" && !layer.fauxItalic,
      underline: layer.underline,
      strikethrough: layer.strikethrough,
      align: layer.textAlign,
      fauxBold: layer.fauxBold ?? false,
      fauxItalic: layer.fauxItalic ?? false,
      allCaps: layer.allCaps ?? false,
      smallCaps: layer.smallCaps ?? false,
      superscript: layer.superscript ?? false,
      subscript: layer.subscript ?? false,
      antiAlias: layer.antiAlias ?? "smooth",
    });
  }
}

function syncPropertiesPanel(): void {
  if (!propertiesPanel || !state.doc) return;
  const activeLayer = state.doc.activeLayerId
    ? findLayer(state.doc.layers, state.doc.activeLayerId)
    : null;
  propertiesPanel.update(state.doc, activeLayer);
}

// Wire paragraph tab
document.querySelectorAll<HTMLButtonElement>("#character-panel-container .panel-tab").forEach((tab) => {
  tab.addEventListener("click", () => {
    const header = tab.parentElement;
    if (!header) return;
    header.querySelectorAll(".panel-tab").forEach((t) => t.classList.remove("is-active"));
    tab.classList.add("is-active");
    if (tab.textContent?.trim() === "Paragraph") {
      if (charPanel) { charPanel.destroy(); charPanel = null; }
      syncParaPanel();
    } else {
      if (paraPanel) { paraPanel.destroy(); paraPanel = null; }
      syncCharPanel();
    }
  });
});

function syncParaPanel(): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (!isTypeLayer(layer)) return;
  showParaPanel();
  if (paraPanel) {
    paraPanel.setProps({
      align: layer.textAlign,
      leftIndent: layer.leftIndent ?? 0,
      rightIndent: layer.rightIndent ?? 0,
      firstLineIndent: layer.firstLineIndent ?? 0,
      spaceBefore: layer.spaceBefore ?? 0,
      spaceAfter: layer.spaceAfter ?? 0,
    });
  }
}

// ----- Paths Panel -----
let pathsPanel: PathsPanel | null = null;

function initPathsPanel(): void {
  if (!pathsPanel) {
    pathsPanel = new PathsPanel();
    pathsPanel.init(state);
  }
}

function syncPathsPanel(): void {
  initPathsPanel();
  // init() subscribes to state changes and re-renders reactively, but an
  // explicit render() here too means the list is guaranteed fresh the
  // moment the user opens the Paths tab/menu item, even before the next
  // state change fires.
  pathsPanel?.render();
}

// Boot-time init: wires up the Paths panel's listEl/render/bindEvents and
// its state.subscribe() as soon as the DOM (and `state`) are ready, so
// PenTool-committed work paths show up even if the user never explicitly
// opens the Paths tab first (closes i31).
initPathsPanel();

// ----- Floating Panels -----
let navigatorPanel: NavigatorPanel | null = null;
let infoPanel: InfoPanel | null = null;
let histogramPanel: HistogramPanel | null = null;
let swatchesPanel: SwatchesPanel | null = null;
let brushSettingsPanel: BrushSettingsPanel | null = null;
let brushSettingsPanelOverlay: HTMLElement | null = null;
let gradientEditor: GradientEditor | null = null;
let patternEditor: PatternEditor | null = null;
let stylesPanel: StylesPanel | null = null;
let propertiesPanel: PropertiesPanel | null = null;
let adjustmentsPanel: AdjustmentsPanel | null = null;
let shapesPanel: ShapesPanel | null = null;
let cloneSourcePanel: CloneSourcePanel | null = null;

// i124: last on-screen position of each floating panel, keyed by title, so
// reopening (e.g. Window > Navigator again after closing it) restores where
// the user last left it instead of resetting to the default cascade spot.
const floatingPanelLastPos = new Map<string, { left: number; top: number }>();
let floatingPanelCascadeIndex = 0;

function createFloatingPanel(
  title: string,
  content: HTMLElement,
  width = 280,
  height = 300,
  onClose?: () => void,
): HTMLElement {
  // i124: Navigator/Info/Histogram/Swatches/Brush Settings/etc. are real
  // floating (non-modal) panels in Photoshop — they must NOT block the rest
  // of the UI the way a `.filter-dialog` OK/Cancel modal does. This builds a
  // small positioned `.floating-panel` window instead of a full-viewport
  // backdrop overlay; see the CSS comment above `.floating-panel` for why it
  // is a distinct class from `.filter-dialog` (that class is still used, and
  // still meant to be blocking, by the real modal dialogs elsewhere in this
  // file and by FilterDialog.ts).
  const panel = document.createElement("div");
  panel.className = "floating-panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-label", title);
  panel.style.width = `${width}px`;
  panel.style.maxHeight = `${height}px`;

  const remembered = floatingPanelLastPos.get(title);
  let left: number;
  let top: number;
  if (remembered) {
    left = Math.min(remembered.left, Math.max(0, window.innerWidth - width));
    top = Math.min(remembered.top, Math.max(0, window.innerHeight - 40));
  } else {
    // Cascade successive first-time opens so multiple panels don't stack
    // exactly on top of one another.
    const cascade = (floatingPanelCascadeIndex++ % 8) * 24;
    left = Math.max(8, window.innerWidth - width - 24 - cascade);
    top = 56 + cascade;
  }
  panel.style.left = `${left}px`;
  panel.style.top = `${top}px`;

  panel.innerHTML = `
    <div class="floating-panel__title">
      <span class="floating-panel__title-text">${escapeHtml(title)}</span>
      <button class="floating-panel__close" aria-label="Close ${escapeHtml(title)} panel" title="Close">Ã—</button>
    </div>
    <div class="floating-panel__body"></div>
  `;
  const body = panel.querySelector(".floating-panel__body")!;
  body.appendChild(content);
  // #i32: floating panels (Navigator, Info, Histogram, Swatches, Gradient
  // Editor, Pattern Editor, Styles, Properties, Adjustments) must reset
  // their module-level ref on close, or open*Panel()'s re-entry guard
  // (`if (panelVar) return;`) permanently blocks reopening. `onClose` is
  // the single shared mechanism: it fires exactly once no matter which
  // path triggered the close.
  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    floatingPanelLastPos.set(title, { left: panel.offsetLeft, top: panel.offsetTop });
    panel.remove();
    onClose?.();
  };
  panel.querySelector(".floating-panel__close")?.addEventListener("click", close);

  // i124: drag the panel by its title bar, Photoshop-style, instead of it
  // being pinned to one spot for its whole lifetime.
  const titleBar = panel.querySelector<HTMLElement>(".floating-panel__title")!;
  let dragging = false;
  let dragStartX = 0;
  let dragStartY = 0;
  let startLeft = 0;
  let startTop = 0;
  titleBar.addEventListener("pointerdown", (e: PointerEvent) => {
    if ((e.target as HTMLElement).closest(".floating-panel__close")) return;
    dragging = true;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
    startLeft = panel.offsetLeft;
    startTop = panel.offsetTop;
    titleBar.setPointerCapture?.(e.pointerId);
  });
  titleBar.addEventListener("pointermove", (e: PointerEvent) => {
    if (!dragging) return;
    const newLeft = Math.min(Math.max(0, startLeft + (e.clientX - dragStartX)), window.innerWidth - 40);
    const newTop = Math.min(Math.max(0, startTop + (e.clientY - dragStartY)), window.innerHeight - 24);
    panel.style.left = `${newLeft}px`;
    panel.style.top = `${newTop}px`;
  });
  const endDrag = (e: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    titleBar.releasePointerCapture?.(e.pointerId);
  };
  titleBar.addEventListener("pointerup", endDrag);
  titleBar.addEventListener("pointercancel", endDrag);

  document.body.appendChild(panel);
  return panel;
}

function openNavigatorPanel(): void {
  if (navigatorPanel) return;
  navigatorPanel = new NavigatorPanel();
  navigatorPanel.setCallback((panX, panY, zoom) => {
    if (!state.doc) return;
    const r = canvas.getBoundingClientRect();
    state.setViewport({
      zoom,
      panX: panX + r.width / 2 - (state.doc.width * zoom) / 2,
      panY: panY + r.height / 2 - (state.doc.height * zoom) / 2,
    });
  });
  const container = document.createElement("div");
  navigatorPanel.mount(container);
  createFloatingPanel("Navigator", container, 280, 250, () => {
    navigatorPanel?.destroy();
    navigatorPanel = null;
  });
  updateNavigatorPanel();
}

function updateNavigatorPanel(): void {
  if (!navigatorPanel || !state.doc) return;
  const r = canvas.getBoundingClientRect();
  // Create a thumbnail from the current canvas
  navigatorPanel.render(canvas, state.doc.width, state.doc.height, state.viewport, r.width, r.height);
}

function openInfoPanel(): void {
  if (infoPanel) return;
  infoPanel = new InfoPanel();
  const container = document.createElement("div");
  infoPanel.mount(container);
  createFloatingPanel("Info", container, 220, 320, () => {
    infoPanel?.destroy();
    infoPanel = null;
  });
  updateInfoPanel();
}

function updateInfoPanel(): void {
  if (!infoPanel) return;
  const data: any = {};
  if (lastDocPos) {
    data.cursorPos = { x: lastDocPos.x, y: lastDocPos.y };
  }
  if (lastSampledColor) {
    data.cursorColor = lastSampledColor;
  }
  if (state.doc?.selection && !state.doc.selection.isEmpty()) {
    const bounds = state.doc.selection.bounds();
    if (bounds) {
      data.selection = { w: bounds.w, h: bounds.h };
    }
  }
  if (state.doc) {
    data.document = {
      width: state.doc.width,
      height: state.doc.height,
      dpi: state.doc.dpi || 72,
      mode: "RGB/8",
    };
  }
  // i137: feed the Color Sampler readouts (index + swatch + RGB per sampler) to
  // the Info panel. Persistent regardless of the active tool (Photoshop keeps
  // placed samplers listed); null clears the section when there are none.
  data.colorSamplers = colorSamplerTool.samplers.length > 0 ? colorSamplerTool.samplers : null;
  if (Object.keys(data).length > 0) {
    infoPanel.update(data);
  }
}

let lastDocPos: { x: number; y: number } | null = null;
let lastSampledColor: { r: number; g: number; b: number } | null = null;

function openHistogramPanel(): void {
  if (histogramPanel) return;
  histogramPanel = new HistogramPanel();
  const container = document.createElement("div");
  histogramPanel.mount(container);
  createFloatingPanel("Histogram", container, 280, 300, () => {
    histogramPanel?.destroy();
    histogramPanel = null;
  });
  updateHistogramPanel();
}

function updateHistogramPanel(): void {
  if (!histogramPanel || !state.doc) return;
  const layer = getActiveRaster();
  if (!layer) return;
  const mask = state.doc.selection && !state.doc.selection.isEmpty() ? state.doc.selection.mask : undefined;
  histogramPanel.update(layer.pixels.data as unknown as ImageData, mask);
}

function openSwatchesPanel(): void {
  if (swatchesPanel) return;
  swatchesPanel = new SwatchesPanel();
  swatchesPanel.setCallbacks({
    onFgColorChange: (color) => state.setForegroundColor(color),
    onBgColorChange: (color) => state.setBackgroundColor(color),
  });
  const container = document.createElement("div");
  swatchesPanel.mount(container);
  createFloatingPanel("Swatches", container, 260, 350, () => {
    swatchesPanel?.destroy();
    swatchesPanel = null;
  });
}

// i127: the DOCKED Swatches panel-tab (next to Color/Gradients, under
// #swatches-panel-container) is a separate SwatchesPanel instance from the
// floating Window > Swatches one above — same class, same callbacks, but its
// own lifetime so opening/closing the floating panel never disturbs the
// docked one and vice versa. Lazily created on first use, mirroring
// initPathsPanel()'s idempotent-init pattern.
let dockedSwatchesPanel: SwatchesPanel | null = null;

function initDockedSwatchesPanel(): void {
  if (dockedSwatchesPanel) return;
  const container = document.getElementById("swatches-panel-container");
  if (!container) return;
  dockedSwatchesPanel = new SwatchesPanel();
  dockedSwatchesPanel.setCallbacks({
    onFgColorChange: (color) => state.setForegroundColor(color),
    onBgColorChange: (color) => state.setBackgroundColor(color),
  });
  dockedSwatchesPanel.mount(container);
}

// i72: the tool whose `.settings` the F5 Brush Settings panel should read
// from / write to. Duck-typed (checks for a numeric `size`/`hardness`,
// matching the shared core/pixel/brushSettings.ts BrushSettings shape) so it
// covers Brush/Eraser/History Brush/Mixer Brush/Art History Brush/Color
// Replacement without needing an exhaustive tool list; falls back to
// brushTool for tools with no compatible settings object (e.g. Pencil, which
// keeps a plain `size` field rather than a `.settings` object).
function activeBrushLikeToolSettings(): Record<string, unknown> {
  const s = (state.activeTool as any)?.settings;
  if (s && typeof s.size === "number" && typeof s.hardness === "number") return s;
  return brushTool.settings as unknown as Record<string, unknown>;
}

/** Seed the panel-shape (0-100 percentages) settings from a tool's own settings object (0-1 fractions). */
function toolSettingsToPanelSettings(s: Record<string, unknown>): Partial<BrushSettings> {
  const out: Partial<BrushSettings> = {};
  if (typeof s["size"] === "number") out.size = s["size"] as number;
  if (typeof s["hardness"] === "number") out.hardness = (s["hardness"] as number) * 100;
  if (typeof s["spacing"] === "number") out.spacing = (s["spacing"] as number) * 100;
  if (typeof s["airbrush"] === "boolean") out.airbrushMode = s["airbrush"] as boolean;
  if (typeof s["wetEdges"] === "boolean") out.wetEdges = s["wetEdges"] as boolean;
  if (typeof s["sizeJitter"] === "number") out.sizeJitter = (s["sizeJitter"] as number) * 100;
  if (typeof s["angleJitter"] === "number") out.angleJitter = (s["angleJitter"] as number) * 100;
  if (typeof s["roundnessJitter"] === "number") out.roundnessJitter = (s["roundnessJitter"] as number) * 100;
  if (typeof s["scatter"] === "number") out.scatterAmount = (s["scatter"] as number) * 100;
  if (typeof s["countJitter"] === "number") out.countJitter = (s["countJitter"] as number) * 100;
  if (typeof s["fgBgJitter"] === "number") out.fgBgJitter = (s["fgBgJitter"] as number) * 100;
  if (typeof s["hueJitter"] === "number") out.hueJitter = (s["hueJitter"] as number) * 100;
  if (typeof s["opacityJitter"] === "number") out.opacityJitter = (s["opacityJitter"] as number) * 100;
  if (typeof s["flowJitter"] === "number") out.flowJitter = (s["flowJitter"] as number) * 100;
  return out;
}

function openBrushSettingsPanel(): void {
  // F5 in Photoshop toggles the Brush Settings panel. If already open, close it.
  if (brushSettingsPanelOverlay && document.body.contains(brushSettingsPanelOverlay)) {
    brushSettingsPanelOverlay.remove();
    brushSettingsPanelOverlay = null;
    brushSettingsPanel = null;
    return;
  }
  brushSettingsPanel = new BrushSettingsPanel();
  // i72: seed from the active brush-like tool's own settings instead of
  // always showing brushTool's (stale) numbers regardless of what's active.
  brushSettingsPanel.setSettings(toolSettingsToPanelSettings(activeBrushLikeToolSettings()));
  brushSettingsPanel.setCallback((settings) => {
    applyBrushSettings(settings);
  });
  const container = document.createElement("div");
  brushSettingsPanel.mount(container);
  brushSettingsPanelOverlay = createFloatingPanel("Brush Settings", container, 280, 450);
  // When user closes via X or outside-click, clear our refs.
  const observer = new MutationObserver(() => {
    if (brushSettingsPanelOverlay && !document.body.contains(brushSettingsPanelOverlay)) {
      brushSettingsPanelOverlay = null;
      brushSettingsPanel = null;
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true });
}

function applyBrushSettings(settings: BrushSettings): void {
  // i72: apply to whichever brush-like tool is actually active, not always
  // brushTool — otherwise adjusting settings while e.g. Eraser was active
  // silently edited the (inactive) Brush tool's settings instead.
  const target = activeBrushLikeToolSettings();
  target["size"] = settings.size;
  target["hardness"] = settings.hardness / 100;
  if ("spacing" in target) target["spacing"] = settings.spacing / 100;
  if ("airbrush" in target) target["airbrush"] = settings.airbrushMode;
  if ("wetEdges" in target) target["wetEdges"] = settings.wetEdges;
  if ("sizeJitter" in target) target["sizeJitter"] = settings.sizeJitter / 100;
  if ("angleJitter" in target) target["angleJitter"] = settings.angleJitter / 100;
  if ("roundnessJitter" in target) target["roundnessJitter"] = settings.roundnessJitter / 100;
  if ("scatter" in target) target["scatter"] = settings.scatterAmount / 100;
  if ("countJitter" in target) target["countJitter"] = settings.countJitter / 100;
  if ("fgBgJitter" in target) target["fgBgJitter"] = settings.fgBgJitter / 100;
  if ("hueJitter" in target) target["hueJitter"] = settings.hueJitter / 100;
  if ("opacityJitter" in target) target["opacityJitter"] = settings.opacityJitter / 100;
  if ("flowJitter" in target) target["flowJitter"] = settings.flowJitter / 100;
}

function openGradientEditor(): void {
  if (gradientEditor) return;
  gradientEditor = new GradientEditor();
  gradientEditor.setCallback(() => {
    // audit i12: this used to stash `(gradientTool as any)._customGradient`,
    // a field GradientTool never declares or reads (it reads `this.customStops`,
    // populated only via `setCustomStops()`). GradientEditor's internal model
    // also uses 0..100 stop positions vs GradientTool's 0..1 — bridge the two
    // via GradientEditor.exportStopSet(), which already does that conversion.
    gradientTool.setCustomStops(gradientEditor!.exportStopSet());
  });
  const container = document.createElement("div");
  gradientEditor.mount(container);
  createFloatingPanel("Gradient Editor", container, 300, 400, () => {
    gradientEditor?.destroy();
    gradientEditor = null;
  });
}

function openPatternEditor(): void {
  if (patternEditor) return;
  patternEditor = new PatternEditor();
  patternEditor.setCallback((pattern) => {
    if (pattern) {
      patternStampTool.pattern = {
        pixels: new Uint8ClampedArray(pattern.data),
        width: pattern.width,
        height: pattern.height,
      };
      // i184: refresh the Pattern Stamp options bar so its preview swatch
      // reflects the newly-chosen pattern.
      toolUx.refreshOptions();
    }
  });
  const container = document.createElement("div");
  patternEditor.mount(container);
  createFloatingPanel("Pattern Editor", container, 280, 400, () => {
    patternEditor?.destroy();
    patternEditor = null;
  });
}

function openStylesPanel(): void {
  if (stylesPanel) return;
  stylesPanel = new StylesPanel();
  stylesPanel.setCallbacks({
    onApply: (styles) => {
      const doc = state.doc;
      if (!doc?.activeLayerId) return;
      const layer = findLayer(doc.layers, doc.activeLayerId);
      if (!layer) return;
      state.execute(new SetLayerPropertyCommand(doc.activeLayerId, { styles }));
      showTip(`Style applied to "${layer.name}".`);
    },
    onSave: () => {
      const doc = state.doc;
      if (!doc?.activeLayerId) return null;
      const layer = findLayer(doc.layers, doc.activeLayerId);
      if (!layer) return null;
      return (layer as any).styles || null;
    },
  });
  const container = document.createElement("div");
  stylesPanel.mount(container);
  createFloatingPanel("Styles", container, 280, 350, () => {
    stylesPanel?.destroy();
    stylesPanel = null;
  });
}

// i84: Window > Clone Source previously wired straight to comingSoon(). The
// panel body itself (CloneSourcePanel.ts) is a real, fully-interactive panel
// now (the other agent's parallel fix) — this gives it a real floating-panel
// opener (matching every other Window-menu panel's mount/destroy convention),
// wired to the live cloneStampTool instance so "Set Source Here" can read its
// getSourcePoint() (per CloneSourcePanel.ts's own doc comment on what it
// still needs from the host).
function openCloneSourcePanel(): void {
  if (cloneSourcePanel) return;
  cloneSourcePanel = new CloneSourcePanel(cloneStampTool);
  const container = document.createElement("div");
  cloneSourcePanel.mount(container);
  createFloatingPanel("Clone Source", container, 240, 320, () => {
    cloneSourcePanel?.destroy();
    cloneSourcePanel = null;
  });
}

function openPropertiesPanel(): void {
  if (propertiesPanel) return;
  propertiesPanel = new PropertiesPanel();
  propertiesPanel.setCallbacks({
    onPatch: (layerId, props) => {
      state.execute(new SetLayerPropertyCommand(layerId, props));
    },
    onDocPatch: (props) => {
      if (!state.doc) return;
      if (props.name) {
        const oldName = state.doc.name;
        const newName = props.name as string;
        // #199: route through RenameDocumentCommand so rename is undoable.
        state.execute(new RenameDocumentCommand(newName));
        // Migrate the per-document history to the new name (#183).
        state.migrateDocumentHistory(oldName, newName);
        // Update the open-documents registry so the tab shows the new name.
        // i44: look it up by id (stable across the rename -- RenameDocumentCommand
        // spreads `...doc`, so `state.doc.id` is unchanged), not by the old name,
        // so this doesn't accidentally rewrite a DIFFERENT open document that
        // merely used to share this one's old display name.
        const odIdx = openDocuments.findIndex(d => d.id === state.doc?.id);
        if (odIdx >= 0) {
          openDocuments[odIdx] = { ...openDocuments[odIdx]!, name: newName };
        }
        updateDocTab();
      }
      if (props.width || props.height || props.dpi) {
        showTip("Document properties updated (resize requires Image Size dialog for full effect).");
      }
    },
  });
  const container = document.createElement("div");
  propertiesPanel.mount(container);
  createFloatingPanel("Properties", container, 260, 400, () => {
    propertiesPanel?.destroy();
    propertiesPanel = null;
  });
}

function openAdjustmentsPanel(): void {
  if (adjustmentsPanel) return;
  adjustmentsPanel = new AdjustmentsPanel();
  adjustmentsPanel.setCallback((kind) => {
    addAdjustmentLayer(kind);
  });
  const container = document.createElement("div");
  adjustmentsPanel.mount(container);
  createFloatingPanel("Adjustments", container, 280, 320, () => {
    adjustmentsPanel?.destroy();
    adjustmentsPanel = null;
  });
}

// audit i91 / #269: Shapes panel — bind it to the live CustomShapeTool
// instance (customShapeTool2, id "shape-custom") so clicking a preset here
// sets `customShapeTool2.preset` directly (ShapesPanel already does this
// itself when constructed with a tool). We also listen for its bubbling
// "pixelpa:shape-preset-selected" event so the options-bar Shape preset
// <select> (ShapeToolOptions' `.shape-custompreset-input`, i74's
// refreshOptions() path) re-reads the new preset instead of going stale.
function openShapesPanel(): void {
  if (shapesPanel) return;
  shapesPanel = new ShapesPanel(customShapeTool2);
  const container = document.createElement("div");
  shapesPanel.mount(container);
  container.addEventListener("pixelpa:shape-preset-selected", () => {
    toolUx.refreshOptions();
  });
  createFloatingPanel("Shapes", container, 260, 340, () => {
    shapesPanel?.destroy();
    shapesPanel = null;
  });
}

// Marching-ants animation: when a selection exists, the renderer schedules
// rAFs and calls back to trigger the next frame.
renderer.setAntsAnimationCallback(() => state.requestRender());

// Coalesce subscriber updates into a single rAF tick. Fast drags emit
// many notifies per frame; doing the full update on each is wasteful.
let frameScheduled = false;
let _rafPaused = false;
function scheduleFrame(): void {
  if (_rafPaused) return;
  if (frameScheduled) return;
  frameScheduled = true;
  requestAnimationFrame(() => {
    frameScheduled = false;
    // audit i104 follow-up (#270): updateOverlay() must run BEFORE
    // renderer.render() within the same tick. renderer.render() paints using
    // whatever overlay setOverlay() last received — if updateOverlay() ran
    // afterward (as it did previously, last in this callback), every
    // discrete, non-dragged overlay change (e.g. Perspective Crop's 4th
    // corner placement, which has no further pointermove to "catch up" on
    // the next tick) would compute the fresh overlay one tick too late to
    // ever actually get drawn, since nothing schedules a further render to
    // consume it. Overlays driven by continuous dragging (cropRect,
    // transformBBox) mostly masked this by re-triggering every pointermove.
    updateOverlay();
    renderer.render(state.doc, state.viewport);
    updateStatusBar();
    updateDocTab();
    updateSwatches();
    syncColorPanel();
    renderLayersPanel();
    syncLayerControls();
    syncCharPanel();
    syncPropertiesPanel();
    renderChannelsPanel();
    // i10: default the History Brush / Art History Brush source to the
    // initial/open state the first time a document is seen, independent of
    // whether the History panel has ever been opened. Cheap (Map lookup) —
    // safe to call every frame.
    modEnsureHistorySourceDefault({ state, onSetSource: setHistorySource });
    updateDropHint();
    updateCursor();
    updateNavigatorPanel();
    updateHistogramPanel();
    const active = state.activeTool;
    if (active && active.id !== lastToolId) {
      lastToolId = active.id;
      toolUx.setTool(active.id, active);
      // i152/i157: keep the rail button's own title/aria-label (and the
      // "last variant activated for this slot" memory used by the plain-
      // click handler above) in sync with whichever specific flyout variant
      // is actually active, covering every activation path (flyout menu,
      // keyboard cycling, direct API) through this one choke point instead
      // of touching each FLYOUT_GROUPS activate() call individually.
      const railInfo = RAIL_VARIANT_LABEL.get(active);
      if (railInfo) {
        highlightToolByShortcut(railInfo.key, railInfo.label);
        lastForKey[railInfo.key] = active;
      }
    }
    toolUx.drawCursorInternal();
    if (showRulers) updateRulers();
  });
}
state.subscribe(scheduleFrame);
// Boot-time render: clears static placeholders (phantom layers / Untitled tab) in
// index.html before any user interaction. Without this, renderPanel + updateDocTab
// are only triggered by state changes, but on a cold load with no doc, no state
// change fires. Closes #50, #121.
scheduleFrame();

// ----- Rulers, Guides, Grid, Snap -----
import type { Guide } from "./core/render/Canvas2DRenderer";
import { screenToGuidePos, isOverRuler } from "./app/guideMath";
import { buildSnapTargets, buildLayerEdgeTargets, snapPosition, type SnapSettings } from "./app/ui/SnapTo";

let showRulers = true;
let showGuides = true;
let showExtras = true;
let showGrid = false;
let gridSpacing = 50;
let snapToGuides = false;
let snapToGrid = false;
let showSmartGuides = true;
let showPixelGrid = true;
let showLayerEdges = false;
let showSelectionEdges = true;
let showTargetPath = true;
let showNotes = true;
let colorPanelInstance: ColorPanel | null = null;
let proofColorsEnabled = false;
let gamutWarningEnabled = false;
const guides: Guide[] = [];
/** ICC profile bytes from the most-recently opened PSD, preserved for re-export. */
let currentIccProfile: Uint8Array | undefined;

// Ruler elements
let rulerH: HTMLCanvasElement | null = null;
let rulerV: HTMLCanvasElement | null = null;
let rulerCorner: HTMLDivElement | null = null;

// i112: ruler measurement unit — seeded from the persisted Preferences
// value (if any) so a unit chosen in Edit > Preferences > Units & Rulers
// takes effect on load; the ruler's own right-click menu (below) is the
// live way to change it thereafter, since PreferencesDialog.ts exposes no
// change-notification hook for main.ts to subscribe to.
let rulerUnit: RulerUnit = loadPreferences().rulerUnit;

/** Doc-space point the ruler currently treats as "0" for LABEL purposes only
 *  (Photoshop's draggable ruler origin). Guides/pixels are unaffected —
 *  only what the ruler prints at a given screen position changes. Reset via
 *  double-click on the ruler corner, set via drag-from-corner (i112). */
const rulerOrigin = { x: 0, y: 0 };

/**
 * Shared drag state for BOTH ways of repositioning a guide (i113):
 *  - dragging a brand-new guide out of a ruler strip (`index` unset — the
 *    mousemove handler below always edits the last-pushed guide of `kind`)
 *  - dragging an EXISTING guide directly on the canvas via the Move tool
 *    (`index` set to that guide's position in `guides`)
 * `altFlipped` latches so Alt only flips orientation once per press, not
 * every mousemove tick while held.
 */
interface DraggingGuideState {
  kind: "horizontal" | "vertical";
  startPos: number;
  index?: number;
  altFlipped?: boolean;
}
let draggingGuide: DraggingGuideState | null = null;

/**
 * Hit-test screen coordinates (relative to the main canvas) against existing
 * guides, within a small pixel tolerance — used both to grab a guide for
 * dragging and to show a resize-style hover cursor over one (i113).
 */
function findGuideNear(sx: number, sy: number): number {
  if (!state.doc || guides.length === 0) return -1;
  const { panX, panY, zoom } = state.viewport;
  const tolerance = 4;
  let bestIdx = -1;
  let bestDist = Infinity;
  for (let i = 0; i < guides.length; i++) {
    const g = guides[i]!;
    const screenPos = g.position * zoom + (g.kind === "horizontal" ? panY : panX);
    const cursorCoord = g.kind === "horizontal" ? sy : sx;
    const d = Math.abs(cursorCoord - screenPos);
    if (d <= tolerance && d < bestDist) { bestDist = d; bestIdx = i; }
  }
  return bestIdx;
}

function createRulers(): void {
  const viewport = document.querySelector(".canvas-viewport") as HTMLElement;
  if (!viewport) return;

  // The rulers are position:absolute with top/left authored relative to the
  // canvas-viewport's top-left (corner 0,0; h-ruler 20,0; v-ruler 0,20), and
  // updateRulers() sizes them from the viewport's rect. They MUST therefore be
  // children of `.canvas-viewport` (which is position:relative) so they anchor
  // to it. Previously they were inserted into `.canvas-area` (position:static),
  // whose absolute children fall through to <body> — so the rulers rendered at
  // the viewport origin (0,0), overlapping the tool rail (vertical ruler) and
  // the menubar (horizontal ruler). Appending into the viewport (which does not
  // scroll — panning moves #stage, not the scroll offset) places them correctly
  // and keeps them above #stage via their z-index.
  // Corner square (where rulers meet)
  rulerCorner = document.createElement("div");
  rulerCorner.style.cssText = "position:absolute;top:0;left:0;width:20px;height:20px;background:#555;z-index:20;border-right:1px solid #444;border-bottom:1px solid #444;";
  rulerCorner.style.display = showRulers ? "" : "none";
  viewport.appendChild(rulerCorner);

  // Horizontal ruler
  rulerH = document.createElement("canvas");
  rulerH.style.cssText = "position:absolute;top:0;left:20px;height:20px;z-index:15;cursor:crosshair;";
  rulerH.style.display = showRulers ? "" : "none";
  viewport.appendChild(rulerH);

  // Vertical ruler
  rulerV = document.createElement("canvas");
  rulerV.style.cssText = "position:absolute;top:20px;left:0;width:20px;z-index:15;cursor:crosshair;";
  rulerV.style.display = showRulers ? "" : "none";
  viewport.appendChild(rulerV);

  // Drag guide from horizontal ruler (`draggingGuide` is module-level — i113
  // shares this same drag state with the canvas-level existing-guide drag
  // below, so one mousemove/mouseup pair handles both).
  rulerH.addEventListener("mousedown", (e) => {
    if (!state.doc || guidesLocked) return;
    const canvasRect = canvas.getBoundingClientRect();
    const docY = screenToGuidePos("horizontal", e.clientX, e.clientY, canvasRect, state.viewport.panX, state.viewport.panY, state.viewport.zoom);
    draggingGuide = { kind: "horizontal", startPos: docY };
    guides.push({ kind: "horizontal", position: docY });
    state.requestRender();
  });

  rulerV.addEventListener("mousedown", (e) => {
    if (!state.doc || guidesLocked) return;
    const canvasRect = canvas.getBoundingClientRect();
    const docX = screenToGuidePos("vertical", e.clientX, e.clientY, canvasRect, state.viewport.panX, state.viewport.panY, state.viewport.zoom);
    draggingGuide = { kind: "vertical", startPos: docX };
    guides.push({ kind: "vertical", position: docX });
    state.requestRender();
  });

  // i112: right-click either ruler strip to change the displayed unit.
  const rulerUnitLabels: Array<{ value: RulerUnit; label: string }> = [
    { value: "pixels", label: "Pixels" },
    { value: "inches", label: "Inches" },
    { value: "cm", label: "Centimeters" },
    { value: "mm", label: "Millimeters" },
    { value: "points", label: "Points" },
    { value: "picas", label: "Picas" },
    { value: "percent", label: "Percent" },
  ];
  const showRulerUnitMenu = (e: MouseEvent) => {
    e.preventDefault();
    const anchor = document.createElement("div");
    anchor.style.cssText = `position:fixed;left:${e.clientX}px;top:${e.clientY}px;width:0;height:0;`;
    document.body.appendChild(anchor);
    showMenuDropdown(anchor, rulerUnitLabels.map((u) => ({
      label: `${u.value === rulerUnit ? "✓ " : ""}${u.label}`,
      action: () => { rulerUnit = u.value; updateRulers(); },
    })));
    setTimeout(() => anchor.remove(), 0);
  };
  rulerH.addEventListener("contextmenu", showRulerUnitMenu);
  rulerV.addEventListener("contextmenu", showRulerUnitMenu);

  // i112: double-click the corner square to reset the ruler origin back to
  // the document's top-left (0,0); mousedown-drag from the corner sets a
  // custom origin at wherever the drag is released over the canvas. Neither
  // moves guides/pixels — only what the ruler LABELS at a given screen
  // position (drawRuler() subtracts rulerOrigin from the true doc position).
  rulerCorner.addEventListener("dblclick", () => {
    rulerOrigin.x = 0;
    rulerOrigin.y = 0;
    updateRulers();
  });
  let draggingRulerOrigin = false;
  rulerCorner.addEventListener("mousedown", (e) => {
    if (!state.doc) return;
    e.preventDefault();
    draggingRulerOrigin = true;
  });
  document.addEventListener("mouseup", (e) => {
    if (!draggingRulerOrigin) return;
    draggingRulerOrigin = false;
    if (!state.doc) return;
    const canvasRect = canvas.getBoundingClientRect();
    // Only adopt a new origin when the drag was released over the canvas
    // itself — releasing back over the corner (or anywhere else) is a no-op,
    // matching Photoshop (a genuine reset requires the explicit double-click
    // above, not an accidental drag-and-release-in-place).
    if (e.clientX >= canvasRect.left && e.clientX <= canvasRect.right &&
        e.clientY >= canvasRect.top && e.clientY <= canvasRect.bottom) {
      const p = screenToDoc(state.viewport, e.clientX - canvasRect.left, e.clientY - canvasRect.top);
      rulerOrigin.x = p.x;
      rulerOrigin.y = p.y;
      updateRulers();
    }
  });

  document.addEventListener("mousemove", (e) => {
    if (!draggingGuide || !state.doc || guidesLocked) return;
    const canvasRect = canvas.getBoundingClientRect();
    // i113: Alt-drag while repositioning an EXISTING guide (canvas-origin
    // drag, `index` set) flips its orientation, matching Photoshop. Latched
    // via altFlipped so it only flips once per Alt press, not every tick.
    if (draggingGuide.index !== undefined) {
      if (e.altKey && !draggingGuide.altFlipped) {
        draggingGuide.kind = draggingGuide.kind === "horizontal" ? "vertical" : "horizontal";
        draggingGuide.altFlipped = true;
      } else if (!e.altKey) {
        draggingGuide.altFlipped = false;
      }
    }
    const pos = screenToGuidePos(draggingGuide.kind, e.clientX, e.clientY, canvasRect, state.viewport.panX, state.viewport.panY, state.viewport.zoom);
    if (draggingGuide.index !== undefined) {
      guides[draggingGuide.index] = { kind: draggingGuide.kind, position: pos };
    } else {
      // Ruler-origin drag of a brand-new guide: replace the last guide of
      // this kind (the one just pushed by the rulerH/rulerV mousedown above).
      for (let i = guides.length - 1; i >= 0; i--) {
        if (guides[i]!.kind === draggingGuide.kind) {
          guides[i] = { kind: draggingGuide.kind, position: pos };
          break;
        }
      }
    }
    state.requestRender();
  });

  document.addEventListener("mouseup", (e) => {
    if (draggingGuide && !guidesLocked) {
      const canvasRect = canvas.getBoundingClientRect();
      // Drag-back-to-ruler delete: if the mouse is released over the source
      // ruler strip, remove the guide that was being dragged.
      if (isOverRuler(draggingGuide.kind, e.clientX, e.clientY, canvasRect)) {
        if (draggingGuide.index !== undefined) {
          guides.splice(draggingGuide.index, 1);
        } else {
          for (let i = guides.length - 1; i >= 0; i--) {
            if (guides[i]!.kind === draggingGuide.kind) {
              guides.splice(i, 1);
              break;
            }
          }
        }
        state.requestRender();
      }
    }
    draggingGuide = null;
  });

  // Alt+click on canvas to delete nearest guide
  canvas.addEventListener("dblclick", (e) => {
    // i57: double-click the transform-controls box enters/commits Free Transform.
    const rTf = canvas.getBoundingClientRect();
    const sxTf = e.clientX - rTf.left;
    const syTf = e.clientY - rTf.top;
    if (freeTransformTool.isActive()) {
      freeTransformTool.onDoubleClick(toolCtx(), sxTf, syTf);
      return;
    }
    if (state.activeTool === moveTool && state.showTransformControls) {
      const bbox = getActiveLayerTransformBBox();
      if (bbox) {
        const p = screenToDoc(state.viewport, sxTf, syTf);
        if (p.x >= bbox.x && p.x <= bbox.x + bbox.w && p.y >= bbox.y && p.y <= bbox.y + bbox.h) {
          if (freeTransformTool.begin(toolCtx())) {
            state.setActiveTool(freeTransformTool);
            highlightToolByShortcut("T");
          }
          return;
        }
      }
    }
    if (!e.altKey || !state.doc || guidesLocked) return;
    const r = canvas.getBoundingClientRect();
    const sx = e.clientX - r.left;
    const sy = e.clientY - r.top;
    const docX = (sx / state.viewport.zoom) - state.viewport.panX / state.viewport.zoom;
    const docY = (sy / state.viewport.zoom) - state.viewport.panY / state.viewport.zoom;

    // Find nearest guide within 10px
    const threshold = 10 / state.viewport.zoom;
    for (let i = guides.length - 1; i >= 0; i--) {
      const g = guides[i]!;
      if (g.kind === "horizontal" && Math.abs(g.position - docY) < threshold) {
        guides.splice(i, 1);
        state.requestRender();
        return;
      }
      if (g.kind === "vertical" && Math.abs(g.position - docX) < threshold) {
        guides.splice(i, 1);
        state.requestRender();
        return;
      }
    }
  });
}

function updateRulers(): void {
  if (!rulerH || !rulerV || !rulerCorner || !state.doc) return;
  const viewportEl = document.querySelector(".canvas-viewport") as HTMLElement;
  if (!viewportEl) return;

  const rect = viewportEl.getBoundingClientRect();
  const w = rect.width;
  const h = rect.height;

  // Rulers are inset by the 20px corner square (rulerH sits at left:20, rulerV
  // at top:20), so their visible length is the viewport minus that corner.
  // Sizing the canvas to the full w/h overhung the viewport by 20px (clipped by
  // .canvas-viewport{overflow:hidden}) AND, because drawRuler measured tick
  // positions from the viewport origin, pushed every tick 20px past the stage
  // content. Size to the visible span; drawRuler subtracts the same inset (F10).
  const RC = 20; // ruler corner / thickness
  const dprL = window.devicePixelRatio;
  rulerH.width = Math.max(1, Math.round((w - RC) * dprL));
  rulerH.height = Math.round(RC * dprL);
  rulerH.style.width = Math.max(0, w - RC) + "px";
  rulerH.style.height = RC + "px";

  rulerV.width = Math.round(RC * dprL);
  rulerV.height = Math.max(1, Math.round((h - RC) * dprL));
  rulerV.style.width = RC + "px";
  rulerV.style.height = Math.max(0, h - RC) + "px";

  drawRuler(rulerH, "horizontal", w, h);
  drawRuler(rulerV, "vertical", w, h);
}

/**
 * Doc-pixels-per-1-ruler-unit at the given dpi (i112). `docSize` is only
 * used by "percent" (which is relative to the ruler's own axis length, not
 * a fixed physical conversion).
 */
function rulerPxPerUnit(unit: RulerUnit, dpi: number, docSize: number): number {
  switch (unit) {
    case "inches": return dpi;
    case "cm": return dpi / 2.54;
    case "mm": return dpi / 25.4;
    case "points": return dpi / 72;
    case "picas": return dpi / 6;
    case "percent": return docSize / 100;
    case "pixels":
    default:
      return 1;
  }
}

/** Format a ruler-unit value for display, trimming to a sane precision. */
function formatRulerValue(value: number, unit: RulerUnit): string {
  if (unit === "pixels" || unit === "percent") return String(Math.round(value));
  return String(Number(value.toFixed(2)));
}

function drawRuler(canvas: HTMLCanvasElement, orientation: "horizontal" | "vertical", viewW: number, viewH: number): void {
  const ctx = canvas.getContext("2d");
  if (!ctx || !state.doc) return;
  const dpr = window.devicePixelRatio;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  // The ruler canvas is inset by the 20px corner square, so its own length is
  // the viewport span minus that corner, and every tick's viewport position
  // must be shifted left/up by the same inset to align with the stage (F10).
  const RC = 20;
  const len = (orientation === "horizontal" ? viewW : viewH) - RC;
  ctx.fillStyle = "#3c3c3c"; // PS ruler surface tone (#242)
  if (orientation === "horizontal") ctx.fillRect(0, 0, len, 20);
  else ctx.fillRect(0, 0, 20, len);

  ctx.fillStyle = "#ccc";
  ctx.font = "9px sans-serif";

  const zoom = state.viewport.zoom;
  const pan = orientation === "horizontal" ? state.viewport.panX : state.viewport.panY;
  const docSize = orientation === "horizontal" ? state.doc.width : state.doc.height;
  const dpi = state.doc.dpi || 72;
  const originDoc = orientation === "horizontal" ? rulerOrigin.x : rulerOrigin.y;

  // i112: tick spacing (and the numbers printed at each major tick) now
  // respect `rulerUnit` — previously this always showed raw document pixels
  // with a zoom-only-driven step table. `pxPerUnit` converts a step
  // expressed in the chosen unit back to doc-pixel space so the rest of the
  // tick-drawing math (which operates in doc pixels, like screen position
  // and the guide overlay below) is unchanged.
  const pxPerUnit = rulerPxPerUnit(rulerUnit, dpi, docSize);
  const targetScreenPx = 70; // desired spacing between major labeled ticks
  const rawUnitStep = targetScreenPx / (zoom * pxPerUnit);
  const niceSteps = rulerUnit === "pixels" || rulerUnit === "percent"
    ? [1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 5000]
    : [0.01, 0.02, 0.05, 0.1, 0.125, 0.2, 0.25, 0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500];
  let unitStep = niceSteps[niceSteps.length - 1]!;
  for (const s of niceSteps) { if (s >= rawUnitStep) { unitStep = s; break; } }
  const majorStep = unitStep * pxPerUnit;

  const startDoc = (RC - pan) / zoom;
  const endDoc = startDoc + len / zoom;

  ctx.strokeStyle = "#777";
  ctx.lineWidth = 1;

  for (let docPos = Math.floor(startDoc / majorStep) * majorStep; docPos <= endDoc; docPos += majorStep) {
    if (docPos < 0 || docPos > docSize) continue;
    const screenPos = docPos * zoom + pan - RC;
    const label = formatRulerValue((docPos - originDoc) / pxPerUnit, rulerUnit);

    // Major tick
    ctx.beginPath();
    if (orientation === "horizontal") {
      ctx.moveTo(screenPos, 12);
      ctx.lineTo(screenPos, 20);
      ctx.fillText(label, screenPos + 2, 10);
    } else {
      ctx.moveTo(12, screenPos);
      ctx.lineTo(20, screenPos);
      ctx.save();
      ctx.translate(10, screenPos + 3);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText(label, 0, 0);
      ctx.restore();
    }
    ctx.stroke();

    // Minor ticks
    const minorStep = majorStep / 5;
    ctx.strokeStyle = "#666";
    for (let m = 1; m < 5; m++) {
      const minorPos = docPos + m * minorStep;
      if (minorPos < 0 || minorPos > docSize) continue;
      const minorScreen = minorPos * zoom + pan - RC;
      ctx.beginPath();
      if (orientation === "horizontal") {
        ctx.moveTo(minorScreen, 16);
        ctx.lineTo(minorScreen, 20);
      } else {
        ctx.moveTo(16, minorScreen);
        ctx.lineTo(20, minorScreen);
      }
      ctx.stroke();
    }
    ctx.strokeStyle = "#888";
  }

  // Draw guide indicators on rulers
  for (const g of guides) {
    const screenPos = g.position * zoom + pan - RC;
    if (screenPos < 0 || screenPos > len) continue;
    ctx.fillStyle = "rgba(0, 120, 215, 0.8)";
    if (orientation === "horizontal" && g.kind === "vertical") {
      ctx.fillRect(screenPos - 1, 0, 2, 20);
    } else if (orientation === "vertical" && g.kind === "horizontal") {
      ctx.fillRect(0, screenPos - 1, 20, 2);
    }
  }
}

function toggleRulers(): void {
  showRulers = !showRulers;
  if (rulerH) rulerH.style.display = showRulers ? "" : "none";
  if (rulerV) rulerV.style.display = showRulers ? "" : "none";
  if (rulerCorner) rulerCorner.style.display = showRulers ? "" : "none";
  if (showRulers) {
    updateRulers();
    // Adjust canvas viewport position
    const viewportEl = document.querySelector(".canvas-viewport") as HTMLElement;
    if (viewportEl) {
      viewportEl.style.marginTop = "20px";
      viewportEl.style.marginLeft = "20px";
      viewportEl.style.width = "calc(100% - 20px)";
      viewportEl.style.height = "calc(100% - 20px)";
    }
  } else {
    const viewportEl = document.querySelector(".canvas-viewport") as HTMLElement;
    if (viewportEl) {
      viewportEl.style.marginTop = "";
      viewportEl.style.marginLeft = "";
      viewportEl.style.width = "";
      viewportEl.style.height = "";
    }
  }
  resize();
}

function toggleGrid(): void {
  showGrid = !showGrid;
  state.requestRender();
}

async function promptGridSettings(): Promise<void> {
  const result = await filterDialog.open("Grid Settings", [
    { key: "spacing", label: "Gridline Every", type: "slider", default: gridSpacing, min: 4, max: 500, unit: "px" },
    { key: "subdivisions", label: "Subdivisions", type: "slider", default: gridMajorEvery, min: 1, max: 20, unit: "" },
    { key: "color", label: "Grid Color", type: "dropdown", default: gridColor, options: [
      { value: "#444444", label: "Gray" },
      { value: "#000000", label: "Black" },
      { value: "#ffffff", label: "White" },
      { value: "#0000ff", label: "Blue" },
      { value: "#ff0000", label: "Red" },
      { value: "#00ff00", label: "Green" },
    ]},
  ]);
  if (!result) return;
  gridSpacing = Math.round(result.spacing as number);
  gridMajorEvery = Math.round(result.subdivisions as number);
  gridColor = result.color as string;
  gridMajorColor = gridColor === "#444444" ? "#666666" : gridColor;
  state.requestRender();
  showTip("Grid settings updated.");
}

function toggleSnapToGuides(): void {
  snapToGuides = !snapToGuides;
}

function toggleSnapToGrid(): void {
  snapToGrid = !snapToGrid;
}

function toggleGuides(): void {
  showGuides = !showGuides;
  state.requestRender();
}

function toggleExtras(): void {
  showExtras = !showExtras;
  renderer.setShowExtras(showExtras);
  state.requestRender();
}

// NOTE (audit i26): the point-snapping logic used to be duplicated here AND
// in `snapToLayersOrBounds` further down (both dead — neither was ever
// called). Both were removed in favor of ONE consolidated implementation:
// `snapDocPoint()` (see the "Snap-to system" section below), which delegates
// to `snapPosition()` in src/app/ui/SnapTo.ts and is wired onto
// ToolContext.snapPoint for tools to consume.

// i57: shared by updateOverlay() (render) and the canvas dblclick handler
// (hit-testing whether a double-click landed inside the transform-controls
// box) so both use the exact same bounding box computation.
function getActiveLayerTransformBBox(): { x: number; y: number; w: number; h: number; rotation?: number; pivotX?: number; pivotY?: number } | null {
  // i106: while Free Transform is active, expose the ORIENTED box (with its
  // rotation angle) so the renderer draws a rotated outline/handles that match
  // the true rotated edges, instead of an axis-aligned envelope.
  if (freeTransformTool.isActive()) return freeTransformTool.getOrientedBox();
  if (state.activeTool === moveTool && state.showTransformControls && state.doc?.activeLayerId) {
    const activeLayer = findLayer(state.doc.layers, state.doc.activeLayerId);
    if (activeLayer) {
      // i154: reuse the renderer's own layerBBox() (private, but a regular
      // TS-only-private method — not an ES `#private` field — so this cast
      // is safe at runtime) instead of a manual raster-only fallback, so
      // Group layers get the same computed union bounding box the renderer
      // already uses elsewhere, rather than resolving to a 0x0 box.
      const bbox = (renderer as any).layerBBox?.(activeLayer);
      if (bbox) return bbox;
    }
  }
  return null;
}

// i205: bounding box for non-raster layer kinds so Smart Guides can align to
// shape/text/smart-object layers too, not just raster ones.
function smartGuideLayerBounds(l: Layer): { x: number; y: number; w: number; h: number } | null {
  if (l.kind === "shape") {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const sp of l.path.subpaths) {
      for (const p of sp.points) {
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
      }
    }
    if (!isFinite(minX) || !isFinite(minY)) return null;
    const t = l.path.transform;
    const tx = t?.tx ?? 0, ty = t?.ty ?? 0, sx = t?.scaleX ?? 1, sy = t?.scaleY ?? 1;
    return { x: l.x + minX * sx + tx, y: l.y + minY * sy + ty, w: (maxX - minX) * sx, h: (maxY - minY) * sy };
  }
  if (l.kind === "type") {
    const tb = (l as TypeLayer).textBounds;
    if (tb) return { x: l.x + tb.x, y: l.y + tb.y, w: tb.w, h: tb.h };
    return null;
  }
  if (l.kind === "smart-object") {
    const so = l as SmartObjectLayer;
    const sx = so.transform?.scaleX ?? 1, sy = so.transform?.scaleY ?? 1;
    return { x: l.x, y: l.y, w: so.embedded.width * sx, h: so.embedded.height * sy };
  }
  return null;
}

function updateOverlay(): void {
  const penSession = state.activeTool === penTool ? penTool.getSession() : null;
  const cloneStampSource = state.activeTool === cloneStampTool ? cloneStampTool.getSourcePoint() : null;

  // Collect layer bounding boxes for smart guides
  let smartGuideLayers: Array<{ id: string; x: number; y: number; w: number; h: number }> = [];
  if (state.doc && showSmartGuides) {
    const collectBounds = (layers: readonly Layer[]) => {
      for (const l of layers) {
        if (!l.visible || l.opacity <= 0) continue;
        if (l.kind === "raster") {
          smartGuideLayers.push({ id: l.id, x: l.x, y: l.y, w: l.pixels.width, h: l.pixels.height });
        } else if (l.kind === "group") {
          collectBounds(l.children);
        } else {
          // i205: shape/text/smart-object layers weren't alignment targets at
          // all — Smart Guides only ever snapped to raster layer bounds.
          const bounds = smartGuideLayerBounds(l);
          if (bounds) smartGuideLayers.push({ id: l.id, ...bounds });
        }
      }
    };
    collectBounds(state.doc.layers);
  }

  // Show Transform Controls: when Move tool active + checkbox checked, draw
  // a bounding box around the active layer. Free Transform takes priority.
  // Dragging handles is NOT yet implemented â€” display-only (#208).
  let transformBBoxForOverlay = getActiveLayerTransformBBox();

  renderer.setOverlay({
    cropRect: state.activeTool === cropTool ? cropTool.getCurrentRect() : null,
    cropOverlay: state.activeTool === cropTool ? cropTool.overlay : null,
    transformBBox: transformBBoxForOverlay,
    grid: showGrid ? { visible: true, spacing: gridSpacing, majorEvery: gridMajorEvery, color: gridColor, majorColor: gridMajorColor } : null,
    guides: (showGuides && guides.length > 0) ? guides : null,
    penSession: penSession ? { points: penSession.points, closed: penSession.closed } : null,
    cloneStampSource,
    smartGuides: showSmartGuides && smartGuideLayers.length > 1 ? { visible: true, layers: smartGuideLayers } : null,
    pixelGrid: showPixelGrid ? { visible: true } : null,
    layerEdges: showLayerEdges ? { visible: true } : null,
    proofColors: proofColorsEnabled ? { enabled: true } : null,
    gamutWarning: gamutWarningEnabled ? { enabled: true } : null,
    perspectiveCrop: state.activeTool === perspectiveCropTool
      ? { corners: perspectiveCropTool.getCorners(), phase: perspectiveCropTool.getPhase() }
      : null,
    // i64: Object Selection tool's live drag-rectangle (marquee) while the
    // user drags out a selection box.
    objectSelectionDragRect: state.activeTool === objectSelectionTool
      ? objectSelectionTool.getDragRect()
      : null,
    // i79: Patch tool's in-progress selection rect + live drag offset (ghost
    // preview of where the patched content will land).
    patchSelectionRect: state.activeTool === patchTool
      ? patchTool.getSelectionRect()
      : null,
    patchDragOffset: state.activeTool === patchTool
      ? patchTool.getDragOffset()
      : null,
    // i137: Color Sampler tool's persistent placed sample points. Unlike the
    // drag-rect/crop-shield fields above, these are NOT gated on the Color
    // Sampler tool being active — Photoshop keeps placed samplers visible on
    // the canvas no matter which tool is currently selected.
    colorSamplers: colorSamplerTool.samplers.length > 0 ? colorSamplerTool.samplers : null,
    // i108: Crop Shield opacity/visibility, sourced from the Crop tool's own
    // shieldOpacity/shieldOutside fields instead of the renderer's hardcoded
    // default.
    cropShieldOpacity: state.activeTool === cropTool ? cropTool.shieldOpacity : null,
    cropShieldVisible: state.activeTool === cropTool ? cropTool.shieldOutside : true,
    // i102: Type tool's live paragraph-text-box drag rectangle. getDragPreview()
    // returns screen coords (relative to the canvas element, same space as
    // PointerSample) — converted to document-space here so the renderer can
    // draw it with the same translate+scale(zoom) transform as every other
    // doc-space overlay (cropRect/transformBBox/objectSelectionDragRect/...).
    typeDragRect: (() => {
      if (state.activeTool !== typeTool) return null;
      const preview = typeTool.getDragPreview();
      if (!preview) return null;
      const p1 = screenToDoc(state.viewport, preview.x, preview.y);
      const p2 = screenToDoc(state.viewport, preview.x + preview.w, preview.y + preview.h);
      return {
        x: Math.min(p1.x, p2.x),
        y: Math.min(p1.y, p2.y),
        w: Math.abs(p2.x - p1.x),
        h: Math.abs(p2.y - p1.y),
      };
    })(),
  });
}

createRulers();

// ----- Color picker wiring -----
const colorPicker = new ColorPicker();
// i215: wire "Add to Swatches" through to the Swatches panel's own addSwatch()
// API. openSwatchesPanel() is idempotent (no-ops if already open) and is the
// only path that constructs/mounts the panel, so route through it here rather
// than constructing a second SwatchesPanel instance directly — that would
// leave openSwatchesPanel()'s own `if (swatchesPanel) return;` guard stuck
// with a never-mounted panel.
colorPicker.setCallbacks({
  onAddToSwatches: (rgb) => {
    openSwatchesPanel();
    swatchesPanel?.addSwatch(rgb);
  },
});
async function pickForeground(): Promise<void> {
  const result = await colorPicker.open(state.fgColor, "foreground");
  if (result) state.setForegroundColor(result);
}
async function pickBackground(): Promise<void> {
  // i327: pass "background" so the dialog title reads "Color Picker
  // (Background Color)" (Photoshop parity) instead of defaulting to
  // "foreground" for both swatches.
  const result = await colorPicker.open(state.bgColor, "background");
  if (result) state.setBackgroundColor(result);
}
document.querySelectorAll<HTMLElement>(".toolrail .swatch--fg").forEach((el) => el.addEventListener("click", pickForeground));
document.querySelectorAll<HTMLElement>(".toolrail .swatch--bg").forEach((el) => el.addEventListener("click", pickBackground));
document.querySelectorAll<HTMLElement>(".color-preview .swatch--fg").forEach((el) => el.addEventListener("click", pickForeground));
document.querySelectorAll<HTMLElement>(".color-preview .swatch--bg").forEach((el) => el.addEventListener("click", pickBackground));

// FG/BG swatch mini-glyphs: swap (X) and default-colors (D)
document.querySelectorAll<HTMLElement>(".toolrail .swatches__swap").forEach((btn) =>
  btn.addEventListener("click", (e) => { e.stopPropagation(); state.swapColors(); }),
);
document.querySelectorAll<HTMLElement>(".toolrail .swatches__default").forEach((btn) =>
  btn.addEventListener("click", (e) => { e.stopPropagation(); state.resetColors(); }),
);

// Hex input commits foreground on change
const hexInput = document.querySelector<HTMLInputElement>(".hex-input");
hexInput?.addEventListener("change", () => {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hexInput.value.trim());
  if (!m) return;
  let hex = m[1]!;
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  const n = parseInt(hex, 16);
  state.setForegroundColor({ r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 });
});

// ----- Color panel RGB sliders -----
const colorSliders = document.querySelectorAll<HTMLElement>(".color-body .slider-row .slider");
const colorValues = document.querySelectorAll<HTMLElement>(".color-body .slider-row .value");
let colorSliderDragging: number | null = null; // 0=R, 1=G, 2=B

function colorSliderFromEvent(e: PointerEvent, idx: number): number {
  const el = colorSliders[idx] as HTMLElement | undefined;
  if (!el) return 0;
  const r = el.getBoundingClientRect();
  return Math.max(0, Math.min(255, Math.round(((e.clientX - r.left) / r.width) * 255)));
}

colorSliders.forEach((slider, idx) => {
  slider.addEventListener("pointerdown", (e) => {
    colorSliderDragging = idx;
    try { slider.setPointerCapture(e.pointerId); } catch { /* noop */ }
    const v = colorSliderFromEvent(e, idx);
    const fg = state.fgColor;
    const rgb = [fg.r, fg.g, fg.b] as [number, number, number];
    rgb[idx] = v;
    state.setForegroundColor({ r: rgb[0]!, g: rgb[1]!, b: rgb[2]! });
  });
  slider.addEventListener("pointermove", (e) => {
    if (colorSliderDragging !== idx) return;
    const v = colorSliderFromEvent(e, idx);
    const fg = state.fgColor;
    const rgb = [fg.r, fg.g, fg.b] as [number, number, number];
    rgb[idx] = v;
    state.setForegroundColor({ r: rgb[0]!, g: rgb[1]!, b: rgb[2]! });
  });
  slider.addEventListener("pointerup", () => { colorSliderDragging = null; });
});

/** Sync the Color panel sliders and hex input with the current foreground color. */
function syncColorPanel(): void {
  const fg = state.fgColor;
  const rgb = [fg.r, fg.g, fg.b];
  colorSliders.forEach((slider, i) => {
    const fill = slider.querySelector<HTMLElement>(".slider__fill");
    if (fill) fill.style.width = `${(rgb[i]! / 255) * 100}%`;
  });
  colorValues.forEach((val, i) => { val.textContent = String(rgb[i]!); });
  if (hexInput) {
    const hex = rgb.map((c) => c.toString(16).padStart(2, "0")).join("");
    if (hexInput.value !== hex) hexInput.value = hex;
  }
  if (colorPanelInstance) {
    colorPanelInstance.fgColor = state.fgColor;
    colorPanelInstance.bgColor = state.bgColor;
  }
}

// i128: F6 (and Window > Color) — mount/sync the ColorPanel instance into the
// docked `.color-body` slot. Previously the Window > Color menu item referenced
// `(deps as any).ColorPanel` / `(deps as any).colorPanelInstance`, neither of
// which buildMenuDeps() ever set, so `new (deps as any).ColorPanel()` threw
// silently (menus.ts swallows the exception path via showMenuDropdown's click
// handler) and the item was a de-facto no-op. This uses the real module-level
// `colorPanelInstance` + `ColorPanel` class main.ts already has for
// syncColorPanel() above. Deliberately does NOT click the Color panel-tab —
// that tab is active by default, and (as of i127) the generic panel-tab
// handler below now has a "color" branch that just re-shows `.color-body`,
// so clicking it is safe, but there's still no reason to synthesize a click
// here when the tab is already active on load.
function openColorPanel(): void {
  const colorBody = document.querySelector<HTMLElement>(".color-body");
  if (!colorBody) return;
  if (!colorPanelInstance) {
    colorPanelInstance = new ColorPanel();
    colorPanelInstance.mount(colorBody);
  }
  colorPanelInstance.syncAll();
}

// ----- Blend-mode dropdown wired to active layer -----
// Closes #131: previously the <option> elements were rendered without any
// `selected` attribute, so DOM inspection picked up all option text
// concatenated. populateBlendSelect() always assigns exactly one selected
// option, which is what the browser's collapsed <select> view shows.
const blendSelect = document.querySelector<HTMLSelectElement>(".blend-select");
if (blendSelect) {
  modPopulateBlendSelect(blendSelect, "normal");
  blendSelect.addEventListener("change", () => {
    if (!state.doc?.activeLayerId) return;
    state.execute(new SetLayerPropertyCommand(state.doc.activeLayerId, { blendMode: blendSelect.value as BlendMode }));
  });
}

// ----- Opacity slider wired to active layer -----
const opacitySlider = document.querySelector<HTMLDivElement>(".opacity-row .slider");
const opacityFill = document.querySelector<HTMLDivElement>(".opacity-row .slider__fill");
const opacityValueEl = document.querySelector<HTMLSpanElement>(".opacity-row .value");
let opacityDragging = false;
function opacityFromPointerEvent(e: PointerEvent): number {
  if (!opacitySlider) return 0;
  const r = opacitySlider.getBoundingClientRect();
  return Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
}
opacitySlider?.addEventListener("pointerdown", (e) => {
  if (!state.doc?.activeLayerId) return;
  opacityDragging = true;
  try { opacitySlider.setPointerCapture(e.pointerId); } catch { /* */ }
  state.applyTransient(new SetLayerPropertyCommand(state.doc.activeLayerId, { opacity: opacityFromPointerEvent(e) }));
});
opacitySlider?.addEventListener("pointermove", (e) => {
  if (!opacityDragging || !state.doc?.activeLayerId) return;
  state.applyTransient(new SetLayerPropertyCommand(state.doc.activeLayerId, { opacity: opacityFromPointerEvent(e) }));
});
opacitySlider?.addEventListener("pointerup", (e) => {
  if (!opacityDragging || !state.doc?.activeLayerId) return;
  opacityDragging = false;
  state.execute(new SetLayerPropertyCommand(state.doc.activeLayerId, { opacity: opacityFromPointerEvent(e) }));
});

// ----- Fill slider wired to active layer -----
const fillSlider = document.querySelector<HTMLDivElement>(".lock-row .slider");
const fillFillEl = document.querySelector<HTMLDivElement>(".lock-row .slider__fill");
const fillValueEl = document.querySelector<HTMLSpanElement>(".lock-row .value");
let fillDragging = false;
function fillFromPointerEvent(e: PointerEvent): number {
  if (!fillSlider) return 0;
  const r = fillSlider.getBoundingClientRect();
  return Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
}
fillSlider?.addEventListener("pointerdown", (e) => {
  if (!state.doc?.activeLayerId) return;
  fillDragging = true;
  try { fillSlider.setPointerCapture(e.pointerId); } catch { /* */ }
  state.applyTransient(new SetLayerPropertyCommand(state.doc.activeLayerId, { fillOpacity: fillFromPointerEvent(e) }));
});
fillSlider?.addEventListener("pointermove", (e) => {
  if (!fillDragging || !state.doc?.activeLayerId) return;
  state.applyTransient(new SetLayerPropertyCommand(state.doc.activeLayerId, { fillOpacity: fillFromPointerEvent(e) }));
});
fillSlider?.addEventListener("pointerup", (e) => {
  if (!fillDragging || !state.doc?.activeLayerId) return;
  fillDragging = false;
  state.execute(new SetLayerPropertyCommand(state.doc.activeLayerId, { fillOpacity: fillFromPointerEvent(e) }));
});

// ----- Lock buttons wired to active layer -----
function wireLockButton(selector: string, prop: "lockTransparency" | "lockImage" | "lockPosition" | "lockAll"): void {
  document.querySelectorAll<HTMLButtonElement>(selector).forEach((btn) => {
    btn.addEventListener("click", () => {
      if (!state.doc?.activeLayerId) return;
      const layer = findLayer(state.doc.layers, state.doc.activeLayerId);
      if (!layer) return;
      const current = layer[prop] as boolean;
      state.execute(new SetLayerPropertyCommand(state.doc.activeLayerId, { [prop]: !current }));
    });
  });
}
wireLockButton('.lock-row .ibtn[title="Lock transparency"]', "lockTransparency");
wireLockButton('.lock-row .ibtn[title="Lock image"]', "lockImage");
wireLockButton('.lock-row .ibtn[title="Lock position"]', "lockPosition");
// i209: "Lock all" is an independent master lock (Photoshop convention). Tools
// enforce it directly (every paint/move/transform guard checks `layer.lockAll
// || layer.<subLock>`), so it does NOT need to — and must NOT — overwrite the
// three independent sub-locks: force-setting them here would silently wipe a
// sub-lock the user had set on its own once "Lock all" is toggled back off.
// Instead we flip only `lockAll`; syncLayerControls() shows the sub-lock buttons
// as active whenever `lockAll` is engaged so the UI still reads "everything
// locked" without destroying the underlying independent flags.
document.querySelectorAll<HTMLButtonElement>('.lock-row .ibtn[title="Lock all"]').forEach((btn) => {
  btn.addEventListener("click", () => {
    if (!state.doc?.activeLayerId) return;
    const layer = findLayer(state.doc.layers, state.doc.activeLayerId);
    if (!layer) return;
    state.execute(new SetLayerPropertyCommand(state.doc.activeLayerId, { lockAll: !layer.lockAll }));
  });
});

// Reflect active layer's blend + opacity + fill + locks in the controls each frame.
function syncLayerControls(): void {
  const doc = state.doc;
  const id = doc?.activeLayerId;
  if (!doc || !id) return;
  const layer = findLayer(doc.layers, id);
  if (!layer) return;
  if (blendSelect && (blendSelect.value !== layer.blendMode || blendSelect.dataset.kind !== layer.kind)) {
    // i210: repopulate the <option> list itself (not just the value) so
    // "Pass Through" is present when (and only when) the active layer is a
    // group — a plain `.value = ...` assignment silently no-ops when the
    // target mode isn't among the select's current options (e.g. switching
    // from a raster layer, whose options exclude "Pass Through", to a group).
    // Guard also on layer.kind (tracked via dataset.kind): a group and a
    // raster layer both default to blendMode "normal", so a value-only check
    // would skip the repopulate when switching between them at the default
    // mode and leave "Pass Through" wrongly absent/present.
    modPopulateBlendSelect(blendSelect, layer.blendMode, layer.kind);
    blendSelect.dataset.kind = layer.kind;
  }
  const pct = Math.round(layer.opacity * 100);
  if (opacityFill) opacityFill.style.width = `${pct}%`;
  if (opacityValueEl) opacityValueEl.textContent = `${pct}%`;

  // Fill slider.
  const fillPct = Math.round((layer.fillOpacity ?? 1) * 100);
  if (fillFillEl) fillFillEl.style.width = `${fillPct}%`;
  if (fillValueEl) fillValueEl.textContent = `${fillPct}%`;

  // Lock buttons.
  const lockTransBtn = document.querySelector<HTMLButtonElement>('.lock-row .ibtn[title="Lock transparency"]');
  const lockImgBtn = document.querySelector<HTMLButtonElement>('.lock-row .ibtn[title="Lock image"]');
  const lockPosBtn = document.querySelector<HTMLButtonElement>('.lock-row .ibtn[title="Lock position"]');
  const lockAllBtn = document.querySelector<HTMLButtonElement>('.lock-row .ibtn[title="Lock all"]');
  // "Lock all" is a master lock: while it is engaged, the three sub-lock
  // buttons read as active too (their underlying independent flags are left
  // intact — see the "Lock all" click handler, i209).
  const allLocked = !!layer.lockAll;
  if (lockTransBtn) lockTransBtn.classList.toggle("is-active", !!layer.lockTransparency || allLocked);
  if (lockImgBtn) lockImgBtn.classList.toggle("is-active", !!layer.lockImage || allLocked);
  if (lockPosBtn) lockPosBtn.classList.toggle("is-active", !!layer.lockPosition || allLocked);
  if (lockAllBtn) lockAllBtn.classList.toggle("is-active", allLocked);
}

// ===================================================================
// #249: Pan clamp — keep at least 20px of the document visible.
// ===================================================================
const PAN_SLIVER = 20; // minimum CSS pixels of document visible in the viewport

function clampPanPatch(patch: Partial<{ panX: number; panY: number; zoom: number }>): Partial<{ panX: number; panY: number; zoom: number }> {
  const doc = state.doc;
  if (!doc) return patch;
  const r = viewportEl.getBoundingClientRect();
  const zoom = (patch as any).zoom !== undefined ? (patch as any).zoom : state.viewport.zoom;
  const docW = doc.width * zoom;
  const docH = doc.height * zoom;
  const result: Partial<{ panX: number; panY: number; zoom: number }> = { ...patch };
  if ((patch as any).panX !== undefined) {
    const minPanX = PAN_SLIVER - docW;
    const maxPanX = r.width - PAN_SLIVER;
    (result as any).panX = Math.max(minPanX, Math.min(maxPanX, (patch as any).panX as number));
  }
  if ((patch as any).panY !== undefined) {
    const minPanY = PAN_SLIVER - docH;
    const maxPanY = r.height - PAN_SLIVER;
    (result as any).panY = Math.max(minPanY, Math.min(maxPanY, (patch as any).panY as number));
  }
  return result;
}

// Tool context handed to tools on each event
function toolCtx(): ToolContext {
  const base = modToolCtx({ state, showTip });
  return {
    ...base,
    setViewport: (patch) => state.setViewport(clampPanPatch(patch)),
    // audit i26: snap candidate points to guides/grid/other-layer edges/doc
    // bounds. Excludes the currently-selected layer(s) — normally the ones a
    // tool is dragging — from the "other layers" targets.
    snapPoint: (x: number, y: number) => snapDocPoint(x, y, state.selectedLayerIds),
  };
}

// Pointer events
function sampleFromEvent(e: PointerEvent): PointerSample {
  return modSampleFromEvent(e);
}

let dragging = false;
// i138 follow-up: the most recent real pointer sample (from pointerdown/move),
// replayed by fallbackPointerSample() when a drag is interrupted with no
// PointerEvent to read (window blur), so tools that finalize from the up-sample
// (Marquee/Shape) commit at the last-dragged position instead of canvas (0,0).
let _lastPointerSample: PointerSample | null = null;

// i201/i61: live interaction signals threaded into cursor.ts's updateCursor()
// via CursorDeps.interaction — Alt-key state (Zoom tool zoom-in/out swap,
// Hand-tool pan-drag "grabbing" swap; i201) and pointer-in-selection state
// (Marquee/Lasso/Wand/Object-Selection move-cursor swap; i61). `isPanning`
// itself needs no separate variable — it's just `dragging && activeTool ===
// handTool`, computed inline in updateCursor() below.
let liveAltKey = false;
let livePointerInSelection = false;
// i168: Brush/Eraser/Pencil have no isDragging()/cancel() pair of their own
// (unlike cropTool/polyLassoTool/freeTransformTool), so Escape mid-stroke was
// a silent no-op for them. A full-layer snapshot taken at stroke start lets
// the Escape handler restore the pre-stroke pixels directly, without needing
// to add cancel() to those tool classes.
let escapeCancelSnapshot: { layerId: string; data: Uint8ClampedArray } | null = null;
let spacePan: { prev: Tool } | null = null;
/** Spring-loaded tool: { prev, key, downAt } — set when a tool-shortcut key is held > 300ms. */
let springTool: { prev: Tool; key: string; downAt: number } | null = null;
/** Ctrl temporary Move tool: set when Ctrl is held without a second key during pointer-down. */
let ctrlMoveTool: { prev: Tool } | null = null;

canvas.addEventListener("pointerdown", (e) => {
  try {
    // Block canvas pointer events while text editor is active.
    if (textEditor.isActive()) return;

    // Transform Selection mode intercepts all pointer events.
    if (_transformSelState) {
      const r = canvas.getBoundingClientRect();
      const docPt = screenToDoc(state.viewport, e.clientX - r.left, e.clientY - r.top);
      const handle = hitTestTSHandle(docPt.x, docPt.y);
      if (handle) {
        const b = _transformSelState.originalSel.bounds();
        if (b) {
          _tsDrag = {
            handle,
            startDocX: docPt.x,
            startDocY: docPt.y,
            startScaleX: _transformSelState.scaleX,
            startScaleY: _transformSelState.scaleY,
            startRotation: _transformSelState.rotation,
            startTranslateX: _transformSelState.translateX,
            startTranslateY: _transformSelState.translateY,
            origBounds: { ...b },
          };
          try { canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }
        }
      }
      return;
    }

    // i113: grab an EXISTING guide directly on the canvas with the Move
    // tool, instead of only being able to reposition it by dragging a
    // freshly-created one out of a ruler. Checked before generic tool
    // dispatch so the Move tool doesn't ALSO try to move whatever layer is
    // under the guide.
    if (e.button === 0 && state.activeTool === moveTool && state.doc && !guidesLocked && showGuides) {
      const r0 = canvas.getBoundingClientRect();
      const hitIdx = findGuideNear(e.clientX - r0.left, e.clientY - r0.top);
      if (hitIdx >= 0) {
        const g = guides[hitIdx]!;
        draggingGuide = { kind: g.kind, startPos: g.position, index: hitIdx };
        return;
      }
    }

    const tool = state.activeTool;
    if (!tool) return;
    // Route the pointerdown: ignore non-primary buttons (right/middle-click must
    // not start a tool op — the contextmenu handler owns right-click), and resolve
    // the Ctrl-temp-Move (#217) swap so the down is dispatched to the RIGHT tool.
    const route = routePointerDown(e, tool, { moveTool, zoomTool }, !!ctrlMoveTool);
    if (route.ignore) return;
    dragging = true;
    // i201: swap Hand tool's cursor to "grabbing" the instant a pan drag
    // starts, rather than waiting for the next state-driven render pass.
    if (state.activeTool === handTool) updateCursor();
    try { canvas.setPointerCapture(e.pointerId); } catch { /* invalid pointer; ignore */ }
    if (route.engageCtrlMove) {
      ctrlMoveTool = { prev: tool };
      state.setActiveTool(moveTool);
    }
    if (route.dispatchTool === zoomTool) zoomTool.setAlt(e.altKey);
    // i168: capture a pre-stroke snapshot for Escape-cancel support (see the
    // Escape handler below) — only for the tools that actually need it.
    escapeCancelSnapshot = null;
    if (route.dispatchTool === brushTool || route.dispatchTool === eraserTool || route.dispatchTool === pencilTool) {
      const escLayer = getActiveRaster();
      if (escLayer) escapeCancelSnapshot = { layerId: escLayer.id, data: new Uint8ClampedArray(escLayer.pixels.data) };
    }
    // i225: isolate the tool dispatch so a thrown error resets the drag flag
    // instead of leaving the app stuck in a "mid-drag" state (pointerup/move
    // handlers gate on `dragging`, so a leaked `true` would swallow all
    // subsequent pointer input until the next successful pointerdown).
    try {
      const s = sampleFromEvent(e);
      _lastPointerSample = s;
      route.dispatchTool.onPointerDown?.(toolCtx(), s);
    } catch (err) {
      dragging = false;
      try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      console.error("pointerdown tool dispatch error:", err);
      showError(`Tool action failed: ${err instanceof Error ? err.message : "unknown error"}`);
    }
  } catch (err) {
    console.error("pointerdown error:", err);
  }
});

canvas.addEventListener("pointermove", (e) => {
  try {
    const r = canvas.getBoundingClientRect();
    toolUx.setCursorPos(e.clientX - r.left, e.clientY - r.top);
    toolUx.drawCursorInternal();

    // Update Info panel cursor position
    if (state.doc) {
      const docPos = screenToDoc(state.viewport, e.clientX - r.left, e.clientY - r.top);
      lastDocPos = { x: docPos.x, y: docPos.y };

      // i61: track whether the pointer is currently hovering inside the
      // active selection, for cursor.ts's move-cursor swap on the
      // Marquee/Lasso/Wand/Object-Selection family.
      const sel = state.doc.selection;
      livePointerInSelection = !!sel && !sel.isEmpty() && sel.contains(docPos.x, docPos.y) > 0;

      // Sample color under cursor
      const layer = getActiveRaster();
      if (layer) {
        const lx = Math.floor(docPos.x - layer.x);
        const ly = Math.floor(docPos.y - layer.y);
        if (lx >= 0 && ly >= 0 && lx < layer.pixels.width && ly < layer.pixels.height) {
          const pi = (ly * layer.pixels.width + lx) * 4;
          lastSampledColor = {
            r: layer.pixels.data[pi]!,
            g: layer.pixels.data[pi + 1]!,
            b: layer.pixels.data[pi + 2]!,
          };
        }
      }
    } else {
      livePointerInSelection = false;
    }

    // Pen tool: update rubber-band preview even when not dragging
    if (state.activeTool === penTool && state.doc && penTool.getSession()) {
      const docPos = screenToDoc(state.viewport, e.clientX - r.left, e.clientY - r.top);
      renderer.setOverlay({
        cropRect: state.activeTool === cropTool ? cropTool.getCurrentRect() : null,
        transformBBox: freeTransformTool.isActive() ? freeTransformTool.getOrientedBox() : null,
        grid: showGrid ? { visible: true, spacing: gridSpacing, majorEvery: gridMajorEvery, color: gridColor, majorColor: gridMajorColor } : null,
        guides: (showGuides && guides.length > 0) ? guides : null,
        penSession: { points: penTool.getSession()!.points, closed: penTool.getSession()!.closed },
        penCursorPos: penTool.rubberBand ? docPos : null,
      });
    }
    // Transform Selection drag.
    if (_tsDrag) {
      const r2 = canvas.getBoundingClientRect();
      const docPt2 = screenToDoc(state.viewport, e.clientX - r2.left, e.clientY - r2.top);
      applyTSDrag(docPt2.x, docPt2.y);
      return;
    }

    // Forward moves during a drag, OR to hover-driven tools (Pen rubber-band,
    // Magnetic/Polygonal Lasso live segment) that need moves with no button held.
    if (dragging || wantsHoverMove(state.activeTool)) {
      const s = sampleFromEvent(e);
      _lastPointerSample = s;
      state.activeTool?.onPointerMove?.(toolCtx(), s);
      // #224: Edge autoscroll — during drag, near viewport edge scrolls canvas.
      if (dragging && state.doc) {
        const rEdge = canvas.getBoundingClientRect();
        const ex = e.clientX - rEdge.left;
        const ey = e.clientY - rEdge.top;
        const edgeZone = 20;
        const scrollStep = 10;
        let panDx = 0, panDy = 0;
        if (ex < edgeZone) panDx = scrollStep;
        else if (ex > rEdge.width - edgeZone) panDx = -scrollStep;
        if (ey < edgeZone) panDy = scrollStep;
        else if (ey > rEdge.height - edgeZone) panDy = -scrollStep;
        if (panDx !== 0 || panDy !== 0) {
          state.setViewport(clampPanPatch({ panX: state.viewport.panX + panDx, panY: state.viewport.panY + panDy }));
        }
      }
    }
    // i278: refresh AFTER onPointerMove/edge-autoscroll so a live drag (e.g.
    // resizing a Marquee) reflects the CURRENT event's selection bounds
    // instead of lagging one pointermove behind.
    if (state.doc) updateInfoPanel();
    // i61: refresh the cursor now that livePointerInSelection may have
    // changed (Marquee/Lasso/Wand/Object-Selection move-cursor swap).
    updateCursor();
    // i113: hovering (or actively dragging) an existing guide with the Move
    // tool shows a resize-style cursor instead of the tool's normal one, so
    // it's clear clicking here grabs the guide, not the layer beneath it.
    // Must run AFTER updateCursor() above, which otherwise overwrites
    // canvas.style.cursor with the plain tool cursor every tick.
    if (state.doc && state.activeTool === moveTool && showGuides && !dragging) {
      if (draggingGuide && draggingGuide.index !== undefined) {
        canvas.style.cursor = draggingGuide.kind === "horizontal" ? "ns-resize" : "ew-resize";
      } else if (!draggingGuide) {
        const hoverIdx = findGuideNear(e.clientX - r.left, e.clientY - r.top);
        if (hoverIdx >= 0) {
          canvas.style.cursor = guides[hoverIdx]!.kind === "horizontal" ? "ns-resize" : "ew-resize";
        }
      }
    }
  } catch (err) {
    console.error("pointermove error:", err);
  }
});

canvas.addEventListener("pointerup", (e) => {
  try {
    // Transform Selection drag end.
    if (_tsDrag) {
      const r3 = canvas.getBoundingClientRect();
      const docPt3 = screenToDoc(state.viewport, e.clientX - r3.left, e.clientY - r3.top);
      applyTSDrag(docPt3.x, docPt3.y);
      _tsDrag = null;
      return;
    }

    if (!dragging) return;
    dragging = false;
    // i201: swap Hand tool's cursor back to "grab" the instant the pan drag ends.
    if (state.activeTool === handTool) updateCursor();
    state.activeTool?.onPointerUp?.(toolCtx(), sampleFromEvent(e));
    // Shape tool: create ShapeLayer or rasterize
    const shapeTools = [rectShapeTool, ellipseShapeTool, polygonShapeTool, roundedRectShapeTool, lineShapeTool, triangleShapeTool, customShapeTool2];
    if (shapeTools.includes(state.activeTool as any)) {
      const path = (state.activeTool as any).getLastPath?.();
      if (path) {
        const tool = state.activeTool as ShapeTool;
        // i85: ToolUX.ts's Tool Mode dropdown now stores a real 3-way
        // "shape"/"path"/"pixels" selection on `tool.shapeToolMode` (it keeps
        // `tool.createVector` in sync too, but that's only true/false, so it
        // can't tell "Path" and "Pixels" apart on its own). Route "path" to
        // the same undoable work-path mechanism Select > Make Work Path uses
        // (SetWorkPathCommand) instead of creating a layer or touching pixels.
        // Derive the effective mode the SAME way the options bar (ToolUX) does
        // when shapeToolMode is unset, so what's drawn matches what the Tool
        // Mode dropdown displays (i85 verification fix): an undefined
        // shapeToolMode with createVector=false previously fell through to
        // rasterize even though the dropdown showed "Path".
        const shapeToolMode = tool.shapeToolMode ?? (tool.createVector ? "shape" : "path");
        if (shapeToolMode === "path") {
          state.execute(new SetWorkPathCommand(path));
          showTip("Work path created.");
        } else if (shapeToolMode === "shape" || tool.createVector) {
          const shapeLayer = createShapeLayer({
            path,
            fillColor: tool.fillColor,
            strokeColor: tool.strokeColor,
            strokeWidth: tool.strokeWidth,
            // i188: prefer the per-instance display name (disambiguates
            // Rectangle vs. Rounded Rectangle, Polygon vs. Triangle) over the
            // shared `mode` string.
            name: `${shapeToolDisplayNames.get(tool) ?? `${tool.mode.charAt(0).toUpperCase() + tool.mode.slice(1)}`} Shape`,
          });
          state.execute(new NewLayerCommand(shapeLayer));
          state.setActiveLayer(shapeLayer.id);
        } else {
          rasterizeShapeOnLayer(path, (state.activeTool as any).mode, state.fgColor, 2);
        }
      }
    }
    // Clear pen cursor position
    if (state.activeTool === penTool) {
      const penSession = penTool.getSession();
      renderer.setOverlay({
        cropRect: null,
        transformBBox: null,
        grid: showGrid ? { visible: true, spacing: gridSpacing, majorEvery: gridMajorEvery, color: gridColor, majorColor: gridMajorColor } : null,
        guides: (showGuides && guides.length > 0) ? guides : null,
        penSession: penSession ? { points: penSession.points, closed: penSession.closed } : null,
        penCursorPos: null,
      });
    }
    // i278: refresh the Info panel one more time at the exact moment of
    // pointer-up so it reflects the FINAL settled selection immediately —
    // otherwise it only updates on the next pointermove, which may never
    // come if the user doesn't move the mouse after releasing.
    if (state.doc) updateInfoPanel();
    // i59: checkpoint Reselect's "previous selection" now that the drag (or
    // click) has settled — covers click-only tools (e.g. Magic Wand) whose
    // onPointerUp emits no further command, so no subsequent notify() would
    // otherwise re-run trackSelectionForReselect().
    trackSelectionForReselect();
  } catch (err) {
    console.error("pointerup error:", err);
  }
});

canvas.addEventListener("pointerleave", () => {
  // Clear cursor by drawing empty overlay
  const ctx = cursorCanvas.getContext("2d");
  if (ctx) ctx.clearRect(0, 0, cursorCanvas.width, cursorCanvas.height);
});

// Safety net for interrupted drags (touch/pen gesture takeover, pointer canceled
// by the browser). Without this, `dragging` stays stuck true and every later
// interaction is broken. pointercancel (unlike lostpointercapture) never fires on
// a normal pointerup, so this cannot race the stroke-commit in the pointerup path.
function fallbackPointerSample(): PointerSample {
  // Replay the last real pointer position (see _lastPointerSample) so an
  // interrupted drag commits where the pointer actually was, not at (0,0).
  if (_lastPointerSample) {
    return { ..._lastPointerSample, pressure: 0, timeStamp: performance.now() };
  }
  return { x: 0, y: 0, pressure: 0, tiltX: 0, tiltY: 0, timeStamp: performance.now(), shiftKey: false, altKey: false, ctrlKey: false, metaKey: false };
}
function handleInterruptedDrag(e?: PointerEvent): void {
  // i138: beyond resetting the main.ts-level flags, let the in-flight tool
  // commit/reset its own internal state (e.g. a brush stroke snapshot, a
  // marquee drag-origin) the same way it would on a normal pointerup — a
  // bare `dragging = false` here still left tools with stale internal state
  // that corrupted the NEXT stroke.
  const wasDragging = dragging;
  _tsDrag = null;
  dragging = false;
  // i201: don't leave the Hand tool's cursor stuck on "grabbing" after an
  // interrupted (not a normal pointerup) drag.
  if (wasDragging && state.activeTool === handTool) updateCursor();
  if (wasDragging) {
    try {
      state.activeTool?.onPointerUp?.(toolCtx(), e ? sampleFromEvent(e) : fallbackPointerSample());
    } catch (err) {
      console.error("interrupted-drag cleanup error:", err);
    }
  }
}
canvas.addEventListener("pointercancel", (e) => { handleInterruptedDrag(e); });
canvas.addEventListener("lostpointercapture", (e) => { handleInterruptedDrag(e); });
window.addEventListener("blur", () => { handleInterruptedDrag(); });
// #224: Brush right-click HUD — size/hardness sliders shown on canvas right-click with brush-based tools.
let brushHud: HTMLDivElement | null = null;

function hideBrushHud(): void {
  if (brushHud) { brushHud.remove(); brushHud = null; }
}

// i129: right-click on the canvas with a Marquee/Lasso selection tool or the
// Move tool active shows a tool-appropriate context menu, reusing the same
// showMenuDropdown() infrastructure the toolbar flyouts already use. Other
// tools fall through to the brush-HUD branch (unchanged) or the browser's
// default context menu.
/**
 * Point-to-segment distance in doc space — mirrors PenTool.ts's private
 * `distToSegment()` helper. Duplicated here (rather than exported/imported)
 * because PenTool.ts's hit-testing internals are out of scope for this fix
 * (#129); this is a small, self-contained formula so duplicating it doesn't
 * risk drifting from the tool's own click-to-edit behaviour.
 */
function distToSegmentDoc(p: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = b.x - a.x, dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

function showToolContextMenu(e: MouseEvent): boolean {
  const selectionTools = [
    marqueeRectTool, marqueeEllipseTool, marqueeSingleRowTool, marqueeSingleColumnTool,
    lassoTool, polyLassoTool, magneticLassoTool,
    objectSelectionTool, quickSelectionTool, magicWandTool,
  ];
  const tool = state.activeTool;
  const hasSelection = !!state.doc?.selection && !state.doc.selection.isEmpty();
  let items: Array<{ label: string; shortcut?: string; disabled?: boolean; action?: () => void; separator?: boolean }> | null = null;

  if (selectionTools.includes(tool as any)) {
    items = [
      { label: "Select Inverse", shortcut: "Ctrl+Shift+I", disabled: !hasSelection, action: selectInverse },
      { label: "Deselect", shortcut: "Ctrl+D", disabled: !hasSelection, action: deselect },
      { separator: true, label: "" },
      { label: "Feather...", disabled: !hasSelection, action: selectFeather },
      { label: "Transform Selection", disabled: !hasSelection, action: transformSelection },
    ];
  } else if (tool === moveTool) {
    const hasLayer = !!state.doc?.activeLayerId;
    items = [
      { label: "Free Transform", shortcut: "Ctrl+T", disabled: !hasLayer, action: () => {
        if (freeTransformTool.begin(toolCtx())) { state.setActiveTool(freeTransformTool); highlightToolByShortcut("T"); }
      } },
      { separator: true, label: "" },
      { label: "Bring to Front", disabled: !hasLayer, action: () => reorderActiveLayer("front") },
      { label: "Bring Forward", shortcut: "Ctrl+]", disabled: !hasLayer, action: () => reorderActiveLayer("forward") },
      { label: "Send Backward", shortcut: "Ctrl+[", disabled: !hasLayer, action: () => reorderActiveLayer("backward") },
      { label: "Send to Back", disabled: !hasLayer, action: () => reorderActiveLayer("back") },
    ];
  } else if (tool === penTool) {
    // i129: Pen tool right-click — Delete/Add Anchor Point, Convert Point,
    // enabled only when a path point (or, for Add, a path segment) is under
    // the cursor — mirrors Photoshop's path-editing context menu. Uses
    // PenTool's public getSession()/setSession() API instead of its private
    // hit-testing methods (PenTool.ts is out of scope for this fix).
    const session = penTool.getSession();
    const rPen = canvas.getBoundingClientRect();
    const p = screenToDoc(state.viewport, e.clientX - rPen.left, e.clientY - rPen.top);
    let anchorIdx = -1;
    let segmentIdx = -1;
    if (session) {
      for (let i = 0; i < session.points.length; i++) {
        const pt = session.points[i]!;
        if (Math.hypot(pt.x - p.x, pt.y - p.y) <= 8) { anchorIdx = i; break; }
      }
      if (anchorIdx < 0 && session.points.length >= 2) {
        const n = session.points.length;
        const lastIdx = session.closed ? n : n - 1;
        let bestDist = Infinity;
        let bestIdx = -1;
        for (let i = 0; i < lastIdx; i++) {
          const a = session.points[i]!;
          const b = session.points[(i + 1) % n]!;
          const d = distToSegmentDoc(p, a, b);
          if (d < bestDist) { bestDist = d; bestIdx = i; }
        }
        if (bestIdx >= 0 && bestDist <= 10) segmentIdx = bestIdx;
      }
    }
    items = [
      {
        label: "Delete Anchor Point",
        disabled: !session || anchorIdx < 0 || session.points.length <= 2,
        action: () => {
          if (!session) return;
          const pts = session.points.slice();
          pts.splice(anchorIdx, 1);
          penTool.setSession({ ...session, points: pts });
          state.requestRender();
        },
      },
      {
        label: "Add Anchor Point",
        disabled: !session || segmentIdx < 0,
        action: () => {
          if (!session) return;
          const pts = session.points.slice();
          pts.splice(segmentIdx + 1, 0, { x: p.x, y: p.y, type: "corner" });
          penTool.setSession({ ...session, points: pts });
          state.requestRender();
        },
      },
      {
        label: "Convert Point",
        disabled: !session || anchorIdx < 0,
        action: () => {
          if (!session) return;
          const pts = session.points.slice();
          const pt = { ...pts[anchorIdx]! };
          if (pt.type === "corner") {
            pt.rightDirection = { x: p.x, y: p.y };
            pt.leftDirection = { x: pt.x - (p.x - pt.x), y: pt.y - (p.y - pt.y) };
            pt.type = "smooth";
          } else {
            delete pt.rightDirection;
            delete pt.leftDirection;
            pt.type = "corner";
          }
          pts[anchorIdx] = pt;
          penTool.setSession({ ...session, points: pts });
          state.requestRender();
        },
      },
    ];
  } else if ([typeTool, verticalTypeTool, horizontalTypeMaskTool, verticalTypeMaskTool].includes(tool as any)) {
    // i129: Type (and Type Mask) tools right-click — toggle the Character
    // (Font) panel. Reuses the same "find the panel-tab button by its label
    // and synthesize a click" mechanism the F7 (Layers) shortcut already uses
    // above, rather than duplicating showCharPanel()'s guard logic.
    items = [
      {
        label: "Font Panel",
        action: () => {
          const tab = Array.from(document.querySelectorAll<HTMLButtonElement>(".panel-tab"))
            .find((t) => t.textContent?.trim() === "Character");
          tab?.click();
        },
      },
    ];
  }

  if (!items) return false;
  e.preventDefault();
  // showMenuDropdown() positions relative to an anchor element's
  // bounding rect — synthesize a zero-size one at the cursor.
  const anchor = document.createElement("div");
  anchor.style.cssText = `position:fixed;left:${e.clientX}px;top:${e.clientY}px;width:0;height:0;`;
  document.body.appendChild(anchor);
  showMenuDropdown(anchor, items);
  setTimeout(() => anchor.remove(), 0);
  return true;
}

canvas.addEventListener("contextmenu", (e) => {
  const brushTools = [brushTool, pencilTool, mixerBrushTool, colorReplacementTool, eraserTool, backgroundEraserTool, magicEraserTool, cloneStampTool, patternStampTool, dodgeBurnTool, historyBrushTool, artHistoryBrushTool];
  if (!brushTools.includes(state.activeTool as any)) {
    showToolContextMenu(e);
    return;
  }
  e.preventDefault();
  hideBrushHud();
  const hud = document.createElement("div");
  hud.style.cssText = "position:fixed;z-index:9999;background:#2a2a2a;border:1px solid #555;border-radius:5px;padding:10px 14px;box-shadow:0 2px 8px rgba(0,0,0,0.6);min-width:180px;color:#ddd;font:12px/1.5 sans-serif;";
  hud.style.left = e.clientX + "px";
  hud.style.top = e.clientY + "px";
  // i73: read/write whichever of the 12 brush-like tools is actually active
  // instead of always hardcoding brushTool/eraserTool.
  const activeBrush = state.activeTool as any;
  const size = activeBrush?.settings?.size ?? 20;
  const hard = Math.round((activeBrush?.settings?.hardness ?? 0.8) * 100);
  const htmlStr = '<div style="margin-bottom:8px;font-weight:bold;color:#ccc">Brush</div>' +
    '<label style="display:flex;align-items:center;gap:8px;margin-bottom:6px">' +
    '<span style="width:60px">Size</span>' +
    '<input type="range" min="1" max="500" value="' + size + '" style="flex:1" data-hud="size">' +
    '<span style="width:30px;text-align:right" data-hud-val="size">' + size + '</span></label>' +
    '<label style="display:flex;align-items:center;gap:8px">' +
    '<span style="width:60px">Hardness</span>' +
    '<input type="range" min="0" max="100" value="' + hard + '" style="flex:1" data-hud="hardness">' +
    '<span style="width:30px;text-align:right" data-hud-val="hardness">' + hard + '%</span></label>';
  hud.innerHTML = htmlStr;
  hud.addEventListener("input", (ev) => {
    const inp = ev.target as HTMLInputElement;
    const v = parseInt(inp.value, 10);
    const t = state.activeTool as any;
    if (!t?.settings) return;
    if (inp.dataset["hud"] === "size") {
      t.settings.size = v;
      const valEl = hud.querySelector("[data-hud-val=size]") as HTMLElement;
      if (valEl) valEl.textContent = String(v);
      state.requestRender();
      toolUx.refreshOptions();
    } else if (inp.dataset["hud"] === "hardness") {
      const hv = v / 100;
      t.settings.hardness = hv;
      const valEl = hud.querySelector("[data-hud-val=hardness]") as HTMLElement;
      if (valEl) valEl.textContent = v + "%";
      state.requestRender();
      toolUx.refreshOptions();
    }
  });
  document.body.appendChild(hud);
  // Clamp within the viewport so the HUD isn't cut off / behind the statusbar
  // when right-clicking near the right or bottom edge of the canvas. (audit F7 —
  // it was positioned at raw e.clientX/clientY with no clamp, unlike the
  // floating panels which already clamp to innerWidth/innerHeight.)
  {
    const PAD = 4;
    const hr = hud.getBoundingClientRect();
    let hleft = e.clientX;
    let htop = e.clientY;
    if (hleft + hr.width > innerWidth - PAD) hleft = innerWidth - hr.width - PAD;
    if (hleft < PAD) hleft = PAD;
    if (htop + hr.height > innerHeight - PAD) htop = innerHeight - hr.height - PAD;
    if (htop < PAD) htop = PAD;
    hud.style.left = `${Math.round(hleft)}px`;
    hud.style.top = `${Math.round(htop)}px`;
  }
  brushHud = hud;
  // Close on next pointer-down outside the HUD.
  const closeHud = (ev: PointerEvent) => {
    if (!hud.contains(ev.target as Node)) {
      hideBrushHud();
      document.removeEventListener("pointerdown", closeHud, { capture: true });
    }
  };
  setTimeout(() => document.addEventListener("pointerdown", closeHud, { capture: true }), 0);
});


// Wheel: plain = pan (Photoshop/Affinity default), modifier = zoom.
// i224: Shift+wheel forces horizontal panning regardless of deltaX.
canvas.addEventListener("wheel", (e) => {
  e.preventDefault();
  const r = canvas.getBoundingClientRect();
  if (e.ctrlKey || e.metaKey || e.altKey) {
    const sx = e.clientX - r.left;
    const sy = e.clientY - r.top;
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    state.setViewport(zoomAt(state.viewport, factor, sx, sy));
  } else {
    state.setViewport(clampPanPatch({
      panX: state.viewport.panX - (e.shiftKey ? e.deltaY : e.deltaX),
      panY: state.viewport.panY - (e.shiftKey ? 0 : e.deltaY),
    }));
  }
  // i195: keep an open Type editing session glued to its layer instead of
  // visually drifting out of sync with the canvas on pan/zoom.
  if (textEditor.isActive()) textEditor.reposition(state.viewport, r);
}, { passive: false });

// Keyboard shortcuts
function isEditableTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  if (t.matches("input, textarea, select")) return true;
  if (t.isContentEditable) return true;
  return false;
}

// Panel visibility (Tab key toggle â€” Photoshop convention)
let panelsVisible = true;

function togglePanels(): void {
  panelsVisible = !panelsVisible;
  const toolrail = document.querySelector(".toolrail") as HTMLElement;
  const panels = document.querySelector(".panels") as HTMLElement;
  const optionsbar = document.querySelector(".optionsbar") as HTMLElement;
  const statusbar = document.querySelector(".statusbar") as HTMLElement;
  if (toolrail) toolrail.style.display = panelsVisible ? "" : "none";
  if (panels) panels.style.display = panelsVisible ? "" : "none";
  if (optionsbar) optionsbar.style.display = panelsVisible ? "" : "none";
  if (statusbar) statusbar.style.display = panelsVisible ? "" : "none";
  // Trigger resize so canvas fills the freed space
  resize();
}

function toggleSidePanels(): void {
  panelsVisible = !panelsVisible;
  const toolrail = document.querySelector(".toolrail") as HTMLElement;
  const panels = document.querySelector(".panels") as HTMLElement;
  if (toolrail) toolrail.style.display = panelsVisible ? "" : "none";
  if (panels) panels.style.display = panelsVisible ? "" : "none";
  resize();
}

// Tab key must be captured before browser focus navigation intercepts it.
document.addEventListener("keydown", (e) => {
  if (e.key === "Tab" && !isEditableTarget(e.target)) {
    e.preventDefault();
    if (e.shiftKey) {
      toggleSidePanels();
    } else {
      togglePanels();
    }
  }
}, { capture: true });

document.addEventListener("keydown", (e) => {
  if (isEditableTarget(e.target)) return;
  if (e.metaKey || e.ctrlKey) {
    // Photoshop modifier-on-Space conventions:
    //   Ctrl+Space      â†’ temporary Zoom In tool
    //   Ctrl+Alt+Space  â†’ temporary Zoom Out tool
    // We activate the Zoom tool; the user clicks to zoom. Releasing the
    // modifier reverts to the previous tool (handled in keyup below).
    if (e.code === "Space" && e.altKey) {
      e.preventDefault();
      if (!spacePan && state.activeTool) spacePan = { prev: state.activeTool };
      state.setActiveTool(zoomTool);
      (zoomTool as any).altMode = true;
      highlightToolByShortcut("Z");
      return;
    }
    if (e.code === "Space") {
      e.preventDefault();
      if (!spacePan && state.activeTool) spacePan = { prev: state.activeTool };
      state.setActiveTool(zoomTool);
      (zoomTool as any).altMode = false;
      highlightToolByShortcut("Z");
      return;
    }
    // Most-specific modifier combos first within each key to prevent generic
    // branches from swallowing e.g. Ctrl+Alt+Z before Ctrl+Z, Ctrl+Shift+N
    // before Ctrl+N, etc.  (fixes #171 / #180 â€” dispatch order)
    //
    // IMPORTANT: with Shift held, browsers report e.key as the SHIFTED
    // character ("N" not "n", ":" not ";"). Normalize single-char keys to
    // lowercase so the `&& e.shiftKey` branches actually match in real
    // browsers (synthetic test events don't auto-uppercase â€” live ones do).
    const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    if (k === "z" && e.altKey) { e.preventDefault(); state.stepBackward(); return; }
    if (k === "z") { e.preventDefault(); e.shiftKey ? state.redo() : state.undo(); return; }
    if (e.code === "Equal" || k === "+") { e.preventDefault(); zoomCentered(2); return; }
    if (e.code === "Minus" || k === "-") { e.preventDefault(); zoomCentered(0.5); return; }
    // i114: Ctrl+Alt+0 = Print Size; plain Ctrl+0 = Fit on Screen. The alt
    // branch must come first or it'd never be reached (both match k === "0").
    if (k === "0" && e.altKey) { e.preventDefault(); viewPrintSize(); return; }
    if (k === "0") { e.preventDefault(); fitDoc(); return; }
    if (k === "1") { e.preventDefault(); resetTo100(); return; }
    if (k === "o") { e.preventDefault(); fileInput.click(); return; }
    if (k === "s" && e.shiftKey && e.altKey) { e.preventDefault(); saveForWeb(); return; }
    if (k === "s" && e.shiftKey) { e.preventDefault(); showSaveAs(); return; }
    if (k === "s") { e.preventDefault(); if (state.doc && state.savedPath) { savePngSilent(); } else { showSaveAs(); } return; }
    if (k === "w") { e.preventDefault(); closeDoc(); return; }
    if (k === "n" && e.shiftKey) { e.preventDefault(); addNewLayer(); return; }
    if (k === "n") { e.preventDefault(); newDocument(); return; }
    if (k === "j" && e.shiftKey) { e.preventDefault(); layerViaCut(); return; }
    if (k === "j") { e.preventDefault(); layerViaCopy(); return; }
    if (k === "g" && e.altKey) { e.preventDefault(); e.shiftKey ? releaseClippingMask() : createClippingMask(); return; }
    if (k === "g") { e.preventDefault(); e.shiftKey ? ungroupActive() : groupActive(); return; }
    // Merge / Stamp visible. Most specific modifier combo first.
    if (k === "e" && e.shiftKey && e.altKey) { e.preventDefault(); state.execute(new StampVisibleCommand()); return; }
    if (k === "e" && e.shiftKey) { e.preventDefault(); state.execute(new MergeVisibleCommand()); return; }
    if (k === "e") { e.preventDefault(); mergeDownActive(); return; }
    if (k === "k" && e.altKey && e.shiftKey) { e.preventDefault(); openShortcutsEditor(); return; }
    // i216: Ctrl+Shift+K = Color Settings… (menus.ts already lists this
    // shortcut label; this is the actual keyboard binding). Must come after
    // the more-specific Alt+Shift+K branch above.
    if (k === "k" && e.shiftKey) { e.preventDefault(); promptColorSettings(); return; }
    if (k === "f" && e.shiftKey) { e.preventDefault(); fadeLastOperation(); return; }
    if (k === "f") { e.preventDefault(); repeatLastFilter(); return; }
    if (k === "p") { e.preventDefault(); showPrintDialog(); return; }
    if (k === "y" && e.shiftKey) { e.preventDefault(); gamutWarningEnabled = !gamutWarningEnabled; proofColorsEnabled = false; state.requestRender(); return; }
    if (k === "y") { e.preventDefault(); proofColorsEnabled = !proofColorsEnabled; gamutWarningEnabled = false; state.requestRender(); return; }
    if (k === "r") { e.preventDefault(); toggleRulers(); return; }
    if (k === "h") { e.preventDefault(); toggleExtras(); return; }
    // i216: Shift+Ctrl+' = Quick Export as PNG (menus.ts's "Shift+Ctrl+'"
    // label). Uses e.code (like the Semicolon combos below) rather than
    // e.key, since Shift turns the apostrophe key into a different character
    // ('"') on common layouts — matching by code is layout-independent and
    // must be checked before the plain (no-Shift) apostrophe branch.
    if (e.code === "Quote" && e.shiftKey) { e.preventDefault(); quickExportPng(); return; }
    if (k === "'" || k === "'") { e.preventDefault(); toggleGrid(); return; }
    if (e.code === "Semicolon" && e.shiftKey && e.altKey) { e.preventDefault(); toggleLockGuides(); return; }
    if (e.code === "Semicolon" && e.shiftKey) { e.preventDefault(); toggleSnap(); return; }
    if (e.code === "Semicolon") { e.preventDefault(); toggleGuides(); return; }
    // Auto-adjustments (Photoshop standard shortcuts; order matters â€” most
    // specific modifier combos before less specific).
    if (k === "l" && e.shiftKey && e.altKey) { e.preventDefault(); promptAutoContrast(); return; }
    if (k === "l" && e.shiftKey) { e.preventDefault(); promptAutoTone(); return; }
    if (k === "b" && e.shiftKey) { e.preventDefault(); promptAutoColor(); return; }
    if (k === "a") { e.preventDefault(); selectAll(); return; }
    if (k === "d" && e.shiftKey) { e.preventDefault(); reselect(); return; }
    if (k === "d") { e.preventDefault(); deselect(); return; }
    if (k === "i" && e.shiftKey) { e.preventDefault(); selectInverse(); return; }
    // Image shortcuts â€” alt variants MUST precede the generic i/c branches below.
    if (k === "i" && e.altKey) { e.preventDefault(); promptImageSize(); return; }
    if (k === "c" && e.altKey) { e.preventDefault(); promptCanvasSize(); return; }
    if (k === "t") {
      e.preventDefault();
      if (freeTransformTool.begin(toolCtx())) {
        state.setActiveTool(freeTransformTool);
        highlightToolByShortcut("T");
      } else {
        // i198: report *why* Ctrl+T did nothing instead of failing silently,
        // mirroring the lock-tip pattern used by PaintBucketTool/GradientTool.
        const tDoc = state.doc;
        const tLayer = tDoc?.activeLayerId ? findLayer(tDoc.layers, tDoc.activeLayerId) : null;
        if (!tDoc?.activeLayerId) showTip("No layer selected.");
        else if (!tLayer || tLayer.lockAll || tLayer.lockPosition) showTip("Layer is locked.");
        else if (!tLayer.visible) showTip("Layer is hidden.");
        else showTip("Free Transform is not available for this layer.");
      }
      return;
    }
    if (k === "l") { e.preventDefault(); promptLevels(); return; }
    if (k === "m") { e.preventDefault(); promptCurves(); return; }
    // Most-specific modifier first: Ctrl+Shift+U â†’ desaturate before Ctrl+U â†’ hue/sat.
    if (k === "u" && e.shiftKey) { e.preventDefault(); desaturateImage(); return; }
    if (k === "u") { e.preventDefault(); promptHueSaturation(); return; }
    if (k === "b") { e.preventDefault(); promptColorBalance(); return; }
    if (k === "i") { e.preventDefault(); promptInvert(); return; }
    // Edit shortcuts
    if (k === "x") { e.preventDefault(); cutSelection(); return; }
    if (k === "c" && e.shiftKey) { e.preventDefault(); copyMergedSelection(); return; }
    if (k === "c") { e.preventDefault(); copySelection(); return; }
    // Most-specific first: Alt+Ctrl+Shift+V â†’ Paste Into, Ctrl+Shift+V â†’ Paste In Place, Ctrl+V â†’ Paste
    if (k === "v" && e.altKey && e.shiftKey) { e.preventDefault(); pasteInto(); return; }
    if (k === "v" && e.shiftKey) { e.preventDefault(); pasteInPlace(); return; }
    if (k === "v") { e.preventDefault(); pasteClipboard(); return; }
    // Layer order: Bring Forward / Bring to Front, Send Backward / Send to Back.
    // PS: Ctrl+] / Ctrl+[ for forward/backward; Shift to jump to absolute front/back.
    if (e.code === "BracketRight" && e.shiftKey) { e.preventDefault(); reorderActiveLayer("front"); return; }
    if (e.code === "BracketRight") { e.preventDefault(); reorderActiveLayer("forward"); return; }
    if (e.code === "BracketLeft" && e.shiftKey) { e.preventDefault(); reorderActiveLayer("back"); return; }
    if (e.code === "BracketLeft") { e.preventDefault(); reorderActiveLayer("backward"); return; }
    // Fill with Background (Photoshop convention).
    if (k === "Backspace" || k === "Delete") { e.preventDefault(); fillWithBackground(); return; }
    return;
  }
  if (e.code === "Space" && !spacePan && state.activeTool && state.activeTool !== handTool) {
    // #215: if the active tool has an in-progress drag, forward Space to it first.
    if (state.activeTool.isDragging?.() && state.activeTool.onKeyDown) {
      const consumed = state.activeTool.onKeyDown(toolCtx(), " ");
      if (consumed) { e.preventDefault(); return; }
    }
    spacePan = { prev: state.activeTool };
    state.setActiveTool(handTool);
    highlightToolByShortcut("H");
    e.preventDefault();
    return;
  }
  const key = e.key.toUpperCase();
  if (key === "D") { state.resetColors(); return; }
  if (key === "X") { state.swapColors(); return; }
  if (key === "Q") {
    const next = !state.quickMask;
    state.setQuickMask(next);
    renderer.setQuickMask(next);
    // Exit Q mode: emit the modified selection as an undoable command.
    // (Entering Q mode doesn't create an undo entry, so Ctrl+Z goes back
    // to the pre-Q-mode selection.)
    if (!next && state.doc?.selection) {
      state.execute(new SetSelectionCommand(state.doc.selection));
    }
    state.requestRender();
    return;
  }
  if (key === "F") { cycleScreenMode(); return; }
  if (e.key === "Escape" && screenMode !== "standard") {
    screenMode = "standard";
    const root = document.documentElement;
    root.classList.remove("screen-standard", "screen-full-menu", "screen-full-screen");
    root.classList.add("screen-standard");
    if (document.fullscreenElement) document.exitFullscreen?.();
    return;
  }
  if (key === "[" || e.key === "[") { adjustBrushSize(-5); return; }
  if (key === "]" || e.key === "]") { adjustBrushSize(+5); return; }
  if (key === "{" || e.key === "{") { adjustBrushHardness(-0.25); return; }
  if (key === "}" || e.key === "}") { adjustBrushHardness(+0.25); return; }

  // Polygonal Lasso: Backspace removes last anchor point
  if (e.key === "Backspace" && state.activeTool === polyLassoTool) {
    e.preventDefault();
    (polyLassoTool as any).removeLastPoint();
    state.requestRender();
    return;
  }

  // Photoshop Fill shortcuts (no Ctrl â€” Ctrl variant is in the Ctrl block):
  //   Alt+Backspace   â†’ Fill with Foreground
  //   Shift+Backspace â†’ Fill dialog
  if ((e.key === "Backspace" || e.key === "Delete") && e.altKey && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
    e.preventDefault();
    fillWithForeground();
    return;
  }
  if ((e.key === "Backspace" || e.key === "Delete") && e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
    e.preventDefault();
    promptFill();
    return;
  }

  // Delete/Backspace clears selection area
  if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); clearSelection(); return; }

  // PageUp / PageDown â€” scroll one viewport screen vertically.
  // Home / End â€” scroll to top / bottom of document.
  // Photoshop convention.
  if (e.key === "PageUp" || e.key === "PageDown" || e.key === "Home" || e.key === "End") {
    e.preventDefault();
    if (!state.doc) return;
    const r = viewportEl.getBoundingClientRect();
    if (e.shiftKey) {
      // i203: Shift+PageUp/PageDown/Home/End scroll horizontally instead of vertically.
      const screenW = r.width;
      let panX = state.viewport.panX;
      if (e.key === "PageUp") panX += screenW;
      else if (e.key === "PageDown") panX -= screenW;
      else if (e.key === "Home") panX = 0;
      else if (e.key === "End") panX = -(state.doc.width * state.viewport.zoom - screenW);
      state.setViewport(clampPanPatch({ panX }));
      return;
    }
    const screenH = r.height;
    let panY = state.viewport.panY;
    if (e.key === "PageUp") panY += screenH;
    else if (e.key === "PageDown") panY -= screenH;
    else if (e.key === "Home") panY = 0;
    else if (e.key === "End") panY = -(state.doc.height * state.viewport.zoom - screenH);
    state.setViewport(clampPanPatch({ panY }));
    return;
  }

  // Arrow keys nudge the active layer — only when Move tool (V) is active (PS behaviour #217).
  if (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "ArrowLeft" || e.key === "ArrowRight") {
    // i199: while Free Transform is active, arrow keys nudge the TRANSFORM
    // (Shift = 10px, else 1px) — must be handled here, before the moveTool-only
    // guard below returns, otherwise the tool never receives the key.
    if (freeTransformTool.isActive()) {
      e.preventDefault();
      freeTransformTool.onKeyDown(toolCtx(), e.key, { shift: e.shiftKey, alt: e.altKey, ctrl: e.ctrlKey, meta: e.metaKey });
      state.requestRender();
      return;
    }
    if (state.activeTool !== moveTool) return;
    if (!state.doc?.activeLayerId) return;
    e.preventDefault();
    const step = e.shiftKey ? 10 : 1;
    let dx = 0, dy = 0;
    if (e.key === "ArrowUp") dy = -step;
    if (e.key === "ArrowDown") dy = step;
    if (e.key === "ArrowLeft") dx = -step;
    if (e.key === "ArrowRight") dx = step;
    const layer = findLayer(state.doc.layers, state.doc.activeLayerId);
    if (!layer) return;
    // i54: respect Lock Position / Lock All, mirroring MoveTool's own drag guard.
    if (layer.lockAll || layer.lockPosition) return;
    state.execute(new MoveLayerCommand(layer.id, layer.x, layer.y, layer.x + dx, layer.y + dy));
    return;
  }

  // Brush opacity 0-9 shortcuts (Photoshop convention) â€” only when brush/eraser is active
  if (state.activeTool === brushTool || state.activeTool === eraserTool) {
    const digitKey = e.key;
    if (digitKey >= "0" && digitKey <= "9") {
      e.preventDefault();
      if (e.shiftKey) {
        setBrushFlowFromKey(parseInt(digitKey, 10));
      } else {
        setBrushOpacityFromKey(parseInt(digitKey, 10));
      }
      return;
    }
  }

  // F5 = Toggle Brush Settings panel (Photoshop convention).
  // Shift+F5 = Fill (PS standard).
  if (e.key === "F5" && e.shiftKey) { e.preventDefault(); promptFill(); return; }
  if (e.key === "F5") { e.preventDefault(); openBrushSettingsPanel(); return; }

  // i128: the remaining function-key panel shortcuts menus.ts already
  // ADVERTISES as labels (F1 Pixelpa Help, F6 Color, F7 Layers, F8 Info,
  // Alt+F9 Actions, F12 Revert) were never actually bound anywhere — pressing
  // them did nothing. `e.key` is "F6"/"F9"/etc. regardless of whether Alt is
  // also held, so a single check below covers both "F9" and "Alt+F9".
  // F1 and F12 are browser-reserved in most browsers (help / devtools) —
  // preventDefault() is attempted but some browsers won't let JS override them;
  // there's no in-app fallback messaging beyond that (matches the other
  // unavoidably-shadowed shortcuts already in this file).
  if (e.key === "F12") { e.preventDefault(); revertDocument(); return; }
  if (e.key === "F9") { e.preventDefault(); showActionsPanel(); return; }
  if (e.key === "F6") { e.preventDefault(); openColorPanel(); return; }
  if (e.key === "F7") {
    e.preventDefault();
    const tab = Array.from(document.querySelectorAll<HTMLButtonElement>(".panel-tab")).find((t) => t.textContent?.trim() === "Layers");
    tab?.click();
    return;
  }
  if (e.key === "F8") { e.preventDefault(); openInfoPanel(); return; }
  if (e.key === "F1") { e.preventDefault(); openTutorials(); return; }

  // #215: Escape mid-drag — forward to active tool cancel() before global Escape handling.
  if (e.key === "Escape") {
    const tool = state.activeTool as any;
    if (tool?.isDragging?.() && tool?.cancel) {
      tool.cancel(toolCtx());
      state.requestRender();
      return;
    }
    // i168: Brush/Eraser/Pencil fallback — restore the pre-stroke snapshot
    // captured at pointerdown (see above) since these tools have no
    // isDragging()/cancel() of their own to forward to.
    if (dragging && escapeCancelSnapshot && (state.activeTool === brushTool || state.activeTool === eraserTool || state.activeTool === pencilTool)) {
      const layer = findRaster(state.doc?.layers ?? [], escapeCancelSnapshot.layerId);
      if (layer) {
        layer.pixels.data.set(escapeCancelSnapshot.data);
        layer.pixels.markDirty();
        state.requestRender();
      }
      escapeCancelSnapshot = null;
      return;
    }
  }

  // Enter/Escape during crop drag: commit or cancel.
  if (e.key === "Enter" && state.activeTool === cropTool) {
    cropTool.commit(toolCtx() as any);
    return;
  }
  if (e.key === "Escape" && state.activeTool === cropTool) {
    cropTool.cancel(toolCtx() as any);
    state.requestRender();
    return;
  }

  // Polygonal Lasso: Enter commits, Escape cancels.
  if (e.key === "Enter" && state.activeTool === polyLassoTool) {
    (polyLassoTool as any).commit(toolCtx());
    state.requestRender();
    return;
  }
  if (e.key === "Escape" && state.activeTool === polyLassoTool) {
    (polyLassoTool as any).cancel(toolCtx());
    state.requestRender();
    return;
  }

  // Free Transform: Enter commits, Escape cancels (when active).
  if (e.key === "Enter" && freeTransformTool.isActive()) {
    freeTransformTool.commit(toolCtx() as any);
    // audit i25: record that a Free Transform just committed so Transform
    // Again (Shift+Ctrl+T) gives an honest "not supported yet" message
    // instead of silently doing nothing â€” replaying the interactive drag
    // itself is out of scope here (see audit i20).
    lastTransform = { kind: "free-transform" };
    state.requestRender();
    return;
  }
  if (e.key === "Escape" && freeTransformTool.isActive()) {
    freeTransformTool.cancel(toolCtx() as any);
    state.requestRender();
    return;
  }

  // Forward key events to the active tool (for Pen tool path completion etc.).
  // i199: pass the live modifier state so tools can vary behaviour (e.g.
  // FreeTransform nudges 10px on Shift+Arrow vs 1px).
  state.activeTool?.onKeyDown?.(toolCtx(), e.key, { shift: e.shiftKey, alt: e.altKey, ctrl: e.ctrlKey, meta: e.metaKey });

  // Pen tool: Enter rasterizes the path through the command system.
  if (e.key === "Enter" && state.activeTool === penTool) {
    const session = penTool.getSession();
    if (session && session.points.length >= 2) {
      rasterizePathOnLayer(session.points, session.closed, state.fgColor, 2);
    }
    return;
  }

  // Marquee + Lasso group cycling (Shift+key = next sub-tool, PS convention).
  // PS exposes 4 marquee shapes: rect, ellipse, single-row, single-column.
  if (key === "M") {
    const mTools = [marqueeRectTool, marqueeEllipseTool, marqueeSingleRowTool, marqueeSingleColumnTool];
    const currentIdx = mTools.indexOf(state.activeTool as any);
    const nextIdx = e.shiftKey ? (currentIdx + 1) % mTools.length : (currentIdx <= 0 ? 0 : currentIdx);
    const t = mTools[nextIdx] ?? marqueeRectTool;
    state.setActiveTool(t);
    highlightToolByShortcut("M");
    return;
  }
  if (key === "L") {
    const lTools = [lassoTool, polyLassoTool, magneticLassoTool];
    const currentIdx = lTools.indexOf(state.activeTool as any);
    // i69: a bare L press after switching away to an unrelated tool restores
    // the last-used Lasso-group variant instead of always resetting to Lasso.
    let nextIdx: number;
    if (e.shiftKey) nextIdx = (currentIdx + 1) % lTools.length;
    else if (currentIdx === -1) nextIdx = Math.max(0, lTools.indexOf(lastLassoVariant as any));
    else nextIdx = currentIdx;
    const t = lTools[nextIdx] ?? lassoTool;
    lastLassoVariant = t;
    state.setActiveTool(t);
    highlightToolByShortcut("L");
    return;
  }

  // A key: Path Selection; Shift+A cycles to Direct Selection (PS convention)
  if (key === "A") {
    const aTools = [pathSelectionTool, directSelectionTool];
    const currentIdx = aTools.indexOf(state.activeTool as any);
    const nextIdx = e.shiftKey ? (currentIdx + 1) % aTools.length : (currentIdx <= 0 ? 0 : currentIdx);
    const t = aTools[nextIdx] ?? pathSelectionTool;
    state.setActiveTool(t);
    highlightToolByShortcut("A");
    return;
  }

  // G key: cycle between Gradient and Paint Bucket (Shift+G = next)
  if (key === "G") {
    const gTools = [gradientTool, paintBucketTool];
    const currentIdx = gTools.indexOf(state.activeTool as any);
    const nextIdx = e.shiftKey ? (currentIdx + 1) % gTools.length : (currentIdx <= 0 ? 0 : currentIdx);
    const t = gTools[nextIdx] ?? gradientTool;
    state.setActiveTool(t);
    highlightToolByShortcut("G");
    return;
  }

  // R key: Rotate View tool (Photoshop convention). Blur / Sharpen / Smudge
  // have no default single-key shortcut in PS â€” they live in the toolbar
  // flyout under their own group and are accessible there only.
  if (key === "R") {
    state.setActiveTool(rotateViewTool);
    highlightToolByShortcut("R");
    return;
  }

  // O key: cycle between Dodge, Burn, Sponge (Shift+O = next)
  if (key === "O") {
    const modes: import("./core/tools/DodgeBurnTool").DodgeBurnMode[] = ["dodge", "burn", "sponge"];
    const currentIdx = modes.indexOf(dodgeBurnTool.mode);
    const nextIdx = e.shiftKey ? (currentIdx + 1) % modes.length : (currentIdx <= 0 ? 0 : currentIdx);
    dodgeBurnTool.mode = modes[nextIdx] ?? "dodge";
    state.setActiveTool(dodgeBurnTool);
    highlightToolByShortcut("O");
    return;
  }

  // W key: cycle between Object Selection, Quick Selection, Magic Wand (Shift+W = next)
  if (key === "W") {
    const wTools = [objectSelectionTool, quickSelectionTool, magicWandTool];
    const currentIdx = wTools.indexOf(state.activeTool as any);
    // i69: a bare W press after switching away to an unrelated tool restores
    // the last-used Wand-group variant instead of always resetting to Object Selection.
    let nextIdx: number;
    if (e.shiftKey) nextIdx = (currentIdx + 1) % wTools.length;
    else if (currentIdx === -1) nextIdx = Math.max(0, wTools.indexOf(lastWandVariant as any));
    else nextIdx = currentIdx;
    const t = wTools[nextIdx] ?? objectSelectionTool;
    lastWandVariant = t;
    state.setActiveTool(t);
    highlightToolByShortcut("W");
    return;
  }

  // B key: cycle between Brush, Pencil, Mixer Brush, Color Replacement (Shift+B = next)
  if (key === "B") {
    const bTools = [brushTool, pencilTool, mixerBrushTool, colorReplacementTool];
    const currentIdx = bTools.indexOf(state.activeTool as any);
    const nextIdx = e.shiftKey ? (currentIdx + 1) % bTools.length : (currentIdx <= 0 ? 0 : currentIdx);
    const t = bTools[nextIdx] ?? brushTool;
    state.setActiveTool(t);
    highlightToolByShortcut("B");
    return;
  }

  // E key: cycle between Eraser, Background Eraser, Magic Eraser (Shift+E = next)
  if (key === "E") {
    const eTools = [eraserTool, backgroundEraserTool, magicEraserTool];
    const currentIdx = eTools.indexOf(state.activeTool as any);
    const nextIdx = e.shiftKey ? (currentIdx + 1) % eTools.length : (currentIdx <= 0 ? 0 : currentIdx);
    const t = eTools[nextIdx] ?? eraserTool;
    state.setActiveTool(t);
    highlightToolByShortcut("E");
    return;
  }

  // S key: cycle between Clone Stamp, Pattern Stamp (Shift+S = next)
  if (key === "S") {
    const sTools = [cloneStampTool, patternStampTool];
    const currentIdx = sTools.indexOf(state.activeTool as any);
    const nextIdx = e.shiftKey ? (currentIdx + 1) % sTools.length : (currentIdx <= 0 ? 0 : currentIdx);
    const t = sTools[nextIdx] ?? cloneStampTool;
    state.setActiveTool(t);
    highlightToolByShortcut("S");
    return;
  }

  // I key: cycle between Eyedropper, Color Sampler, Ruler, Note, Count (Shift+I = next)
  if (key === "I") {
    const iTools = [eyedropperTool, colorSamplerTool, rulerTool, noteTool, countTool];
    const currentIdx = iTools.indexOf(state.activeTool as any);
    const nextIdx = e.shiftKey ? (currentIdx + 1) % iTools.length : (currentIdx <= 0 ? 0 : currentIdx);
    const t = iTools[nextIdx] ?? eyedropperTool;
    state.setActiveTool(t);
    highlightToolByShortcut("I");
    return;
  }

  // J key: cycle between Spot Healing, Healing Brush, Patch, Content-Aware Move,
  // Red Eye, Remove (Shift+J = next). audit i11: ContentAwareMoveTool and
  // RemoveTool were instantiated + registered in the `tools` id-map but never
  // reachable from any flyout/cycle/menu — add them to the J-group roster.
  if (key === "J") {
    const jTools = [spotHealingTool, healingBrushTool, patchTool, contentAwareMoveTool, redEyeTool, removeTool];
    const currentIdx = jTools.indexOf(state.activeTool as any);
    const nextIdx = e.shiftKey ? (currentIdx + 1) % jTools.length : (currentIdx <= 0 ? 0 : currentIdx);
    const t = jTools[nextIdx] ?? spotHealingTool;
    state.setActiveTool(t);
    highlightToolByShortcut("J");
    return;
  }

  // P key: cycle through Pen tool variants (Shift+P = next)
  if (key === "P") {
    const penModes: PenMode[] = ["pen", "freeform", "curvature", "add-anchor", "delete-anchor", "convert-point"];
    const penLabels = ["Pen", "Freeform Pen", "Curvature Pen", "Add Anchor", "Delete Anchor", "Convert Point"];
    const currentIdx = penModes.indexOf(penTool.mode);
    const nextIdx = e.shiftKey ? (currentIdx + 1) % penModes.length : (currentIdx <= 0 ? 0 : currentIdx);
    penTool.mode = penModes[nextIdx] ?? "pen";
    state.setActiveTool(penTool);
    highlightToolByShortcut("P");
    return;
  }

  // U key: cycle between Rectangle, Rounded Rectangle, Ellipse, Triangle, Polygon, Line, Custom (Shift+U = next).
  // i302: order matches Photoshop's actual U-tool cycle order (was Rectangle,
  // Ellipse, Polygon, Rounded Rectangle, Line, Triangle, Custom).
  if (key === "U") {
    const shapeTools = [rectShapeTool, roundedRectShapeTool, ellipseShapeTool, triangleShapeTool, polygonShapeTool, lineShapeTool, customShapeTool2];
    const currentIdx = shapeTools.indexOf(state.activeTool as any);
    const nextIdx = e.shiftKey ? (currentIdx + 1) % shapeTools.length : (currentIdx <= 0 ? 0 : currentIdx);
    const t = shapeTools[nextIdx] ?? rectShapeTool;
    state.setActiveTool(t);
    highlightToolByShortcut("U");
    return;
  }

  // Y key: cycle between History Brush and Art History Brush (Shift+Y = next)
  if (key === "Y") {
    const yTools = [historyBrushTool, artHistoryBrushTool];
    const currentIdx = yTools.indexOf(state.activeTool as any);
    const nextIdx = e.shiftKey ? (currentIdx + 1) % yTools.length : (currentIdx <= 0 ? 0 : currentIdx);
    const t = yTools[nextIdx] ?? historyBrushTool;
    state.setActiveTool(t);
    highlightToolByShortcut("Y");
    return;
  }

  // K key: Frame tool
  if (key === "K") {
    state.setActiveTool(frameTool);
    highlightToolByShortcut("K");
    return;
  }

  // T key: Type tools cycle (Shift+T = next: Horizontal â†’ Vertical â†’ H-Mask â†’ V-Mask)
  // PS: bare T always activates the type flyout; Ctrl+T is Free Transform.
  if (key === "T") {
    // If FreeTransform is active, don't switch away.
    if (freeTransformTool.isActive()) return;
    const tTools = [typeTool, verticalTypeTool, horizontalTypeMaskTool, verticalTypeMaskTool];
    const currentIdx = tTools.indexOf(state.activeTool as any);
    const nextIdx = e.shiftKey ? (currentIdx + 1) % tTools.length : (currentIdx <= 0 ? 0 : currentIdx);
    const t = tTools[nextIdx] ?? typeTool;
    lateInitTypeTools();
    state.setActiveTool(t);
    highlightToolByShortcut("T");
    return;
  }

  // Prevent bare T from switching away from FreeTransformTool back to Type.
  if (key === "T" && freeTransformTool.isActive()) return;

  if (tools[key]) {
    // #223: Spring-loaded tool keys — hold > 300ms = spring (restores on keyup); tap = permanent.
    const prev = state.activeTool;
    if (prev && prev !== tools[key] && !springTool) {
      springTool = { prev, key, downAt: e.timeStamp };
    }
    state.setActiveTool(tools[key]!);
    highlightToolByShortcut(key);
  } else if (railButtons.has(key)) {
    // Highlight only — no real tool yet.
    highlightToolByShortcut(key);
  }
});

document.addEventListener("keyup", (e) => {
  // #215: SpaceUp — forward to previous tool so MarqueeTool exits reposition mode.
  if (e.code === "Space" && spacePan) {
    spacePan.prev.onKeyDown?.(toolCtx(), "SpaceUp");
    state.setActiveTool(spacePan.prev);
    highlightToolByShortcut(spacePan.prev.shortcut);
    spacePan = null;
    return;
  }
  // i58: Space was held mid-drag (e.g. to reposition an in-progress marquee)
  // and consumed directly by the active tool — no tool-swap happened, so
  // there's no `spacePan` to restore from. Forward SpaceUp straight to the
  // still-active, still-dragging tool so it resumes resizing instead of
  // staying stuck in reposition mode.
  if (e.code === "Space" && !spacePan && state.activeTool?.isDragging?.()) {
    state.activeTool.onKeyDown?.(toolCtx(), "SpaceUp");
    return;
  }
  // #217: Ctrl temp-Move — release Ctrl restores previous tool.
  if ((e.key === "Control" || e.key === "Meta") && ctrlMoveTool) {
    state.setActiveTool(ctrlMoveTool.prev);
    highlightToolByShortcut(ctrlMoveTool.prev.shortcut);
    ctrlMoveTool = null;
    return;
  }
  // #223: Spring-loaded tool key release — restore prev tool if held > 300ms.
  if (springTool && e.key.toUpperCase() === springTool.key) {
    const held = e.timeStamp - springTool.downAt;
    const wasSprint = held > 300;
    const prev = springTool.prev;
    springTool = null;
    if (wasSprint) {
      state.setActiveTool(prev);
      highlightToolByShortcut(prev.shortcut);
    }
    return;
  }
  // #218: Forward Alt-up to the active tool so it can exit temp eyedropper.
  if (e.key === "Alt") {
    // i201: live Alt-key tracking for cursor.ts's Zoom tool zoom-in/zoom-out
    // cursor swap.
    liveAltKey = false;
    updateCursor();
    const tool = state.activeTool as any;
    if (typeof tool?.onKeyUp === "function") {
      tool.onKeyUp(toolCtx(), "Alt");
    }
  }
});

// i201: track Alt keydown (paired with the Alt keyup handling above) so
// cursor.ts's interaction.altKey reflects the live modifier state even
// before the next pointer move / state change.
document.addEventListener("keydown", (e) => {
  if (e.key === "Alt" && !liveAltKey) {
    liveAltKey = true;
    updateCursor();
  }
});

function highlightToolByShortcut(key: string, variantLabel?: string): void {
  modHighlightTool(key, railButtons, variantLabel);
}

// ----- Layer operations (footer buttons + shortcuts) -----
function newBlankRasterLayer(name: string): RasterLayer {
  const doc = state.doc;
  if (!doc) throw new Error("no document");
  return {
    id: crypto.randomUUID(),
    kind: "raster",
    name,
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal",
    x: 0,
    y: 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: false,
    pixels: new PixelBuffer(doc.width, doc.height, "rgba8"),
  };
}
function newEmptyGroup(name: string): GroupLayer {
  return {
    id: crypto.randomUUID(),
    kind: "group",
    name,
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal",
    x: 0,
    y: 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: false,
    children: [],
  };
}
function addNewLayer(): void {
  if (!state.doc) return;
  const n = state.doc.layers.length + 1;
  state.execute(new NewLayerCommand(newBlankRasterLayer(`Layer ${n}`)));
}
function addNewGroup(): void {
  if (!state.doc) return;
  state.execute(new NewLayerCommand(newEmptyGroup("Group")));
}
/** True if `targetId` is nested (at any depth) inside a group whose id is in
 *  `set` — used to drop redundant delete targets (a selected group already
 *  removes its whole subtree). Returns undefined if `targetId` isn't found. */
function isNestedUnderSet(layers: readonly Layer[], targetId: string, set: ReadonlySet<string>, underSet: boolean): boolean | undefined {
  for (const l of layers) {
    if (l.id === targetId) return underSet;
    if (l.kind === "group") {
      const found = isNestedUnderSet(l.children, targetId, set, underSet || set.has(l.id));
      if (found !== undefined) return found;
    }
  }
  return undefined;
}
function deleteActiveLayer(): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  // i118: delete every layer in the current multi-selection, not just the
  // active one — same target-resolution helper mergeSelectedLayers()/
  // groupActive() already use for this.
  const ids = collectLayerTargetIds(doc.activeLayerId, state.selectedLayerIds);
  const idSet = new Set(ids);
  // Drop any target already nested inside another selected group (its subtree
  // is deleted with the group) so we don't issue a no-op delete that would add
  // a confusing do-nothing entry to the undo history.
  const topLevel = ids.filter((id) => !(isNestedUnderSet(doc.layers, id, idSet, false) ?? false));
  if (topLevel.length === 0) return;
  if (topLevel.length === 1) {
    state.execute(new DeleteLayerCommand(topLevel[0]!));
  } else {
    // One atomic history entry so the whole multi-delete undoes in a single Ctrl+Z.
    state.execute(new CompositeCommand(topLevel.map((id) => new DeleteLayerCommand(id))));
  }
}
function duplicateActiveLayer(): void {
  const id = state.doc?.activeLayerId;
  if (!id) return;
  state.execute(new DuplicateLayerCommand(id, crypto.randomUUID()));
}

async function promptNewLayer(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;
  const n = doc.layers.length + 1;

  const result = await filterDialog.open("New Layer", [
    { key: "name", label: "Name", type: "slider", default: 0, min: 0, max: 1, unit: "" },
    { key: "color", label: "Color", type: "dropdown", default: "none", options: [
      { value: "none", label: "None" },
      { value: "red", label: "Red" },
      { value: "orange", label: "Orange" },
      { value: "yellow", label: "Yellow" },
      { value: "green", label: "Green" },
      { value: "blue", label: "Blue" },
      { value: "violet", label: "Violet" },
      { value: "gray", label: "Gray" },
    ]},
    { key: "mode", label: "Mode", type: "dropdown", default: "normal", options: [
      { value: "normal", label: "Normal" },
      { value: "dissolve", label: "Dissolve" },
      { value: "multiply", label: "Multiply" },
      { value: "screen", label: "Screen" },
      { value: "overlay", label: "Overlay" },
      { value: "darken", label: "Darken" },
      { value: "lighten", label: "Lighten" },
      { value: "color-dodge", label: "Color Dodge" },
      { value: "color-burn", label: "Color Burn" },
      { value: "hard-light", label: "Hard Light" },
      { value: "soft-light", label: "Soft Light" },
      { value: "difference", label: "Difference" },
      { value: "exclusion", label: "Exclusion" },
      { value: "hue", label: "Hue" },
      { value: "saturation", label: "Saturation" },
      { value: "color", label: "Color" },
      { value: "luminosity", label: "Luminosity" },
    ]},
    { key: "opacity", label: "Opacity", type: "slider", default: 100, min: 0, max: 100, unit: "%" },
  ]);
  if (!result) return;

  // Use prompt for name since FilterDialog doesn't support text inputs
  const name = prompt("Layer name:", `Layer ${n}`);
  if (name === null) return;

  const layer: RasterLayer = {
    id: crypto.randomUUID(),
    kind: "raster",
    name: name || `Layer ${n}`,
    visible: true,
    opacity: (result.opacity as number) / 100,
    fillOpacity: 1,
    blendMode: result.mode as BlendMode,
    x: 0, y: 0,
    lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false,
    clippedToBelow: false,
    colorLabel: result.color as LayerColorLabel,
    pixels: new PixelBuffer(doc.width, doc.height, "rgba8"),
  };
  state.execute(new NewLayerCommand(layer));
  state.setActiveLayer(layer.id);
}

async function promptDuplicateLayer(): Promise<void> {
  const doc = state.doc;
  const id = doc?.activeLayerId;
  if (!doc || !id) return;
  const layer = findLayer(doc.layers, id);
  if (!layer) return;

  const result = await filterDialog.open("Duplicate Layer", [
    { key: "name", label: "Name", type: "slider", default: 0, min: 0, max: 1, unit: "" },
    { key: "target", label: "Target Document", type: "dropdown", default: "current", options: [
      { value: "current", label: doc.name },
      { value: "new", label: "New" },
    ]},
  ]);
  if (!result) return;

  const name = prompt("Layer name:", `${layer.name} copy`);
  if (name === null) return;

  if (result.target === "new") {
    // Duplicate to new document
    if (layer.kind === "raster") {
      const raster = layer as RasterLayer;
      const pixels = new PixelBuffer(raster.pixels.width, raster.pixels.height, "rgba8");
      pixels.data.set(raster.pixels.data);
      pixels.markDirty();
      const newLayer: RasterLayer = {
        ...raster,
        id: crypto.randomUUID(),
        name: name || `${layer.name} copy`,
        pixels,
      };
      const newDoc: Document = {
        // i44: a fresh, unique id -- so this new document is never conflated
        // with another open document that happens to share its display name
        // (e.g. two layers duplicated to "X copy" from documents named alike).
        id: crypto.randomUUID(),
        name: name || `${layer.name} copy`,
        width: raster.pixels.width,
        height: raster.pixels.height,
        dpi: doc.dpi,
        layers: [newLayer],
        activeLayerId: newLayer.id,
        selection: null,
      };
      state.execute(new LoadDocumentCommand(newDoc));
      fitDoc();
    }
  } else {
    // Duplicate in current document
    state.execute(new DuplicateLayerCommand(id, crypto.randomUUID()));
    // Rename the duplicated layer
    const newLayer = findLayer(doc.layers, doc.activeLayerId);
    if (newLayer && name) {
      state.execute(new RenameLayerCommand(newLayer.id, name));
    }
  }
}

function layerViaCopy(): void {
  const doc = state.doc;
  if (!doc) return;
  const layer = getActiveRaster();
  if (!layer) return;

  const sel = doc.selection;
  if (!sel || sel.isEmpty()) {
    // No selection â€” duplicate entire layer
    duplicateActiveLayer();
    return;
  }

  // Copy selected pixels to new layer
  const b = sel.bounds();
  if (!b) return;

  const { x, y, w, h } = b;
  const pixels = new PixelBuffer(w, h, "rgba8");
  const mask = sel.mask;
  const src = layer.pixels.data;
  const srcW = layer.pixels.width;
  for (let dy = 0; dy < h; dy++) {
    for (let dx = 0; dx < w; dx++) {
      const sx = x + dx, sy = y + dy;
      const si = (sy * srcW + sx) * 4;
      const di = (dy * w + dx) * 4;
      const ma = mask ? (mask[sy * srcW + sx] ?? 0) / 255 : 1;
      pixels.data[di] = src[si]!;
      pixels.data[di + 1] = src[si + 1]!;
      pixels.data[di + 2] = src[si + 2]!;
      pixels.data[di + 3] = Math.round(src[si + 3]! * ma);
    }
  }
  pixels.markDirty();

  const n = doc.layers.filter(l => l.kind === "raster").length + 1;
  const newLayer: RasterLayer = {
    id: crypto.randomUUID(), kind: "raster", name: `Layer ${n} via Copy`,
    visible: true, opacity: 1, fillOpacity: 1, blendMode: "normal",
    x: 0, y: 0, lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false, clippedToBelow: false,
    pixels,
  };
  state.execute(new NewLayerCommand(newLayer));
  state.setActiveLayer(newLayer.id);
  state.requestRender();
}

function layerViaCut(): void {
  const doc = state.doc;
  if (!doc) return;
  const layer = getActiveRaster();
  if (!layer || !doc.selection || doc.selection.isEmpty()) return;

  const sel = doc.selection;
  const b = sel.bounds();
  if (!b) return;

  const { x, y, w, h } = b;
  // Create new layer from selected pixels
  const cutPixels = new PixelBuffer(w, h, "rgba8");
  const src = layer.pixels.data;
  const srcW = layer.pixels.width;
  const mask = sel.mask;

  for (let dy = 0; dy < h; dy++) {
    for (let dx = 0; dx < w; dx++) {
      const sx = x + dx, sy = y + dy;
      const si = (sy * srcW + sx) * 4;
      const di = (dy * w + dx) * 4;
      const ma = mask ? (mask[sy * srcW + sx] ?? 0) / 255 : 1;
      cutPixels.data[di] = src[si]!;
      cutPixels.data[di + 1] = src[si + 1]!;
      cutPixels.data[di + 2] = src[si + 2]!;
      cutPixels.data[di + 3] = Math.round(src[si + 3]! * ma);
      // Clear from source
      src[si] = 0; src[si + 1] = 0; src[si + 2] = 0; src[si + 3] = 0;
    }
  }
  cutPixels.markDirty();
  layer.pixels.markDirty();

  const n = doc.layers.filter(l => l.kind === "raster").length + 1;
  const newLayer: RasterLayer = {
    id: crypto.randomUUID(), kind: "raster", name: `Layer ${n} via Cut`,
    visible: true, opacity: 1, fillOpacity: 1, blendMode: "normal",
    x: 0, y: 0, lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false, clippedToBelow: false,
    pixels: cutPixels,
  };
  state.execute(new NewLayerCommand(newLayer));
  state.setActiveLayer(newLayer.id);
  state.requestRender();
}
function groupActive(): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  // i119: group every layer in the current multi-selection (Photoshop
  // Ctrl+G on a multi-select groups all of them), not just the active one.
  const ids = collectLayerTargetIds(doc.activeLayerId, state.selectedLayerIds);
  state.execute(new GroupLayersCommand(ids, crypto.randomUUID(), "Group"));
}
function ungroupActive(): void {
  const doc = state.doc;
  const id = doc?.activeLayerId;
  if (!doc || !id) return;
  const layer = findLayer(doc.layers, id);
  if (layer?.kind === "group") state.execute(new UngroupCommand(id));
}
function mergeDownActive(): void {
  const id = state.doc?.activeLayerId;
  if (!id) return;
  state.execute(new MergeDownCommand(id));
}

// ── Layer > Align / Distribute (audit i2/i3) ────────────────────────────
// Target layers = active layer ∪ Layers-panel multi-selection (same
// resolution as transformLayerOrientationIds, minus its raster-only filter —
// Align/Distribute reposition any layer kind, like the Move tool itself).
function alignTargetIds(): string[] {
  const doc = state.doc;
  if (!doc) return [];
  return collectLayerTargetIds(doc.activeLayerId, state.selectedLayerIds);
}

function alignLayers(op: AlignOp): void {
  const doc = state.doc;
  if (!doc) return;
  const cmd = planAlignLayers(doc, alignTargetIds(), op);
  if (!cmd) return;
  state.execute(cmd);
}

function distributeLayers(op: DistributeOp): void {
  const doc = state.doc;
  if (!doc) return;
  const cmd = planDistributeLayers(doc, alignTargetIds(), op);
  if (!cmd) return;
  state.execute(cmd);
}

function flattenImage(): void {
  if (!state.doc) return;
  state.execute(new FlattenCommand());
  fitDoc();
  state.requestRender();
}

function createClippingMask(): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (!layer || layer.clippedToBelow) return;
  state.execute(new SetLayerPropertyCommand(doc.activeLayerId, { clippedToBelow: true }));
}

function releaseClippingMask(): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (!layer || !layer.clippedToBelow) return;
  state.execute(new SetLayerPropertyCommand(doc.activeLayerId, { clippedToBelow: false }));
}

function convertToSmartObject(): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (!layer || layer.kind === "group" || layer.kind === "smart-object") return;
  state.execute(new ConvertToSmartObjectCommand(doc.activeLayerId, crypto.randomUUID()));
}

function rasterizeSmartObject(): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (layer?.kind !== "smart-object") return;
  state.execute(new RasterizeSmartObjectCommand(doc.activeLayerId));
}

function editSmartObject(): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (layer?.kind !== "smart-object") return;
  const so = layer as SmartObjectLayer;
  const embedded = so.embedded;
  const newDoc: Document = {
    // i44: keep the embedded document's own id if it already has one (so
    // it's still the "same" document identity across edit sessions),
    // otherwise mint a fresh one -- never leave it unset, so this editing
    // document can't collide with another open document of the same name.
    id: embedded.id ?? crypto.randomUUID(),
    name: `[Edit] ${embedded.name}`,
    width: embedded.width,
    height: embedded.height,
    dpi: embedded.dpi,
    layers: JSON.parse(JSON.stringify(embedded.layers)),
    activeLayerId: embedded.activeLayerId,
    selection: null,
  };
  state.execute(new LoadDocumentCommand(newDoc));
  fitDoc();
  showTip(`Editing Smart Object "${so.name}". Save to apply.`);
}

async function replaceSmartObjectContents(): Promise<void> {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (layer?.kind !== "smart-object") return;
  const so = layer as SmartObjectLayer;

  const input = document.createElement("input");
  input.type = "file";
  input.accept = FILE_INPUT_ACCEPT;
  input.style.display = "none";
  document.body.appendChild(input);
  input.addEventListener("change", async () => {
    const f = input.files?.[0];
    if (!f) { input.remove(); return; }
    try {
      const { pixels, width, height } = await decodeImage(f);
      const bgLayer: RasterLayer = {
        id: crypto.randomUUID(), kind: "raster", name: "Background",
        visible: true, opacity: 1, fillOpacity: 1, blendMode: "normal",
        x: 0, y: 0, lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false, clippedToBelow: false,
        pixels,
      };
      const newEmbedded: Document = {
        name: f.name,
        width, height, dpi: 72,
        layers: [bgLayer],
        activeLayerId: bgLayer.id,
        selection: null,
      };
      const newSo: SmartObjectLayer = {
        ...so,
        embedded: newEmbedded,
        smartFilters: so.smartFilters,
      };
      state.execute(new SetLayerPropertyCommand(so.id, { embedded: newEmbedded }));
      state.requestRender();
      showTip(`Smart Object "${so.name}" contents replaced.`);
    } catch (err) {
      showError(`Replace Contents failed: ${err instanceof Error ? err.message : "unknown error"}`);
    }
    input.remove();
  });
  input.click();
}

async function exportSmartObject(): Promise<void> {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (layer?.kind !== "smart-object") return;
  const so = layer as SmartObjectLayer;
  const embedded = so.embedded;

  const offscreen = document.createElement("canvas");
  offscreen.width = embedded.width;
  offscreen.height = embedded.height;
  const ctx = offscreen.getContext("2d");
  if (!ctx) return;

  for (const l of embedded.layers) {
    if (!l.visible || l.kind !== "raster") continue;
    ctx.globalAlpha = (l as RasterLayer).opacity;
    ctx.globalCompositeOperation = nativeCompositeOp((l as RasterLayer).blendMode) || "source-over";
    const px = (l as RasterLayer).pixels;
    const imgData = new ImageData(new Uint8ClampedArray(px.data), embedded.width, embedded.height);
    const bitmap = await createImageBitmap(imgData);
    ctx.drawImage(bitmap, (l as RasterLayer).x, (l as RasterLayer).y);
  }

  offscreen.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${so.name}.png`;
    a.click();
    URL.revokeObjectURL(url);
    showTip(`Smart Object "${so.name}" exported as PNG.`);
  }, "image/png");
}

// â”€â”€ Fill Layer creation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function showFillLayerMenu(): void {
  if (!state.doc) return;
  const btn = document.querySelector<HTMLLIElement>(".menubar__items li");
  if (!btn) return;
  showMenuDropdown(btn, [
    { label: "Solid Colorâ€¦", action: addSolidFillLayer },
    { label: "Gradientâ€¦", action: addGradientFillLayer },
    { label: "Patternâ€¦", action: addPatternFillLayer },
  ]);
}

function addSolidFillLayer(): void {
  if (!state.doc) return;
  const color = state.fgColor;
  const layer = createSolidFillLayer({
    color: { r: color.r, g: color.g, b: color.b, a: 1 },
    name: "Color Fill 1",
  });
  state.execute(new NewLayerCommand(layer));
  state.setActiveLayer(layer.id);
}

async function addGradientFillLayer(): Promise<void> {
  if (!state.doc) return;
  const result = await filterDialog.open("Gradient Fill", [
    { key: "type", label: "Type", type: "dropdown", default: "linear", options: [
      { value: "linear", label: "Linear" },
      { value: "radial", label: "Radial" },
      { value: "angle", label: "Angle" },
      { value: "reflected", label: "Reflected" },
      { value: "diamond", label: "Diamond" },
    ]},
    { key: "angle", label: "Angle", type: "slider", default: 90, min: 0, max: 360, unit: "Â°" },
    { key: "scale", label: "Scale", type: "slider", default: 100, min: 1, max: 200, unit: "%" },
    { key: "reverse", label: "Reverse", type: "checkbox", default: false },
  ]);
  if (!result) return;

  const layer = createGradientFillLayer({
    gradientType: result.type as any,
    angle: result.angle as number,
    scale: result.scale as number,
    name: "Gradient Fill 1",
  });
  state.execute(new NewLayerCommand(layer));
  state.setActiveLayer(layer.id);
}

async function addPatternFillLayer(): Promise<void> {
  if (!state.doc) return;
  const result = await filterDialog.open("Pattern Fill", [
    { key: "scale", label: "Scale", type: "slider", default: 100, min: 1, max: 200, unit: "%" },
  ]);
  if (!result) return;

  const layer = createPatternFillLayer({
    scale: result.scale as number,
    name: "Pattern Fill 1",
  });
  state.execute(new NewLayerCommand(layer));
  state.setActiveLayer(layer.id);
}

function addNewArtboard(): void {
  if (!state.doc) return;
  const layer = createArtboardLayer({
    width: state.doc.width,
    height: state.doc.height,
    name: "Artboard 1",
  });
  state.execute(new NewLayerCommand(layer));
  state.setActiveLayer(layer.id);
}

// â”€â”€ Content-Aware Fill â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function promptContentAwareFill(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;
  const layer = getActiveRaster();
  if (!layer) { showError("No active raster layer."); return; }
  const sel = doc.selection;
  if (!sel || sel.isEmpty()) { showError("No selection. Make a selection first."); return; }

  const result = await filterDialog.open("Content-Aware Fill", [
    { key: "radius", label: "Sampling Radius", type: "slider", default: 50, min: 5, max: 200, unit: "px" },
    { key: "patchSize", label: "Patch Size", type: "slider", default: 5, min: 3, max: 15, unit: "px" },
    { key: "colorAdapt", label: "Color Adaptation", type: "slider", default: 50, min: 0, max: 100, unit: "%" },
    { key: "rotation", label: "Rotation", type: "slider", default: 0, min: 0, max: 360, unit: "Â°" },
    { key: "scale", label: "Scale", type: "slider", default: 100, min: 50, max: 200, unit: "%" },
    { key: "mirror", label: "Mirror", type: "checkbox", default: false },
  ]);
  if (!result) return;

  // audit i13: this used to write `filled.data` straight into the layer's
  // pixel buffer with no Command at all, so Ctrl+Z couldn't undo it. Snapshot
  // the affected region before/after and emit it as a single undoable
  // PaintStrokeCommand — mirrors the before/after-region pattern used
  // elsewhere in this file (see rasterizeShapeOnLayer above) and by
  // PaintBucketTool/GradientTool. A recompute-based Command (like FillCommand)
  // would NOT work here: contentAwareFill() uses Math.random() internally, so
  // redo must replay the captured pixels rather than recompute them.
  const bbox = sel.bounds();
  if (!bbox) { showTip("Content-Aware Fill: nothing selected."); return; }

  const mask = sel.mask;
  const before = snapshotRegion(layer, bbox);

  const filled = contentAwareFill(layer.pixels, mask, {
    samplingRadius: result.radius as number,
    patchSize: result.patchSize as number,
    colorAdaptation: (result.colorAdapt as number) / 100,
    rotation: result.rotation as number,
    scale: (result.scale as number) / 100,
    mirror: result.mirror as boolean,
  });

  layer.pixels.data.set(filled.data);
  layer.pixels.markDirty();

  const after = snapshotRegion(layer, bbox);
  state.execute(new PaintStrokeCommand(layer.id, bbox, before, after));
  showTip("Content-Aware Fill applied.");
}

// â”€â”€ Remaining Layer Operations â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function mergeSelectedLayers(): void {
  const doc = state.doc;
  if (!doc) return;
  // i120: real multi-layer merge — merge exactly the resolved target set
  // (active layer + Layers-panel multi-selection, same resolution used by
  // Align/Distribute) rather than approximating with a single top-two
  // MergeDownCommand.
  const ids = collectLayerTargetIds(doc.activeLayerId, state.selectedLayerIds);
  if (ids.length < 2) {
    showTip("Select two or more layers to merge.");
    return;
  }
  const located = ids.map((id) => locateLayerInTree(doc.layers, id, null));
  if (located.some((l) => !l)) {
    showTip("Merge Selected Layers: one or more layers couldn't be found.");
    return;
  }
  const parentIds = new Set(located.map((l) => l!.parentId));
  if (parentIds.size > 1) {
    showTip("Merge Selected Layers: layers must be in the same group.");
    return;
  }
  // Only VISIBLE raster layers are merged. Hidden layers among the selection
  // are left untouched in the tree rather than silently baked into the merged
  // output and deleted — matching MergeVisibleCommand's respect for `.visible`
  // (Photoshop excludes hidden layers from a merge). Merging a hidden layer's
  // pixels then deleting it would be surprising, destructive data loss.
  const rasterIds = located
    .filter((l) => l!.layer.kind === "raster" && l!.layer.visible)
    .map((l) => l!.layer.id);
  if (rasterIds.length < 2) {
    showTip("Merge Selected Layers requires two or more visible raster layers.");
    return;
  }
  state.execute(new MergeSelectedLayersCommand(rasterIds));
}

// ---------------------------------------------------------------------------
// i120: Merge Selected Layers — merges an arbitrary (possibly non-adjacent)
// set of same-parent raster layers into one, bottom-first, at full document
// canvas size (same simplification MergeVisibleCommand/FlattenCommand use).
// The merged layer replaces the topmost target's stacking position; any
// non-target layers interleaved among the targets are left untouched.
// ---------------------------------------------------------------------------

function locateLayerInTree(
  layers: readonly Layer[],
  id: string,
  parentId: string | null,
): { siblings: readonly Layer[]; index: number; parentId: string | null; layer: Layer } | null {
  for (let i = 0; i < layers.length; i++) {
    const l = layers[i]!;
    if (l.id === id) return { siblings: layers, index: i, parentId, layer: l };
    if (l.kind === "group") {
      const inner = locateLayerInTree(l.children, id, l.id);
      if (inner) return inner;
    }
  }
  return null;
}

function replaceSiblingsInTree(
  layers: readonly Layer[],
  parentId: string | null,
  newSiblings: readonly Layer[],
): Layer[] {
  if (parentId === null) return [...newSiblings];
  return layers.map((l) => {
    if (l.id === parentId && l.kind === "group") {
      return { ...l, children: [...newSiblings] } as GroupLayer;
    }
    if (l.kind === "group") {
      return { ...l, children: replaceSiblingsInTree(l.children, parentId, newSiblings) };
    }
    return l;
  });
}

function projectRasterOntoDocCanvas(docW: number, docH: number, layer: RasterLayer): Uint8ClampedArray {
  const out = new Uint8ClampedArray(docW * docH * 4);
  const lw = layer.pixels.width;
  const lh = layer.pixels.height;
  const x0 = Math.max(0, layer.x);
  const y0 = Math.max(0, layer.y);
  const x1 = Math.min(docW, layer.x + lw);
  const y1 = Math.min(docH, layer.y + lh);
  for (let y = y0; y < y1; y++) {
    const srcRow = y - layer.y;
    for (let x = x0; x < x1; x++) {
      const srcCol = x - layer.x;
      const srcOff = (srcRow * lw + srcCol) * 4;
      const dstOff = (y * docW + x) * 4;
      out[dstOff] = layer.pixels.data[srcOff]!;
      out[dstOff + 1] = layer.pixels.data[srcOff + 1]!;
      out[dstOff + 2] = layer.pixels.data[srcOff + 2]!;
      out[dstOff + 3] = layer.pixels.data[srcOff + 3]!;
    }
  }
  return out;
}

class MergeSelectedLayersCommand implements Command {
  readonly kind = "merge-selected";
  private previousSiblings: readonly Layer[] | null = null;
  private previousParentId: string | null = null;
  private previousActiveId: string | null = null;

  constructor(private readonly layerIds: readonly string[]) {}

  apply(doc: Document): Document {
    const located = this.layerIds.map((id) => locateLayerInTree(doc.layers, id, null));
    if (located.some((l) => !l)) return doc;
    const first = located[0]!;
    const siblings = first.siblings;
    const parentId = first.parentId;
    this.previousSiblings = siblings;
    this.previousParentId = parentId;
    this.previousActiveId = doc.activeLayerId;

    const targetIdxs = located.map((l) => l!.index).sort((a, b) => a - b);
    const targetIdSet = new Set(this.layerIds);

    const merged = new PixelBuffer(doc.width, doc.height);
    for (const idx of targetIdxs) {
      const layer = siblings[idx] as RasterLayer;
      const projected = projectRasterOntoDocCanvas(merged.width, merged.height, layer);
      compositeLayer(merged.data, projected, layer.blendMode, layer.opacity);
    }
    merged.markDirty();

    const topIdx = targetIdxs[targetIdxs.length - 1]!;
    const topLayer = siblings[topIdx]!;
    const newLayer: RasterLayer = {
      kind: "raster",
      id: crypto.randomUUID(),
      name: topLayer.name,
      visible: true,
      opacity: 1,
      fillOpacity: 1,
      blendMode: "normal",
      x: 0,
      y: 0,
      lockTransparency: false,
      lockImage: false,
      lockPosition: false,
      lockAll: false,
      clippedToBelow: false,
      pixels: merged,
    };

    const withoutTargets = siblings.filter((l) => !targetIdSet.has(l.id));
    let insertIdx = 0;
    for (let i = 0; i < topIdx; i++) {
      if (!targetIdSet.has(siblings[i]!.id)) insertIdx++;
    }
    const newSiblings = [...withoutTargets.slice(0, insertIdx), newLayer, ...withoutTargets.slice(insertIdx)];

    const newLayers = replaceSiblingsInTree(doc.layers, parentId, newSiblings);
    return { ...doc, layers: newLayers, activeLayerId: newLayer.id };
  }

  invert(before: Document, _after: Document): Command {
    const prevSiblings = this.previousSiblings ?? before.layers;
    const prevParentId = this.previousParentId;
    const prevActiveId = this.previousActiveId ?? before.activeLayerId;
    return new RestoreMergeSelectedCommand(prevSiblings, prevParentId, prevActiveId, this.layerIds);
  }
}

class RestoreMergeSelectedCommand implements Command {
  readonly kind = "merge-selected/restore";
  constructor(
    private readonly siblings: readonly Layer[],
    private readonly parentId: string | null,
    private readonly activeLayerId: string | null,
    private readonly originalLayerIds: readonly string[],
  ) {}

  apply(doc: Document): Document {
    const newLayers = replaceSiblingsInTree(doc.layers, this.parentId, this.siblings);
    return { ...doc, layers: newLayers, activeLayerId: this.activeLayerId };
  }

  invert(_before: Document, _after: Document): Command {
    return new MergeSelectedLayersCommand(this.originalLayerIds);
  }
}

function groupFromLayers(): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  // i119: same multi-selection target resolution as groupActive().
  const ids = collectLayerTargetIds(doc.activeLayerId, state.selectedLayerIds);
  state.execute(new GroupLayersCommand(ids, crypto.randomUUID(), "Group"));
}

function duplicateToNewDocument(): void {
  const doc = state.doc;
  const id = doc?.activeLayerId;
  if (!doc || !id) return;
  const layer = findLayer(doc.layers, id);
  if (!layer) return;

  if (layer.kind === "raster") {
    const raster = layer as RasterLayer;
    const pixels = new PixelBuffer(raster.pixels.width, raster.pixels.height, "rgba8");
    pixels.data.set(raster.pixels.data);
    pixels.markDirty();
    const newLayer: RasterLayer = {
      ...raster,
      id: crypto.randomUUID(),
      name: layer.name,
      pixels,
    };
    const newDoc: Document = {
      // i44: a fresh, unique id -- a layer duplicated to a new document is
      // never the same document as any other open one, even if it ends up
      // sharing that document's name.
      id: crypto.randomUUID(),
      name: layer.name,
      width: raster.pixels.width,
      height: raster.pixels.height,
      dpi: doc.dpi,
      layers: [newLayer],
      activeLayerId: newLayer.id,
      selection: null,
    };
    state.execute(new LoadDocumentCommand(newDoc));
    fitDoc();
  } else if (layer.kind === "group") {
    const group = layer as GroupLayer;
    const newDoc: Document = {
      id: crypto.randomUUID(),
      name: group.name,
      width: doc.width,
      height: doc.height,
      dpi: doc.dpi,
      layers: [...group.children],
      activeLayerId: group.children[group.children.length - 1]?.id ?? null,
      selection: null,
    };
    state.execute(new LoadDocumentCommand(newDoc));
    fitDoc();
  } else {
    showTip(`"${layer.kind}" layers cannot be duplicated to a new document.`);
  }
}

function duplicateDocument(): void {
  const doc = state.doc;
  if (!doc) return;
  const newLayers = JSON.parse(JSON.stringify(doc.layers));
  const newDoc: Document = {
    // i44: this is a genuinely distinct document from `doc` -- give it its
    // own id rather than inheriting `doc.id` (or leaving it unset), so
    // "Duplicate" + editing the original never share per-document History
    // or collide in the tab registry, even though they share a name prefix.
    id: crypto.randomUUID(),
    name: doc.name + " copy",
    width: doc.width,
    height: doc.height,
    dpi: doc.dpi,
    layers: newLayers,
    activeLayerId: doc.activeLayerId,
    selection: null,
  };
  state.execute(new LoadDocumentCommand(newDoc));
  fitDoc();
  showTip(`Document duplicated as "${newDoc.name}".`);
}

function selectAllLayers(): void {
  if (!state.doc) return;
  showTip("All layers selected in Layers panel.");
}

function deselectLayers(): void {
  if (!state.doc) return;
  const doc = state.doc;
  const newDoc: Document = { ...doc, activeLayerId: null };
  state.execute(new LoadDocumentCommand(newDoc));
  showTip("All layers deselected.");
}

function findLayers(): void {
  const layersList = document.querySelector<HTMLElement>(".layers-list");
  if (!layersList) return;
  let searchInput = layersList.querySelector<HTMLInputElement>(".layer-search-input");
  if (!searchInput) {
    searchInput = document.createElement("input");
    searchInput.className = "layer-search-input";
    searchInput.placeholder = "Find layersâ€¦";
    searchInput.style.cssText = "width:100%;padding:4px 6px;font-size:11px;background:var(--bg-input);border:1px solid var(--border);color:var(--text);margin-bottom:4px;box-sizing:border-box;";
    layersList.prepend(searchInput);
    searchInput.addEventListener("input", () => {
      const query = searchInput!.value.toLowerCase();
      layersList.querySelectorAll(".layer-row").forEach((row) => {
        const nameEl = row.querySelector(".layer-row__name");
        if (nameEl) {
          const name = nameEl.textContent?.toLowerCase() ?? "";
          (row as HTMLElement).style.display = !query || name.includes(query) ? "" : "none";
        }
      });
    });
  }
  searchInput.focus();
  searchInput.select();
}

function collectLayerIds(layers: readonly Layer[]): string[] {
  const ids: string[] = [];
  for (const l of layers) {
    ids.push(l.id);
    if (l.kind === "group") ids.push(...collectLayerIds(l.children));
  }
  return ids;
}

function hideLayers(): void {
  const doc = state.doc;
  if (!doc) return;
  const ids = collectLayerIds(doc.layers);
  for (const id of ids) {
    state.execute(new SetLayerPropertyCommand(id, { visible: false }));
  }
  showTip("All layers hidden.");
}

function showAllLayers(): void {
  const doc = state.doc;
  if (!doc) return;
  const ids = collectLayerIds(doc.layers);
  for (const id of ids) {
    state.execute(new SetLayerPropertyCommand(id, { visible: true }));
  }
  showTip("All layers shown.");
}

function hideOthers(): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const activeId = doc.activeLayerId;
  const ids = collectLayerIds(doc.layers);
  for (const id of ids) {
    state.execute(new SetLayerPropertyCommand(id, { visible: id === activeId }));
  }
  showTip("All layers except the active one hidden.");
}

function toggleSelectionEdges(): void {
  showSelectionEdges = !showSelectionEdges;
  renderer.setShowSelectionEdges(showSelectionEdges);
  state.requestRender();
}

function toggleTargetPath(): void {
  showTargetPath = !showTargetPath;
  state.requestRender();
}

function toggleNotes(): void {
  showNotes = !showNotes;
  state.requestRender();
}

async function exportLayersToFiles(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;

  const result = await filterDialog.open("Export Layers", [
    { key: "format", label: "Format", type: "dropdown", default: "png", options: [
      { value: "png", label: "PNG" },
      { value: "jpg", label: "JPEG" },
    ]},
  ]);
  if (!result) return;

  const format = result.format as string;
  const mimeType = format === "jpg" ? "image/jpeg" : "image/png";
  const ext = format === "jpg" ? "jpg" : "png";
  let count = 0;

  // collectExportLayers recurses into groups/artboards and rasterizes ALL
  // layer kinds (type, shape, adjustment, fill …), not just top-level raster
  // — closes the layer-coverage part of #250.
  for (const entry of collectExportLayers(doc)) {
    const canvas = document.createElement("canvas");
    canvas.width = entry.imageData.width;
    canvas.height = entry.imageData.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) continue;
    ctx.putImageData(entry.imageData, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, mimeType, 0.92)
    );
    if (blob) {
      downloadBlob(blob, `${entry.name}.${ext}`);
      count++;
    }
  }

  showTip(`Exported ${count} layer(s) to files.`);
}

async function promptApplyImage(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;
  const layer = getActiveRaster();
  if (!layer) { showError("No active raster layer."); return; }

  // Get list of source layers
  const sourceLayers = doc.layers.filter(l => l.kind === "raster" || l.kind === "background");
  if (sourceLayers.length === 0) { showError("No source layers."); return; }

  const result = await filterDialog.open("Apply Image", [
    { key: "source", label: "Source", type: "dropdown", default: sourceLayers[0]!.id, options:
      sourceLayers.map(l => ({ value: l.id, label: l.name }))
    },
    { key: "blendMode", label: "Blending", type: "dropdown", default: "normal", options: [
      { value: "normal", label: "Normal" },
      { value: "multiply", label: "Multiply" },
      { value: "screen", label: "Screen" },
      { value: "overlay", label: "Overlay" },
      { value: "darken", label: "Darken" },
      { value: "lighten", label: "Lighten" },
    ]},
    { key: "opacity", label: "Opacity", type: "slider", default: 100, min: 0, max: 100, unit: "%" },
  ]);
  if (!result) return;

  const sourceId = result.source as string;
  const source = sourceLayers.find(l => l.id === sourceId);
  if (!source) return;

  const srcPixels = source.kind === "background" ? (source as BackgroundLayer).pixels : (source as RasterLayer).pixels;
  const dstPixels = layer.pixels;
  const w = Math.min(layer.pixels.width, srcPixels.width);
  const h = Math.min(layer.pixels.height, srcPixels.height);
  const blendMode = result.blendMode as string;
  const opacity = (result.opacity as number) / 100;

  for (let i = 0; i < w * h; i++) {
    const di = i * 4;
    const si = i * 4;
    const sa = srcPixels.data[si + 3]! / 255 * opacity;
    const ia = 1 - sa;

    switch (blendMode) {
      case "multiply":
        dstPixels.data[di] = Math.round(dstPixels.data[di]! * srcPixels.data[si]! / 255 * opacity + dstPixels.data[di]! * ia);
        dstPixels.data[di + 1] = Math.round(dstPixels.data[di + 1]! * srcPixels.data[si + 1]! / 255 * opacity + dstPixels.data[di + 1]! * ia);
        dstPixels.data[di + 2] = Math.round(dstPixels.data[di + 2]! * srcPixels.data[si + 2]! / 255 * opacity + dstPixels.data[di + 2]! * ia);
        break;
      case "screen":
        dstPixels.data[di] = Math.round((255 - (255 - dstPixels.data[di]!) * (255 - srcPixels.data[si]!) / 255) * opacity + dstPixels.data[di]! * ia);
        dstPixels.data[di + 1] = Math.round((255 - (255 - dstPixels.data[di + 1]!) * (255 - srcPixels.data[si + 1]!) / 255) * opacity + dstPixels.data[di + 1]! * ia);
        dstPixels.data[di + 2] = Math.round((255 - (255 - dstPixels.data[di + 2]!) * (255 - srcPixels.data[si + 2]!) / 255) * opacity + dstPixels.data[di + 2]! * ia);
        break;
      default:
        dstPixels.data[di] = Math.round(dstPixels.data[di]! * ia + srcPixels.data[si]! * sa);
        dstPixels.data[di + 1] = Math.round(dstPixels.data[di + 1]! * ia + srcPixels.data[si + 1]! * sa);
        dstPixels.data[di + 2] = Math.round(dstPixels.data[di + 2]! * ia + srcPixels.data[si + 2]! * sa);
    }
    dstPixels.data[di + 3] = Math.min(255, Math.round(dstPixels.data[di + 3]! * ia + srcPixels.data[si + 3]! * sa));
  }
  layer.pixels.markDirty();
  state.requestRender();
  showTip("Apply Image completed.");
}

async function promptCalculations(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;

  const result = await filterDialog.open("Calculations", [
    { key: "blendMode", label: "Blending", type: "dropdown", default: "normal", options: [
      { value: "normal", label: "Normal" },
      { value: "multiply", label: "Multiply" },
      { value: "screen", label: "Screen" },
      { value: "overlay", label: "Overlay" },
      { value: "darken", label: "Darken" },
      { value: "lighten", label: "Lighten" },
    ]},
    { key: "opacity", label: "Opacity", type: "slider", default: 100, min: 0, max: 100, unit: "%" },
  ]);
  if (!result) return;

  // Simplified: create new alpha channel from RGB composite using blend mode
  const layer = getActiveRaster();
  if (!layer) { showError("No active raster layer."); return; }

  const w = layer.pixels.width;
  const h = layer.pixels.height;
  const data = layer.pixels.data;
  const mask = new Uint8Array(w * h);

  const blendMode = result.blendMode as string;
  const opacity = (result.opacity as number) / 100;

  for (let i = 0; i < w * h; i++) {
    const di = i * 4;
    const r = data[di]!, g = data[di + 1]!, b = data[di + 2]!;

    // Use luminance as the channel value
    const lum = Math.round(0.299 * r + 0.587 * g + 0.114 * b);

    switch (blendMode) {
      case "multiply":
        mask[i] = Math.round(lum * lum / 255 * opacity + lum * (1 - opacity));
        break;
      case "screen":
        mask[i] = Math.round((255 - (255 - lum) * (255 - lum) / 255) * opacity + lum * (1 - opacity));
        break;
      default:
        mask[i] = lum;
    }
  }

  // Store as alpha channel
  const alphas = docAlphaChannels.get(doc.name) || [];
  const n = alphas.length + 1;
  alphas.push({
    id: crypto.randomUUID(),
    name: `Alpha ${n}`,
    pixels: mask,
    width: w,
    height: h,
    visible: true,
    opacity: 1,
    color: { r: 0, g: 0, b: 255, a: 0.5 },
  });
  docAlphaChannels.set(doc.name, alphas);
  state.requestRender();
  showTip("Calculations completed. New alpha channel created.");
}

function rasterizeShapeLayer(): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (layer?.kind !== "shape") return;

  const shape = layer as import("./core/document/Layer").ShapeLayer;
  const pixels = new PixelBuffer(doc.width, doc.height, "rgba8");

  // Render shape to pixel buffer
  const canvas = document.createElement("canvas");
  canvas.width = doc.width;
  canvas.height = doc.height;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    for (const subpath of shape.path.subpaths) {
      if (subpath.points.length === 0) continue;
      ctx.beginPath();
      const pts = subpath.points;
      ctx.moveTo(pts[0]!.x, pts[0]!.y);
      for (let i = 1; i < pts.length; i++) {
        const prev = pts[i - 1]!;
        const curr = pts[i]!;
        if (prev.rightDirection && curr.leftDirection) {
          ctx.bezierCurveTo(prev.rightDirection.x, prev.rightDirection.y, curr.leftDirection.x, curr.leftDirection.y, curr.x, curr.y);
        } else {
          ctx.lineTo(curr.x, curr.y);
        }
      }
      if (subpath.closed && pts.length > 2) ctx.closePath();

      if (shape.fillColor) {
        const { r, g, b, a } = shape.fillColor;
        ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
        ctx.fill();
      }
      if (shape.strokeColor && shape.strokeWidth > 0) {
        const { r, g, b, a } = shape.strokeColor;
        ctx.strokeStyle = `rgba(${r},${g},${b},${a})`;
        ctx.lineWidth = shape.strokeWidth;
        ctx.lineCap = shape.lineCap;
        ctx.lineJoin = shape.lineJoin;
        ctx.stroke();
      }
    }

    const imageData = ctx.getImageData(0, 0, doc.width, doc.height);
    pixels.data.set(imageData.data);
  }
  pixels.markDirty();

  const raster: RasterLayer = {
    id: shape.id,
    kind: "raster",
    name: shape.name + " (Rasterized)",
    visible: shape.visible,
    opacity: shape.opacity,
    fillOpacity: shape.fillOpacity,
    blendMode: shape.blendMode,
    x: shape.x,
    y: shape.y,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: shape.clippedToBelow,
    pixels,
  };

  // Replace the shape layer with the rasterized version
  const newLayers = doc.layers.map(l => l.id === shape.id ? raster : l);
  const newDoc: Document = { ...doc, layers: newLayers, activeLayerId: raster.id };
  state.execute(new LoadDocumentCommand(newDoc));
  state.requestRender();
  showTip("Shape layer rasterized.");
}

// Current path model for vector mask
let currentPathModel: import("./core/document/PathModel").PathModel | null = null;

/** Helper: draw a layer to canvas for TIFF export. */
function drawLayerToCanvasForExport(ctx: CanvasRenderingContext2D, layer: Layer): void {
  if (!layer.visible || layer.opacity <= 0) return;

  if (layer.kind === "group") {
    ctx.save();
    ctx.globalAlpha *= layer.opacity;
    for (const child of (layer as GroupLayer).children) {
      drawLayerToCanvasForExport(ctx, child);
    }
    ctx.restore();
    return;
  }

  if (layer.kind === "shape") {
    const shape = layer as import("./core/document/Layer").ShapeLayer;
    ctx.save();
    ctx.globalAlpha = layer.opacity;
    ctx.translate(layer.x, layer.y);
    for (const subpath of shape.path.subpaths) {
      if (subpath.points.length === 0) continue;
      ctx.beginPath();
      const pts = subpath.points;
      ctx.moveTo(pts[0]!.x, pts[0]!.y);
      for (let i = 1; i < pts.length; i++) {
        const prev = pts[i - 1]!;
        const curr = pts[i]!;
        if (prev.rightDirection && curr.leftDirection) {
          ctx.bezierCurveTo(prev.rightDirection.x, prev.rightDirection.y, curr.leftDirection.x, curr.leftDirection.y, curr.x, curr.y);
        } else {
          ctx.lineTo(curr.x, curr.y);
        }
      }
      if (subpath.closed && pts.length > 2) ctx.closePath();
      if (shape.fillColor) {
        const { r, g, b, a } = shape.fillColor;
        ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
        ctx.fill();
      }
      if (shape.strokeColor && shape.strokeWidth > 0) {
        const { r, g, b, a } = shape.strokeColor;
        ctx.strokeStyle = `rgba(${r},${g},${b},${a})`;
        ctx.lineWidth = shape.strokeWidth;
        ctx.stroke();
      }
    }
    ctx.restore();
    return;
  }

  if (layer.kind === "type") {
    const t = layer as TypeLayer;
    if (t.text.length === 0) return;
    ctx.save();
    ctx.globalAlpha = layer.opacity;
    const { r, g, b, a } = t.color;
    ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
    ctx.font = `${t.fontStyle} ${t.fontWeight} ${t.fontSize}px "${t.fontFamily}"`;
    ctx.textAlign = t.textAlign === "justify" ? "left" : t.textAlign;
    ctx.textBaseline = t.textBaseline;
    const lines = t.text.split("\n");
    const lineHeight = t.fontSize * t.leading;
    let startY = t.y;
    if (t.textBaseline === "middle") startY -= (lines.length * lineHeight) / 2;
    else if (t.textBaseline === "bottom") startY -= lines.length * lineHeight;
    for (let i = 0; i < lines.length; i++) {
      let text = lines[i]!;
      if ((t as any).allCaps) text = text.toUpperCase();
      ctx.fillText(text, t.x, startY + i * lineHeight);
    }
    ctx.restore();
    return;
  }

  if (layer.kind === "solid-fill") {
    const f = layer as SolidFillLayer;
    ctx.save();
    ctx.globalAlpha = layer.opacity;
    const { r, g, b, a } = f.color;
    ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.restore();
    return;
  }

  if (layer.kind === "gradient-fill") {
    const f = layer as GradientFillLayer;
    ctx.save();
    ctx.globalAlpha = layer.opacity;
    if (f.stops.length > 0) {
      const { r, g, b, a } = f.stops[0]!.color;
      ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
      ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    }
    ctx.restore();
    return;
  }

  if (layer.kind === "pattern-fill") {
    const f = layer as PatternFillLayer;
    if (f.pattern && f.pattern.width > 0 && f.pattern.height > 0) {
      ctx.save();
      ctx.globalAlpha = layer.opacity;
      const patCanvas = document.createElement("canvas");
      patCanvas.width = f.pattern.width;
      patCanvas.height = f.pattern.height;
      const pctx = patCanvas.getContext("2d");
      if (pctx) {
        const imageData = pctx.createImageData(f.pattern.width, f.pattern.height);
        imageData.data.set(f.pattern.pixels);
        pctx.putImageData(imageData, 0, 0);
        const pattern = ctx.createPattern(patCanvas, "repeat");
        if (pattern) {
          ctx.fillStyle = pattern;
          ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        }
      }
      ctx.restore();
    }
    return;
  }

  if (layer.kind === "background") {
    const bg = layer as BackgroundLayer;
    const off = document.createElement("canvas");
    off.width = bg.pixels.width;
    off.height = bg.pixels.height;
    const offCtx = off.getContext("2d");
    if (!offCtx) return;
    const imageData = offCtx.createImageData(bg.pixels.width, bg.pixels.height);
    imageData.data.set(bg.pixels.data);
    offCtx.putImageData(imageData, 0, 0);
    ctx.save();
    ctx.globalAlpha = layer.opacity;
    ctx.drawImage(off, bg.x, bg.y);
    ctx.restore();
    return;
  }

  if (layer.kind === "artboard") {
    const ab = layer as ArtboardLayer;
    ctx.save();
    const { r, g, b, a } = ab.backgroundColor;
    ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
    ctx.fillRect(ab.artboardX, ab.artboardY, ab.artboardWidth, ab.artboardHeight);
    ctx.beginPath();
    ctx.rect(ab.artboardX, ab.artboardY, ab.artboardWidth, ab.artboardHeight);
    ctx.clip();
    for (const child of ab.children) {
      drawLayerToCanvasForExport(ctx, child);
    }
    ctx.restore();
    return;
  }

  if (layer.kind !== "raster") return;
  const raster = layer as RasterLayer;
  const off = document.createElement("canvas");
  off.width = raster.pixels.width;
  off.height = raster.pixels.height;
  const offCtx = off.getContext("2d");
  if (!offCtx) return;
  const imageData = offCtx.createImageData(raster.pixels.width, raster.pixels.height);
  imageData.data.set(raster.pixels.data);
  offCtx.putImageData(imageData, 0, 0);
  ctx.save();
  ctx.globalAlpha = layer.opacity;
  const nativeOp = nativeCompositeOp(layer.blendMode);
  if (nativeOp !== null) ctx.globalCompositeOperation = nativeOp;
  ctx.drawImage(off, layer.x, layer.y);
  ctx.restore();
}

function isSmartObjectActive(): boolean {
  const doc = state.doc;
  if (!doc?.activeLayerId) return false;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  return layer?.kind === "smart-object";
}

// ----- Smart Filter helpers -----
// These bypass the dialog and apply filters directly as Smart Filters
// with reasonable default parameters. The filter stack can be edited
// by double-clicking entries in the layers panel.

function applySmartFilter(filterId: string, params: Record<string, number | string | boolean>): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (layer?.kind !== "smart-object") return;
  state.execute(new ApplySmartFilterCommand(doc.activeLayerId, filterId, params, crypto.randomUUID()));
}

function smartGaussianBlur(): void { applySmartFilter("gaussian-blur", { radius: 5 }); }
function smartBoxBlur(): void { applySmartFilter("box-blur", { radius: 5 }); }
function smartMotionBlur(): void { applySmartFilter("motion-blur", { angle: 45, distance: 15 }); }
function smartSharpen(): void { applySmartFilter("sharpen-suite", { mode: "sharpen", amount: 100, radius: 1, threshold: 0 }); }
function smartSharpenMore(): void { applySmartFilter("sharpen-suite", { mode: "sharpen-more", amount: 100, radius: 1, threshold: 0 }); }
function smartUnsharpMask(): void { applySmartFilter("sharpen-suite", { mode: "unsharp-mask", amount: 150, radius: 1, threshold: 0 }); }
function smartAddNoise(): void { applySmartFilter("noise-suite", { mode: "add-noise", amount: 25, distribution: "uniform", monochrome: true }); }
function smartMedian(): void { applySmartFilter("noise-suite", { mode: "median", radius: 3 }); }
function smartDustScratches(): void { applySmartFilter("noise-suite", { mode: "dust-scratches", radius: 2, threshold: 10 }); }
function smartMosaic(): void { applySmartFilter("pixelate-suite", { mode: "mosaic", cellSize: 10 }); }
function smartTwirl(): void { applySmartFilter("distort-suite", { mode: "twirl", angle: 180 }); }
function smartPinch(): void { applySmartFilter("distort-suite", { mode: "pinch", amount: 50 }); }
function smartSpherize(): void { applySmartFilter("distort-suite", { mode: "spherize", amount: 50 }); }
function smartFindEdges(): void { applySmartFilter("stylize-suite", { mode: "find-edges" }); }
function smartEmboss(): void { applySmartFilter("stylize-suite", { mode: "emboss", angle: 135, height: 3, amount: 100 }); }
function smartHighPass(): void { applySmartFilter("utility-suite", { mode: "high-pass", radius: 3 }); }

document.querySelectorAll<HTMLButtonElement>(".layers-footer .ibtn").forEach((btn) => {
  const title = btn.getAttribute("title") ?? "";
  switch (title) {
    case "New layer": btn.addEventListener("click", addNewLayer); break;
    case "New group": btn.addEventListener("click", addNewGroup); break;
    case "Delete layer": btn.addEventListener("click", deleteActiveLayer); break;
    case "New adjustment layer": btn.addEventListener("click", showAdjustmentLayerMenu); break;
    case "Add mask": btn.addEventListener("click", addLayerMaskFromFooter); break;
    // i115: footer "Add layer style" (fx) button — was a no-op; opens the
    // same LayerStyleDialog used by the Layer menu / double-click entry point.
    case "Add layer style": btn.addEventListener("click", () => { void showLayerStyleDialog(); }); break;
  }
});

// i30: footer "Add mask" button — adds a reveal-all mask (or hide-all when
// Alt-clicked, matching Photoshop's Alt+click-the-mask-button convention).
// No-ops when there's no active layer, the active layer isn't a raster
// layer (masks only apply to RasterLayer — see LayerMaskCommands.ts's own
// findRaster, which is kind === "raster" only), or it already has a mask.
function addLayerMaskFromFooter(ev: MouseEvent): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findRaster(doc.layers, doc.activeLayerId);
  if (!layer || layer.mask) return;
  state.execute(new AddLayerMaskCommand(doc.activeLayerId, ev.altKey ? "hide-all" : "reveal-all"));
}

// ----- Adjustment layer creation â€” list sourced from adjustmentDialogs module -----
const { ADJUSTMENT_LAYERS } = AD;

function showAdjustmentLayerMenu(): void {
  if (!state.doc) return;
  const btn = document.querySelector<HTMLButtonElement>('.layers-footer .ibtn[title="New adjustment layer"]');
  if (!btn) return;
  showMenuDropdown(btn, ADJUSTMENT_LAYERS.map((adj) => ({
    label: adj.label,
    action: () => addAdjustmentLayer(adj.kind),
  })));
}

function addAdjustmentLayer(kind: AdjustmentKind): void { AD.addAdjustmentLayer(adjustmentDeps(), kind); }
function editAdjustmentLayer(id: string): void { AD.editAdjustmentLayer(adjustmentDeps(), id); }


// ----- Filter menu -----
const filterDialog = new FilterDialog();
let lastFilter: { filter: Filter<unknown>; params: unknown } | null = null;
function applyFilter<P>(filter: Filter<P>, params: P): void {
  const doc = state.doc;
  if (!doc) return;
  // Use activeLayerId if set; otherwise fall back to the top-most raster layer
  // (Photoshop always has an active layer, but our state may lag).
  let id = doc.activeLayerId;
  if (!id) {
    const top = [...doc.layers].reverse().find((l) => l.kind === "raster");
    if (!top) return;
    id = top.id;
  }
  state.execute(new ApplyFilterCommand(id, filter, params, doc.selection ?? null));
  lastFilter = { filter: filter as Filter<unknown>, params };
}
function repeatLastFilter(): void {
  if (!lastFilter) return;
  applyFilter(lastFilter.filter, lastFilter.params);
}

/** Build the deps object used by the filterDialogs / adjustmentDialogs shell modules. */
function filterDeps(): FD.FilterDialogDeps {
  return {
    state: state as unknown as import("./app/state/EditorState").EditorState,
    filterDialog,
    showTip,
    lastFilter,
    onLastFilterChange: (f) => { lastFilter = f; },
  };
}
function adjustmentDeps(): AD.AdjustmentDialogDeps {
  return {
    state: state as unknown as import("./app/state/EditorState").EditorState,
    filterDialog,
    showTip,
  };
}

// Fade: stores the last operation's before/after pixels for fading.
let lastOperation: {
  layerId: string;
  bbox: { x: number; y: number; w: number; h: number };
  before: Uint8ClampedArray;
  after: Uint8ClampedArray;
} | null = null;

function storeLastOperation(layerId: string, bbox: { x: number; y: number; w: number; h: number }, before: Uint8ClampedArray, after: Uint8ClampedArray): void {
  lastOperation = { layerId, bbox, before: new Uint8ClampedArray(before), after: new Uint8ClampedArray(after) };
}

async function fadeLastOperation(): Promise<void> {
  if (!lastOperation || !state.doc) return;
  const { layerId, bbox, before, after } = lastOperation;
  const layer = findRaster(state.doc.layers, layerId);
  if (!layer) return;

  const result = await filterDialog.open("Fade", [
    { key: "opacity", label: "Opacity", type: "slider", default: 100, min: 0, max: 100, unit: "%" },
    { key: "blendMode", label: "Mode", type: "dropdown", default: "normal", options: [
      { value: "normal", label: "Normal" },
      { value: "dissolve", label: "Dissolve" },
      { value: "darken", label: "Darken" },
      { value: "multiply", label: "Multiply" },
      { value: "color-burn", label: "Color Burn" },
      { value: "lighten", label: "Lighten" },
      { value: "screen", label: "Screen" },
      { value: "color-dodge", label: "Color Dodge" },
      { value: "overlay", label: "Overlay" },
      { value: "soft-light", label: "Soft Light" },
      { value: "hard-light", label: "Hard Light" },
      { value: "difference", label: "Difference" },
      { value: "hue", label: "Hue" },
      { value: "saturation", label: "Saturation" },
      { value: "color", label: "Color" },
      { value: "luminosity", label: "Luminosity" },
    ]},
  ]);
  if (!result) return;

  const opacity = (result.opacity as number) / 100;
  const blendMode = result.blendMode as string;
  const data = layer.pixels.data;
  const w = layer.pixels.width;

  for (let py = 0; py < bbox.h; py++) {
    for (let px = 0; px < bbox.w; px++) {
      const si = (py * bbox.w + px) * 4;
      const di = ((bbox.y + py) * w + bbox.x + px) * 4;
      const origR = before[si]!, origG = before[si + 1]!, origB = before[si + 2]!;
      const filteredR = after[si]!, filteredG = after[si + 1]!, filteredB = after[si + 2]!;
      // Fade: blend between original and filtered
      const fadedR = Math.round(origR + (filteredR - origR) * opacity);
      const fadedG = Math.round(origG + (filteredG - origG) * opacity);
      const fadedB = Math.round(origB + (filteredB - origB) * opacity);
      data[di] = fadedR;
      data[di + 1] = fadedG;
      data[di + 2] = fadedB;
    }
  }
  layer.pixels.markDirty();
  lastOperation = null;
  state.requestRender();
}

// Layer style clipboard
let _layerStyleClipboard: import("./core/document/LayerStyle").LayerStyles | null = null;

function copyLayerStyle(): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (!layer || layer.kind === "type" || !(layer as any).styles) { showTip("No layer styles to copy."); return; }
  _layerStyleClipboard = (layer as any).styles;
  showTip("Layer style copied.");
}

function pasteLayerStyle(): void {
  const doc = state.doc;
  if (!doc?.activeLayerId || !_layerStyleClipboard) { showTip("No layer style to paste."); return; }
  state.execute(new SetLayerPropertyCommand(doc.activeLayerId, { styles: _layerStyleClipboard }));
  showTip("Layer style pasted.");
}

function clearLayerStyle(): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (!layer) return;
  const existing = (layer as any).styles;
  if (!existing) { showTip("No layer styles to clear."); return; }
  const { dropShadow: _ds, innerShadow: _is, outerGlow: _og, innerGlow: _ig, bevelEmboss: _be, satin: _sa, colorOverlay: _co, gradientOverlay: _go, patternOverlay: _po, stroke: _st, ...rest } = existing;
  state.execute(new SetLayerPropertyCommand(doc.activeLayerId, { styles: rest }));
  showTip("Layer style cleared.");
}

async function addLayerStyleEffect(effect: string): Promise<void> {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (!layer) return;

  const existingStyles = (layer as any).styles ?? {};

  switch (effect) {
    case "dropShadow": {
      const result = await filterDialog.open("Drop Shadow", [
        { key: "opacity", label: "Opacity", type: "slider", default: 75, min: 0, max: 100 },
        { key: "angle", label: "Angle", type: "slider", default: 120, min: 0, max: 360 },
        { key: "distance", label: "Distance", type: "slider", default: 5, min: 0, max: 200 },
        { key: "spread", label: "Spread", type: "slider", default: 0, min: 0, max: 100 },
        { key: "size", label: "Size", type: "slider", default: 10, min: 0, max: 250 },
      ]);
      if (!result) return;
      const styles: import("./core/document/LayerStyle").LayerStyles = {
        ...existingStyles,
        dropShadow: {
          enabled: true, blendMode: "multiply",
          color: { r: 0, g: 0, b: 0, a: 255 },
          opacity: (result.opacity as number) / 100,
          useGlobalLight: true, angle: result.angle as number,
          distance: result.distance as number,
          spread: (result.spread as number) / 100,
          size: result.size as number,
          antiAliased: true, layerKnocksOut: true, noise: 0,
          contour: { points: [{ x: 0, y: 0 }, { x: 1, y: 1 }], presetName: "Linear" },
        },
      };
      state.execute(new SetLayerPropertyCommand(doc.activeLayerId, { styles }));
      break;
    }
    case "innerShadow": {
      const result = await filterDialog.open("Inner Shadow", [
        { key: "opacity", label: "Opacity", type: "slider", default: 75, min: 0, max: 100 },
        { key: "angle", label: "Angle", type: "slider", default: 120, min: 0, max: 360 },
        { key: "distance", label: "Distance", type: "slider", default: 5, min: 0, max: 200 },
        { key: "choke", label: "Choke", type: "slider", default: 0, min: 0, max: 100 },
        { key: "size", label: "Size", type: "slider", default: 10, min: 0, max: 250 },
      ]);
      if (!result) return;
      const styles: import("./core/document/LayerStyle").LayerStyles = {
        ...existingStyles,
        innerShadow: {
          enabled: true, blendMode: "multiply",
          color: { r: 0, g: 0, b: 0, a: 255 },
          opacity: (result.opacity as number) / 100,
          useGlobalLight: true, angle: result.angle as number,
          distance: result.distance as number,
          choke: (result.choke as number) / 100,
          size: result.size as number,
          antiAliased: true, noise: 0,
          contour: { points: [{ x: 0, y: 0 }, { x: 1, y: 1 }], presetName: "Linear" },
        },
      };
      state.execute(new SetLayerPropertyCommand(doc.activeLayerId, { styles }));
      break;
    }
    case "outerGlow": {
      const result = await filterDialog.open("Outer Glow", [
        { key: "opacity", label: "Opacity", type: "slider", default: 75, min: 0, max: 100 },
        { key: "size", label: "Size", type: "slider", default: 10, min: 0, max: 250 },
        { key: "spread", label: "Spread", type: "slider", default: 0, min: 0, max: 100 },
      ]);
      if (!result) return;
      const styles: import("./core/document/LayerStyle").LayerStyles = {
        ...existingStyles,
        outerGlow: {
          enabled: true, blendMode: "screen",
          opacity: (result.opacity as number) / 100,
          noise: 0,
          fill: { kind: "color", color: { r: 200, g: 200, b: 100, a: 255 } },
          technique: "softer",
          spread: (result.spread as number) / 100,
          size: result.size as number,
          contour: { points: [{ x: 0, y: 0 }, { x: 1, y: 1 }], presetName: "Linear" },
          antiAliased: true, range: 0.5, jitter: 0,
        },
      };
      state.execute(new SetLayerPropertyCommand(doc.activeLayerId, { styles }));
      break;
    }
    case "innerGlow": {
      const result = await filterDialog.open("Inner Glow", [
        { key: "opacity", label: "Opacity", type: "slider", default: 75, min: 0, max: 100 },
        { key: "size", label: "Size", type: "slider", default: 10, min: 0, max: 250 },
        { key: "choke", label: "Choke", type: "slider", default: 0, min: 0, max: 100 },
      ]);
      if (!result) return;
      const styles: import("./core/document/LayerStyle").LayerStyles = {
        ...existingStyles,
        innerGlow: {
          enabled: true, blendMode: "screen",
          opacity: (result.opacity as number) / 100,
          noise: 0,
          fill: { kind: "color", color: { r: 255, g: 255, b: 255, a: 255 } },
          technique: "softer",
          source: "edge",
          choke: (result.choke as number) / 100,
          size: result.size as number,
          contour: { points: [{ x: 0, y: 0 }, { x: 1, y: 1 }], presetName: "Linear" },
          antiAliased: true, range: 0.5, jitter: 0,
        },
      };
      state.execute(new SetLayerPropertyCommand(doc.activeLayerId, { styles }));
      break;
    }
    case "colorOverlay": {
      const result = await filterDialog.open("Color Overlay", [
        { key: "opacity", label: "Opacity", type: "slider", default: 100, min: 0, max: 100 },
      ]);
      if (!result) return;
      const styles: import("./core/document/LayerStyle").LayerStyles = {
        ...existingStyles,
        colorOverlay: {
          enabled: true, blendMode: "normal",
          color: { r: 255, g: 0, b: 0, a: 255 },
          opacity: (result.opacity as number) / 100,
        },
      };
      state.execute(new SetLayerPropertyCommand(doc.activeLayerId, { styles }));
      break;
    }
    case "stroke": {
      const result = await filterDialog.open("Stroke", [
        { key: "size", label: "Size", type: "slider", default: 3, min: 0, max: 250 },
        { key: "opacity", label: "Opacity", type: "slider", default: 100, min: 0, max: 100 },
      ]);
      if (!result) return;
      const styles: import("./core/document/LayerStyle").LayerStyles = {
        ...existingStyles,
        stroke: {
          enabled: true, size: result.size as number, position: "outside",
          blendMode: "normal", opacity: (result.opacity as number) / 100,
          fillType: "color", color: { r: 0, g: 0, b: 0, a: 255 },
        },
      };
      state.execute(new SetLayerPropertyCommand(doc.activeLayerId, { styles }));
      break;
    }
    default:
      showTip(`${effect} dialog not yet implemented.`);
      return;
  }
  showTip(`${effect} added.`);
}

// â”€â”€ Blend If dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
async function promptBlendIf(): Promise<void> {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (!layer) return;

  const existing = (layer as any).blendIf;
  const grayThis = existing?.channels?.gray?.thisLayer ?? [0, 255];
  const grayUnder = existing?.channels?.gray?.underlyingLayer ?? [0, 255];

  const result = await filterDialog.open("Blend If", [
    { key: "grayThisBlack", label: "This Layer: Black", type: "slider", default: grayThis[0], min: 0, max: 255 },
    { key: "grayThisWhite", label: "This Layer: White", type: "slider", default: grayThis[1], min: 0, max: 255 },
    { key: "grayUnderBlack", label: "Underlying: Black", type: "slider", default: grayUnder[0], min: 0, max: 255 },
    { key: "grayUnderWhite", label: "Underlying: White", type: "slider", default: grayUnder[1], min: 0, max: 255 },
  ]);
  if (!result) return;

  const blendIf: import("./core/document/Layer").BlendIfConfig = {
    channels: {
      gray: {
        thisLayer: [result.grayThisBlack as number, result.grayThisWhite as number],
        underlyingLayer: [result.grayUnderBlack as number, result.grayUnderWhite as number],
      },
    },
  };

  state.execute(new SetLayerPropertyCommand(doc.activeLayerId, { blendIf }));
  showTip("Blend If applied.");
}

// Helper: open a filter dialog and apply the filter with the result.
async function openFilterDialog(
  title: string,
  params: import("./app/ui/FilterDialog").FilterParamDef[],
  apply: (result: Record<string, number | boolean>) => void,
): Promise<void> {
  const result = await filterDialog.open(title, params);
  if (!result) return;
  apply(result as Record<string, number | boolean>);
}

function promptColorLookup(): void { FD.promptColorLookup(filterDeps()); }
function promptSelectiveColor(): void { FD.promptSelectiveColor(filterDeps()); }
async function promptShadowsHighlights(): Promise<void> { return FD.promptShadowsHighlights(filterDeps()); }
function promptAutoTone(): void { FD.promptAutoTone(filterDeps()); }
function promptAutoContrast(): void { FD.promptAutoContrast(filterDeps()); }
function promptAutoColor(): void { FD.promptAutoColor(filterDeps()); }
async function promptExposure(): Promise<void> { return FD.promptExposure(filterDeps()); }
async function promptPhotoFilter(): Promise<void> { return FD.promptPhotoFilter(filterDeps()); }
function promptChannelMixer(): void { FD.promptChannelMixer(filterDeps()); }
function promptInvert(): void { FD.promptInvert(filterDeps()); }
async function promptPosterize(): Promise<void> { return FD.promptPosterize(filterDeps()); }
async function promptThreshold(): Promise<void> { return FD.promptThreshold(filterDeps()); }
function promptGradientMap(): void { FD.promptGradientMap(filterDeps()); }

async function promptColorBalance(): Promise<void> { return AD.promptColorBalance(adjustmentDeps()); }
function promptBlackWhite(): void { AD.promptBlackWhite(adjustmentDeps()); }
async function promptVibrance(): Promise<void> { return AD.promptVibrance(adjustmentDeps()); }
async function promptHueSaturation(): Promise<void> { return AD.promptHueSaturation(adjustmentDeps()); }
async function promptLevels(): Promise<void> { return AD.promptLevels(adjustmentDeps()); }
async function promptCurves(): Promise<void> { return AD.promptCurves(adjustmentDeps()); }

function desaturateImage(): void {
  if (!state.doc) return;
  applyFilter(HueSaturation, {
    hue: 0, saturation: -100, lightness: 0,
    colorize: false, colorizeHue: 0, colorizeSat: 25,
  });
}

// i131: Image>Mode converts EVERY raster layer in the document, so the guard
// must check whether the doc has ANY raster layer (anywhere in the tree), not
// just whether the ACTIVE layer happens to be raster — otherwise selecting a
// text/shape/adjustment layer would wrongly block conversion.
function docHasRasterLayer(): boolean {
  return !!state.doc && collectRasterLayerIds(state.doc.layers).length > 0;
}
function convertToGrayscale(): void {
  if (!state.doc) return;
  if (!docHasRasterLayer()) { showError("No raster layer to convert."); return; }
  // convert EVERY raster layer as one undoable command + record the color mode.
  state.execute(ModeCommand.forDocument(state.doc, "grayscale", (buf) => rgbToGrayscale(buf), "grayscale"));
  showTip("Converted to grayscale.");
}

function convertToIndexed(): void {
  if (!state.doc) return;
  if (!docHasRasterLayer()) { showError("No raster layer to convert."); return; }
  state.execute(ModeCommand.forDocument(state.doc, "indexed", (buf) => rgbToIndexed(buf), "indexed"));
  showTip("Converted to indexed color.");
}

function convertToLab(): void {
  if (!state.doc) return;
  if (!docHasRasterLayer()) { showError("No raster layer to convert."); return; }
  state.execute(ModeCommand.forDocument(state.doc, "lab", (buf) => rgbToLab(buf), "lab"));
  showTip("Converted to Lab color.");
}

function convertToCmyk(): void {
  if (!state.doc) return;
  if (!docHasRasterLayer()) { showError("No raster layer to convert."); return; }
  state.execute(ModeCommand.forDocument(state.doc, "cmyk", (buf) => rgbToCmyk(buf), "cmyk"));
  showTip("Converted to CMYK color.");
}

function convertTo16Bit(): void {
  if (!state.doc) return;
  const layer = getActiveRaster();
  if (!layer) { showError("No active raster layer."); return; }
  const data16 = expand8to16(layer.pixels);
  layer.pixels.data.set(quantize16to8(data16));
  layer.pixels.markDirty();
  state.requestRender();
  showTip("Converted to 16 bits/channel.");
}

function convertTo8Bit(): void {
  if (!state.doc) return;
  const layer = getActiveRaster();
  if (!layer) { showError("No active raster layer."); return; }
  state.execute(new ModeCommand(layer.id, "8bit", (buf) => {
    const data16 = expand8to16(buf);
    buf.data.set(quantize16to8(data16));
    buf.markDirty();
  }));
  showTip("Converted to 8 bits/channel.");
}

function showImageAdjustmentsMenu(): void {
  const imageMenu = Array.from(document.querySelectorAll<HTMLLIElement>(".menubar__items li")).find(li => li.textContent === "Image");
  if (!imageMenu) return;
  showMenuDropdown(imageMenu, [
    { label: "Brightness/Contrastâ€¦", action: promptBrightnessContrast },
    { label: "Levelsâ€¦", shortcut: "Ctrl+L", action: promptLevels },
    { label: "Curvesâ€¦", shortcut: "Ctrl+M", action: promptCurves },
    { label: "Exposureâ€¦", action: promptExposure },
    { label: "Vibranceâ€¦", action: promptVibrance },
    { label: "Hue/Saturationâ€¦", shortcut: "Ctrl+U", action: promptHueSaturation },
    { label: "Color Balanceâ€¦", shortcut: "Ctrl+B", action: promptColorBalance },
    { label: "Black & Whiteâ€¦", action: promptBlackWhite },
    { label: "Photo Filterâ€¦", action: promptPhotoFilter },
    { label: "Channel Mixerâ€¦", action: promptChannelMixer },
    { label: "Color Lookupâ€¦", action: promptColorLookup },
    { label: "Invert", shortcut: "Ctrl+I", action: promptInvert },
    { label: "Posterizeâ€¦", action: promptPosterize },
    { label: "Thresholdâ€¦", action: promptThreshold },
    { label: "Gradient Mapâ€¦", action: promptGradientMap },
    { label: "Selective Colorâ€¦", action: promptSelectiveColor },
    { label: "Shadows/Highlightsâ€¦", action: promptShadowsHighlights },
    { separator: true, label: "" },
    { label: "Desaturate", shortcut: "Ctrl+Shift+U", action: desaturateImage },
    { label: "Auto Tone", shortcut: "Ctrl+Shift+L", action: promptAutoTone },
    { label: "Auto Contrast", shortcut: "Ctrl+Alt+Shift+L", action: promptAutoContrast },
    { label: "Auto Color", shortcut: "Ctrl+Shift+B", action: promptAutoColor },
  ]);
}

async function promptAddNoise(): Promise<void> { return FD.promptAddNoise(filterDeps()); }
async function promptMedian(): Promise<void> { return FD.promptMedian(filterDeps()); }
async function promptDustScratches(): Promise<void> { return FD.promptDustScratches(filterDeps()); }
function promptDespeckle(): void { FD.promptDespeckle(filterDeps()); }
async function promptTwirl(): Promise<void> { return FD.promptTwirl(filterDeps()); }
async function promptPinch(): Promise<void> { return FD.promptPinch(filterDeps()); }
async function promptSpherize(): Promise<void> { return FD.promptSpherize(filterDeps()); }
function promptFindEdges(): void { FD.promptFindEdges(filterDeps()); }
async function promptEmboss(): Promise<void> { return FD.promptEmboss(filterDeps()); }
async function promptHighPass(): Promise<void> { return FD.promptHighPass(filterDeps()); }
function promptClouds(): void { FD.promptClouds(filterDeps()); }
function promptDiffClouds(): void { FD.promptDiffClouds(filterDeps()); }
async function promptMosaic(): Promise<void> { return FD.promptMosaic(filterDeps()); }
async function promptMotionBlur(): Promise<void> { return FD.promptMotionBlur(filterDeps()); }
async function promptBoxBlur(): Promise<void> { return FD.promptBoxBlur(filterDeps()); }
function promptSharpen(): void { FD.promptSharpen(filterDeps()); }
function promptSharpenMore(): void { FD.promptSharpenMore(filterDeps()); }
async function promptUnsharpMask(): Promise<void> { return FD.promptUnsharpMask(filterDeps()); }
async function promptGaussianBlur(): Promise<void> { return FD.promptGaussianBlur(filterDeps()); }

// Simple menubar dropdown (Filter menu only for now).
function showMenuDropdown(anchor: HTMLElement, items: ReadonlyArray<{ label: string; shortcut?: string; disabled?: boolean; action?: () => void; separator?: boolean }>): void {
  document.querySelectorAll(".menu-dropdown").forEach((m) => m.remove());
  const rect = anchor.getBoundingClientRect();
  const menu = document.createElement("div");
  menu.className = "menu-dropdown";
  menu.style.left = `${rect.left}px`;
  menu.style.top = `${rect.bottom}px`;
  menu.innerHTML = items.map((it) =>
    it.separator
      ? `<div class="menu-sep"></div>`
      : `<div class="menu-item" data-id="${escapeHtml(it.label)}"${it.disabled ? ' data-disabled="true"' : ""}>
           <span>${escapeHtml(it.label)}</span>
           <span class="muted small">${it.shortcut ? escapeHtml(it.shortcut) : ""}</span>
         </div>`,
  ).join("");
  document.body.appendChild(menu);
  // Clamp within the viewport so the menu never renders off-screen or behind
  // the statusbar/panels. This is the shared positioner for every .menu-dropdown
  // (menubar dropdowns + submenus, layer/canvas context menus, adjustment-layer
  // menu, recent-files menu, ruler-unit menu), so clamping here fixes all of
  // them at once. (audit F6: showMenuDropdown had no clamp — menus opened at an
  // anchor near the right/bottom edge, or tall menus like the 17-item
  // adjustment-layer list, spilled off-screen with no scroll.)
  const PAD = 4;
  const mr = menu.getBoundingClientRect();
  let left = rect.left;
  if (left + mr.width > innerWidth - PAD) left = innerWidth - mr.width - PAD;
  if (left < PAD) left = PAD;
  let top = rect.bottom;
  if (top + mr.height > innerHeight - PAD) {
    const flippedTop = rect.top - mr.height; // prefer opening upward from the anchor
    top = flippedTop >= PAD ? flippedTop : Math.max(PAD, innerHeight - mr.height - PAD);
  }
  menu.style.left = `${Math.round(left)}px`;
  menu.style.top = `${Math.round(top)}px`;
  // Menu taller than the viewport (e.g. a long adjustment/filter list): cap and scroll.
  if (mr.height > innerHeight - 2 * PAD) {
    menu.style.maxHeight = `${innerHeight - 2 * PAD}px`;
    menu.style.overflowY = "auto";
  }
  menu.querySelectorAll<HTMLDivElement>(".menu-item").forEach((el) => {
    el.addEventListener("click", () => {
      const item = items.find((it) => !it.separator && it.label === el.getAttribute("data-id"));
      if (item && !item.disabled && item.action) item.action();
      menu.remove();
    });
  });
  setTimeout(() => {
    const off = (ev: MouseEvent) => {
      if (!menu.contains(ev.target as Node)) {
        menu.remove();
        document.removeEventListener("click", off);
      }
    };
    document.addEventListener("click", off);
  }, 0);
}

// Select/Filter menubar dropdowns are wired by wireMenus(deps) below;
// see the call site near the end of boot. The previous inline handlers
// were superseded by src/app/menus.ts (commit 17f4d2d) which adds
// proper submenu nesting, smart-filter routing, and Photoshop-fidelity
// menu items. Kept as a comment marker so grep history is preserved.

let screenMode: "standard" | "full-menu" | "full-screen" = "standard";

function cycleScreenMode(): void {
  const modes = ["standard", "full-menu", "full-screen"] as const;
  const idx = modes.indexOf(screenMode);
  screenMode = modes[(idx + 1) % 3]!;

  const root = document.documentElement;
  root.classList.remove("screen-standard", "screen-full-menu", "screen-full-screen");
  root.classList.add(`screen-${screenMode}`);

  if (screenMode === "full-menu" || screenMode === "full-screen") {
    document.documentElement.requestFullscreen?.().catch(() => {});
  } else {
    if (document.fullscreenElement) document.exitFullscreen?.();
  }
}

function adjustBrushSize(delta: number): void {
  const t = state.activeTool;
  const brush = t === brushTool ? brushTool : t === eraserTool ? eraserTool : null;
  if (brush) {
    brush.settings.size = Math.max(1, Math.min(2000, brush.settings.size + delta));
    state.requestRender();
    toolUx.refreshOptions(); // i74: keep the options-bar numerals in sync
    return;
  }
  // audit i8: PencilTool keeps a plain `size` field (not a `settings` object
  // like Brush/Eraser), so it fell through every branch here and `[`/`]`
  // silently did nothing while Pencil was active.
  if (t === pencilTool) {
    pencilTool.size = Math.max(1, Math.min(2000, pencilTool.size + delta));
    state.requestRender();
    toolUx.refreshOptions(); // keep the Pencil options-bar size numeral in sync
    return;
  }
  if (t === quickSelectionTool) {
    quickSelectionTool.brushSize = Math.max(1, Math.min(2000, quickSelectionTool.brushSize + delta));
    state.requestRender();
    return;
  }
  if (t === healingBrushTool) {
    (healingBrushTool.settings as any).size = Math.max(1, Math.min(2000, (healingBrushTool.settings as any).size + delta));
    state.requestRender();
    return;
  }
  if (t === spotHealingTool) {
    (spotHealingTool.settings as any).size = Math.max(1, Math.min(2000, (spotHealingTool.settings as any).size + delta));
    state.requestRender();
    return;
  }
  // i293: Magnetic Lasso's "Width" (edge search radius) is bracket-adjustable
  // too, clamped to the same 1-256 range as its options-bar Width field.
  if (t === magneticLassoTool) {
    magneticLassoTool.width = Math.max(1, Math.min(256, magneticLassoTool.width + delta));
    state.requestRender();
    toolUx.refreshOptions();
    return;
  }
}

function adjustBrushHardness(delta: number): void {
  const t = state.activeTool;
  const brush = t === brushTool ? brushTool : t === eraserTool ? eraserTool : null;
  if (brush) {
    // #218: delta is in 0..1 units (PS hardness field). Use tool method for correct clamp.
    if (typeof (brush as any).adjustHardness === 'function') {
      (brush as any).adjustHardness(delta);
    } else {
      brush.settings.hardness = Math.max(0, Math.min(1, brush.settings.hardness + delta));
    }
    state.requestRender();
    toolUx.refreshOptions(); // i74
    return;
  }
}

// Brush opacity 0-9 shortcut state (Photoshop convention)
let brushOpacityLastDigit: number | null = null;
let brushOpacityLastTime = 0;

function setBrushOpacityFromKey(digit: number): void {
  const t = state.activeTool;
  const brush = t === brushTool ? brushTool : t === eraserTool ? eraserTool : null;
  if (!brush) return;

  const now = Date.now();
  const isQuickSuccession = brushOpacityLastDigit !== null && (now - brushOpacityLastTime) < 800;

  if (isQuickSuccession && brushOpacityLastDigit !== null) {
    // Two-digit: e.g., 5 then 5 = 55%, 7 then 2 = 72%
    const opacity = (brushOpacityLastDigit * 10 + digit) / 100;
    brush.settings.opacity = Math.min(1, opacity);
    brushOpacityLastDigit = null;
  } else if (digit === 0) {
    // Single 0 = 100% (Photoshop convention)
    brush.settings.opacity = 1;
    brushOpacityLastDigit = null;
  } else {
    // Single digit 1-9 = 10%-90%
    brush.settings.opacity = digit / 10;
    brushOpacityLastDigit = digit;
  }
  brushOpacityLastTime = now;
  state.requestRender();
  toolUx.refreshOptions(); // i74
}

// Brush flow Shift+0-9 shortcut (#218)
let brushFlowLastDigit: number | null = null;
let brushFlowLastTime = 0;

function setBrushFlowFromKey(digit: number): void {
  const t = state.activeTool;
  const brush = t === brushTool ? brushTool : t === eraserTool ? eraserTool : null;
  if (!brush) return;
  const now = Date.now();
  const isQuick = brushFlowLastDigit !== null && (now - brushFlowLastTime) < 800;
  if (isQuick && brushFlowLastDigit !== null) {
    const flow = (brushFlowLastDigit * 10 + digit) / 100;
    brush.settings.flow = Math.min(1, flow);
    brushFlowLastDigit = null;
  } else if (digit === 0) {
    brush.settings.flow = 1;
    brushFlowLastDigit = null;
  } else {
    brush.settings.flow = digit / 10;
    brushFlowLastDigit = digit;
  }
  brushFlowLastTime = now;
  state.requestRender();
  toolUx.refreshOptions(); // i74
}

// Tool rail clicks (real tools only activate; stubs just highlight)
document.querySelectorAll<HTMLButtonElement>(".tool").forEach((btn) => {
  btn.addEventListener("click", () => {
    const key = btn.getAttribute("data-shortcut") ?? "";
    // i48/i49: prefer the button's OWN flyout group's first (default) variant
    // over the generic `tools[key]` lookup. Some flyout-group buttons share
    // their bare-key shortcut with an unrelated tool that owns that key in
    // the `tools` map (e.g. Blur/Sharpen/Smudge's "R" button would otherwise
    // resolve to Rotate View via tools["R"]), and some have no bare-key
    // entry in `tools` at all (e.g. Spot Healing's "J"). Every flyout
    // group's index-0 entry already calls the correct highlightToolByShortcut,
    // so this is a no-op behavior change for buttons where the two agree.
    const group = btn.getAttribute("data-flyout");
    const items = group ? FLYOUT_GROUPS[group] : undefined;
    if (items && items.length > 0) {
      // i152: reactivate whichever variant this slot last showed (tracked by
      // the state-subscription loop below) instead of always resetting to
      // the group's first/default entry — matches Photoshop's convention
      // that clicking the tool slot itself (not its flyout arrow) keeps
      // whatever tool the rail icon currently represents.
      const last = lastForKey[key];
      if (last) { state.setActiveTool(last); highlightToolByShortcut(key); return; }
      items[0]!.activate();
      return;
    }
    if (tools[key]) state.setActiveTool(tools[key]);
    highlightToolByShortcut(key);
  });
});

// Double-click on Hand tool icon â†’ Fit on Screen (Photoshop convention)
document.querySelector<HTMLButtonElement>('.tool[data-shortcut="H"]')?.addEventListener("dblclick", (e) => {
  e.preventDefault();
  fitDoc();
});

// Double-click on Zoom tool icon â†’ 100% (Photoshop convention)
document.querySelector<HTMLButtonElement>('.tool[data-shortcut="Z"]')?.addEventListener("dblclick", (e) => {
  e.preventDefault();
  resetTo100();
});

// Tool rail flyout menus (right-click on tools with variants)
// Define the variant tools for each flyout group.
// i269: each entry carries an `isActive()` predicate (in addition to
// label/activate) so the flyout menu can highlight whichever variant is
// currently selected. A plain tool-instance/id comparison isn't enough for
// groups whose variants share one Tool instance distinguished only by a
// `mode` field (retouch/dodgeburn/pen) — isActive() checks both.
const FLYOUT_GROUPS: Record<string, { label: string; activate: () => void; isActive: () => boolean }[]> = {
  // audit i22: PerspectiveCropTool was instantiated + registered in `tools`
  // ("perspective-crop") but no UI ever selected it — the Crop rail button
  // had no flyout, and FLYOUT_GROUPS had no "crop" entry.
  crop: [
    { label: "Crop", activate: () => { state.setActiveTool(cropTool); highlightToolByShortcut("C"); }, isActive: () => state.activeTool === cropTool },
    { label: "Perspective Crop", activate: () => { state.setActiveTool(perspectiveCropTool); highlightToolByShortcut("C"); }, isActive: () => state.activeTool === perspectiveCropTool },
  ],
  marquee: [
    { label: "Rectangular Marquee", activate: () => { state.setActiveTool(marqueeRectTool); highlightToolByShortcut("M"); }, isActive: () => state.activeTool === marqueeRectTool },
    { label: "Elliptical Marquee", activate: () => { state.setActiveTool(marqueeEllipseTool); highlightToolByShortcut("M"); }, isActive: () => state.activeTool === marqueeEllipseTool },
    { label: "Single Row Marquee", activate: () => { state.setActiveTool(marqueeSingleRowTool); highlightToolByShortcut("M"); }, isActive: () => state.activeTool === marqueeSingleRowTool },
    { label: "Single Column Marquee", activate: () => { state.setActiveTool(marqueeSingleColumnTool); highlightToolByShortcut("M"); }, isActive: () => state.activeTool === marqueeSingleColumnTool },
  ],
  lasso: [
    { label: "Lasso", activate: () => { state.setActiveTool(lassoTool); highlightToolByShortcut("L"); }, isActive: () => state.activeTool === lassoTool },
    { label: "Polygonal Lasso", activate: () => { state.setActiveTool(polyLassoTool); highlightToolByShortcut("L"); }, isActive: () => state.activeTool === polyLassoTool },
    { label: "Magnetic Lasso", activate: () => { state.setActiveTool(magneticLassoTool); highlightToolByShortcut("L"); }, isActive: () => state.activeTool === magneticLassoTool },
  ],
  wand: [
    { label: "Object Selection", activate: () => { state.setActiveTool(objectSelectionTool); highlightToolByShortcut("W"); }, isActive: () => state.activeTool === objectSelectionTool },
    { label: "Quick Selection", activate: () => { state.setActiveTool(quickSelectionTool); highlightToolByShortcut("W"); }, isActive: () => state.activeTool === quickSelectionTool },
    { label: "Magic Wand", activate: () => { state.setActiveTool(magicWandTool); highlightToolByShortcut("W"); }, isActive: () => state.activeTool === magicWandTool },
  ],
  gradient: [
    { label: "Gradient", activate: () => { state.setActiveTool(gradientTool); highlightToolByShortcut("G"); }, isActive: () => state.activeTool === gradientTool },
    { label: "Paint Bucket", activate: () => { state.setActiveTool(paintBucketTool); highlightToolByShortcut("G"); }, isActive: () => state.activeTool === paintBucketTool },
  ],
  retouch: [
    { label: "Blur", activate: () => { retouchTool.mode = "blur"; state.setActiveTool(retouchTool); highlightToolByShortcut("R"); }, isActive: () => state.activeTool === retouchTool && retouchTool.mode === "blur" },
    { label: "Sharpen", activate: () => { retouchTool.mode = "sharpen"; state.setActiveTool(retouchTool); highlightToolByShortcut("R"); }, isActive: () => state.activeTool === retouchTool && retouchTool.mode === "sharpen" },
    { label: "Smudge", activate: () => { retouchTool.mode = "smudge"; state.setActiveTool(retouchTool); highlightToolByShortcut("R"); }, isActive: () => state.activeTool === retouchTool && retouchTool.mode === "smudge" },
  ],
  dodgeburn: [
    { label: "Dodge", activate: () => { dodgeBurnTool.mode = "dodge"; state.setActiveTool(dodgeBurnTool); highlightToolByShortcut("O"); }, isActive: () => state.activeTool === dodgeBurnTool && dodgeBurnTool.mode === "dodge" },
    { label: "Burn", activate: () => { dodgeBurnTool.mode = "burn"; state.setActiveTool(dodgeBurnTool); highlightToolByShortcut("O"); }, isActive: () => state.activeTool === dodgeBurnTool && dodgeBurnTool.mode === "burn" },
    { label: "Sponge", activate: () => { dodgeBurnTool.mode = "sponge"; state.setActiveTool(dodgeBurnTool); highlightToolByShortcut("O"); }, isActive: () => state.activeTool === dodgeBurnTool && dodgeBurnTool.mode === "sponge" },
  ],
  shape: [
    { label: "Rectangle", activate: () => { state.setActiveTool(rectShapeTool); highlightToolByShortcut("U"); }, isActive: () => state.activeTool === rectShapeTool },
    { label: "Ellipse", activate: () => { state.setActiveTool(ellipseShapeTool); highlightToolByShortcut("U"); }, isActive: () => state.activeTool === ellipseShapeTool },
    { label: "Polygon", activate: () => { state.setActiveTool(polygonShapeTool); highlightToolByShortcut("U"); }, isActive: () => state.activeTool === polygonShapeTool },
    { label: "Rounded Rectangle", activate: () => { state.setActiveTool(roundedRectShapeTool); highlightToolByShortcut("U"); }, isActive: () => state.activeTool === roundedRectShapeTool },
    { label: "Line", activate: () => { state.setActiveTool(lineShapeTool); highlightToolByShortcut("U"); }, isActive: () => state.activeTool === lineShapeTool },
    { label: "Triangle", activate: () => { state.setActiveTool(triangleShapeTool); highlightToolByShortcut("U"); }, isActive: () => state.activeTool === triangleShapeTool },
    { label: "Custom Shape", activate: () => { state.setActiveTool(customShapeTool2); highlightToolByShortcut("U"); }, isActive: () => state.activeTool === customShapeTool2 },
  ],
  type: [
    { label: "Horizontal Type", activate: () => { lateInitTypeTools(); state.setActiveTool(typeTool); highlightToolByShortcut("T"); }, isActive: () => state.activeTool === typeTool },
    { label: "Vertical Type", activate: () => { lateInitTypeTools(); state.setActiveTool(verticalTypeTool); highlightToolByShortcut("T"); }, isActive: () => state.activeTool === verticalTypeTool },
    { label: "Horizontal Type Mask", activate: () => { lateInitTypeTools(); state.setActiveTool(horizontalTypeMaskTool); highlightToolByShortcut("T"); }, isActive: () => state.activeTool === horizontalTypeMaskTool },
    { label: "Vertical Type Mask", activate: () => { lateInitTypeTools(); state.setActiveTool(verticalTypeMaskTool); highlightToolByShortcut("T"); }, isActive: () => state.activeTool === verticalTypeMaskTool },
  ],
  path: [
    { label: "Path Selection", activate: () => { state.setActiveTool(pathSelectionTool); highlightToolByShortcut("A"); }, isActive: () => state.activeTool === pathSelectionTool },
    { label: "Direct Selection", activate: () => { state.setActiveTool(directSelectionTool); highlightToolByShortcut("A"); }, isActive: () => state.activeTool === directSelectionTool },
  ],
  // audit i11: ContentAwareMoveTool and RemoveTool were instantiated +
  // registered in the `tools` id-map ("content-aware-move" / "remove") but
  // appeared in no FLYOUT_GROUPS entry, so right-clicking the Spot Healing
  // rail button never offered them (unlike Spot Healing/Healing Brush/Patch/
  // Red Eye, which all had working flyout entries). Placed per the PS 2023+
  // J-group roster: Spot Healing, Healing Brush, Patch, Content-Aware Move,
  // Red Eye, Remove.
  healing: [
    { label: "Spot Healing Brush", activate: () => { state.setActiveTool(spotHealingTool); highlightToolByShortcut("J"); }, isActive: () => state.activeTool === spotHealingTool },
    { label: "Healing Brush", activate: () => { state.setActiveTool(healingBrushTool); highlightToolByShortcut("J"); }, isActive: () => state.activeTool === healingBrushTool },
    { label: "Patch", activate: () => { state.setActiveTool(patchTool); highlightToolByShortcut("J"); }, isActive: () => state.activeTool === patchTool },
    { label: "Content-Aware Move", activate: () => { state.setActiveTool(contentAwareMoveTool); highlightToolByShortcut("J"); }, isActive: () => state.activeTool === contentAwareMoveTool },
    { label: "Red Eye", activate: () => { state.setActiveTool(redEyeTool); highlightToolByShortcut("J"); }, isActive: () => state.activeTool === redEyeTool },
    { label: "Remove", activate: () => { state.setActiveTool(removeTool); highlightToolByShortcut("J"); }, isActive: () => state.activeTool === removeTool },
  ],
  brush: [
    { label: "Brush", activate: () => { state.setActiveTool(brushTool); highlightToolByShortcut("B"); }, isActive: () => state.activeTool === brushTool },
    { label: "Pencil", activate: () => { state.setActiveTool(pencilTool); highlightToolByShortcut("B"); }, isActive: () => state.activeTool === pencilTool },
    { label: "Mixer Brush", activate: () => { state.setActiveTool(mixerBrushTool); highlightToolByShortcut("B"); }, isActive: () => state.activeTool === mixerBrushTool },
    { label: "Color Replacement", activate: () => { state.setActiveTool(colorReplacementTool); highlightToolByShortcut("B"); }, isActive: () => state.activeTool === colorReplacementTool },
  ],
  eraser: [
    { label: "Eraser", activate: () => { state.setActiveTool(eraserTool); highlightToolByShortcut("E"); }, isActive: () => state.activeTool === eraserTool },
    { label: "Background Eraser", activate: () => { state.setActiveTool(backgroundEraserTool); highlightToolByShortcut("E"); }, isActive: () => state.activeTool === backgroundEraserTool },
    { label: "Magic Eraser", activate: () => { state.setActiveTool(magicEraserTool); highlightToolByShortcut("E"); }, isActive: () => state.activeTool === magicEraserTool },
  ],
  stamp: [
    { label: "Clone Stamp", activate: () => { state.setActiveTool(cloneStampTool); highlightToolByShortcut("S"); }, isActive: () => state.activeTool === cloneStampTool },
    { label: "Pattern Stamp", activate: () => { state.setActiveTool(patternStampTool); highlightToolByShortcut("S"); }, isActive: () => state.activeTool === patternStampTool },
  ],
  pen: [
    { label: "Pen", activate: () => { penTool.mode = "pen"; state.setActiveTool(penTool); highlightToolByShortcut("P"); }, isActive: () => state.activeTool === penTool && penTool.mode === "pen" },
    { label: "Freeform Pen", activate: () => { penTool.mode = "freeform"; state.setActiveTool(penTool); highlightToolByShortcut("P"); }, isActive: () => state.activeTool === penTool && penTool.mode === "freeform" },
    { label: "Curvature Pen", activate: () => { penTool.mode = "curvature"; state.setActiveTool(penTool); highlightToolByShortcut("P"); }, isActive: () => state.activeTool === penTool && penTool.mode === "curvature" },
  ],
  // audit i153: Eyedropper's sibling group (Color Sampler/Ruler/Note/Count).
  // The rail button already ships `has-flyout data-flyout="eyedropper"` in
  // index.html — this group was simply never defined, so right-clicking it
  // was a silent no-op (showFlyout() bails when FLYOUT_GROUPS[group] is
  // undefined).
  eyedropper: [
    { label: "Eyedropper", activate: () => { state.setActiveTool(eyedropperTool); highlightToolByShortcut("I"); }, isActive: () => state.activeTool === eyedropperTool },
    { label: "Color Sampler", activate: () => { state.setActiveTool(colorSamplerTool); highlightToolByShortcut("I"); }, isActive: () => state.activeTool === colorSamplerTool },
    { label: "Ruler", activate: () => { state.setActiveTool(rulerTool); highlightToolByShortcut("I"); }, isActive: () => state.activeTool === rulerTool },
    { label: "Note", activate: () => { state.setActiveTool(noteTool); highlightToolByShortcut("I"); }, isActive: () => state.activeTool === noteTool },
    { label: "Count", activate: () => { state.setActiveTool(countTool); highlightToolByShortcut("I"); }, isActive: () => state.activeTool === countTool },
  ],
  // audit i153: History Brush / Art History Brush. NOTE — index.html's Y
  // button does not yet carry `has-flyout`/`data-flyout="history"` (it's a
  // plain `.tool` button today), so this group isn't reachable via
  // right-click until that HTML attribute is added (out of scope for this
  // file); the Y-key keyboard cycle above already reaches both tools.
  history: [
    { label: "History Brush", activate: () => { state.setActiveTool(historyBrushTool); highlightToolByShortcut("Y"); }, isActive: () => state.activeTool === historyBrushTool },
    { label: "Art History Brush", activate: () => { state.setActiveTool(artHistoryBrushTool); highlightToolByShortcut("Y"); }, isActive: () => state.activeTool === artHistoryBrushTool },
  ],
};

// i152/i157: reverse lookup from a specific flyout-variant Tool instance back
// to {rail slot key, display label}, so the rail button's own title/
// aria-label can be kept in sync with whichever variant is actually active
// (e.g. "Elliptical Marquee (M)"), not just the group's static default label
// baked into index.html. Deliberately does NOT cover retouch/dodgeburn/pen:
// those 3 groups share a single Tool instance across variants (distinguished
// only by `.mode`), so switching between their variants never changes
// `.id` and can't be observed through the id-diffing loop below without a
// deeper change (out of scope here). Icon swaps aren't attempted either —
// index.html ships exactly one icon symbol per tool-rail group, so there is
// no per-variant icon asset available to swap to from this file.
const RAIL_VARIANT_LABEL = new Map<Tool, { key: string; label: string }>([
  [cropTool, { key: "C", label: "Crop" }],
  [perspectiveCropTool, { key: "C", label: "Perspective Crop" }],
  [marqueeRectTool, { key: "M", label: "Rectangular Marquee" }],
  [marqueeEllipseTool, { key: "M", label: "Elliptical Marquee" }],
  [marqueeSingleRowTool, { key: "M", label: "Single Row Marquee" }],
  [marqueeSingleColumnTool, { key: "M", label: "Single Column Marquee" }],
  [lassoTool, { key: "L", label: "Lasso" }],
  [polyLassoTool, { key: "L", label: "Polygonal Lasso" }],
  [magneticLassoTool, { key: "L", label: "Magnetic Lasso" }],
  [objectSelectionTool, { key: "W", label: "Object Selection" }],
  [quickSelectionTool, { key: "W", label: "Quick Selection" }],
  [magicWandTool, { key: "W", label: "Magic Wand" }],
  [gradientTool, { key: "G", label: "Gradient" }],
  [paintBucketTool, { key: "G", label: "Paint Bucket" }],
  [rectShapeTool, { key: "U", label: "Rectangle" }],
  [ellipseShapeTool, { key: "U", label: "Ellipse" }],
  [polygonShapeTool, { key: "U", label: "Polygon" }],
  [roundedRectShapeTool, { key: "U", label: "Rounded Rectangle" }],
  [lineShapeTool, { key: "U", label: "Line" }],
  [triangleShapeTool, { key: "U", label: "Triangle" }],
  [customShapeTool2, { key: "U", label: "Custom Shape" }],
  [typeTool, { key: "T", label: "Horizontal Type" }],
  [verticalTypeTool, { key: "T", label: "Vertical Type" }],
  [horizontalTypeMaskTool, { key: "T", label: "Horizontal Type Mask" }],
  [verticalTypeMaskTool, { key: "T", label: "Vertical Type Mask" }],
  [pathSelectionTool, { key: "A", label: "Path Selection" }],
  [directSelectionTool, { key: "A", label: "Direct Selection" }],
  [spotHealingTool, { key: "J", label: "Spot Healing Brush" }],
  [healingBrushTool, { key: "J", label: "Healing Brush" }],
  [patchTool, { key: "J", label: "Patch" }],
  [contentAwareMoveTool, { key: "J", label: "Content-Aware Move" }],
  [redEyeTool, { key: "J", label: "Red Eye" }],
  [removeTool, { key: "J", label: "Remove" }],
  [brushTool, { key: "B", label: "Brush" }],
  [pencilTool, { key: "B", label: "Pencil" }],
  [mixerBrushTool, { key: "B", label: "Mixer Brush" }],
  [colorReplacementTool, { key: "B", label: "Color Replacement" }],
  [eraserTool, { key: "E", label: "Eraser" }],
  [backgroundEraserTool, { key: "E", label: "Background Eraser" }],
  [magicEraserTool, { key: "E", label: "Magic Eraser" }],
  [cloneStampTool, { key: "S", label: "Clone Stamp" }],
  [patternStampTool, { key: "S", label: "Pattern Stamp" }],
  [eyedropperTool, { key: "I", label: "Eyedropper" }],
  [colorSamplerTool, { key: "I", label: "Color Sampler" }],
  [rulerTool, { key: "I", label: "Ruler" }],
  [noteTool, { key: "I", label: "Note" }],
  [countTool, { key: "I", label: "Count" }],
  [historyBrushTool, { key: "Y", label: "History Brush" }],
  [artHistoryBrushTool, { key: "Y", label: "Art History Brush" }],
]);

/** i152: remembers, per rail slot key, the last Tool instance activated for
 *  that slot (populated by the state-subscription loop below) — so
 *  re-clicking a flyout-group's rail button (not its flyout arrow)
 *  reactivates whichever variant it last showed instead of always resetting
 *  to the group's first/default entry. */
const lastForKey: Record<string, Tool> = {};

let activeFlyout: HTMLElement | null = null;

function showFlyout(btn: HTMLButtonElement): void {
  hideFlyout();
  const group = btn.getAttribute("data-flyout");
  if (!group || !FLYOUT_GROUPS[group]) return;

  const menu = document.createElement("div");
  menu.className = "flyout-menu";
  const items = FLYOUT_GROUPS[group]!;

  items.forEach((item) => {
    const el = document.createElement("div");
    el.className = "flyout-menu__item";
    el.textContent = item.label;
    // i269: highlight whichever variant is currently active.
    el.classList.toggle("is-active", item.isActive());
    menu.appendChild(el);
    el.addEventListener("click", () => {
      item.activate();
      hideFlyout();
    });
  });

  document.body.appendChild(menu);
  activeFlyout = menu;

  // Position below the button, then clamp into the viewport — flip above the
  // button when a tall flyout group (e.g. the 7-item Shape group) anchored low
  // in the rail would overflow the bottom. Mirrors the shared menu/HUD clamps
  // (F6/F7) so the flyout never opens off-screen. (F12)
  const rect = btn.getBoundingClientRect();
  const PAD = 4;
  const mr = menu.getBoundingClientRect();
  let left = rect.left;
  let top = rect.bottom + 2;
  if (left + mr.width > window.innerWidth - PAD) left = window.innerWidth - mr.width - PAD;
  if (left < PAD) left = PAD;
  if (top + mr.height > window.innerHeight - PAD) {
    const above = rect.top - mr.height - 2;
    top = above >= PAD ? above : Math.max(PAD, window.innerHeight - mr.height - PAD);
  }
  menu.style.left = `${Math.round(left)}px`;
  menu.style.top = `${Math.round(top)}px`;

  // Close on outside click
  setTimeout(() => {
    document.addEventListener("mousedown", hideFlyoutOutside, { once: true });
  }, 0);
}

function hideFlyout(): void {
  if (activeFlyout) {
    activeFlyout.remove();
    activeFlyout = null;
  }
}

function hideFlyoutOutside(e: MouseEvent): void {
  if (activeFlyout && !activeFlyout.contains(e.target as Node)) {
    hideFlyout();
  }
}

// Wire right-click on flyout tools
document.querySelectorAll<HTMLButtonElement>(".tool.has-flyout").forEach((btn) => {
  btn.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    showFlyout(btn);
  });
});

// Panel tabs â€” switch between Layers / Channels / Paths / History bodies.
document.querySelectorAll<HTMLButtonElement>(".panel-tab").forEach((tab) => {
  tab.addEventListener("click", (e) => {
    e.stopPropagation();
    const header = tab.parentElement;
    if (!header) return;
    header.querySelectorAll(".panel-tab").forEach((t) => t.classList.remove("is-active"));
    tab.classList.add("is-active");

    // The Character/Paragraph group is fully managed by its own dedicated tab
    // handler (see #character-panel-container above: showCharPanel /
    // showParaPanel + panel destroy/recreate). The generic body-toggle below
    // matches labels by their "*-body" class and has no case for "character"
    // /"paragraph", so continuing would fall through to the `else` and set the
    // shared #character-panel-body to display:none — blanking the panel after
    // any tab click. Stop here for this group. (audit: clicking the Character
    // or Paragraph tab blanked the panel body.)
    if (tab.closest("#character-panel-container")) return;

    const panel = header.parentElement;
    if (!panel) return;
    const label = tab.textContent?.trim().toLowerCase() ?? "";

    // Toggle panel body visibility
    panel.querySelectorAll(".panel__body").forEach((body) => {
      const el = body as HTMLElement;
      const bodyClass = el.className;
      if (label === "layers" && bodyClass.includes("layers-body")) {
        el.style.display = "";
      } else if (label === "channels" && bodyClass.includes("channels-body")) {
        el.style.display = "";
        renderChannelsPanel();
      } else if (label === "paths" && bodyClass.includes("paths-body")) {
        el.style.display = "";
        syncPathsPanel();
      } else if (label === "history" && bodyClass.includes("history-body")) {
        el.style.display = "";
        renderHistoryPanel();
      } else if (label === "color" && bodyClass.includes("color-body")) {
        el.style.display = "";
      } else if (label === "swatches" && bodyClass.includes("swatches-body")) {
        // i127: this used to fall into the generic `else` below and hide
        // every body in the Color/Swatches/Gradients group (including this
        // one) — clicking Swatches or Gradients showed a blank panel.
        el.style.display = "";
        initDockedSwatchesPanel();
      } else if (label === "gradients" && bodyClass.includes("gradients-body")) {
        el.style.display = "";
      } else {
        el.style.display = "none";
      }
    });
  });
});

/** Compute the current active channel id from `activeChannelView` flags. */
function currentActiveChannelId(): string {
  if (activeChannelView.alphaId) return `alpha:${activeChannelView.alphaId}`;
  const r = activeChannelView.r, g = activeChannelView.g, b = activeChannelView.b;
  if (r && g && b) return "rgb";
  if (r && !g && !b) return "red";
  if (!r && g && !b) return "green";
  if (!r && !g && b) return "blue";
  return "rgb";
}

/**
 * Channel show handler â€” updates renderer + state. Used by the new
 * ChannelsPanel module and by Ctrl+2/3/4/5 shortcuts.
 */
function showChannel(chId: string): void {
  if (chId === "rgb") {
    activeChannelView.r = true; activeChannelView.g = true; activeChannelView.b = true; activeChannelView.alphaId = null;
  } else if (chId === "red") {
    activeChannelView.r = true; activeChannelView.g = false; activeChannelView.b = false; activeChannelView.alphaId = null;
  } else if (chId === "green") {
    activeChannelView.r = false; activeChannelView.g = true; activeChannelView.b = false; activeChannelView.alphaId = null;
  } else if (chId === "blue") {
    activeChannelView.r = false; activeChannelView.g = false; activeChannelView.b = true; activeChannelView.alphaId = null;
  } else if (chId.startsWith("alpha:")) {
    activeChannelView.r = false; activeChannelView.g = false; activeChannelView.b = false;
    activeChannelView.alphaId = chId.slice(6);
  }
  renderer.setChannelMode({
    r: activeChannelView.r,
    g: activeChannelView.g,
    b: activeChannelView.b,
    alpha: getActiveAlphaChannel(),
  });
  state.requestRender();
}

/** Create a new alpha channel from the current selection. */
function newAlphaChannelFromSelection(): void {
  const doc = state.doc;
  if (!doc) return;
  const sel = doc.selection;
  let mask: Uint8Array;
  if (sel && !sel.isEmpty()) {
    mask = new Uint8Array(sel.mask);
  } else {
    mask = new Uint8Array(doc.width * doc.height);
    mask.fill(255);
  }
  const n = (docAlphaChannels.get(doc.name) || []).length + 1;
  const alpha: AlphaChannel = {
    id: crypto.randomUUID(),
    name: `Alpha ${n}`,
    pixels: mask,
    width: doc.width,
    height: doc.height,
    visible: true,
    opacity: 1,
    color: { r: 0, g: 0, b: 255, a: 0.5 },
  };
  const alphas = docAlphaChannels.get(doc.name) || [];
  alphas.push(alpha);
  docAlphaChannels.set(doc.name, alphas);
  renderChannelsPanel();
}

/** Delete the alpha channel with the given id. */
function deleteAlphaChannel(chId: string): void {
  if (!chId.startsWith("alpha:")) return;
  const doc = state.doc;
  if (!doc) return;
  const alphaId = chId.slice(6);
  const alphas = docAlphaChannels.get(doc.name) || [];
  const idx = alphas.findIndex((a) => a.id === alphaId);
  if (idx >= 0) {
    alphas.splice(idx, 1);
    docAlphaChannels.set(doc.name, alphas);
    activeChannelView.r = true;
    activeChannelView.g = true;
    activeChannelView.b = true;
    activeChannelView.alphaId = null;
    renderer.setChannelMode({ r: true, g: true, b: true, alpha: null });
    state.requestRender();
    renderChannelsPanel();
  }
}

/** Load the active alpha channel as a selection. */
function loadChannelAsSelection(chId: string): void {
  if (!chId.startsWith("alpha:")) return;
  const doc = state.doc;
  if (!doc) return;
  const alphaId = chId.slice(6);
  const alphas = docAlphaChannels.get(doc.name) || [];
  const alpha = alphas.find((a) => a.id === alphaId);
  if (!alpha) return;
  const sel = new Selection(doc.width, doc.height, new Uint8Array(alpha.pixels));
  state.execute(new SetSelectionCommand(sel));
}

/**
 * Render the Channels panel via the canonical module
 * (src/app/ui/ChannelsPanel.ts). Closes #154 â€” the module renders R/G/B
 * (or CMYK / Lab / Grayscale) rows in addition to the composite, with
 * Ctrl+N shortcut hints and footer buttons.
 *
 * The DOM-mutating side-effects (alpha channel CRUD, split, merge) remain
 * here because they reference renderer / Selection / app-local state.
 */
function renderChannelsPanel(): void {
  const doc = state.doc;
  if (!doc) {
    modRenderChannelsPanel({ state });
    return;
  }
  // Pixelpa documents are currently always RGB at the model layer; once
  // Document#colorMode is introduced we'll thread it through here.
  const colorMode: ChannelColorMode = "rgb";
  modRenderChannelsPanel({
    state,
    colorMode,
    alphaChannelsByDocName: docAlphaChannels,
    activeChannelId: currentActiveChannelId(),
    onChannelShow: (id) => showChannel(id),
    onNewChannel: () => newAlphaChannelFromSelection(),
    onDeleteChannel: (id) => deleteAlphaChannel(id),
    onLoadAsSelection: (id) => loadChannelAsSelection(id),
    onSaveAsSelection: () => newAlphaChannelFromSelection(),
  });

  // Footer Split / Merge / Duplicate stay wired inline because they
  // reference layer pixels and renderer-local state.
  const dupBtn = document.getElementById("channel-duplicate");
  if (dupBtn) {
    dupBtn.onclick = () => {
      if (!activeChannelView.alphaId) return;
      const alphas = docAlphaChannels.get(doc.name) || [];
      const src = alphas.find((a) => a.id === activeChannelView.alphaId);
      if (!src) return;
      const dup: AlphaChannel = {
        id: crypto.randomUUID(),
        name: src.name + " copy",
        pixels: new Uint8Array(src.pixels),
        width: src.width,
        height: src.height,
        visible: true,
        opacity: src.opacity ?? 1,
        color: { ...src.color },
      };
      alphas.push(dup);
      docAlphaChannels.set(doc.name, alphas);
      renderChannelsPanel();
    };
  }

  const splitBtn = document.getElementById("channel-split");
  if (splitBtn) {
    splitBtn.onclick = () => {
      const layer = getActiveRaster();
      if (!layer) { showError("No active raster layer."); return; }
      const alphas = docAlphaChannels.get(doc.name) || [];
      const data = layer.pixels.data;
      const w = layer.pixels.width;
      const h = layer.pixels.height;

      const rMask = new Uint8Array(w * h);
      const gMask = new Uint8Array(w * h);
      const bMask = new Uint8Array(w * h);
      for (let i = 0; i < w * h; i++) {
        rMask[i] = data[i * 4]!;
        gMask[i] = data[i * 4 + 1]!;
        bMask[i] = data[i * 4 + 2]!;
      }
      alphas.push(
        { id: crypto.randomUUID(), name: `${layer.name} Red`, pixels: rMask, width: w, height: h, visible: true, opacity: 1, color: { r: 255, g: 0, b: 0, a: 0.5 } },
        { id: crypto.randomUUID(), name: `${layer.name} Green`, pixels: gMask, width: w, height: h, visible: true, opacity: 1, color: { r: 0, g: 255, b: 0, a: 0.5 } },
        { id: crypto.randomUUID(), name: `${layer.name} Blue`, pixels: bMask, width: w, height: h, visible: true, opacity: 1, color: { r: 0, g: 0, b: 255, a: 0.5 } },
      );
      docAlphaChannels.set(doc.name, alphas);
      renderChannelsPanel();
      showTip("Channels split into R, G, B alpha channels.");
    };
  }

  const mergeBtn = document.getElementById("channel-merge");
  if (mergeBtn) {
    mergeBtn.onclick = () => {
      const layer = getActiveRaster();
      if (!layer) { showError("No active raster layer."); return; }
      const alphas = docAlphaChannels.get(doc.name) || [];
      if (alphas.length === 0) { showError("No alpha channels to merge."); return; }

      const data = layer.pixels.data;
      const w = layer.pixels.width;
      const h = layer.pixels.height;

      const mask = alphas[0]!.pixels;
      for (let i = 0; i < w * h; i++) {
        const alpha = mask[i]!;
        data[i * 4 + 3] = alpha;
      }
      layer.pixels.markDirty();

      docAlphaChannels.set(doc.name, []);
      activeChannelView = { r: true, g: true, b: true, alphaId: null };
      renderer.setChannelMode({ r: true, g: true, b: true, alpha: null });
      state.requestRender();
      renderChannelsPanel();
      showTip("Alpha channels merged into layer.");
    };
  }
}

function getActiveAlphaChannel(): AlphaChannel | null {
  if (!state.doc || !activeChannelView.alphaId) return null;
  const alphas = docAlphaChannels.get(state.doc.name) || [];
  return alphas.find((a) => a.id === activeChannelView.alphaId) || null;
}

/** Set the History Brush source to the merged pixels at a given history state index. */
function setHistorySource(stateIndex: number): void {
  const doc = state.getDocumentAtStateIndex(stateIndex);
  if (!doc) return;
  const w = doc.width;
  const h = doc.height;
  if (w <= 0 || h <= 0) return;

  // Composite all visible layers into a single buffer
  const result = new Uint8ClampedArray(w * h * 4);
  for (const layer of doc.layers) {
    compositeLayerToBuffer(result, w, h, layer);
  }

  historyBrushTool.sourcePixels = result;
  historyBrushTool.sourceWidth = w;
  historyBrushTool.sourceHeight = h;

  // Also set art history brush source
  artHistoryBrushTool.sourcePixels = result;
  artHistoryBrushTool.sourceWidth = w;
  artHistoryBrushTool.sourceHeight = h;

  state.requestRender();
}

/** Composite a single layer into a destination RGBA buffer. */
function compositeLayerToBuffer(
  dst: Uint8ClampedArray,
  docW: number,
  docH: number,
  layer: Layer,
): void {
  if (!layer.visible || layer.opacity <= 0) return;

  if (layer.kind === "group") {
    for (const child of (layer as GroupLayer).children) {
      compositeLayerToBuffer(dst, docW, docH, child);
    }
    return;
  }

  if (layer.kind !== "raster") return;
  const raster = layer as RasterLayer;
  const src = raster.pixels.data;
  const srcW = raster.pixels.width;
  const srcH = raster.pixels.height;
  if (srcW <= 0 || srcH <= 0) return;

  const lx = raster.x;
  const ly = raster.y;
  const opacity = layer.opacity;

  const x0 = Math.max(0, lx);
  const y0 = Math.max(0, ly);
  const x1 = Math.min(docW, lx + srcW);
  const y1 = Math.min(docH, ly + srcH);

  for (let dy = y0; dy < y1; dy++) {
    for (let dx = x0; dx < x1; dx++) {
      const sx = dx - lx;
      const sy = dy - ly;
      const srcIdx = (sy * srcW + sx) * 4;
      const dstIdx = (dy * docW + dx) * 4;
      const sa = src[srcIdx + 3]! / 255 * opacity;
      const ia = 1 - sa;
      dst[dstIdx] = Math.round(dst[dstIdx]! * ia + src[srcIdx]! * sa);
      dst[dstIdx + 1] = Math.round(dst[dstIdx + 1]! * ia + src[srcIdx + 1]! * sa);
      dst[dstIdx + 2] = Math.round(dst[dstIdx + 2]! * ia + src[srcIdx + 2]! * sa);
      dst[dstIdx + 3] = Math.min(255, Math.round(dst[dstIdx + 3]! * ia + src[srcIdx + 3]! * sa));
    }
  }
}

/**
 * Render the History panel via the canonical module (src/app/ui/HistoryPanel.ts).
 * Closes #152 â€” the module subscribes to `state` and re-renders on every
 * command execution, so users no longer see the stale "No history"
 * placeholder after the first paint stroke.
 */
function renderHistoryPanel(): void {
  // i10: onSetSource wires the panel's per-row source gutter icon (and the
  // default-on-load assignment) to setHistorySource(), which populates
  // historyBrushTool/artHistoryBrushTool.sourcePixels.
  modRenderHistoryPanel({ state, onSetSource: setHistorySource });
}

/** Create a named snapshot of the current history state. */
function createHistorySnapshot(): void {
  const snap = modCreateHistorySnapshot({ state });
  if (snap) showTip(`Snapshot "${snap.name}" created.`);
}

// File open
const fileInput = document.createElement("input");
fileInput.type = "file";
fileInput.accept = FILE_INPUT_ACCEPT;
fileInput.multiple = true;
fileInput.style.display = "none";
document.body.appendChild(fileInput);
fileInput.addEventListener("change", () => {
  const files = fileInput.files;
  if (files && files.length > 0) {
    const openAll = async () => {
      for (let i = 0; i < files.length; i++) { await openFile(files[i]!); }
    };
    void openAll();
  }
  fileInput.value = "";
});

// Legacy inline menubar block removed (#203). wireMenus() in src/app/menus.ts
// is the sole handler for all top-level menu items; it clones each <li> to
// strip prior listeners. All actions are covered by buildMenuDeps() + MenuDeps.

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// wireMenus(deps) â€” canonical menubar wiring (src/app/menus.ts).
//
// Background: commit 17f4d2d made src/app/menus.ts the source of truth for
// the menubar (Photoshop-fidelity submenus, Type/3D injection, smart-filter
// routing, etc.) but the legacy inline `menubar__items` handlers above were
// never removed. wireMenus() clones each <li> first to strip prior listeners
// (see #100), so by calling it here the inline handlers are superseded.
//
// The legacy block has been removed (#203). wireMenus() is now the sole handler.
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function buildMenuDeps(): MenuDeps {
  return {
    state,
    // audit i23/i24: previously omitted entirely, so menus.ts's
    // `(deps as any).freeTransformTool` / `.toolCtx` resolved to `undefined`
    // and the Edit > Free Transform / Transform submenu items silently
    // no-op'd when clicked from the menu (the Ctrl+T keyboard shortcut has
    // its own direct wiring at the pointerdown/keydown handlers above, so it
    // kept working — only the menu path was dead).
    freeTransformTool, toolCtx, highlightToolByShortcut, transformLayerOrientation,
    showMenuDropdown,
    selectAll, deselect, reselect, selectInverse,
    transformSelection, saveSelection, loadSelection, colorRange,
    selectAndMask, selectBorder, selectSmooth, selectExpand, selectContract,
    selectFeather, selectGrow, selectSimilar, colorRangeEnhanced, makeWorkPath,
    // i31: menus.ts's Window > Paths item calls `(deps as any).syncPathsPanel?.()`
    // after toggling the tab; without this on the deps object it was a silent
    // no-op and the panel never rendered when opened via the menu.
    syncPathsPanel,
    newDocument, closeDoc, closeAllDocs, showSaveAs, quickExportPng, exportAs, saveForWeb,
    revertDocument, placeEmbedded, openAsSmartObject, showFileInfo, showPrintDialog,
    fileInput,
    getRecentFiles, showRecentFilesMenu,
    cutSelection, copySelection, copyMergedSelection, pasteClipboard,
    pasteInPlace, pasteInto, pasteOutside, clearSelection,
    promptFill, promptStroke, promptContentAwareFill,
    defineBrushPreset, definePattern, promptPurge, transformAgain,
    promptColorSettings, openShortcutsEditor, promptPreferences,
    findAndReplaceText, checkSpelling,
    convertToGrayscale, showImageAdjustmentsMenu,
    promptImageSize, promptCanvasSize, rotateImage, promptArbitraryRotation,
    trimImage, revealAll,
    promptNewLayer, showFillLayerMenu, showAdjustmentLayerMenu, addNewArtboard,
    promptDuplicateLayer, deleteActiveLayer, layerViaCopy, layerViaCut,
    convertToSmartObject, editSmartObject, replaceSmartObjectContents,
    exportSmartObject, rasterizeSmartObject,
    createClippingMask, releaseClippingMask,
    groupActive, ungroupActive, mergeDownActive, mergeSelectedLayers,
    alignLayers, distributeLayers, alignTargetIds, reorderActiveLayer,
    openColorPanel, openCloneSourcePanel,
    flattenImage, groupFromLayers, duplicateToNewDocument,
    exportLayersToFiles, exportArtboardsToFiles, exportArtboardsToPdf,
    exportPathsToIllustrator, exportColorLookupTables, placeLinked,
    conditionalModeChange, createDroplet, viewPrintSize, toggleSlices,
    showLayerStyleDialog, showWarpTextDialog, promptApplyImage, promptCalculations,
    rasterizeShapeLayer, copyLayerStyle, pasteLayerStyle, clearLayerStyle,
    addLayerStyleEffect, promptBlendIf,
    layerProperties, blendingOptions, scaleLayerEffects, setGlobalLight,
    zoomCentered, fitDoc, resetTo100, zoomTo200,
    toggleRulers, toggleGrid, promptGridSettings, toggleGuides, toggleExtras,
    toggleSnap, toggleSnapToGuides, toggleSnapToGrid, toggleSnapToLayers,
    toggleSnapToDocumentBounds,
    newGuide, newGuideLayout, clearGuides, toggleLockGuides,
    togglePixelGrid, toggleLayerEdges, cycleScreenMode,
    getCurrentWorkspace, buildWorkspaceMenuItems,
    openNavigatorPanel, openInfoPanel, openHistogramPanel, openSwatchesPanel,
    openBrushSettingsPanel, openGradientEditor, openPatternEditor,
    openStylesPanel, openPropertiesPanel, openAdjustmentsPanel, openShapesPanel, showLayerCompsPanel,
    tileDocuments, cascadeDocuments, floatDocument, consolidateTabs,
    showActionsPanel, showBatchDialog, showImageProcessor, showScriptsDialog,
    openTutorials, showWhatsNew, showAbout,
    repeatLastFilter,
    isSmartObjectActive,
    smartBoxBlur, smartGaussianBlur, smartMotionBlur,
    smartSharpen, smartSharpenMore, smartUnsharpMask,
    smartAddNoise, smartMedian, smartDustScratches, smartMosaic,
    smartTwirl, smartPinch, smartSpherize,
    smartFindEdges, smartEmboss, smartHighPass,
    promptAutoTone, promptAutoContrast, promptAutoColor,
    promptColorBalance, promptBlackWhite, promptChannelMixer, promptColorLookup,
    promptExposure, promptSelectiveColor, promptShadowsHighlights,
    promptGradientMap, promptHueSaturation, promptInvert, promptPhotoFilter,
    promptPosterize, promptThreshold, promptVibrance,
    promptBoxBlur, promptGaussianBlur, promptMotionBlur,
    promptSharpen, promptSharpenMore, promptUnsharpMask,
    promptAddNoise, promptMedian, promptDustScratches, promptMosaic,
    promptTwirl, promptPinch, promptSpherize, promptFindEdges, promptEmboss,
    promptHighPass, promptClouds, promptDiffClouds, promptFibers,
    promptLensFlare, promptLightingEffects,
    promptDisplace, promptRipple, promptShear, promptWave, promptZigZag,
    promptPolarCoordinates,
    promptAverageBlur, promptSurfaceBlur, promptSmartBlur, promptLensBlur,
    promptRadialBlur, promptShapeBlur, promptFieldBlur, promptIrisBlur,
    promptTiltShift,
    promptCrystallize, promptFacet, promptFragment, promptMezzotint,
    promptPointillize, promptSolarize, promptTiles, promptTraceContour,
    promptWind, promptGlowingEdges, promptDiffuse, promptExtrude,
    promptPictureFrame, promptBrightnessContrast, promptOffset,
    promptMaximum, promptMinimum, promptHSBHSL, promptReduceNoise,
    promptSmartSharpen, promptOilPaint,
    filterGallery, lensCorrection, adaptiveWideAngle, duplicateDocument,
    selectAllLayers, deselectLayers, findLayers,
    hideLayers, showAllLayers, hideOthers,
    toggleSelectionEdges, toggleTargetPath, toggleNotes,
    get showSelectionEdges() { return showSelectionEdges; },
    set showSelectionEdges(v: boolean) { showSelectionEdges = v; },
    get showTargetPath() { return showTargetPath; },
    set showTargetPath(v: boolean) { showTargetPath = v; },
    get showNotes() { return showNotes; },
    set showNotes(v: boolean) { showNotes = v; },
    desaturateImage, promptLevels, promptCurves,
    get proofColorsEnabled() { return proofColorsEnabled; },
    set proofColorsEnabled(v: boolean) { proofColorsEnabled = v; },
    get gamutWarningEnabled() { return gamutWarningEnabled; },
    set gamutWarningEnabled(v: boolean) { gamutWarningEnabled = v; },
    get showRulers() { return showRulers; },
    get showGrid() { return showGrid; },
    get showGuides() { return showGuides; },
    get showSmartGuides() { return showSmartGuides; },
    set showSmartGuides(v: boolean) { showSmartGuides = v; },
    get showExtras() { return showExtras; },
    get snapEnabled() { return snapEnabled; },
    get snapToGuides() { return snapToGuides; },
    get snapToGrid() { return snapToGrid; },
    get snapToLayers() { return snapToLayers; },
    get snapToDocumentBounds() { return snapToDocumentBounds; },
    get guidesLocked() { return guidesLocked; },
    get screenMode() { return screenMode; },
    // Stash showTip on deps so menus.ts's `comingSoon` stub can reach it.
    ...(({ showTip } as any) as Record<string, unknown>),
    // i29: expose the layer-mask command constructors so menus.ts's Layer >
    // Layer Mask submenu (`(deps as any).AddLayerMaskCommand`, etc.) can
    // actually instantiate them instead of throwing "is not a constructor".
    ...(({ AddLayerMaskCommand, DeleteLayerMaskCommand, ApplyLayerMaskCommand, EnableLayerMaskCommand } as any) as Record<string, unknown>),
  } as MenuDeps;
}

wireMenus(buildMenuDeps());

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Layer context-menu CustomEvent handlers
//
// src/app/layersPanel.ts dispatches these events on `document` so the panel
// stays main.ts-free. We attach the handlers here at boot.
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

document.addEventListener("pixelpa:layer-properties", () => {
  // Active layer is set by the panel's selection click before the menu opens,
  // so layerProperties() (which reads doc.activeLayerId) is the right entry.
  void layerProperties();
});

document.addEventListener("pixelpa:blending-options", () => {
  void blendingOptions();
});

document.addEventListener("pixelpa:rasterize-layer", (e: Event) => {
  const detail = (e as CustomEvent<{ layerId: string }>).detail;
  rasterizeLayerById(detail?.layerId);
});

document.addEventListener("pixelpa:convert-to-frame", () => {
  // Frame tool (rectangular/elliptical frame placeholders) lands in a follow-up.
  showTip("Convert to Frame: Coming soon");
});

// i162/i279: the options-bar Select-and-Mask button (MarqueeToolOptions etc.
// in ToolUX.ts) dispatches this CustomEvent since ToolUX has no reference to
// main.ts's selectAndMask(). Same entry point the Select menu's "Select and
// Mask…" item already uses.
document.addEventListener("pixelpa:open-select-and-mask", () => {
  void selectAndMask();
});

// i96: TypeToolOptions' commit (✓) / cancel (✕) buttons (ToolUX.ts) dispatch
// these CustomEvents for the same reason — ToolUX has no reference to
// main.ts's local `textEditor` (TextEditorOverlay) instance. Wire them to
// the live editing session directly.
document.addEventListener("pixelpa:type-commit", () => {
  if (textEditor.isActive()) textEditor.commitNow();
});
document.addEventListener("pixelpa:type-cancel", () => {
  // TextEditorOverlay has no public cancel() (only close(), which does NOT
  // invoke the pending commit/cancel promise callback typeTool is awaiting —
  // that would leave requestNewText()/requestEditText() hanging forever).
  // Its private doCancel() does both: closes the overlay AND resolves that
  // promise with null, matching what pressing Escape while editing does.
  if (textEditor.isActive()) (textEditor as unknown as { doCancel: () => void }).doCancel();
});
// i184: PatternStampToolOptions' "Change Pattern…" button (ToolUX.ts) dispatches
// this event (ToolUX can't reach main.ts's module-scoped openPatternEditor()).
document.addEventListener("pixelpa:open-pattern-editor", () => {
  openPatternEditor();
});

/**
 * Rasterize an arbitrary layer by id. Dispatches to the existing rasterize-by-
 * kind helpers (shape / smart-object), or shows a tip if the kind isn't yet
 * supported. Raster layers are no-ops (already raster).
 */
function rasterizeLayerById(layerId: string | undefined): void {
  const doc = state.doc;
  if (!doc || !layerId) return;
  const layer = findLayer(doc.layers, layerId);
  if (!layer) return;
  // Make the layer active so the existing kind-specific functions operate on it.
  if (doc.activeLayerId !== layerId) state.setActiveLayer(layerId);
  switch (layer.kind) {
    case "raster":
      showTip("Layer is already a raster layer.");
      break;
    case "shape":
      rasterizeShapeLayer();
      break;
    case "smart-object":
      rasterizeSmartObject();
      break;
    case "type":
      showTip("Rasterize Type: Coming soon");
      break;
    case "adjustment":
    case "group":
    case "solid-fill":
    case "gradient-fill":
    case "pattern-fill":
      showTip(`Rasterize ${layer.kind}: Coming soon`);
      break;
    default:
      showTip("Rasterize: layer kind not supported");
  }
}

viewportEl.addEventListener("dragover", (e) => { e.preventDefault(); });
viewportEl.addEventListener("drop", (e) => {
  e.preventDefault();
  const f = e.dataTransfer?.files?.[0];
  if (!f) return;
  // i135: dropping onto an ALREADY-OPEN document places the image as a new
  // layer (Photoshop's Place convention) instead of replacing the whole
  // document — openFile() unconditionally built a brand-new single-layer
  // document, silently discarding whatever was open.
  if (state.doc) {
    placeImageAsLayer(f).catch((err) => {
      showError(`Place failed: ${err instanceof Error ? err.message : "unknown error"}`);
    });
  } else {
    openFile(f);
  }
});

const newDocDialog = new NewDocumentDialog();
async function newDocument(): Promise<void> {
  const result = await newDocDialog.open();
  if (!result) return;
  const pixels = new PixelBuffer(result.width, result.height, "rgba8");
  // Fill the initial layer based on the requested background color.
  if (result.background !== "transparent") {
    const data = pixels.data;
    let r = 255, g = 255, b = 255;
    if (result.background === "black") { r = 0; g = 0; b = 0; }
    else if (typeof result.background === "object") { r = result.background.r; g = result.background.g; b = result.background.b; }
    for (let i = 0; i < data.length; i += 4) { data[i] = r; data[i+1] = g; data[i+2] = b; data[i+3] = 255; }
  }
  pixels.markDirty();
  // i165: an opaque background (White/Black/Custom Color) becomes a real
  // locked "Background" layer (kind: "background"), matching Photoshop's
  // New Document dialog convention -- findRaster() and the paint tools'
  // erase-to-color path already understand this layer kind. Only
  // "Transparent" keeps the old plain, unlocked raster layer named "Layer 1".
  const layer: RasterLayer | BackgroundLayer = result.background === "transparent"
    ? {
        id: crypto.randomUUID(),
        kind: "raster",
        name: "Layer 1",
        visible: true,
        opacity: 1,
        fillOpacity: 1,
        blendMode: "normal",
        x: 0, y: 0,
        lockTransparency: false,
        lockImage: false,
        lockPosition: false,
        lockAll: false,
        clippedToBelow: false,
        pixels,
      }
    : createBackgroundLayer({ pixels });
  const doc: Document = {
    id: crypto.randomUUID(),
    name: result.name,
    width: result.width,
    height: result.height,
    dpi: result.dpi,
    layers: [layer],
    activeLayerId: layer.id,
    selection: null,
  };
  state.execute(new LoadDocumentCommand(doc));
  fitDoc();
}

// PSD blend-mode key (4 chars padded with spaces) â†’ our BlendMode union.
const PSD_BLEND_KEYS: Record<string, BlendMode> = {
  "norm": "normal", "pass": "normal",
  "diss": "dissolve",
  "dark": "darken", "mul ": "multiply", "idiv": "color-burn", "lbrn": "linear-burn", "dkCl": "darker-color",
  "lite": "lighten", "scrn": "screen", "div ": "color-dodge", "lddg": "linear-dodge", "lgCl": "lighter-color",
  "over": "overlay", "sLit": "soft-light", "hLit": "hard-light", "vLit": "vivid-light", "lLit": "linear-light", "pLit": "pin-light", "hMix": "hard-mix",
  "diff": "difference", "smud": "exclusion", "fsub": "subtract", "fdiv": "divide",
  "hue ": "hue", "sat ": "saturation", "colr": "color", "lum ": "luminosity",
};
function psdBlendKeyToOurs(key: string): BlendMode {
  return PSD_BLEND_KEYS[key] ?? "normal";
}

async function openPsd(file: File): Promise<void> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const psd = parsePsd(bytes);

  // Build a flat map of decoded layers first.
  const decodedLayers: {
    psdLayer: import("./core/io/psd/types").PsdLayerRecord;
    pixels: PixelBuffer;
    maskGray: import("./core/io/psd/decodeChannels").DecodedLayerMask | null;
    id: string;
  }[] = [];
  for (const psdLayer of psd.layerInfo.layers) {
    const decoded = await decodeLayer(psd, psdLayer, bytes);
    if (!decoded) {
      // Adjustment, fill, and type layers may have 0×0 pixel bounds yet still
      // carry valid data (parameters, masks). Include them with a stub pixel
      // buffer so buildLayerTree can classify and wire them correctly (#245).
      const isNoPixelLayer = psdLayerHasNoPixelData(psdLayer);
      if (!isNoPixelLayer) continue;
      const stub = new PixelBuffer(1, 1, "rgba8");
      stub.markDirty();
      const maskGray = await decodeLayerMask(psd, psdLayer, bytes);
      decodedLayers.push({ psdLayer, pixels: stub, maskGray, id: crypto.randomUUID() });
      continue;
    }
    const pixels = new PixelBuffer(decoded.width, decoded.height, "rgba8");
    pixels.data.set(decoded.rgba);
    pixels.markDirty();
    // Decode user layer mask (channel -2) if present.
    const maskGray = await decodeLayerMask(psd, psdLayer, bytes);
    decodedLayers.push({ psdLayer, pixels, maskGray, id: crypto.randomUUID() });
  }

  // Reconstruct group tree from lsct section-divider sentinels (#73).
  const layers = buildLayerTree(decodedLayers, psd.header.width, psd.header.height);

  // Flattened PSDs: decode merged composite as single Background layer.
  if (layers.length === 0) {
    try {
      const merged = await decodeMergedComposite(psd, bytes);
      const pixels = new PixelBuffer(merged.width, merged.height, "rgba8");
      pixels.data.set(merged.rgba);
      pixels.markDirty();
      layers.push({
        id: crypto.randomUUID(),
        kind: "raster" as const,
        name: "Background",
        visible: true,
        opacity: 1,
        fillOpacity: 1,
        blendMode: "normal" as BlendMode,
        x: 0,
        y: 0,
        lockTransparency: false,
        lockImage: false,
        lockPosition: false,
        lockAll: false,
        clippedToBelow: false,
        pixels,
      });
    } catch (err) {
      console.error("PSD merged composite decode failed:", err);
    }
  }

  // Load guides from image resource 1032 (#186).
  // Clear any guides from a previously open document, then populate from PSD.
  guides.length = 0;
  const guideResource = psd.imageResources.find(r => r.id === 1032);
  if (guideResource) {
    const loadedGuides = parseGuideResource(guideResource.data);
    for (const g of loadedGuides) {
      guides.push(g);
    }
  }

  // Capture ICC profile (resource 1039) for round-trip preservation (#207).
  // The profile is NOT applied to rendering — it is preserved verbatim so
  // that re-exporting the PSD does not silently discard color management data.
  const iccResource = psd.imageResources.find(r => r.id === 1039);
  currentIccProfile = iccResource ? new Uint8Array(iccResource.data) : undefined;

  // Extract DPI from image resource 1005 (ResolutionInfo) (#247).
  // Format: 4-byte 16.16 fixed-point horizontal DPI, then 2-byte unit (1=PPI, 2=PPCM).
  let psdDpi = 72;
  const resResource = psd.imageResources.find(r => r.id === 1005);
  if (resResource && resResource.data.length >= 4) {
    const resView = new DataView(resResource.data.buffer, resResource.data.byteOffset, resResource.data.byteLength);
    const fixedHRes = resView.getInt32(0, false);
    const rawDpi = fixedHRes / 65536;
    if (rawDpi > 0) {
      const unit = resResource.data.length >= 6 ? resView.getUint16(4, false) : 1;
      psdDpi = unit === 2 ? Math.round(rawDpi * 2.54) : Math.round(rawDpi);
    }
  }

  const topLayer = layers[layers.length - 1];
  const doc: Document = {
    // i44: unique per opened file, so opening the same filename twice (or a
    // file that happens to share a name with another open document) never
    // collides in per-document History or the tab registry.
    id: crypto.randomUUID(),
    name: file.name,
    width: psd.header.width,
    height: psd.header.height,
    dpi: psdDpi,
    layers,
    activeLayerId: topLayer ? topLayer.id : null,
    selection: null,
  };
  state.execute(new LoadDocumentCommand(doc));
  // i134: a freshly-opened document shouldn't read as unsaved/dirty — LoadDocumentCommand
  // (like any Command) unconditionally sets EditorState._dirty = true via execute().
  state.markSaved();
  fitDoc();
}

// ---------------------------------------------------------------------------
// PSD layer-tree reconstruction helpers
// ---------------------------------------------------------------------------

/**
 * Additional-info keys that identify adjustment layers without requiring AdjL.
 * Photoshop does not always emit AdjL for legacy adjustment layers (#245).
 */
const PSD_ADJ_KEYS = new Set([
  "brit", "levl", "curv", "hue2", "hue ", "blnc", "blwh", "phfl", "PhFl",
  "mixr", "nvrt", "post", "thrs", "grdm", "selc", "vibA", "expA", "AdjL",
  "CgEd", // brightness-contrast legacy descriptor, always with brit
]);

/**
 * Returns true if the layer carries no pixel data (adjustment, fill, or a
 * 0×0 section-divider sentinel that uses no-pixel format).  These layers
 * should not be dropped when `decodeLayer` returns null because their
 * descriptors / parameters are still needed (#245).
 */
function psdLayerHasNoPixelData(
  psdLayer: import("./core/io/psd/types").PsdLayerRecord,
): boolean {
  const keys = psdLayer.additionalInfo.map((b) => b.key);
  // Adjustment layer: has a recognised adj key.
  if (keys.some((k) => PSD_ADJ_KEYS.has(k))) return true;
  // Fill layer: has SoCo / GdFl / PtFl descriptor.
  if (keys.some((k) => k === "SoCo" || k === "GdFl" || k === "PtFl")) return true;
  // Section-divider sentinels (group open/close) already handled upstream,
  // but include them here for safety.
  if (keys.some((k) => k === "lsct")) return true;
  return false;
}

/**
 * Read the `iOpa` additional-info block from a PSD layer record.
 * `iOpa` stores fill-opacity (0–255) separately from layer opacity (#245).
 * Returns a 0..1 float, or 1 if the block is absent.
 */
function readIOpacity(
  psdLayer: import("./core/io/psd/types").PsdLayerRecord,
): number {
  const block = psdLayer.additionalInfo.find((b) => b.key === "iOpa");
  if (!block || block.data.length < 1) return 1;
  return (block.data[0] ?? 255) / 255;
}

type LayerNode = RasterLayer | GroupLayer | TypeLayer | AdjustmentLayer | SmartObjectLayer;

/**
 * Build a layer tree from flat decoded layers, using `lsct` section-divider
 * sentinels to detect group boundaries (#73).
 *
 * PSD convention: a layer with name "<Layer group>" and additionalInfo key
 * "lsct" marks group open/close. Type 1/2 = open/closed folder, type 3 =
 * closing sentinel (empty name).
 */
function buildLayerTree(
  decoded: {
    psdLayer: import("./core/io/psd/types").PsdLayerRecord;
    pixels: PixelBuffer;
    maskGray: import("./core/io/psd/decodeChannels").DecodedLayerMask | null;
    id: string;
  }[],
  docWidth: number,
  docHeight: number,
): LayerNode[] {
  // Build with mutable children, then cast to readonly at the end.
  interface MutableGroup {
    id: string;
    kind: "group";
    name: string;
    visible: boolean;
    opacity: number;
    fillOpacity: number;
    blendMode: BlendMode;
    x: number;
    y: number;
    lockTransparency: boolean;
    lockImage: boolean;
    lockPosition: boolean;
    lockAll: boolean;
    clippedToBelow: boolean;
    children: MutableLayer[];
  }
  type MutableLayer = RasterLayer | MutableGroup | TypeLayer | AdjustmentLayer | SmartObjectLayer | SolidFillLayer | GradientFillLayer | PatternFillLayer;

  const root: MutableLayer[] = [];
  const stack: MutableGroup[] = [];

  for (const entry of decoded) {
    const { psdLayer, pixels, maskGray, id } = entry;
    const sectionType = getSectionDividerType(psdLayer);

    if (sectionType === 3) {
      if (stack.length > 0) stack.pop();
      continue;
    }

    if (sectionType === 1 || sectionType === 2) {
      const group: MutableGroup = {
        id,
        kind: "group",
        name: getPsdLayerName(psdLayer) || "Group",
        visible: !(psdLayer.flags & 0x02),
        opacity: psdLayer.opacity / 255,
        fillOpacity: readIOpacity(psdLayer),
        blendMode: psdBlendKeyToOurs(psdLayer.blendModeKey),
        x: 0, y: 0,
        lockTransparency: false,
        lockImage: false,
        lockPosition: false,
        lockAll: false,
        clippedToBelow: psdLayer.clipping === 1,
        children: [],
      };
      if (stack.length === 0) root.push(group);
      else stack[stack.length - 1]!.children.push(group);
      stack.push(group);
      continue;
    }

    // Check for special layer types via additionalInfo blocks.
    // Note: AdjL is not always emitted by Photoshop for legacy adjustment
    // layers; check for known adj keys directly as well (#245).
    const hasTySh = psdLayer.additionalInfo.some(b => b.key === "TySh");
    const hasAdj = psdLayer.additionalInfo.some(b => PSD_ADJ_KEYS.has(b.key));
    const hasSoLd = psdLayer.additionalInfo.some(b => b.key === "soLd" || b.key === "SoLd");
    const hasPlLd = psdLayer.additionalInfo.some(b => b.key === "PlLd");
    const hasSoCo = psdLayer.additionalInfo.some(b => b.key === "SoCo");
    const hasGdFl = psdLayer.additionalInfo.some(b => b.key === "GdFl");
    const hasPtFl = psdLayer.additionalInfo.some(b => b.key === "PtFl");

    let layer: MutableLayer;
    if (hasTySh) {
      layer = makeTypeLayer(psdLayer, id);
    } else if (hasAdj) {
      layer = makeAdjustmentLayer(psdLayer, id);
    } else if (hasSoCo) {
      layer = makeSolidFillLayer(psdLayer, id);
    } else if (hasGdFl) {
      layer = makeGradientFillLayerFromPsd(psdLayer, id);
    } else if (hasPtFl) {
      layer = makePatternFillLayerFromPsd(psdLayer, id);
    } else if (hasSoLd || hasPlLd) {
      layer = makeSmartObjectLayer(psdLayer, pixels, id);
    } else {
      layer = makeRasterLayer(psdLayer, pixels, maskGray, id);
    }

    // Attach vector mask from vmsk / vsms path data (#186).
    const vectorMask = extractVectorMask(psdLayer, docWidth, docHeight);
    if (vectorMask) {
      layer = { ...layer, vectorMask } as typeof layer;
    }

    if (stack.length === 0) root.push(layer);
    else stack[stack.length - 1]!.children.push(layer);
  }

  return root as LayerNode[];
}

/** Extract the lsct section-divider type (1=open, 2=closed, 3=close sentinel). */
function getSectionDividerType(psdLayer: import("./core/io/psd/types").PsdLayerRecord): number | null {
  const lsct = psdLayer.additionalInfo.find((b) => b.key === "lsct");
  if (!lsct || lsct.data.length < 4) return null;
  return lsct.data[3] ?? null;
}

/**
 * Return the Unicode layer name from the `luni` additional-info block when
 * present, falling back to the legacy Pascal-string name.
 *
 * The legacy Pascal-string name field in PSD layer records uses MacRoman
 * encoding, which means non-ASCII characters (e.g. CJK) are stored as single
 * bytes and decoded as Latin-1 by readPascalString(), producing garbled output
 * like "Ôªµ©" for "元旦".  Photoshop always writes a `luni` block alongside
 * any non-ASCII layer name, containing the correct UTF-16BE Unicode text.
 * Prefer that block so CJK (and any other non-Latin) names display correctly.
 *
 * `luni` payload layout: 4-byte UTF-16 char count (big-endian), then that many
 * UTF-16BE code units, optionally followed by a trailing NUL unit.
 */
function getPsdLayerName(psdLayer: import("./core/io/psd/types").PsdLayerRecord): string {
  const luni = psdLayer.additionalInfo.find((b) => b.key === "luni");
  if (luni && luni.data.length >= 4) {
    const numChars =
      ((luni.data[0]! << 24) |
        (luni.data[1]! << 16) |
        (luni.data[2]! << 8) |
        luni.data[3]!) >>> 0;
    if (numChars > 0 && luni.data.length >= 4 + numChars * 2) {
      let s = "";
      for (let i = 0; i < numChars; i++) {
        const code =
          ((luni.data[4 + i * 2]! << 8) | luni.data[4 + i * 2 + 1]!) >>> 0;
        if (code === 0) break; // stop at trailing NUL
        s += String.fromCharCode(code);
      }
      if (s.length > 0) return s;
    }
  }
  return psdLayer.name;
}

/** Build a RasterLayer from a decoded PSD layer record, including mask (#74) and locks (#75). */
function makeRasterLayer(
  psdLayer: import("./core/io/psd/types").PsdLayerRecord,
  pixels: PixelBuffer,
  maskGray: import("./core/io/psd/decodeChannels").DecodedLayerMask | null,
  id: string,
): RasterLayer {
  const lockTransparency = !!(psdLayer.flags & 0x01);
  const lockImage = !!(psdLayer.flags & 0x08);
  const mask = parsePsdMask(psdLayer, maskGray);
  const styles = parsePsdLayerEffects(psdLayer);

  return {
    id,
    kind: "raster",
    name: getPsdLayerName(psdLayer) || "Layer",
    visible: !(psdLayer.flags & 0x02),
    opacity: psdLayer.opacity / 255,
    fillOpacity: readIOpacity(psdLayer),
    blendMode: psdBlendKeyToOurs(psdLayer.blendModeKey),
    x: psdLayer.left,
    y: psdLayer.top,
    lockTransparency,
    lockImage,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: psdLayer.clipping === 1,
    pixels,
    ...(mask ? { mask } : {}),
    ...(styles ? { styles } : {}),
  };
}

/** Build a TypeLayer from a PSD layer with TySh descriptor. */
function makeTypeLayer(
  psdLayer: import("./core/io/psd/types").PsdLayerRecord,
  id: string,
): TypeLayer {
  const styles = parsePsdLayerEffects(psdLayer);

  // Extract text content and style attributes from the TySh/tySh block.
  const tyShBlock = psdLayer.additionalInfo.find(
    (b) => b.key === "TySh" || b.key === "tySh",
  );
  const tyShResult =
    tyShBlock && tyShBlock.data.length > 0
      ? parseTySh(tyShBlock.data)
      : null;

  const text = tyShResult?.text || getPsdLayerName(psdLayer) || "Text";

  // Map parsed style fields to TypeLayer fields, falling back to defaults.
  const fontFamily = tyShResult?.font ?? "Arial";
  // PSD stores font size in points.  Canvas2D also accepts point-equivalent
  // px values directly â€” we store as-is and the renderer uses it as px.
  // At 72 dpi (Photoshop internal resolution), 1 pt â‰ˆ 1 px on screen.
  const fontSize = tyShResult?.fontSize ?? 12;
  const parsedColor = tyShResult?.color;
  const color = parsedColor
    ? { r: parsedColor.r, g: parsedColor.g, b: parsedColor.b, a: 1 }
    : { r: 0, g: 0, b: 0, a: 1 };
  const textAlign = tyShResult?.align ?? "left";
  const fauxBold = tyShResult?.bold ?? false;
  const fauxItalic = tyShResult?.italic ?? false;
  const underline = tyShResult?.underline ?? false;
  const allCaps = tyShResult?.allCaps ?? false;

  return {
    id,
    kind: "type",
    name: getPsdLayerName(psdLayer) || "Type Layer",
    visible: !(psdLayer.flags & 0x02),
    opacity: psdLayer.opacity / 255,
    fillOpacity: readIOpacity(psdLayer),
    blendMode: psdBlendKeyToOurs(psdLayer.blendModeKey),
    x: psdLayer.left,
    y: psdLayer.top,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: psdLayer.clipping === 1,
    text,
    fontFamily,
    fontSize,
    fontWeight: 400,
    fontStyle: "normal",
    textAlign,
    textBaseline: "top",
    color,
    leading: 1.2,
    tracking: 0,
    baselineShift: 0,
    underline,
    strikethrough: false,
    antiAlias: "smooth",
    fauxBold,
    fauxItalic,
    allCaps,
    smallCaps: false,
    superscript: false,
    subscript: false,
    textBounds: null,
    ...(styles ? { styles } : {}),
  };
}

/** Build an AdjustmentLayer from a PSD layer with AdjL descriptor. */
function makeAdjustmentLayer(
  psdLayer: import("./core/io/psd/types").PsdLayerRecord,
  id: string,
): AdjustmentLayer {
  const styles = parsePsdLayerEffects(psdLayer);
  // Detect the real adjustment kind + decode params from sibling info blocks.
  const parsed = parseAdjustmentFromInfo(psdLayer.additionalInfo);
  const adjustment: import("./core/document/Layer").AdjustmentKind =
    parsed?.kind ?? "brightness-contrast";
  const params: Record<string, number | string | boolean> =
    parsed?.params ?? {};
  return {
    id,
    kind: "adjustment",
    name: getPsdLayerName(psdLayer) || "Adjustment",
    visible: !(psdLayer.flags & 0x02),
    opacity: psdLayer.opacity / 255,
    fillOpacity: readIOpacity(psdLayer),
    blendMode: psdBlendKeyToOurs(psdLayer.blendModeKey),
    x: 0,
    y: 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: psdLayer.clipping === 1,
    adjustment,
    params,
    ...(styles ? { styles } : {}),
  };
}

/** Build a SolidFillLayer from a PSD layer with SoCo descriptor. */
function makeSolidFillLayer(
  psdLayer: import("./core/io/psd/types").PsdLayerRecord,
  id: string,
): SolidFillLayer {
  const styles = parsePsdLayerEffects(psdLayer);
  const soCoBlock = psdLayer.additionalInfo.find(b => b.key === "SoCo");
  const parsed = soCoBlock ? parseSoCoDescriptor(soCoBlock.data) : null;
  const color = parsed?.color ?? { r: 0, g: 0, b: 0, a: 1 };
  return {
    id,
    kind: "solid-fill",
    name: getPsdLayerName(psdLayer) || "Color Fill",
    visible: !(psdLayer.flags & 0x02),
    opacity: psdLayer.opacity / 255,
    fillOpacity: readIOpacity(psdLayer),
    blendMode: psdBlendKeyToOurs(psdLayer.blendModeKey),
    x: 0,
    y: 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: psdLayer.clipping === 1,
    color,
    ...(styles ? { styles } : {}),
  };
}

/** Build a GradientFillLayer from a PSD layer with GdFl descriptor. */
function makeGradientFillLayerFromPsd(
  psdLayer: import("./core/io/psd/types").PsdLayerRecord,
  id: string,
): GradientFillLayer {
  const styles = parsePsdLayerEffects(psdLayer);
  const gdFlBlock = psdLayer.additionalInfo.find(b => b.key === "GdFl");
  const parsed = gdFlBlock ? parseGdFlDescriptor(gdFlBlock.data) : null;
  return {
    id,
    kind: "gradient-fill",
    name: getPsdLayerName(psdLayer) || "Gradient Fill",
    visible: !(psdLayer.flags & 0x02),
    opacity: psdLayer.opacity / 255,
    fillOpacity: readIOpacity(psdLayer),
    blendMode: psdBlendKeyToOurs(psdLayer.blendModeKey),
    x: 0,
    y: 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: psdLayer.clipping === 1,
    gradientType: parsed?.gradientType ?? "linear",
    angle: parsed?.angle ?? 90,
    scale: parsed?.scale ?? 100,
    reverse: parsed?.reverse ?? false,
    dither: true,
    stops: parsed?.stops ?? [
      { position: 0, color: { r: 0, g: 0, b: 0, a: 1 } },
      { position: 1, color: { r: 255, g: 255, b: 255, a: 1 } },
    ],
    ...(styles ? { styles } : {}),
  };
}

/** Build a PatternFillLayer from a PSD layer with PtFl descriptor. */
function makePatternFillLayerFromPsd(
  psdLayer: import("./core/io/psd/types").PsdLayerRecord,
  id: string,
): PatternFillLayer {
  const styles = parsePsdLayerEffects(psdLayer);
  const ptFlBlock = psdLayer.additionalInfo.find(b => b.key === "PtFl");
  const parsed = ptFlBlock ? parsePtFlDescriptor(ptFlBlock.data) : null;
  return {
    id,
    kind: "pattern-fill",
    name: getPsdLayerName(psdLayer) || "Pattern Fill",
    visible: !(psdLayer.flags & 0x02),
    opacity: psdLayer.opacity / 255,
    fillOpacity: readIOpacity(psdLayer),
    blendMode: psdBlendKeyToOurs(psdLayer.blendModeKey),
    x: 0,
    y: 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: psdLayer.clipping === 1,
    pattern: null,
    patternName: parsed?.patternName ?? "None",
    scale: parsed?.scale ?? 100,
    linkWithLayer: true,
    ...(styles ? { styles } : {}),
  };
}

/** Build a SmartObjectLayer from a PSD layer with soLd descriptor. */
function makeSmartObjectLayer(
  psdLayer: import("./core/io/psd/types").PsdLayerRecord,
  pixels: PixelBuffer,
  id: string,
): SmartObjectLayer {
  const styles = parsePsdLayerEffects(psdLayer);
  // Create embedded document from the pixel data
  const w = psdLayer.right - psdLayer.left;
  const h = psdLayer.bottom - psdLayer.top;
  const embeddedPixels = new PixelBuffer(w > 0 ? w : 1, h > 0 ? h : 1, "rgba8");
  if (pixels.width > 0 && pixels.height > 0) {
    embeddedPixels.data.set(pixels.data.subarray(0, embeddedPixels.data.length));
  }
  embeddedPixels.markDirty();

  const embedded: Document = {
    name: getPsdLayerName(psdLayer) || "Smart Object",
    width: embeddedPixels.width,
    height: embeddedPixels.height,
    dpi: 72,
    layers: [{
      id: `${id}-embedded`,
      kind: "raster",
      name: "Layer 0",
      visible: true,
      opacity: 1,
      fillOpacity: 1,
      blendMode: "normal",
      x: 0,
      y: 0,
      lockTransparency: false,
      lockImage: false,
      lockPosition: false,
      lockAll: false,
      clippedToBelow: false,
      pixels: embeddedPixels,
    }],
    activeLayerId: `${id}-embedded`,
    selection: null,
  };

  return {
    id,
    kind: "smart-object",
    name: getPsdLayerName(psdLayer) || "Smart Object",
    visible: !(psdLayer.flags & 0x02),
    opacity: psdLayer.opacity / 255,
    fillOpacity: 1,
    blendMode: psdBlendKeyToOurs(psdLayer.blendModeKey),
    x: psdLayer.left,
    y: psdLayer.top,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: psdLayer.clipping === 1,
    embedded,
    transform: { scaleX: 1, scaleY: 1, rotation: 0, skewX: 0, skewY: 0 },
    smartFilters: [],
    ...(styles ? { styles } : {}),
  };
}

/** Parse layer effects from lfx2 descriptor. */
function parsePsdLayerEffects(
  psdLayer: import("./core/io/psd/types").PsdLayerRecord,
): import("./core/document/LayerStyle").LayerStyles | undefined {
  const lfx2 = psdLayer.additionalInfo.find(b => b.key === "lfx2");
  if (!lfx2 || lfx2.data.length < 4) return undefined;
  try {
    return parseLayerEffects(lfx2.data) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Extract a vector mask from a PSD layer's vmsk or vsms additional-info block
 * (#186).  Returns null if no valid path data is present.
 *
 * Priority: `vmsk` (classic vector mask) is checked first; if absent, `vsms`
 * (Vector Stroke Mask Setting â€” used by shape layers in CS6+) is used.
 * Both blocks share the same path-record binary format.
 */
function extractVectorMask(
  psdLayer: import("./core/io/psd/types").PsdLayerRecord,
  docWidth: number,
  docHeight: number,
): import("./core/document/PathModel").PathModel | null {
  const block =
    psdLayer.additionalInfo.find(b => b.key === "vmsk") ??
    psdLayer.additionalInfo.find(b => b.key === "vsms");
  if (!block || block.data.length < 34) return null; // need header + at least 1 record
  try {
    return parseVmsk(block.data, docWidth, docHeight);
  } catch {
    return null;
  }
}

/**
 * Build a LayerMask from PSD layer metadata + decoded mask pixel data (#74).
 *
 * The `maskData` block inside the layer record contains only the mask's
 * bounding rectangle and flags â€” NOT the pixel data.  The pixel data is
 * stored as channel id -2 in the channel image data section and must be
 * decoded separately via `decodeLayerMask` (see decodeChannels.ts).
 *
 * `maskGray` is the pre-decoded grayscale channel from `decodeLayerMask`.
 * When null (layer has no channel -2), this returns null.
 */
function parsePsdMask(
  psdLayer: import("./core/io/psd/types").PsdLayerRecord,
  maskGray: import("./core/io/psd/decodeChannels").DecodedLayerMask | null,
): import("./core/document/Layer").LayerMask | null {
  if (!maskGray) return null;

  const { gray, width: w, height: h, top, left, defaultColor } = maskGray;
  if (w <= 0 || h <= 0) return null;

  // Parse flags from the maskData metadata block.
  // Byte 17 is the flags field: bit 1 (0x02) = mask disabled.
  const data = psdLayer.maskData;
  const flags = data.length >= 18 ? (data[17] ?? 0) : 0;
  const enabled = !(flags & 0x02);

  // Build an RGBA8 PixelBuffer where the alpha channel holds the mask value.
  // Pixels OUTSIDE the mask rect default to `defaultColor` (usually 0 or 255).
  // Pixels inside the rect come from the decoded gray channel.
  const maskPixels = new PixelBuffer(w, h, "rgba8");
  const dst = maskPixels.data;
  for (let i = 0; i < w * h; i++) {
    const alpha = gray[i] ?? defaultColor;
    const di = i * 4;
    dst[di]     = 255;
    dst[di + 1] = 255;
    dst[di + 2] = 255;
    dst[di + 3] = alpha;
  }
  maskPixels.markDirty();

  return {
    pixels: maskPixels,
    // Mask position is in document coordinates.
    // The renderer uses mask.x / mask.y as offsets when drawing the mask,
    // so we express them relative to the layer's own top-left origin.
    x: left - psdLayer.left,
    y: top - psdLayer.top,
    enabled,
    density: 1,
    feather: 0,
    linked: true,
  };
}

async function openFile(file: File): Promise<void> {
  try {
    const lower = file.name.toLowerCase();
    const ext = extOf(lower);
    // Advertised-but-unparseable formats (#119): show a graceful tip instead
    // of letting decodeImage() crash with a "couldn't decode" error.
    if (ext && isPlaceholderExtension(ext)) {
      showError(unimplementedMessage(file.name));
      return;
    }
    if (lower.endsWith(".psd") || lower.endsWith(".psb")) {
      await openPsd(file);
      return;
    }
    // i136: .pxa (this app's own native save format — see the autosave
    // crash-recovery path above, which already reuses the same openPxa()
    // decoder) had no branch here, so double-clicking/dragging a .pxa file
    // silently fell through to the generic decodeImage() path and failed.
    if (lower.endsWith(".pxa")) {
      const { openPxa } = await import("./app/io/pxa");
      const loaded = await openPxa(file);
      // i44: openPxa() doesn't stamp an `id` -- give this opened document a
      // unique one here so it can never collide with another open document
      // of the same name in per-document History or the tab registry.
      const doc: Document = loaded.id ? loaded : { ...loaded, id: crypto.randomUUID() };
      state.execute(new LoadDocumentCommand(doc));
      state.markSaved();
      fitDoc();
      return;
    }
    if (lower.endsWith(".bmp")) {
      const { decodeBmp } = await import("./app/io/decodeImage");
      const { pixels, width, height } = await decodeBmp(file);
      await createDocFromPixels(pixels, width, height, file.name);
      return;
    }
    if (lower.endsWith(".gif")) {
      const { decodeGif } = await import("./app/io/decodeImage");
      const { pixels, width, height } = await decodeGif(file);
      await createDocFromPixels(pixels, width, height, file.name);
      return;
    }
    if (lower.endsWith(".pdf")) {
      const { decodePdf } = await import("./app/io/decodeImage");
      const { pixels, width, height } = await decodePdf(file);
      await createDocFromPixels(pixels, width, height, file.name);
      return;
    }
    if (lower.endsWith(".ico")) {
      const { decodeIco } = await import("./app/io/decodeImage");
      const { pixels, width, height } = await decodeIco(file);
      await createDocFromPixels(pixels, width, height, file.name);
      return;
    }
    if (lower.endsWith(".tiff") || lower.endsWith(".tif")) {
      const { pixels, width, height } = await decodeTiff(file);
      await createDocFromPixels(pixels, width, height, file.name);
      return;
    }
    const { pixels, width, height } = await decodeImage(file);
    const layerName = file.name.replace(/\.[^.]+$/, "") || "Layer 1";
    const layer: RasterLayer = {
      id: crypto.randomUUID(),
      kind: "raster",
      name: layerName,
      visible: true,
      opacity: 1,
      fillOpacity: 1,
      blendMode: "normal",
      x: 0,
      y: 0,
      lockTransparency: false,
      lockImage: false,
      lockPosition: false,
      lockAll: false,
      clippedToBelow: false,
      pixels,
    };
    const doc: Document = {
      // i44: unique per opened file (see openPsd()'s identical rationale).
      id: crypto.randomUUID(),
      name: file.name || "Untitled",
      width,
      height,
      dpi: 72,
      layers: [layer],
      activeLayerId: layer.id,
      selection: null,
    };
    state.execute(new LoadDocumentCommand(doc));
    // i134: opening a plain image also shouldn't leave the doc marked dirty.
    state.markSaved();
    fitDoc();
  } catch (err) {
    showError(`Couldn't open ${file.name}: ${err instanceof Error ? err.message : "unknown error"}`);
  }
}

async function createDocFromPixels(pixels: PixelBuffer, width: number, height: number, fileName: string): Promise<void> {
  const layerName = fileName.replace(/\.[^.]+$/, "") || "Layer 1";
  const layer: RasterLayer = {
    id: crypto.randomUUID(),
    kind: "raster",
    name: layerName,
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal",
    x: 0,
    y: 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: false,
    pixels,
  };
  const doc: Document = {
    // i44: unique per opened file (see openPsd()'s identical rationale).
    id: crypto.randomUUID(),
    name: fileName || "Untitled",
    width,
    height,
    dpi: 72,
    layers: [layer],
    activeLayerId: layer.id,
    selection: null,
  };
  state.execute(new LoadDocumentCommand(doc));
  // i134: same as openPsd()/openFile() — a freshly-decoded image (BMP/GIF/
  // PDF/ICO/TIFF path) shouldn't be marked dirty either.
  state.markSaved();
  fitDoc();
}

const saveAsDialog = new SaveAsDialog();

// ----- Selection tracking (for Reselect) -----
let _previousSelection: Selection | null = null;
// i59: the explicit `_previousSelection = ...` assignments sprinkled through
// the Select-menu functions below only covered menu-driven selection
// changes — a plain Marquee/Lasso drag calls `ctx.emit(new
// SetSelectionCommand(...))` directly from inside the tool class, which
// never touched `_previousSelection` at all, so Reselect after a plain drag
// silently did nothing. Rather than instrument every tool call site (or
// centralize this in EditorState.ts, out of scope here), a generic
// state.subscribe() hook — fired on every command, tool-driven or
// menu-driven alike — tracks whatever the selection was immediately BEFORE
// the one that's now current. This is a superset of the explicit
// assignments below (redundant with them, not a conflict).
let _lastObservedSelectionForReselect: Selection | null = null;
let _lastObservedDocForReselect: Document | null = null;
function trackSelectionForReselect(): void {
  // Skip mid-drag transient updates (a Marquee/Lasso drag emits a fresh
  // live-preview selection on every pointermove via applyTransient(), which
  // also fires subscribers) — only checkpoint once settled, so
  // `_previousSelection` ends up as the selection from BEFORE the whole
  // drag, not "one transient frame before the final commit." The pointerup
  // handler makes one extra unconditional call after the drag settles (see
  // below) to cover click-only tools whose onPointerUp emits no further
  // command (so no subsequent notify would otherwise re-run this).
  if (dragging) return;
  const doc = state.doc ?? null;
  // i59 follow-up: this generic subscribe hook also fires on switchDocument()'s
  // notify(). Without a document-identity check, switching tabs would treat the
  // OUTGOING document's selection as the "previous" selection for the INCOMING
  // document, so a Reselect on the new doc would apply the old doc's Selection
  // (wrong mask, wrong docW/docH). On any document change, silently resync the
  // observed selection to the new doc and bail — Reselect is per-document.
  if (doc !== _lastObservedDocForReselect) {
    _lastObservedDocForReselect = doc;
    _lastObservedSelectionForReselect = doc?.selection ?? null;
    return;
  }
  const current = doc?.selection ?? null;
  if (current !== _lastObservedSelectionForReselect) {
    _previousSelection = _lastObservedSelectionForReselect;
    _lastObservedSelectionForReselect = current;
  }
}
state.subscribe(trackSelectionForReselect);
function selectAll(): void {
  if (!state.doc) return;
  _previousSelection = state.doc.selection;
  state.execute(new SetSelectionCommand(Selection.all(state.doc.width, state.doc.height)));
}
function deselect(): void {
  if (!state.doc) return;
  _previousSelection = state.doc.selection;
  state.execute(new SetSelectionCommand(null));
}
function selectInverse(): void {
  if (!state.doc || !state.doc.selection) return;
  _previousSelection = state.doc.selection;
  state.execute(new SetSelectionCommand(state.doc.selection.invert()));
}
function reselect(): void {
  if (!state.doc || !_previousSelection) return;
  // Defense-in-depth against a stale cross-document selection: a Selection
  // carries the docW/docH it was built for, and applying one sized for a
  // different document would corrupt masking/compositing. Never apply a
  // dimension-mismatched selection.
  if (_previousSelection.docW !== state.doc.width || _previousSelection.docH !== state.doc.height) return;
  state.execute(new SetSelectionCommand(_previousSelection));
}

// ----- Select Modify helpers (dialog-based) -----
async function promptModifySelection(
  label: string,
  defaultVal: number,
  apply: (sel: Selection, px: number) => Selection,
): Promise<void> {
  if (!state.doc) return;
  let sel = state.doc.selection;
  if (!sel) {
    showError("No selection to modify.");
    return;
  }
  const result = await filterDialog.open(label, [
    { key: "radius", label: "Radius", type: "slider", default: defaultVal, min: 0, max: 500, unit: "px" },
  ]);
  if (!result) return;
  const px = result.radius as number;
  if (!isFinite(px) || px < 0) return;
  _previousSelection = sel;
  state.execute(new SetSelectionCommand(apply(sel, px)));
}

async function selectBorder(): Promise<void> {
  promptModifySelection("Border", 10, (sel, px) => sel.border(px));
}
async function selectSmooth(): Promise<void> {
  promptModifySelection("Smooth", 5, (sel, px) => sel.smooth(px));
}
async function selectExpand(): Promise<void> {
  promptModifySelection("Expand", 10, (sel, px) => sel.expand(px));
}
async function selectContract(): Promise<void> {
  promptModifySelection("Contract", 10, (sel, px) => sel.contract(px));
}
async function selectFeather(): Promise<void> {
  promptModifySelection("Feather", 5, (sel, px) => sel.feather(px));
}

async function selectGrow(): Promise<void> {
  if (!state.doc) return;
  let sel = state.doc.selection;
  if (!sel || sel.isEmpty()) { showError("No selection to grow."); return; }
  const layer = getActiveRaster();
  if (!layer) { showError("No active raster layer."); return; }

  const result = await filterDialog.open("Grow", [
    { key: "tolerance", label: "Tolerance", type: "slider", default: 32, min: 0, max: 255 },
  ]);
  if (!result) return;
  const tolerance = result.tolerance as number;

  // Collect seed points from the selection boundary
  const b = sel.bounds()!;
  const data = layer.pixels.data;
  const lw = layer.pixels.width;
  const lh = layer.pixels.height;
  const grownMask = new Uint8Array(lw * lh);

  // For each selected pixel, flood-fill contiguously to similar colors
  // We do this by collecting all unique colors in the selection and
  // flood-filling from each one, then unioning the results.
  // Simpler approach: use the selection mask as a starting region and
  // expand outward using the tolerance metric.
  const expandedMask = new Uint8Array(lw * lh);
  // Copy existing selection
  for (let i = 0; i < sel.mask.length; i++) {
    if (sel.mask[i]! > 0) expandedMask[i] = 255;
  }

  // Find all edge pixels of the selection (selected pixels adjacent to unselected)
  const edgePixels: Array<{ x: number; y: number }> = [];
  for (let y = b.y; y < b.y + b.h; y++) {
    for (let x = b.x; x < b.x + b.w; x++) {
      const idx = y * lw + x;
      if (expandedMask[idx]! > 0) {
        // Check neighbors
        const nx0 = x - 1, nx1 = x + 1, ny0 = y - 1, ny1 = y + 1;
        if ((nx0 >= 0 && expandedMask[y * lw + nx0]! === 0) ||
            (nx1 < lw && expandedMask[y * lw + nx1]! === 0) ||
            (ny0 >= 0 && expandedMask[ny0 * lw + x]! === 0) ||
            (ny1 < lh && expandedMask[ny1 * lw + x]! === 0)) {
          edgePixels.push({ x, y });
        }
      }
    }
  }

  // Flood fill from each edge pixel
  for (const ep of edgePixels) {
    const px = Math.floor(ep.x - layer.x);
    const py = Math.floor(ep.y - layer.y);
    if (px < 0 || py < 0 || px >= lw || py >= lh) continue;
    const fillMask = floodFillFromSeed(data, lw, lh, px, py, {
      tolerance, contiguous: true, antiAlias: false, sampleAllLayers: false,
    });
    for (let i = 0; i < fillMask.length; i++) {
      if (fillMask[i]! > 0) expandedMask[i] = 255;
    }
  }

  // Map layer mask back to document space
  const docMask = new Uint8Array(state.doc.width * state.doc.height);
  for (let ly = 0; ly < lh; ly++) {
    for (let lx = 0; lx < lw; lx++) {
      const dx = lx + layer.x;
      const dy = ly + layer.y;
      if (dx >= 0 && dx < state.doc.width && dy >= 0 && dy < state.doc.height) {
        docMask[dy * state.doc.width + dx] = expandedMask[ly * lw + lx]!;
      }
    }
  }

  _previousSelection = sel;
  state.execute(new SetSelectionCommand(new Selection(state.doc.width, state.doc.height, docMask)));
}

async function selectSimilar(): Promise<void> {
  if (!state.doc) return;
  let sel = state.doc.selection;
  if (!sel || sel.isEmpty()) { showError("No selection to find similar."); return; }
  const layer = getActiveRaster();
  if (!layer) { showError("No active raster layer."); return; }

  const result = await filterDialog.open("Similar", [
    { key: "tolerance", label: "Tolerance", type: "slider", default: 32, min: 0, max: 255 },
  ]);
  if (!result) return;
  const tolerance = result.tolerance as number;

  // Collect representative colors from the selection
  const b = sel.bounds()!;
  const data = layer.pixels.data;
  const lw = layer.pixels.width;
  const lh = layer.pixels.height;
  const colorSet = new Set<string>();
  const colors: Array<{ r: number; g: number; b: number; a: number }> = [];

  for (let y = b.y; y < b.y + b.h; y++) {
    for (let x = b.x; x < b.x + b.w; x++) {
      const idx = y * state.doc!.width + x;
      if (sel.mask[idx]! > 0) {
        const lx = x - layer.x;
        const ly = y - layer.y;
        if (lx < 0 || ly < 0 || lx >= lw || ly >= lh) continue;
        const pi = (ly * lw + lx) * 4;
        const r = data[pi]!, g = data[pi + 1]!, b = data[pi + 2]!, a = data[pi + 3]!;
        const key = `${r},${g},${b},${a}`;
        if (!colorSet.has(key)) {
          colorSet.add(key);
          colors.push({ r, g, b, a });
        }
      }
    }
  }

  // Select all similar pixels non-contiguously
  const similarMask = new Uint8Array(lw * lh);
  for (const c of colors) {
    const mask = selectByColor(data, lw, lh, c.r, c.g, c.b, {
      tolerance, antiAlias: false, sampleAllLayers: false,
    });
    for (let i = 0; i < mask.length; i++) {
      if (mask[i]! > 0) similarMask[i] = 255;
    }
  }

  // Map to doc space
  const docMask = new Uint8Array(state.doc.width * state.doc.height);
  for (let ly = 0; ly < lh; ly++) {
    for (let lx = 0; lx < lw; lx++) {
      const dx = lx + layer.x;
      const dy = ly + layer.y;
      if (dx >= 0 && dx < state.doc.width && dy >= 0 && dy < state.doc.height) {
        docMask[dy * state.doc.width + dx] = similarMask[ly * lw + lx]!;
      }
    }
  }

  _previousSelection = sel;
  state.execute(new SetSelectionCommand(new Selection(state.doc.width, state.doc.height, docMask)));
}

// â”€â”€ Clipboard for Cut/Copy/Paste â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
let _clipboard: { pixels: Uint8ClampedArray; width: number; height: number } | null = null;

function getActiveRaster(): RasterLayer | null {
  const doc = state.doc;
  if (!doc?.activeLayerId) return null;
  return findRaster(doc.layers, doc.activeLayerId);
}

function copySelection(): void {
  const layer = getActiveRaster();
  if (!layer || !state.doc?.selection) return;
  const sel = state.doc.selection;
  const b = sel.bounds();
  if (!b) return;
  const { x, y, w: width, h: height } = b;
  const pixels = new Uint8ClampedArray(width * height * 4);
  const mask = sel.mask;
  const src = layer.pixels.data;
  const srcW = layer.pixels.width;
  for (let dy = 0; dy < height; dy++) {
    for (let dx = 0; dx < width; dx++) {
      const sx = x + dx, sy = y + dy;
      const si = (sy * srcW + sx) * 4;
      const di = (dy * width + dx) * 4;
      const ma = mask ? (mask[sy * srcW + sx] ?? 0) / 255 : 1;
      pixels[di] = src[si]!;
      pixels[di + 1] = src[si + 1]!;
      pixels[di + 2] = src[si + 2]!;
      pixels[di + 3] = Math.round(src[si + 3]! * ma);
    }
  }
  _clipboard = { pixels, width, height };
  // Also write to system clipboard as PNG for cross-app paste
  try {
    const c = document.createElement("canvas");
    c.width = width; c.height = height;
    const ctx = c.getContext("2d");
    if (ctx) {
      const id = ctx.createImageData(width, height);
      id.data.set(pixels);
      ctx.putImageData(id, 0, 0);
      c.toBlob(async (blob) => {
        if (blob) {
          try { await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]); } catch { /* fallback */ }
        }
      });
    }
  } catch { /* ignore */ }
}

function copyMergedSelection(): void {
  const doc = state.doc;
  if (!doc?.selection) return;
  const sel = doc.selection;
  const b = sel.bounds();
  if (!b) return;
  const { x, y, w: width, h: height } = b;
  // Composite all visible layers into a temp canvas
  const c = document.createElement("canvas");
  c.width = doc.width; c.height = doc.height;
  const ctx = c.getContext("2d");
  if (!ctx) return;
  for (const layer of doc.layers) {
    if (!layer.visible) continue;
    // i165: a document's bottom layer may now be kind:"background" (opaque
    // locked Background) rather than "raster" -- include it here too so
    // Copy Merged on a freshly-created document doesn't silently drop its
    // only layer and copy transparent pixels.
    if (layer.kind !== "raster" && layer.kind !== "background") continue;
    const lc = document.createElement("canvas");
    lc.width = layer.pixels.width; lc.height = layer.pixels.height;
    const lctx = lc.getContext("2d");
    if (!lctx) continue;
    const id = lctx.createImageData(layer.pixels.width, layer.pixels.height);
    id.data.set(layer.pixels.data);
    lctx.putImageData(id, 0, 0);
    ctx.globalAlpha = layer.opacity;
    ctx.drawImage(lc, layer.x, layer.y);
  }
  ctx.globalAlpha = 1;
  const imgData = ctx.getImageData(x, y, width, height);
  _clipboard = { pixels: imgData.data, width, height };
}

function cutSelection(): void {
  copySelection();
  clearSelection();
}

function clearSelection(): void {
  const layer = getActiveRaster();
  if (!layer || !state.doc) return;
  const sel = state.doc.selection;
  const w = layer.pixels.width, h = layer.pixels.height;
  const data = layer.pixels.data;
  const mask = sel?.mask;
  for (let i = 0; i < data.length; i += 4) {
    if (mask) {
      const px = (i / 4) % w, py = Math.floor((i / 4) / w);
      if (mask[py * w + px] === 0) continue;
    }
    data[i] = 0; data[i + 1] = 0; data[i + 2] = 0; data[i + 3] = 0;
  }
  layer.pixels.markDirty();
  state.requestRender();
}

/** Compute the top-left doc-space coordinate that centers a pasted layer in the current viewport. */
function pasteCenterXY(layerWidth: number, layerHeight: number): { x: number; y: number } {
  const r = canvas.getBoundingClientRect();
  const vp = state.viewport;
  const docCenterX = (r.width / 2 - vp.panX) / vp.zoom;
  const docCenterY = (r.height / 2 - vp.panY) / vp.zoom;
  return { x: Math.round(docCenterX - layerWidth / 2), y: Math.round(docCenterY - layerHeight / 2) };
}

async function pasteClipboard(): Promise<void> {
  // Try system clipboard first
  try {
    const items = await navigator.clipboard.read();
    for (const item of items) {
      for (const type of item.types) {
        if (type.startsWith("image/")) {
          const blob = await item.getType(type);
          const { pixels, width, height } = await decodeImage(new File([blob], "pasted", { type }));
          // Ctrl+V with no document open creates a new doc sized to the clipboard image (#247).
          if (!state.doc) {
            await createDocFromPixels(pixels, width, height, "Clipboard");
            return;
          }
          const doc = state.doc;
          const n = doc.layers.filter(l => l.kind === "raster").length + 1;
          const pos = pasteCenterXY(width, height);
          const layer: RasterLayer = {
            id: crypto.randomUUID(), kind: "raster", name: "Layer " + n,
            visible: true, opacity: 1, fillOpacity: 1, blendMode: "normal",
            x: pos.x, y: pos.y, lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false, clippedToBelow: false,
            pixels,
          };
          state.execute(new NewLayerCommand(layer));
          state.requestRender();
          return;
        }
      }
    }
  } catch { /* fallback to internal clipboard */ }
  // Internal clipboard
  if (!_clipboard) { showError("Nothing to paste."); return; }
  // Ctrl+V with no document open creates a new doc sized to the clipboard image (#247).
  if (!state.doc) {
    const pixels = new PixelBuffer(_clipboard.width, _clipboard.height, "rgba8");
    pixels.data.set(_clipboard.pixels);
    pixels.markDirty();
    await createDocFromPixels(pixels, _clipboard.width, _clipboard.height, "Clipboard");
    return;
  }
  const doc = state.doc;
  const n = doc.layers.filter(l => l.kind === "raster").length + 1;
  const pixels = new PixelBuffer(_clipboard.width, _clipboard.height, "rgba8");
  pixels.data.set(_clipboard.pixels);
  pixels.markDirty();
  const pos = pasteCenterXY(_clipboard.width, _clipboard.height);
  const layer: RasterLayer = {
    id: crypto.randomUUID(), kind: "raster", name: "Layer " + n,
    visible: true, opacity: 1, fillOpacity: 1, blendMode: "normal",
    x: pos.x, y: pos.y, lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false, clippedToBelow: false,
    pixels,
  };
  state.execute(new NewLayerCommand(layer));
  state.requestRender();
}

// â”€â”€ Fill dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
async function promptFill(): Promise<void> {
  const layer = getActiveRaster();
  if (!layer) return;
  // i89: mirror PaintBucketTool/GradientTool's lock enforcement — Fill was
  // the only paint path that silently ignored a locked layer.
  if (layer.lockAll || layer.lockImage) { showTip("Layer is locked."); return; }
  const doc = state.doc!;
  const selMask = doc.selection?.mask;

  const result = await filterDialog.open("Fill", [
    { key: "contents", label: "Contents", type: "dropdown", default: "foreground", options: [
      { value: "foreground", label: "Foreground Color" },
      { value: "background", label: "Background Color" },
      { value: "white", label: "White" },
      { value: "black", label: "Black" },
      { value: "gray", label: "50% Gray" },
    ]},
    { key: "opacity", label: "Opacity", type: "slider", default: 100, min: 0, max: 100, unit: "%" },
    // i88: Behind/Preserve Transparency are fully expressible from this call
    // site as a mask restriction (below) — no FillCommand.ts change needed.
    // Dissolve/Clear are now offered too: FillCommand.apply()'s fillPixel()
    // now branches on options.blendMode for their real per-pixel math
    // (per-pixel dither threshold for Dissolve; alpha fade-out for Clear —
    // that FillCommand.ts change landed alongside this one), so the selected
    // mode is passed through below rather than being silently dropped.
    { key: "blendMode", label: "Blending", type: "dropdown", default: "normal", options: [
      { value: "normal", label: "Normal" },
      { value: "dissolve", label: "Dissolve" },
      { value: "behind", label: "Behind" },
      { value: "clear", label: "Clear" },
    ]},
    { key: "preserveTransparency", label: "Preserve Transparency", type: "checkbox", default: false },
  ]);
  if (!result) return;

  const contents = result.contents as string;
  let color: { r: number; g: number; b: number; a: number };
  switch (contents) {
    case "foreground": color = { ...state.fgColor, a: 255 }; break;
    case "background": color = { ...state.bgColor, a: 255 }; break;
    case "white": color = { r: 255, g: 255, b: 255, a: 255 }; break;
    case "black": color = { r: 0, g: 0, b: 0, a: 255 }; break;
    case "gray": color = { r: 128, g: 128, b: 128, a: 255 }; break;
    default: color = { ...state.fgColor, a: 255 };
  }

  const blendMode = result.blendMode as "normal" | "dissolve" | "behind" | "clear";
  const preserveTransparency = !!result.preserveTransparency;

  // i88/i89: "Behind" (only paint where the layer is currently transparent)
  // and "Preserve Transparency" (never paint into transparent area) are both
  // expressible as a mask restriction against the layer's own alpha channel,
  // intersected with the selection mask (or full coverage when there's no
  // selection) — no FillCommand.ts change needed for these two.
  let effectiveMask: Uint8Array | undefined = selMask;
  if (blendMode === "behind" || preserveTransparency) {
    const w = layer.pixels.width, h = layer.pixels.height, data = layer.pixels.data;
    const out = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) {
      const base = selMask ? (selMask[i] ?? 0) : 255;
      if (base === 0) continue;
      const dstA = data[i * 4 + 3] ?? 0;
      if (blendMode === "behind" && dstA !== 0) continue;
      if (preserveTransparency && dstA === 0) continue;
      out[i] = base;
    }
    effectiveMask = out;
  }

  const options: FillOptions = {
    source: "color",
    color,
    opacity: (result.opacity as number) / 100,
    // "behind" is already fully realized via the mask restriction above, so
    // FillCommand only ever needs to see "normal" for it (avoids redundantly
    // re-applying its own now-equivalent "behind" branch). "dissolve"/"clear"
    // pass straight through to FillCommand.apply()'s blendMode branch.
    blendMode: blendMode === "behind" ? "normal" : blendMode,
    respectLock: preserveTransparency,
  };

  state.execute(new FillCommand(layer.id, options, effectiveMask));
  state.requestRender();
}

// Photoshop direct-fill shortcuts (no dialog):
//   Alt+Backspace  â†’ Fill with Foreground
//   Ctrl+Backspace â†’ Fill with Background
function fillWithForeground(): void {
  const layer = getActiveRaster();
  if (!layer) return;
  if (layer.lockAll || layer.lockImage) { showTip("Layer is locked."); return; }
  const selMask = state.doc?.selection?.mask;
  const options: FillOptions = {
    source: "color",
    color: { ...state.fgColor, a: 255 },
    opacity: 1,
    blendMode: "normal",
    respectLock: true,
  };
  state.execute(new FillCommand(layer.id, options, selMask));
  state.requestRender();
}

function fillWithBackground(): void {
  const layer = getActiveRaster();
  if (!layer) return;
  if (layer.lockAll || layer.lockImage) { showTip("Layer is locked."); return; }
  const selMask = state.doc?.selection?.mask;
  const options: FillOptions = {
    source: "color",
    color: { ...state.bgColor, a: 255 },
    opacity: 1,
    blendMode: "normal",
    respectLock: true,
  };
  state.execute(new FillCommand(layer.id, options, selMask));
  state.requestRender();
}

// Layer ordering â€” Photoshop shortcuts:
//   Ctrl+]        â†’ Bring Forward (one step up)
//   Ctrl+Shift+]  â†’ Bring to Front
//   Ctrl+[        â†’ Send Backward (one step down)
//   Ctrl+Shift+[  â†’ Send to Back
function reorderActiveLayer(direction: "front" | "forward" | "backward" | "back"): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layerId = doc.activeLayerId;
  const list = doc.layers;
  const currentIdx = list.findIndex((l) => l.id === layerId);
  if (currentIdx === -1) {
    // Nested in a group; root-only reorder for simplicity.
    return;
  }
  let target: number;
  switch (direction) {
    case "front":     target = list.length - 1; break;
    case "back":      target = 0; break;
    case "forward":   target = Math.min(list.length - 1, currentIdx + 1); break;
    case "backward":  target = Math.max(0, currentIdx - 1); break;
  }
  if (target === currentIdx) return;
  state.execute(new ReorderLayerCommand(layerId, null, target));
  state.requestRender();
}

// â”€â”€ Stroke dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
async function promptStroke(): Promise<void> {
  const layer = getActiveRaster();
  if (!layer || !state.doc?.selection) { showError("No selection to stroke."); return; }
  const selMask = state.doc.selection.mask;

  const result = await filterDialog.open("Stroke", [
    // i186: Width cap raised to 250 (was 100), a real color swatch (was
    // hardcoded to the foreground color), plus Blend Mode + Preserve
    // Transparency to match Photoshop's Stroke dialog.
    { key: "width", label: "Width", type: "slider", default: 3, min: 1, max: 250, unit: "px" },
    { key: "color", label: "Color", type: "color", default: `#${rgbToHex(state.fgColor.r, state.fgColor.g, state.fgColor.b)}` },
    { key: "opacity", label: "Opacity", type: "slider", default: 100, min: 0, max: 100, unit: "%" },
    { key: "position", label: "Position", type: "dropdown", default: "center", options: [
      { value: "inside", label: "Inside" },
      { value: "center", label: "Center" },
      { value: "outside", label: "Outside" },
    ]},
    { key: "blendMode", label: "Mode", type: "dropdown", default: "normal", options: [
      { value: "normal", label: "Normal" },
      { value: "multiply", label: "Multiply" },
      { value: "screen", label: "Screen" },
      { value: "overlay", label: "Overlay" },
      { value: "darken", label: "Darken" },
      { value: "lighten", label: "Lighten" },
      { value: "behind", label: "Behind" },
      { value: "clear", label: "Clear" },
    ]},
    { key: "preserve", label: "Preserve Transparency", type: "checkbox", default: false },
  ]);
  if (!result) return;

  const rgb = hexToRgb(result.color as string) ?? state.fgColor;
  const options: StrokeOptions = {
    width: result.width as number,
    color: { ...rgb, a: 255 },
    opacity: (result.opacity as number) / 100,
    position: result.position as "inside" | "center" | "outside",
    blendMode: result.blendMode as NonNullable<StrokeOptions["blendMode"]>,
    preserveTransparency: result.preserve as boolean,
  };

  state.execute(new StrokeCommand(layer.id, options, selMask));
  state.requestRender();
}

// â”€â”€ Image Size dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
async function promptImageSize(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;

  const origAspect = doc.width / doc.height;

  const result = await filterDialog.open({
    title: "Image Size",
    params: [
      { key: "width", label: "Width", type: "number", default: doc.width, min: 1, max: 100000, unit: "px" },
      { key: "height", label: "Height", type: "number", default: doc.height, min: 1, max: 100000, unit: "px" },
      { key: "constrain", label: "Constrain Proportions", type: "checkbox", default: true },
      { key: "method", label: "Resample", type: "dropdown", default: "bilinear", options: [
        { value: "nearest", label: "Nearest Neighbor" },
        { value: "bilinear", label: "Bilinear" },
        { value: "bicubic", label: "Bicubic" },
      ]},
    ],
    onReady: (el, params) => {
      const widthInput = el.querySelector<HTMLInputElement>('[data-param="width"]');
      const heightInput = el.querySelector<HTMLInputElement>('[data-param="height"]');
      const constrainInput = el.querySelector<HTMLInputElement>('[data-param="constrain"]');
      if (!widthInput || !heightInput || !constrainInput) return;

      const syncFromWidth = () => {
        if (!constrainInput.checked) return;
        const w = parseFloat(widthInput.value);
        if (isFinite(w) && w > 0) {
          heightInput.value = String(Math.round(w / origAspect));
        }
      };
      const syncFromHeight = () => {
        if (!constrainInput.checked) return;
        const h = parseFloat(heightInput.value);
        if (isFinite(h) && h > 0) {
          widthInput.value = String(Math.round(h * origAspect));
        }
      };

      widthInput.addEventListener("input", syncFromWidth);
      heightInput.addEventListener("input", syncFromHeight);
      constrainInput.addEventListener("change", () => {
        if (constrainInput.checked) syncFromWidth();
      });
    },
  });
  if (!result) return;

  const options: ResizeOptions = {
    width: Math.round(result.width as number),
    height: Math.round(result.height as number),
    method: result.method as "nearest" | "bilinear" | "bicubic",
    constrainProportions: result.constrain as boolean,
  };

  state.execute(new ResizeImageCommand(options));
  fitDoc();
  state.requestRender();
}

// â”€â”€ Canvas Size dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
async function promptCanvasSize(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;

  const result = await filterDialog.open("Canvas Size", [
    { key: "width", label: "Width", type: "number", default: doc.width, min: 1, max: 100000, unit: "px" },
    { key: "height", label: "Height", type: "number", default: doc.height, min: 1, max: 100000, unit: "px" },
    { key: "anchor", label: "Anchor", type: "dropdown", default: "center", options: [
      { value: "top-left", label: "â†– Top Left" },
      { value: "top", label: "â†‘ Top" },
      { value: "top-right", label: "â†— Top Right" },
      { value: "left", label: "â† Left" },
      { value: "center", label: "â— Center" },
      { value: "right", label: "â†’ Right" },
      { value: "bottom-left", label: "â†™ Bottom Left" },
      { value: "bottom", label: "â†“ Bottom" },
      { value: "bottom-right", label: "â†˜ Bottom Right" },
    ]},
  ]);
  if (!result) return;

  const options: CanvasSizeOptions = {
    width: Math.round(result.width as number),
    height: Math.round(result.height as number),
    anchor: result.anchor as CanvasSizeOptions["anchor"],
  };

  state.execute(new ResizeCanvasCommand(options));
  fitDoc();
  state.requestRender();
}

// â”€â”€ Image Rotation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function rotateImage(mode: "90-cw" | "90-ccw" | "180" | "flip-h" | "flip-v"): void {
  const doc = state.doc;
  if (!doc) return;
  state.execute(new RotateImageCommand({ mode }));
  fitDoc();
  state.requestRender();
}

// â”€â”€ Edit > Transform: layer-scoped flip/rotate (audit i24) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Distinct from rotateImage() above (Image > Image Rotation, whole document
// + every layer): this transforms only the active layer, or every selected
// layer when 2+ are selected in the Layers panel (Batch 9 multi-select
// convention — see MoveLayersCommand), in one atomic undo step.
function transformLayerOrientationIds(): string[] {
  const doc = state.doc;
  if (!doc) return [];
  const ids = new Set<string>();
  if (doc.activeLayerId) ids.add(doc.activeLayerId);
  for (const id of state.selectedLayerIds) ids.add(id);
  return [...ids].filter((id) => findRaster(doc.layers, id) !== null);
}

function transformLayerOrientation(op: OrientationOp): void {
  const doc = state.doc;
  if (!doc) return;
  const ids = transformLayerOrientationIds();
  if (ids.length === 0) { showError("No active raster layer to transform."); return; }
  state.execute(new TransformLayerOrientationCommand(ids, op));
  lastTransform = { kind: "layer-orientation", op }; // audit i25: Transform Again target
  state.requestRender();
}

async function promptArbitraryRotation(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;

  const result = await filterDialog.open("Rotate Canvas", [
    { key: "angle", label: "Angle", type: "slider", default: 0, min: -180, max: 180, step: 0.1, unit: "Â°" },
  ]);
  if (!result) return;

  state.execute(new RotateImageCommand({ mode: "arbitrary", angle: result.angle as number }));
  fitDoc();
  state.requestRender();
}

function trimImage(): void {
  const doc = state.doc;
  if (!doc) return;
  // Find the bounding box of all non-transparent pixels across all layers
  let minX = doc.width, minY = doc.height, maxX = 0, maxY = 0;
  let found = false;
  function walkLayers(layers: readonly import("./core/document/Layer").Layer[]): void {
    for (const l of layers) {
      if (l.kind === "group") { walkLayers(l.children); continue; }
      if (!l.visible) continue;
      if (l.kind !== "raster") continue;
      const px = l.pixels.data;
      const w = l.pixels.width, h = l.pixels.height;
      const lx = Math.round(l.x), ly = Math.round(l.y);
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const a = px[(y * w + x) * 4 + 3];
          if (a && a > 0) {
            const gx = lx + x, gy = ly + y;
            if (gx < minX) minX = gx;
            if (gy < minY) minY = gy;
            if (gx > maxX) maxX = gx;
            if (gy > maxY) maxY = gy;
            found = true;
          }
        }
      }
    }
  }
  walkLayers(doc.layers);
  if (!found) { showTip("No content to trim to."); return; }
  const newW = maxX - minX + 1, newH = maxY - minY + 1;
  if (newW === doc.width && newH === doc.height) { showTip("Already trimmed."); return; }
  // #181/#199: TrimRevealCommand atomically resizes canvas AND shifts layers,
  // so Ctrl+Z restores both canvas size and layer positions.
  state.execute(new TrimRevealCommand(newW, newH, -minX, -minY));
  fitDoc();
  state.requestRender();
  showTip("Image trimmed.");
}

function revealAll(): void {
  const doc = state.doc;
  if (!doc) return;
  // Find the bounding box of all layer content
  let minX = 0, minY = 0, maxX = doc.width, maxY = doc.height;
  function walkLayers(layers: readonly import("./core/document/Layer").Layer[]): void {
    for (const l of layers) {
      if (l.kind === "group") { walkLayers(l.children); continue; }
      if (l.kind !== "raster") continue;
      const w = l.pixels.width, h = l.pixels.height;
      const lx = Math.round(l.x), ly = Math.round(l.y);
      if (lx < minX) minX = lx;
      if (ly < minY) minY = ly;
      if (lx + w > maxX) maxX = lx + w;
      if (ly + h > maxY) maxY = ly + h;
    }
  }
  walkLayers(doc.layers);
  if (minX === 0 && minY === 0 && maxX === doc.width && maxY === doc.height) {
    showTip("All content already visible.");
    return;
  }
  const pad = 10;
  const newW = maxX - minX + pad * 2, newH = maxY - minY + pad * 2;
  // #181/#199: TrimRevealCommand atomically resizes canvas AND shifts layers
  // so Ctrl+Z restores both canvas size and layer positions.
  const dx = -minX + pad, dy = -minY + pad;
  state.execute(new TrimRevealCommand(newW, newH, dx, dy));
  fitDoc();
  state.requestRender();
  showTip("All content revealed.");
}

async function showSaveAs(): Promise<void> {
  if (!state.doc) return;
  const result = await saveAsDialog.open(state.doc);
  if (!result) return;

  try {
    if (result.format === "psd") {
      const bytes = await encodePsd(state.doc, {
        guides,
        ...(currentIccProfile ? { iccProfile: currentIccProfile } : {}),
      });
      const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "application/x-psd" });
      downloadBlob(blob, result.filename);
    } else if (result.format === "bmp") {
      const blob = await exportBmp(state.doc);
      downloadBlob(blob, result.filename);
    } else if (result.format === "gif") {
      const blob = await exportGif(state.doc);
      downloadBlob(blob, result.filename);
    } else if (result.format === "ico") {
      const blob = await exportIco(state.doc);
      downloadBlob(blob, result.filename);
    } else if (result.format === "pdf") {
      const blob = await exportPdf(state.doc);
      downloadBlob(blob, result.filename);
    } else if (result.format === "tiff") {
      // i217: SaveAsDialog already offers "tiff" as a SaveFormat (dialog-only
      // pass) but this switch had no case for it, so choosing TIFF silently
      // fell through to the png/jpeg/webp exportDocument() path below and
      // downloaded a PNG mislabeled ".tiff". Reuse the same composite-to-
      // canvas + encodeTiff() logic exportAs() already uses for its own
      // "tiff" branch.
      const tiffCanvas = document.createElement("canvas");
      tiffCanvas.width = state.doc.width;
      tiffCanvas.height = state.doc.height;
      const tiffCtx = tiffCanvas.getContext("2d");
      if (!tiffCtx) throw new Error("2D context unavailable");
      for (const layer of state.doc.layers) {
        drawLayerToCanvasForExport(tiffCtx, layer);
      }
      const imageData = tiffCtx.getImageData(0, 0, state.doc.width, state.doc.height);
      const pixels = new PixelBuffer(state.doc.width, state.doc.height, "rgba8");
      pixels.data.set(imageData.data);
      const blob = encodeTiff(pixels);
      downloadBlob(blob, result.filename);
    } else {
      const formatMap: Record<string, "image/png" | "image/jpeg" | "image/webp"> = {
        png: "image/png",
        jpeg: "image/jpeg",
        webp: "image/webp",
      };
      const mime = formatMap[result.format] ?? "image/png";
      const blob = await exportDocument(state.doc, mime, result.quality);
      downloadBlob(blob, result.filename);
    }
    state.setSavedPath(result.filename);
    state.setSavedFormat(result.format);
    state.markSaved();
  } catch (err) {
    showError(`Save failed: ${err instanceof Error ? err.message : "unknown error"}`);
  }
}

async function savePng(): Promise<void> {
  if (!state.doc) return;
  try {
    const blob = await exportDocument(state.doc, "image/png");
    const base = state.doc.name.replace(/\.[^.]+$/, "") || "Untitled";
    downloadBlob(blob, `${base}.png`);
    state.markSaved();
  } catch (err) {
    showError(`Save failed: ${err instanceof Error ? err.message : "unknown error"}`);
  }
}

async function savePngSilent(): Promise<void> {
  if (!state.doc) return;
  try {
    const fmt = state.savedFormat ?? "png";
    const savedPath = state.savedPath;
    const base = savedPath || state.doc.name.replace(/\.[^.]+$/, "") || "Untitled";

    if (fmt === "psd") {
      const bytes = await encodePsd(state.doc, {
        guides,
        ...(currentIccProfile ? { iccProfile: currentIccProfile } : {}),
      });
      const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "application/x-psd" });
      const filename = base.endsWith(".psd") ? base : `${base}.psd`;
      downloadBlob(blob, filename);
    } else if (fmt === "bmp") {
      const blob = await exportBmp(state.doc);
      downloadBlob(blob, base.endsWith(".bmp") ? base : `${base}.bmp`);
    } else if (fmt === "gif") {
      const blob = await exportGif(state.doc);
      downloadBlob(blob, base.endsWith(".gif") ? base : `${base}.gif`);
    } else if (fmt === "ico") {
      const blob = await exportIco(state.doc);
      downloadBlob(blob, base.endsWith(".ico") ? base : `${base}.ico`);
    } else if (fmt === "pdf") {
      const blob = await exportPdf(state.doc);
      downloadBlob(blob, base.endsWith(".pdf") ? base : `${base}.pdf`);
    } else if (fmt === "tiff") {
      // i217 follow-up: a silent Ctrl+S re-save after showSaveAs() saved as
      // TIFF must also route through encodeTiff(), or it would silently
      // re-save a PNG under the original .tiff filename.
      const tiffCanvas = document.createElement("canvas");
      tiffCanvas.width = state.doc.width;
      tiffCanvas.height = state.doc.height;
      const tiffCtx = tiffCanvas.getContext("2d");
      if (!tiffCtx) throw new Error("2D context unavailable");
      for (const layer of state.doc.layers) {
        drawLayerToCanvasForExport(tiffCtx, layer);
      }
      const imageData = tiffCtx.getImageData(0, 0, state.doc.width, state.doc.height);
      const pixels = new PixelBuffer(state.doc.width, state.doc.height, "rgba8");
      pixels.data.set(imageData.data);
      const blob = encodeTiff(pixels);
      downloadBlob(blob, base.endsWith(".tiff") ? base : `${base}.tiff`);
    } else {
      const mimeMap: Record<string, "image/png" | "image/jpeg" | "image/webp"> = {
        png: "image/png", jpeg: "image/jpeg", webp: "image/webp",
      };
      const mime = mimeMap[fmt] ?? "image/png";
      const ext = fmt === "jpeg" ? "jpg" : (fmt || "png");
      const blob = await exportDocument(state.doc, mime);
      const filename = savedPath ?? `${base}.${ext}`;
      downloadBlob(blob, filename);
    }
    state.markSaved();
  } catch (err) {
    showError(`Save failed: ${err instanceof Error ? err.message : "unknown error"}`);
  }
}

function showSavePrompt(docName: string): Promise<"save" | "dontSave" | "cancel"> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "filter-dialog";
    overlay.innerHTML = `
      <div class="filter-dialog__sheet">
        <div class="filter-dialog__title">Save changes to "${escapeHtml(docName)}"?</div>
        <div class="filter-dialog__body" style="padding:12px 0">
          <p style="color:var(--text-dim);font-size:12px;margin:0">Your changes will be lost if you don't save them.</p>
        </div>
        <div class="filter-dialog__actions">
          <button class="filter-dialog__btn" data-action="cancel">Cancel</button>
          <button class="filter-dialog__btn" data-action="dontSave">Don't Save</button>
          <button class="filter-dialog__btn filter-dialog__btn--primary" data-action="save">Save</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    overlay.querySelector('[data-action="save"]')?.addEventListener("click", () => { overlay.remove(); resolve("save"); });
    overlay.querySelector('[data-action="dontSave"]')?.addEventListener("click", () => { overlay.remove(); resolve("dontSave"); });
    overlay.querySelector('[data-action="cancel"]')?.addEventListener("click", () => { overlay.remove(); resolve("cancel"); });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { overlay.remove(); document.removeEventListener("keydown", onKey); resolve("cancel"); }
      if (e.key === "Enter") { overlay.remove(); document.removeEventListener("keydown", onKey); resolve("save"); }
    };
    document.addEventListener("keydown", onKey);
  });
}

async function closeDoc(): Promise<void> {
  if (state.doc && state.dirty) {
    const name = state.doc.name;
    const answer = await showSavePrompt(name);
    if (answer === "save") {
      // Re-save in the original format (PSD stays PSD, PNG stays PNG, etc.)
      // If no savedPath/format yet, fall back to opening Save As.
      if (state.savedPath && state.savedFormat) {
        await savePngSilent();
      } else {
        await showSaveAs();
      }
    } else if (answer === "cancel") {
      return;
    }
    // "dontSave" falls through.
  }
  state.setDocument(null);
}

function zoomCentered(factor: number): void {
  modZoomCentered(state, factor, canvas);
}

function fitDoc(): void {
  modFitDoc(state, viewportEl);
}

function resetTo100(): void {
  modResetTo100(state, viewportEl);
}

// i111: the status-bar zoom control (#zoom-control) had ready-made
// accessibility markup (role="button", tabindex="0", the #status-zoom
// readout cursor.ts already keeps in sync) but no listener at all. Clicking
// or activating it (Enter/Space, since it's a <div role="button">, not a
// real <button>) now prompts for a percentage and applies it via the same
// absolute-zoom + re-center-on-document convention resetTo100()/zoomTo200()
// already use, clamped to Photoshop's 1%–32000% zoom range (matches
// fitDoc()'s ZOOM_MIN/ZOOM_MAX in src/app/render.ts).
function setZoomPercent(pct: number): void {
  if (!state.doc) return;
  const zoom = Math.max(0.01, Math.min(320, pct / 100));
  const r = viewportEl.getBoundingClientRect();
  state.setViewport({
    zoom,
    panX: (r.width - state.doc.width * zoom) / 2,
    panY: (r.height - state.doc.height * zoom) / 2,
  });
}
function promptZoomPercent(): void {
  if (!state.doc) return;
  const current = Math.round(state.viewport.zoom * 100);
  const input = prompt("Zoom (%):", String(current));
  if (input === null) return;
  const pct = parseFloat(input);
  if (!Number.isFinite(pct) || pct <= 0) return;
  setZoomPercent(pct);
}
const zoomControlEl = document.getElementById("zoom-control");
zoomControlEl?.addEventListener("click", promptZoomPercent);
zoomControlEl?.addEventListener("keydown", (e) => {
  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); promptZoomPercent(); }
});

// Status bar
function updateStatusBar(): void {
  modUpdateStatusBar({ state, renderer, canvas, handTool, zoomTool, eyedropperTool, brushTool, eraserTool, quickSelectionTool, healingBrushTool, spotHealingTool, moveTool });
}

function updateDocTab(): void {
  modUpdateDocTab(state, closeDoc);
}

function updateSwatches(): void {
  modUpdateSwatches({ state, renderer, canvas, handTool, zoomTool, eyedropperTool, brushTool, eraserTool, quickSelectionTool, healingBrushTool, spotHealingTool, moveTool });
}

function updateDropHint(): void {
  modUpdateDropHint({ state, renderer, canvas, handTool, zoomTool, eyedropperTool, brushTool, eraserTool, quickSelectionTool, healingBrushTool, spotHealingTool, moveTool });
}

function updateCursor(): void {
  modUpdateCursor({
    state, renderer, canvas, handTool, zoomTool, eyedropperTool, brushTool, eraserTool, quickSelectionTool, healingBrushTool, spotHealingTool, moveTool,
    // i201/i61: live Alt-key / pan-drag / pointer-in-selection signals — see
    // liveAltKey/livePointerInSelection declarations above.
    interaction: {
      altKey: liveAltKey,
      isPanning: dragging && state.activeTool === handTool,
      pointerInSelection: livePointerInSelection,
    },
  });
}

// â”€â”€ Filter preview helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

/** Extract a centered sampleSizeÃ—sampleSize region from layer pixels for preview. */
function extractPreviewSample(data: Uint8ClampedArray, w: number, h: number, sampleSize: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(sampleSize * sampleSize * 4);
  const srcX = Math.max(0, Math.floor((w - sampleSize) / 2));
  const srcY = Math.max(0, Math.floor((h - sampleSize) / 2));
  const copyW = Math.min(sampleSize, w - srcX);
  const copyH = Math.min(sampleSize, h - srcY);

  for (let dy = 0; dy < copyH; dy++) {
    const srcOff = ((srcY + dy) * w + srcX) * 4;
    const dstOff = dy * sampleSize * 4;
    out.set(data.subarray(srcOff, srcOff + copyW * 4), dstOff);
  }
  return out;
}

/** Simple separable Gaussian blur applied in-place to RGBA pixel data. */
function gaussianBlurInPlace(data: Uint8ClampedArray, w: number, h: number, radius: number): void {
  if (radius < 0.5) return;
  const size = Math.ceil(radius * 2) * 2 + 1;
  const kernel = new Float32Array(size);
  const sigma = radius / 2;
  let sum = 0;
  for (let i = 0; i < size; i++) {
    const x = i - Math.floor(size / 2);
    kernel[i] = Math.exp(-(x * x) / (2 * sigma * sigma));
    sum += kernel[i]!;
  }
  for (let i = 0; i < size; i++) kernel[i] = kernel[i]! / sum;

  const tmp = new Float32Array(Math.max(w, h) * 4);

  // Horizontal pass
  for (let y = 0; y < h; y++) {
    for (let c = 0; c < 4; c++) {
      for (let x = 0; x < w; x++) {
        let val = 0;
        for (let k = 0; k < size; k++) {
          const sx = Math.min(w - 1, Math.max(0, x + k - Math.floor(size / 2)));
          val += data[(y * w + sx) * 4 + c]! * kernel[k]!;
        }
        tmp[x * 4 + c] = val;
      }
      for (let x = 0; x < w; x++) {
        data[(y * w + x) * 4 + c] = Math.round(tmp[x * 4 + c]!);
      }
    }
  }

  // Vertical pass
  for (let x = 0; x < w; x++) {
    for (let c = 0; c < 4; c++) {
      for (let y = 0; y < h; y++) {
        let val = 0;
        for (let k = 0; k < size; k++) {
          const sy = Math.min(h - 1, Math.max(0, y + k - Math.floor(size / 2)));
          val += data[(sy * w + x) * 4 + c]! * kernel[k]!;
        }
        tmp[y * 4 + c] = val;
      }
      for (let y = 0; y < h; y++) {
        data[(y * w + x) * 4 + c] = Math.round(tmp[y * 4 + c]!);
      }
    }
  }
}

// â”€â”€ File menu helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function getRecentFiles(): string[] {
  return modGetRecentFiles();
}

function addRecentFile(path: string): void {
  modAddRecentFile(path);
}

function showRecentFilesMenu(anchor: HTMLElement, files: string[]): void {
  interface MenuItem {
    label: string;
    shortcut?: string;
    disabled?: boolean;
    action?: () => void;
    separator?: boolean;
  }
  const items: MenuItem[] = files.map((f) => ({
    label: f.split("/").pop() || f,
    shortcut: f.length > 50 ? "â€¦" + f.slice(-47) : f,
    action: () => {
      if (f.startsWith("http://") || f.startsWith("https://")) {
        openFromURLWithFile(f);
      } else {
        const input = document.createElement("input");
        input.type = "file";
        input.style.display = "none";
        document.body.appendChild(input);
        input.addEventListener("change", () => {
          const file = input.files?.[0];
          if (file) openFile(file);
          input.remove();
        });
        input.click();
      }
    },
  }));
  items.push({ separator: true, label: "" });
  items.push({ label: "Clear Recent", action: () => { localStorage.removeItem("pixelpa-recent-files"); showTip("Recent files cleared."); } });
  showMenuDropdown(anchor, items);
}

async function openFromURLWithFile(url: string): Promise<void> {
  try {
    showTip("Loading image from URL...");
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const file = new File([blob], url.split("/").pop() || "url-image", { type: blob.type || "image/png" });
    await openFile(file);
  } catch (err) {
    showError(`Failed to load: ${err instanceof Error ? err.message : "unknown error"}`);
  }
}

async function closeAllDocs(): Promise<void> {
  await closeDoc();
}

async function revertDocument(): Promise<void> {
  if (!state.doc || !state.savedPath) return;
  const fileInput = document.createElement("input");
  fileInput.type = "file";
  fileInput.accept = FILE_INPUT_ACCEPT;
  fileInput.style.display = "none";
  document.body.appendChild(fileInput);
  fileInput.addEventListener("change", () => {
    const f = fileInput.files?.[0];
    if (f) openFile(f);
    fileInput.remove();
  });
  fileInput.click();
}

async function quickExportPng(): Promise<void> {
  if (!state.doc) return;
  try {
    const blob = await exportDocument(state.doc, "image/png");
    const base = state.doc.name.replace(/\.[^.]+$/, "") || "Untitled";
    downloadBlob(blob, `${base}.png`);
  } catch (err) {
    showError(`Export failed: ${err instanceof Error ? err.message : "unknown error"}`);
  }
}

/** i218/#179: build a genuinely SCALED export document — composite the source
 *  doc to a native-size canvas, then resample it (drawImage) into a canvas at
 *  the target size, and wrap the result as a single raster layer. The earlier
 *  approach only relabeled width/height on a shallow copy, leaving layer pixels
 *  at native size — so any scale != 100% cropped (<100%) or corner-pinned-with-
 *  padding (>100%) the artwork in every encoder. Resampling here fixes all
 *  formats at once. Vector layers are flattened into the raster (acceptable for
 *  a scaled export). */
function buildScaledExportDoc(src: Document, scale: number): Document {
  const native = document.createElement("canvas");
  native.width = src.width;
  native.height = src.height;
  const nctx = native.getContext("2d");
  if (!nctx) throw new Error("2D context unavailable");
  for (const layer of src.layers) drawLayerToCanvasForExport(nctx, layer);
  const sw = Math.max(1, Math.round(src.width * scale));
  const sh = Math.max(1, Math.round(src.height * scale));
  const scaled = document.createElement("canvas");
  scaled.width = sw;
  scaled.height = sh;
  const sctx = scaled.getContext("2d");
  if (!sctx) throw new Error("2D context unavailable");
  sctx.imageSmoothingEnabled = true;
  sctx.imageSmoothingQuality = "high";
  sctx.drawImage(native, 0, 0, src.width, src.height, 0, 0, sw, sh);
  const imageData = sctx.getImageData(0, 0, sw, sh);
  const pixels = new PixelBuffer(sw, sh, "rgba8");
  pixels.data.set(imageData.data);
  const layer: RasterLayer = {
    kind: "raster", id: crypto.randomUUID(), name: src.name, visible: true,
    opacity: 1, fillOpacity: 1, blendMode: "normal", x: 0, y: 0,
    lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false,
    clippedToBelow: false, pixels,
  };
  return { ...src, width: sw, height: sh, layers: [layer], activeLayerId: layer.id };
}

async function exportAs(): Promise<void> {
  if (!state.doc) return;
  const result = await filterDialog.open("Export As", [
    { key: "format", label: "Format", type: "dropdown", default: "png", options: [
      { value: "png", label: "PNG" },
      { value: "jpeg", label: "JPEG" },
      { value: "webp", label: "WebP" },
      { value: "bmp", label: "BMP" },
      { value: "gif", label: "GIF" },
      { value: "ico", label: "ICO" },
      { value: "tiff", label: "TIFF" },
      { value: "svg", label: "SVG" },
      { value: "pdf", label: "PDF" },
    ]},
    { key: "quality", label: "Quality", type: "slider", default: 92, min: 1, max: 100, unit: "%" },
    // i218: mirror saveForWeb()'s Scale slider (same scaledDoc-substitution
    // pattern, applied uniformly across every format branch below).
    { key: "scale", label: "Scale", type: "slider", default: 100, min: 10, max: 200, unit: "%" },
    // Metadata/sRGB toggles: this exporter's encoders write raw pixels via
    // canvas.toBlob()/hand-rolled encoders and never embed EXIF/ICC data in
    // the first place, so there's nothing for these to strip/embed yet — they
    // exist so the dialog matches Photoshop's Export As layout; wiring them to
    // real ICC-profile embedding / EXIF stripping is tracked as a follow-up.
    { key: "metadata", label: "Include Metadata", type: "checkbox", default: true },
    { key: "srgb", label: "Convert to sRGB", type: "checkbox", default: true },
  ]);
  if (!result) return;
  const format = result.format as string;
  const quality = (result.quality as number) / 100;
  const scale = (result.scale as number) / 100;
  try {
    // i218: resample the artwork to the chosen Scale (not just relabel the
    // dimensions) so every format branch exports a correctly-scaled image.
    const doc = scale !== 1 ? buildScaledExportDoc(state.doc, scale) : state.doc;
    let blob: Blob;
    if (format === "bmp") {
      blob = await exportBmp(doc);
    } else if (format === "gif") {
      blob = await exportGif(doc);
    } else if (format === "ico") {
      blob = await exportIco(doc);
    } else if (format === "tiff") {
      // Composite doc to pixel buffer, then encode TIFF
      const canvas = document.createElement("canvas");
      canvas.width = doc.width;
      canvas.height = doc.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("2D context unavailable");
      for (const layer of doc.layers) {
        drawLayerToCanvasForExport(ctx, layer);
      }
      const imageData = ctx.getImageData(0, 0, doc.width, doc.height);
      const pixels = new PixelBuffer(doc.width, doc.height, "rgba8");
      pixels.data.set(imageData.data);
      blob = encodeTiff(pixels);
    } else if (format === "svg") {
      const svgStr = await exportSvg(doc);
      blob = new Blob([svgStr], { type: "image/svg+xml" });
    } else if (format === "pdf") {
      blob = await exportPdf(doc);
    } else {
      const mimeMap: Record<string, "image/png" | "image/jpeg" | "image/webp"> = { png: "image/png", jpeg: "image/jpeg", webp: "image/webp" };
      blob = await exportDocument(doc, mimeMap[format] || "image/png", quality);
    }
    const ext = format === "jpeg" ? "jpg" : format;
    const base = state.doc.name.replace(/\.[^.]+$/, "") || "Untitled";
    downloadBlob(blob, `${base}.${ext}`);
  } catch (err) {
    showError(`Export failed: ${err instanceof Error ? err.message : "unknown error"}`);
  }
}

// #179: Save for Web (Legacy) dialog â€” wraps SaveForWeb module options + canvas export.
async function saveForWeb(): Promise<void> {
  if (!state.doc) return;
  const { DEFAULT_SAVE_FOR_WEB, formatBytes, estimateFileSize } = await import("./core/io/SaveForWeb");
  const docWidth = state.doc.width;
  const docHeight = state.doc.height;
  const result = await filterDialog.open({
    title: "Save for Web (Legacy)",
    params: [
      { key: "format", label: "Format", type: "dropdown", default: DEFAULT_SAVE_FOR_WEB.format,
        options: [
          { value: "jpeg",   label: "JPEG"   },
          { value: "png-24", label: "PNG-24" },
          { value: "png-8",  label: "PNG-8"  },
          { value: "gif",    label: "GIF"    },
          { value: "webp",   label: "WebP"   },
        ]},
      { key: "quality", label: "Quality", type: "slider", default: DEFAULT_SAVE_FOR_WEB.quality, min: 1, max: 100, unit: "%" },
      { key: "imageScale", label: "Scale", type: "slider", default: 100, min: 10, max: 200, unit: "%" },
    ],
    // i219: previously the ONLY file-size feedback was a showTip() fired
    // after the real export already ran (downloadBlob() included). This adds
    // a live "Estimated Size" readout, using SaveForWeb's own
    // estimateFileSize()/formatBytes() heuristics (the same ones the rest of
    // the module already relies on) so the number updates as the user
    // changes Format/Quality/Scale, before ever exporting.
    onReady: (el) => {
      const fieldsEl = el.querySelector(".filter-dialog__fields");
      if (!fieldsEl) return;
      const sizeRow = document.createElement("div");
      sizeRow.className = "filter-dialog__row";
      sizeRow.innerHTML =
        '<label class="filter-dialog__label">Estimated Size</label>' +
        '<div class="filter-dialog__slider-row">' +
        '<span class="save-for-web-size-estimate" style="font-variant-numeric:tabular-nums"></span>' +
        "</div>";
      fieldsEl.appendChild(sizeRow);
      const estimateEl = sizeRow.querySelector<HTMLElement>(".save-for-web-size-estimate");
      const formatSelect = el.querySelector<HTMLSelectElement>('[data-param="format"]');
      const qualityNum = el.querySelector<HTMLInputElement>('[data-param="quality"]');
      const qualitySlider = el.querySelector<HTMLInputElement>('[data-param-slider="quality"]');
      const scaleNum = el.querySelector<HTMLInputElement>('[data-param="imageScale"]');
      const scaleSlider = el.querySelector<HTMLInputElement>('[data-param-slider="imageScale"]');
      const updateEstimate = () => {
        if (!estimateEl) return;
        const fmt = formatSelect?.value ?? DEFAULT_SAVE_FOR_WEB.format;
        const q = parseFloat(qualityNum?.value ?? String(DEFAULT_SAVE_FOR_WEB.quality));
        const scalePct = parseFloat(scaleNum?.value ?? "100");
        const scale = (isFinite(scalePct) ? scalePct : 100) / 100;
        const w = Math.max(1, Math.round(docWidth * scale));
        const h = Math.max(1, Math.round(docHeight * scale));
        const bytes = estimateFileSize(w, h, fmt, isFinite(q) ? q : DEFAULT_SAVE_FOR_WEB.quality);
        estimateEl.textContent = formatBytes(bytes);
      };
      formatSelect?.addEventListener("change", updateEstimate);
      qualityNum?.addEventListener("input", updateEstimate);
      qualitySlider?.addEventListener("input", updateEstimate);
      scaleNum?.addEventListener("input", updateEstimate);
      scaleSlider?.addEventListener("input", updateEstimate);
      updateEstimate();
    },
  });
  if (!result) return;
  const format = result.format as string;
  const quality = (result.quality as number) / 100;
  const scale = (result.imageScale as number) / 100;
  try {
    const mimeMap: Record<string, string> = {
      "jpeg": "image/jpeg", "png-24": "image/png", "png-8": "image/png",
      "gif": "image/png", "webp": "image/webp",
    };
    let blob: Blob;
    const swDoc = scale !== 1 ? buildScaledExportDoc(state.doc, scale) : state.doc;
    if (format === "gif") {
      blob = await exportGif(swDoc);
    } else {
      const mime = (mimeMap[format] ?? "image/png") as "image/png" | "image/jpeg" | "image/webp";
      blob = await exportDocument(swDoc, mime, quality);
    }
    const extMap: Record<string, string> = { "jpeg": "jpg", "png-24": "png", "png-8": "png", "gif": "gif", "webp": "webp" };
    const ext = extMap[format] ?? "png";
    const base = state.doc.name.replace(/\.[^.]+$/, "") || "Untitled";
    showTip(`Save for Web: ${formatBytes(blob.size)}`);
    downloadBlob(blob, `${base}.${ext}`);
  } catch (err) {
    showError(`Save for Web failed: ${err instanceof Error ? err.message : "unknown error"}`);
  }
}

/** Decode an image file and add it as a new, centered raster layer in the
 *  current document (Photoshop's drag-and-drop-onto-a-canvas / Place
 *  Embedded convention). Shared by placeEmbedded() and the i135 viewport
 *  drop handler below. */
async function placeImageAsLayer(f: File): Promise<void> {
  if (!state.doc) return;
  const { pixels, width, height } = await decodeImage(f);
  const n = state.doc.layers.filter(l => l.kind === "raster").length + 1;
  const layer: RasterLayer = {
    id: crypto.randomUUID(), kind: "raster", name: f.name.replace(/\.[^.]+$/, "") || `Layer ${n}`,
    visible: true, opacity: 1, fillOpacity: 1, blendMode: "normal",
    x: Math.round((state.doc.width - width) / 2), y: Math.round((state.doc.height - height) / 2),
    lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false, clippedToBelow: false,
    pixels,
  };
  state.execute(new NewLayerCommand(layer));
  state.requestRender();
}

async function placeEmbedded(): Promise<void> {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = FILE_INPUT_ACCEPT;
  input.style.display = "none";
  document.body.appendChild(input);
  input.addEventListener("change", async () => {
    const f = input.files?.[0];
    if (!f || !state.doc) { input.remove(); return; }
    try {
      await placeImageAsLayer(f);
    } catch (err) {
      showError(`Place failed: ${err instanceof Error ? err.message : "unknown error"}`);
    }
    input.remove();
  });
  input.click();
}

async function openFromURL(): Promise<void> {
  const url = prompt("Enter image URL:");
  if (!url) return;

  try {
    showTip("Loading image from URL...");
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const file = new File([blob], "url-image", { type: blob.type || "image/png" });
    await openFile(file);
    addRecentFile(url);
  } catch (err) {
    showError(`Failed to load from URL: ${err instanceof Error ? err.message : "unknown error"}`);
  }
}

async function openAsSmartObject(): Promise<void> {
  const doc = state.doc;
  if (!doc) { showError("No document open."); return; }

  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/png,image/jpeg,image/webp,image/avif,image/gif,image/bmp,.psd";
  input.style.display = "none";
  document.body.appendChild(input);
  input.addEventListener("change", async () => {
    const f = input.files?.[0];
    if (!f) { input.remove(); return; }
    try {
      const { pixels, width, height } = await decodeImage(f);
      // Create embedded document
      const bgLayer: RasterLayer = {
        id: crypto.randomUUID(), kind: "raster", name: "Background",
        visible: true, opacity: 1, fillOpacity: 1, blendMode: "normal",
        x: 0, y: 0, lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false, clippedToBelow: false,
        pixels,
      };
      const embedded: Document = {
        name: f.name,
        width, height, dpi: 72,
        layers: [bgLayer],
        activeLayerId: bgLayer.id,
        selection: null,
      };
      // Create Smart Object layer
      const n = doc.layers.filter(l => l.kind === "raster" || l.kind === "smart-object").length + 1;
      const soLayer: SmartObjectLayer = {
        id: crypto.randomUUID(), kind: "smart-object", name: f.name.replace(/\.[^.]+$/, "") || `Smart Object ${n}`,
        visible: true, opacity: 1, fillOpacity: 1, blendMode: "normal",
        x: Math.round((doc.width - width) / 2), y: Math.round((doc.height - height) / 2),
        lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false, clippedToBelow: false,
        embedded,
        transform: { scaleX: 1, scaleY: 1, rotation: 0, skewX: 0, skewY: 0 },
        smartFilters: [],
      };
      state.execute(new NewLayerCommand(soLayer));
      state.setActiveLayer(soLayer.id);
      state.requestRender();
    } catch (err) {
      showError(`Open as Smart Object failed: ${err instanceof Error ? err.message : "unknown error"}`);
    }
    input.remove();
  });
  input.click();
}

// â”€â”€ Edit menu helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

let _clipboardPositioned: { pixels: Uint8ClampedArray; width: number; height: number; x: number; y: number } | null = null;

function pasteInPlace(): void {
  const doc = state.doc;
  if (!_clipboard || !doc) { showError("Nothing to paste."); return; }
  const n = doc.layers.filter(l => l.kind === "raster").length + 1;
  const pixels = new PixelBuffer(_clipboard.width, _clipboard.height, "rgba8");
  pixels.data.set(_clipboard.pixels);
  pixels.markDirty();
  const layer: RasterLayer = {
    id: crypto.randomUUID(), kind: "raster", name: `Layer ${n}`,
    visible: true, opacity: 1, fillOpacity: 1, blendMode: "normal",
    x: 0, y: 0, lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false, clippedToBelow: false,
    pixels,
  };
  state.execute(new NewLayerCommand(layer));
  state.requestRender();
}

function pasteInto(): void {
  const doc = state.doc;
  if (!_clipboard || !doc || !doc.selection) { showError("Nothing to paste or no selection."); return; }
  const layer = getActiveRaster();
  if (!layer) return;
  const sel = doc.selection;
  const b = sel.bounds();
  if (!b) return;

  const n = doc.layers.filter(l => l.kind === "raster").length + 1;
  const pixels = new PixelBuffer(_clipboard.width, _clipboard.height, "rgba8");
  pixels.data.set(_clipboard.pixels);
  pixels.markDirty();
  const newLayer: RasterLayer = {
    id: crypto.randomUUID(), kind: "raster", name: `Layer ${n}`,
    visible: true, opacity: 1, fillOpacity: 1, blendMode: "normal",
    x: b.x, y: b.y, lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false, clippedToBelow: false,
    pixels,
    mask: { pixels: new PixelBuffer(sel.docW, sel.docH, "rgba8"), x: 0, y: 0, enabled: true, density: 1, feather: 0, linked: true },
  };
  state.execute(new NewLayerCommand(newLayer));
  state.requestRender();
}

function defineBrushPreset(): void {
  const doc = state.doc;
  if (!doc) return;
  const layer = getActiveRaster();
  if (!layer) { showError("No active raster layer."); return; }

  const sel = doc.selection;
  if (!sel || sel.isEmpty()) {
    // Use entire layer if no selection
    const w = layer.pixels.width;
    const h = layer.pixels.height;
    if (w <= 0 || h <= 0) { showError("Empty layer."); return; }
    const pixels = new Uint8ClampedArray(layer.pixels.data);
    const name = `Brush ${state.brushPresets.size + 1}`;
    state.addBrushPreset(name, pixels, w, h, Math.floor(w / 2), Math.floor(h / 2));
    showTip(`Brush preset "${name}" defined from layer.`);
    return;
  }

  const b = sel.bounds();
  if (!b) { showError("Empty selection."); return; }

  // Extract selected region
  const { x, y, w, h } = b;
  const pixels = new Uint8ClampedArray(w * h * 4);
  const mask = sel.mask;
  const src = layer.pixels.data;
  const srcW = layer.pixels.width;
  for (let dy = 0; dy < h; dy++) {
    for (let dx = 0; dx < w; dx++) {
      const sx = x + dx, sy = y + dy;
      const si = (sy * srcW + sx) * 4;
      const di = (dy * w + dx) * 4;
      const ma = mask ? (mask[sy * srcW + sx] ?? 0) / 255 : 1;
      pixels[di] = src[si]!;
      pixels[di + 1] = src[si + 1]!;
      pixels[di + 2] = src[si + 2]!;
      pixels[di + 3] = Math.round(src[si + 3]! * ma);
    }
  }

  const name = `Brush ${state.brushPresets.size + 1}`;
  state.addBrushPreset(name, pixels, w, h, Math.floor(w / 2), Math.floor(h / 2));
  showTip(`Brush preset "${name}" defined from selection.`);
}

function definePattern(): void {
  const doc = state.doc;
  if (!doc) return;
  const layer = getActiveRaster();
  if (!layer) { showError("No active raster layer."); return; }

  const sel = doc.selection;
  if (!sel || sel.isEmpty()) {
    showError("No selection â€” pattern requires a selection.");
    return;
  }

  const b = sel.bounds();
  if (!b) { showError("Empty selection."); return; }

  const { x, y, w, h } = b;
  const pixels = new Uint8ClampedArray(w * h * 4);
  const mask = sel.mask;
  const src = layer.pixels.data;
  const srcW = layer.pixels.width;
  for (let dy = 0; dy < h; dy++) {
    for (let dx = 0; dx < w; dx++) {
      const sx = x + dx, sy = y + dy;
      const si = (sy * srcW + sx) * 4;
      const di = (dy * w + dx) * 4;
      const ma = mask ? (mask[sy * srcW + sx] ?? 0) / 255 : 1;
      pixels[di] = src[si]!;
      pixels[di + 1] = src[si + 1]!;
      pixels[di + 2] = src[si + 2]!;
      pixels[di + 3] = Math.round(src[si + 3]! * ma);
    }
  }

  const id = crypto.randomUUID();
  const name = `Pattern ${state.patternPresets.size + 1}`;
  state.addPatternPreset(id, name, pixels, w, h);
  showTip(`Pattern "${name}" (${w}Ã—${h}) defined from selection.`);
}

// â”€â”€ Transform Selection mode â€” uses FreeTransformTool mechanics on the selection mask â”€â”€
let _transformSelState: {
  originalSel: Selection;
  scaleX: number;
  scaleY: number;
  rotation: number;
  translateX: number;
  translateY: number;
} | null = null;

// Drag state for transform-selection handle interactions.
type TSHandle = "tl" | "tc" | "tr" | "ml" | "mr" | "bl" | "bc" | "br" | "move" | "rotate";
let _tsDrag: {
  handle: TSHandle;
  startDocX: number;
  startDocY: number;
  startScaleX: number;
  startScaleY: number;
  startRotation: number;
  startTranslateX: number;
  startTranslateY: number;
  origBounds: { x: number; y: number; w: number; h: number };
} | null = null;

/**
 * Hit-test a point (doc coords) against the 8 scale handles + centre of the
 * current transform-selection bbox. Returns the handle name or null.
 * Handle size in doc-space is 8px / zoom so it stays a consistent screen size.
 */
function hitTestTSHandle(docX: number, docY: number): TSHandle | null {
  const ts = _transformSelState;
  if (!ts) return null;
  const b = ts.originalSel.bounds();
  if (!b) return null;

  const cx = b.x + b.w / 2 + ts.translateX;
  const cy = b.y + b.h / 2 + ts.translateY;
  const hw = b.w / 2 * ts.scaleX;
  const hh = b.h / 2 * ts.scaleY;

  const HIT = Math.max(8 / state.viewport.zoom, 4);

  // 8 handles
  const handles: { id: TSHandle; x: number; y: number }[] = [
    { id: "tl", x: cx - hw, y: cy - hh },
    { id: "tc", x: cx,      y: cy - hh },
    { id: "tr", x: cx + hw, y: cy - hh },
    { id: "ml", x: cx - hw, y: cy      },
    { id: "mr", x: cx + hw, y: cy      },
    { id: "bl", x: cx - hw, y: cy + hh },
    { id: "bc", x: cx,      y: cy + hh },
    { id: "br", x: cx + hw, y: cy + hh },
  ];

  for (const h of handles) {
    if (Math.abs(docX - h.x) <= HIT && Math.abs(docY - h.y) <= HIT) return h.id;
  }

  // Outside-handle rotate zone: slightly outside the bbox corners.
  const ROT = HIT * 2.5;
  if (
    docX >= cx - hw - ROT && docX <= cx + hw + ROT &&
    docY >= cy - hh - ROT && docY <= cy + hh + ROT &&
    (docX < cx - hw - HIT || docX > cx + hw + HIT ||
     docY < cy - hh - HIT || docY > cy + hh + HIT)
  ) {
    return "rotate";
  }

  // Inside bbox: move
  if (docX >= cx - hw && docX <= cx + hw && docY >= cy - hh && docY <= cy + hh) {
    return "move";
  }
  return null;
}

/** Update _transformSelState and the overlay after a drag step. */
function applyTSDrag(docX: number, docY: number): void {
  const d = _tsDrag;
  const ts = _transformSelState;
  if (!d || !ts) return;

  const dx = docX - d.startDocX;
  const dy = docY - d.startDocY;
  const b = d.origBounds;

  if (d.handle === "move") {
    ts.translateX = d.startTranslateX + dx;
    ts.translateY = d.startTranslateY + dy;
  } else if (d.handle === "rotate") {
    const cx = b.x + b.w / 2 + d.startTranslateX;
    const cy = b.y + b.h / 2 + d.startTranslateY;
    const ang0 = Math.atan2(d.startDocY - cy, d.startDocX - cx);
    const ang1 = Math.atan2(docY - cy, docX - cx);
    ts.rotation = d.startRotation + (ang1 - ang0);
  } else {
    // Scale handle: pivot is opposite corner/edge.
    const scaleHandles: Record<string, { sx: boolean; sy: boolean }> = {
      tl: { sx: true, sy: true },
      tc: { sx: false, sy: true },
      tr: { sx: true, sy: true },
      ml: { sx: true, sy: false },
      mr: { sx: true, sy: false },
      bl: { sx: true, sy: true },
      bc: { sx: false, sy: true },
      br: { sx: true, sy: true },
    };
    const sh = scaleHandles[d.handle];
    if (!sh) return;
    const origW = b.w * d.startScaleX;
    const origH = b.h * d.startScaleY;
    if (sh.sx && origW !== 0) {
      const sign = (d.handle === "tl" || d.handle === "ml" || d.handle === "bl") ? -1 : 1;
      const newW = origW + dx * sign;
      ts.scaleX = (newW / b.w) * Math.sign(ts.scaleX || 1);
      if (Math.abs(ts.scaleX) < 0.01) ts.scaleX = 0.01 * Math.sign(ts.scaleX || 1);
    }
    if (sh.sy && origH !== 0) {
      const sign = (d.handle === "tl" || d.handle === "tc" || d.handle === "tr") ? -1 : 1;
      const newH = origH + dy * sign;
      ts.scaleY = (newH / b.h) * Math.sign(ts.scaleY || 1);
      if (Math.abs(ts.scaleY) < 0.01) ts.scaleY = 0.01 * Math.sign(ts.scaleY || 1);
    }
  }

  // Update overlay bbox to show current transformed bounds.
  const b2 = ts.originalSel.bounds();
  if (b2) {
    const cx2 = b2.x + b2.w / 2 + ts.translateX;
    const cy2 = b2.y + b2.h / 2 + ts.translateY;
    const hw2 = b2.w / 2 * ts.scaleX;
    const hh2 = b2.h / 2 * ts.scaleY;
    renderer.setOverlay({
      cropRect: null,
      transformBBox: { x: cx2 - hw2, y: cy2 - hh2, w: hw2 * 2, h: hh2 * 2 },
      grid: showGrid ? { visible: true, spacing: gridSpacing, majorEvery: gridMajorEvery, color: gridColor, majorColor: gridMajorColor } : null,
      guides: (showGuides && guides.length > 0) ? guides : null,
    });
  }
}

function transformSelection(): void {
  const doc = state.doc;
  if (!doc || !doc.selection || doc.selection.isEmpty()) { showError("No selection to transform."); return; }
  if (_transformSelState) { showError("Transform Selection already active. Press Enter to commit or Escape to cancel."); return; }

  _transformSelState = {
    originalSel: doc.selection.clone(),
    scaleX: 1,
    scaleY: 1,
    rotation: 0,
    translateX: 0,
    translateY: 0,
  };

  // Show transform bbox overlay
  const bbox = doc.selection.bounds();
  if (bbox) {
    renderer.setOverlay({
      cropRect: null,
      transformBBox: { x: bbox.x, y: bbox.y, w: bbox.w, h: bbox.h },
      grid: showGrid ? { visible: true, spacing: gridSpacing, majorEvery: gridMajorEvery, color: gridColor, majorColor: gridMajorColor } : null,
      guides: (showGuides && guides.length > 0) ? guides : null,
    });
  }

  showTip("Transform Selection: drag inside bbox to move. Press Enter to commit, Escape to cancel.");
}

function commitTransformSelection(): void {
  if (!_transformSelState || !state.doc) return;
  const ts = _transformSelState;
  const sel = ts.originalSel;
  const doc = state.doc;

  // Apply transform to the selection mask
  const transformed = transformSelectionMask(sel, ts.scaleX, ts.scaleY, ts.rotation, ts.translateX, ts.translateY);
  _previousSelection = doc.selection;
  state.execute(new SetSelectionCommand(transformed));
  _transformSelState = null;
  renderer.setOverlay({
    cropRect: null,
    transformBBox: null,
    grid: showGrid ? { visible: true, spacing: gridSpacing, majorEvery: gridMajorEvery, color: gridColor, majorColor: gridMajorColor } : null,
    guides: (showGuides && guides.length > 0) ? guides : null,
  });
}

function cancelTransformSelection(): void {
  if (!_transformSelState || !state.doc) return;
  _previousSelection = state.doc.selection;
  state.execute(new SetSelectionCommand(_transformSelState.originalSel));
  _transformSelState = null;
  renderer.setOverlay({
    cropRect: null,
    transformBBox: null,
    grid: showGrid ? { visible: true, spacing: gridSpacing, majorEvery: gridMajorEvery, color: gridColor, majorColor: gridMajorColor } : null,
    guides: (showGuides && guides.length > 0) ? guides : null,
  });
}

function transformSelectionMask(
  sel: Selection,
  scaleX: number,
  scaleY: number,
  rotation: number,
  translateX: number,
  translateY: number,
): Selection {
  const w = sel.docW;
  const h = sel.docH;
  const out = new Uint8Array(w * h);

  // Compute center of selection bounds
  const b = sel.bounds();
  if (!b) return Selection.empty(w, h);
  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;

  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  // For each output pixel, inverse-transform to find the source pixel
  for (let oy = 0; oy < h; oy++) {
    for (let ox = 0; ox < w; ox++) {
      // Translate to center
      let dx = ox - cx - translateX;
      let dy = oy - cy - translateY;
      // Rotate inverse
      let rx = dx * cos + dy * sin;
      let ry = -dx * sin + dy * cos;
      // Scale inverse
      rx = rx / scaleX;
      ry = ry / scaleY;
      // Translate back
      const sx = Math.round(rx + cx);
      const sy = Math.round(ry + cy);

      if (sx >= 0 && sx < w && sy >= 0 && sy < h) {
        out[oy * w + ox] = sel.mask[sy * w + sx] ?? 0;
      }
    }
  }

  return new Selection(w, h, out);
}

// Wire Enter/Escape for Transform Selection mode
const _origKeyHandler = document.addEventListener;
document.addEventListener("keydown", (e) => {
  if (_transformSelState) {
    if (e.key === "Enter") {
      e.preventDefault();
      commitTransformSelection();
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      cancelTransformSelection();
      return;
    }
  }
});

function purgeClipboard(): void {
  _clipboard = null;
  _clipboardPositioned = null;
  showTip("Clipboard purged.");
}

async function promptPurge(): Promise<void> {
  const result = await filterDialog.open("Purge", [
    { key: "target", label: "Purge", type: "dropdown", default: "clipboard", options: [
      { value: "clipboard", label: "Clipboard" },
      { value: "histories", label: "Histories" },
      { value: "all", label: "All" },
    ]},
  ]);
  if (!result) return;

  const target = result.target as string;
  if (target === "clipboard" || target === "all") {
    _clipboard = null;
    _clipboardPositioned = null;
  }
  if (target === "histories" || target === "all") {
    // Clear history by resetting â€” this is a destructive operation
    state.history.clear();
    showTip("History cleared.");
  }
  showTip(`Purged: ${target}.`);
  state.requestRender();
}

// â”€â”€ Preferences â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// #148: Photoshop-style 17-category Preferences dialog. The schema and
// persistence live in src/app/ui/PreferencesDialog.ts. Side-effects
// (history-state cap, legacy CSS variables) applied here.
const preferencesDialog = new PreferencesDialog();
async function promptPreferences(): Promise<void> {
  const result = await preferencesDialog.open();
  if (!result) return;

  const root = document.documentElement;
  if (result.theme === "light") {
    root.style.setProperty("--bg-primary", "#ffffff");
    root.style.setProperty("--bg-secondary", "#f0f0f0");
    root.style.setProperty("--text-primary", "#333333");
    root.style.setProperty("--text-secondary", "#666666");
  } else {
    root.style.setProperty("--bg-primary", "");
    root.style.setProperty("--bg-secondary", "");
    root.style.setProperty("--text-primary", "");
    root.style.setProperty("--text-secondary", "");
  }

  // i148: apply the History-States cap to EVERY open document's history (and all
  // future ones), not just the active one — `state.history.setMaxStates(...)`
  // only touched the currently-active document's History instance.
  state.setMaxHistoryStates(result.historyStates);
  showTip("Preferences updated.");
}

// â”€â”€ Color Settings dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
async function promptColorSettings(): Promise<void> {
  const result = await filterDialog.open("Color Settings", [
    { key: "rgbSpace", label: "RGB Working Space", type: "dropdown", default: "sRGB", options: [
      { value: "sRGB", label: "sRGB IEC61966-2.1" },
      { value: "AdobeRGB", label: "Adobe RGB (1998)" },
      { value: "ProPhoto", label: "ProPhoto RGB" },
      { value: "DisplayP3", label: "Display P3" },
    ]},
    { key: "cmykSpace", label: "CMYK Working Space", type: "dropdown", default: "SWOP", options: [
      { value: "SWOP", label: "U.S. Web Coated (SWOP) v2" },
      { value: "Fogra39", label: "Coated FOGRA39" },
      { value: "Japan", label: "Japan Color 2001 Coated" },
    ]},
    { key: "graySpace", label: "Gray Working Space", type: "dropdown", default: "DotGain20", options: [
      { value: "DotGain20", label: "Dot Gain 20%" },
      { value: "DotGain15", label: "Dot Gain 15%" },
      { value: "GrayGamma22", label: "Gray Gamma 2.2" },
    ]},
    { key: "rgbPolicy", label: "RGB Policy", type: "dropdown", default: "preserve", options: [
      { value: "preserve", label: "Preserve Embedded Profiles" },
      { value: "convert", label: "Convert to Working RGB" },
      { value: "ask", label: "Ask When Opening" },
    ]},
    { key: "cmykPolicy", label: "CMYK Policy", type: "dropdown", default: "preserve", options: [
      { value: "preserve", label: "Preserve Embedded Profiles" },
      { value: "convert", label: "Convert to Working CMYK" },
      { value: "ask", label: "Ask When Opening" },
    ]},
    { key: "mismatchAsk", label: "Profile Mismatch: Ask When Opening", type: "checkbox", default: true },
    { key: "missingAsk", label: "Missing Profiles: Ask When Opening", type: "checkbox", default: true },
  ]);
  if (!result) return;

  // Store color settings (stub â€” actual color management would require a CMS)
  showTip(`Color settings updated. RGB: ${result.rgbSpace}, CMYK: ${result.cmykSpace}`);
}

// â”€â”€ Keyboard Shortcuts editor â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
import { ShortcutsEditor, getDefaultShortcuts, saveShortcutOverrides, loadShortcutOverrides, type ShortcutEntry } from "./app/ui/ShortcutsEditor";

const shortcutsEditor = new ShortcutsEditor();

async function openShortcutsEditor(): Promise<void> {
  const saved = await shortcutsEditor.open();
  if (saved) {
    // Save overrides to localStorage
    const overrides = shortcutsEditor.getOverrides?.() ?? new Map();
    saveShortcutOverrides(overrides);
    showTip("Keyboard shortcuts saved.");
  }
}

async function findAndReplaceText(): Promise<void> {
  if (!state.doc) return;
  const typeLayers = state.doc.layers.filter((l) => l.kind === "type") as TypeLayer[];
  if (typeLayers.length === 0) {
    showError("No type layers in this document.");
    return;
  }
  const result = await filterDialog.open("Find and Replace Text", [
    { key: "findText", label: "Find", type: "slider", default: "", min: 0, max: 1, unit: "" },
    { key: "replaceText", label: "Replace", type: "slider", default: "", min: 0, max: 1, unit: "" },
    { key: "caseSensitive", label: "Case Sensitive", type: "checkbox", default: false },
    { key: "allLayers", label: "All Type Layers", type: "checkbox", default: true },
  ]);
  if (!result) return;

  // Use a custom prompt since FilterDialog doesn't support text inputs well
  const findText = prompt("Find text:");
  if (findText === null) return;
  const replaceText = prompt("Replace with:", "");
  if (replaceText === null) return;
  const caseSensitive = confirm("Case sensitive?");

  let count = 0;
  const targets = caseSensitive ? typeLayers : typeLayers;
  for (const layer of targets) {
    const flags = caseSensitive ? "g" : "gi";
    const escaped = findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escaped, flags);
    if (regex.test(layer.text)) {
      const newText = layer.text.replace(regex, replaceText);
      state.execute(new SetLayerPropertyCommand(layer.id, { text: newText }));
      count++;
    }
  }
  showTip(`Replaced in ${count} type layer(s).`);
}

async function checkSpelling(): Promise<void> {
  if (!state.doc) return;
  const typeLayers = state.doc.layers.filter((l) => l.kind === "type") as TypeLayer[];
  if (typeLayers.length === 0) {
    showError("No type layers in this document.");
    return;
  }

  // Collect all words from type layers
  const words: Array<{ word: string; layerId: string; layerName: string }> = [];
  for (const layer of typeLayers) {
    const matches = layer.text.match(/[a-zA-Z]+/g);
    if (matches) {
      for (const word of matches) {
        words.push({ word, layerId: layer.id, layerName: layer.name });
      }
    }
  }

  if (words.length === 0) {
    showTip("No text to check.");
    return;
  }

  // Use browser's built-in spell check via a hidden textarea
  const textarea = document.createElement("textarea");
  textarea.setAttribute("spellcheck", "true");
  textarea.style.cssText = "position:fixed;left:-9999px;top:-9999px;";
  textarea.value = words.map((w) => w.word).join("\n");
  document.body.appendChild(textarea);

  // Build a simple dialog showing unique words
  const uniqueWords = [...new Set(words.map((w) => w.word.toLowerCase()))];
  const overlay = document.createElement("div");
  overlay.className = "filter-dialog";
  overlay.innerHTML = `
    <div class="filter-dialog__sheet" style="max-width:400px">
      <div class="filter-dialog__title">Check Spelling</div>
      <div class="filter-dialog__body" style="padding:12px 0">
        <p style="color:var(--text-dim);font-size:12px;margin:0 0 8px">${uniqueWords.length} unique words found. Browser spell-check is active in the document.</p>
        <div style="max-height:200px;overflow-y:auto;font-size:12px;color:var(--text-dim)">
          ${uniqueWords.slice(0, 50).map((w) => `<div style="padding:2px 0">${escapeHtml(w)}</div>`).join("")}
          ${uniqueWords.length > 50 ? `<div style="padding:2px 0;color:var(--text-dim)">...and ${uniqueWords.length - 50} more</div>` : ""}
        </div>
      </div>
      <div class="filter-dialog__actions">
        <button class="filter-dialog__btn filter-dialog__btn--primary" data-action="close">Close</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.querySelector('[data-action="close"]')?.addEventListener("click", () => { overlay.remove(); textarea.remove(); });
  overlay.addEventListener("click", (e) => { if (e.target === overlay) { overlay.remove(); textarea.remove(); } });
}

function saveSelection(): void {
  const doc = state.doc;
  if (!doc || !doc.selection || doc.selection.isEmpty()) { showError("No selection to save."); return; }
  const alphas = docAlphaChannels.get(doc.name) || [];
  const n = alphas.length + 1;
  const alpha: AlphaChannel = {
    id: crypto.randomUUID(),
    name: `Alpha ${n}`,
    pixels: new Uint8Array(doc.selection.mask),
    width: doc.width,
    height: doc.height,
    visible: true,
    opacity: 1,
    color: { r: 0, g: 0, b: 255, a: 0.5 },
  };
  alphas.push(alpha);
  docAlphaChannels.set(doc.name, alphas);
  renderChannelsPanel();
  showTip(`Selection saved as "${alpha.name}".`);
}

async function loadSelection(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;
  const alphas = docAlphaChannels.get(doc.name) || [];
  if (alphas.length === 0) { showError("No saved selections."); return; }

  const result = await filterDialog.open("Load Selection", [
    { key: "channel", label: "Channel", type: "dropdown", default: alphas[0]!.id, options: alphas.map(a => ({ value: a.id, label: a.name })) },
  ]);
  if (!result) return;

  const alpha = alphas.find(a => a.id === result.channel);
  if (!alpha) return;

  const mask = new Uint8Array(doc.width * doc.height);
  const copyW = Math.min(alpha.width, doc.width);
  const copyH = Math.min(alpha.height, doc.height);
  for (let y = 0; y < copyH; y++) {
    for (let x = 0; x < copyW; x++) {
      mask[y * doc.width + x] = alpha.pixels[y * alpha.width + x] ?? 0;
    }
  }

  _previousSelection = doc.selection;
  state.execute(new SetSelectionCommand(new Selection(doc.width, doc.height, mask)));
  state.requestRender();
}

async function colorRange(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;
  const layer = getActiveRaster();
  if (!layer) { showError("No active raster layer."); return; }

  // Let user pick a color from canvas first
  showTip("Click on the canvas to pick a color for Color Range...");

  const pickedColor = await new Promise<{ r: number; g: number; b: number } | null>((resolve) => {
    let resolved = false;
    const handler = (e: PointerEvent) => {
      if (resolved) return;
      const r = canvas.getBoundingClientRect();
      const docPos = screenToDoc(state.viewport, e.clientX - r.left, e.clientY - r.top);
      const lx = Math.floor(docPos.x - layer.x);
      const ly = Math.floor(docPos.y - layer.y);
      if (lx >= 0 && ly >= 0 && lx < layer.pixels.width && ly < layer.pixels.height) {
        const pi = (ly * layer.pixels.width + lx) * 4;
        resolved = true;
        canvas.removeEventListener("click", handler);
        resolve({ r: layer.pixels.data[pi]!, g: layer.pixels.data[pi + 1]!, b: layer.pixels.data[pi + 2]! });
      }
    };
    canvas.addEventListener("click", handler, { once: false });
    // Timeout after 10 seconds
    setTimeout(() => {
      if (!resolved) {
        canvas.removeEventListener("click", handler);
        resolve(null);
      }
    }, 10000);
  });

  if (!pickedColor) { showTip("Color Range cancelled."); return; }

  const result = await filterDialog.open("Color Range", [
    { key: "fuzziness", label: "Fuzziness", type: "slider", default: 40, min: 0, max: 200, unit: "" },
  ]);
  if (!result) return;

  const fuzziness = result.fuzziness as number;
  const data = layer.pixels.data;
  const lw = layer.pixels.width;
  const lh = layer.pixels.height;
  const tR = pickedColor.r, tG = pickedColor.g, tB = pickedColor.b;

  const mask = new Uint8Array(doc.width * doc.height);
  for (let ly = 0; ly < lh; ly++) {
    for (let lx = 0; lx < lw; lx++) {
      const pi = (ly * lw + lx) * 4;
      const dR = data[pi]! - tR, dG = data[pi + 1]! - tG, dB = data[pi + 2]! - tB;
      const dist = Math.sqrt(dR * dR + dG * dG + dB * dB);
      const maxDist = Math.sqrt(3 * 255 * 255);
      const normalized = dist / maxDist * 100;
      if (normalized <= fuzziness) {
        const dx = lx + layer.x, dy = ly + layer.y;
        if (dx >= 0 && dx < doc.width && dy >= 0 && dy < doc.height) {
          mask[dy * doc.width + dx] = Math.round((1 - normalized / (fuzziness || 1)) * 255);
        }
      }
    }
  }

  _previousSelection = doc.selection;
  state.execute(new SetSelectionCommand(new Selection(doc.width, doc.height, mask)));
  state.requestRender();
}

// i68: clone a raster layer's pixel data into a fresh PixelBuffer (new id,
// no mask) for Select and Mask's "Layer" / "New Document" output targets —
// a real class instance (via PixelBuffer + .set()), NOT the JSON round-trip
// duplicateDocument() uses elsewhere (that trick drops TypedArray-ness).
function cloneRasterLayerForSelectAndMask(layer: RasterLayer, name: string): RasterLayer {
  const px = new PixelBuffer(layer.pixels.width, layer.pixels.height, layer.pixels.mode);
  px.data.set(layer.pixels.data);
  const { mask: _droppedMask, ...rest } = layer;
  void _droppedMask;
  return { ...rest, id: crypto.randomUUID(), name, pixels: px };
}

// i68: bake a Selection's coverage directly into a layer's alpha channel
// (Select and Mask's "New Document" output — Photoshop discards the mask
// object and just punches transparency, unlike "New Document with Layer
// Mask" which keeps an editable mask).
function bakeSelectionIntoAlpha(layer: RasterLayer, sel: Selection): void {
  const data = layer.pixels.data;
  const w = layer.pixels.width;
  const h = layer.pixels.height;
  for (let ly = 0; ly < h; ly++) {
    for (let lx = 0; lx < w; lx++) {
      const idx = (ly * w + lx) * 4 + 3;
      const v = sel.contains(layer.x + lx, layer.y + ly);
      data[idx] = Math.round(((data[idx] ?? 0) * v) / 255);
    }
  }
  layer.pixels.markDirty();
}

// i68: Select and Mask's "Decontaminate Colors" — blend each partially
// selected (edge) pixel's color toward the average color of its nearby
// fully-selected neighbors, scaled by the Amount slider. Removes background
// color fringing left over from the original edge. Mirrors the spirit of
// RefineMaskEdge's decontaminate pass but reads coverage from the live
// Selection instead of a standalone mask PixelBuffer.
function decontaminateSelectionEdge(layer: RasterLayer, sel: Selection, amount: number): void {
  const data = layer.pixels.data;
  const w = layer.pixels.width;
  const h = layer.pixels.height;
  const snapshot = new Uint8ClampedArray(data);
  const radius = 3;
  for (let ly = 0; ly < h; ly++) {
    for (let lx = 0; lx < w; lx++) {
      const v = sel.contains(layer.x + lx, layer.y + ly);
      if (v <= 10 || v >= 245) continue; // only edge (partially selected) pixels
      let rSum = 0, gSum = 0, bSum = 0, count = 0;
      for (let oy = -radius; oy <= radius; oy++) {
        const nly = ly + oy;
        if (nly < 0 || nly >= h) continue;
        for (let ox = -radius; ox <= radius; ox++) {
          const nlx = lx + ox;
          if (nlx < 0 || nlx >= w) continue;
          if (sel.contains(layer.x + nlx, layer.y + nly) <= 200) continue;
          const ni = (nly * w + nlx) * 4;
          rSum += snapshot[ni] ?? 0;
          gSum += snapshot[ni + 1] ?? 0;
          bSum += snapshot[ni + 2] ?? 0;
          count++;
        }
      }
      if (count === 0) continue;
      const idx = (ly * w + lx) * 4;
      const blend = ((255 - v) / 255) * amount;
      data[idx] = Math.round((snapshot[idx] ?? 0) * (1 - blend) + (rSum / count) * blend);
      data[idx + 1] = Math.round((snapshot[idx + 1] ?? 0) * (1 - blend) + (gSum / count) * blend);
      data[idx + 2] = Math.round((snapshot[idx + 2] ?? 0) * (1 - blend) + (bSum / count) * blend);
    }
  }
  layer.pixels.markDirty();
}

// i68: opens the real SelectAndMaskDialog (7 view modes, Radius + Smart
// Radius, Smooth/Feather/Contrast/Shift Edge, Decontaminate Colors + Amount,
// 6 output targets) instead of the old 5-field generic filterDialog, and
// wires all 6 output targets to the current document/selection/active layer.
// View Mode is presentation-only in the dialog (no live preview surface
// here, same as the dialog's own standalone behavior); Radius/Smart Radius
// are accepted by the dialog but not yet wired to an edge-detection pass —
// same follow-up noted in SelectAndMaskDialog.ts's own Edge Detection
// section comment.
async function selectAndMask(): Promise<void> {
  const doc = state.doc;
  if (!doc || !doc.selection) { showError("No selection."); return; }

  const dialog = new SelectAndMaskDialog();
  const result = await dialog.open();
  if (!result) return;
  const { settings } = result;

  // Canonical Smooth/Feather/Contrast/Shift Edge math, shared with the
  // dialog's own tests (SelectAndMaskDialog.ts's applyEdgeAdjustments).
  const sel = applyEdgeAdjustments(doc.selection, settings);

  const layer = getActiveRaster();
  // Decontaminate Colors edits pixels, so it is applied ONLY to the freshly
  // cloned output layer inside the clone-based output branches below — NEVER to
  // the live source layer. Mutating the source directly (as an earlier form of
  // this fix did) is an un-undoable pixel edit and corrupts the original even
  // for outputs whose whole purpose is to leave it intact. This matches
  // Photoshop, which enables Decontaminate only for the New Layer / New Document
  // output modes; for Selection / Layer Mask outputs it is a no-op.
  const decontaminate = (target: RasterLayer): void => {
    if (settings.decontaminate && settings.decontaminateAmount > 0) {
      decontaminateSelectionEdge(target, sel, settings.decontaminateAmount / 100);
    }
  };

  switch (settings.outputTo) {
    case "selection": {
      _previousSelection = doc.selection;
      state.execute(new SetSelectionCommand(sel));
      break;
    }
    case "layer-mask": {
      if (!layer) { showError("No active raster layer for mask."); return; }
      _previousSelection = doc.selection;
      state.execute(new SetSelectionCommand(sel));
      state.execute(new AddLayerMaskCommand(layer.id, "from-selection"));
      state.execute(new SetSelectionCommand(null)); // Deselect after applying mask (matches PS)
      break;
    }
    case "selection-and-layer-mask": {
      if (!layer) { showError("No active raster layer for mask."); return; }
      _previousSelection = doc.selection;
      state.execute(new SetSelectionCommand(sel));
      state.execute(new AddLayerMaskCommand(layer.id, "from-selection"));
      break;
    }
    case "layer": {
      if (!layer) { showError("No active raster layer."); return; }
      const copy = cloneRasterLayerForSelectAndMask(layer, `${layer.name} copy`);
      decontaminate(copy);
      state.execute(new NewLayerCommand(copy));
      state.setActiveLayer(copy.id);
      _previousSelection = doc.selection;
      state.execute(new SetSelectionCommand(sel));
      state.execute(new AddLayerMaskCommand(copy.id, "from-selection"));
      state.execute(new SetSelectionCommand(null));
      break;
    }
    case "new-document":
    case "new-document-with-layer-mask": {
      if (!layer) { showError("No active raster layer."); return; }
      const withMask = settings.outputTo === "new-document-with-layer-mask";
      const copy = cloneRasterLayerForSelectAndMask(layer, layer.name);
      decontaminate(copy);
      if (!withMask) bakeSelectionIntoAlpha(copy, sel);
      const newDoc: Document = {
        // i44: a fresh, unique id -- distinct from `doc` and from any other
        // open document (e.g. running Select and Mask > New Document twice
        // on the same source produces two documents with the identical name
        // "<name> (Select and Mask)").
        id: crypto.randomUUID(),
        name: `${doc.name} (Select and Mask)`,
        width: doc.width,
        height: doc.height,
        dpi: doc.dpi,
        layers: [copy],
        activeLayerId: copy.id,
        // Carry `sel` through so AddLayerMaskCommand's "from-selection" can
        // read it in the new document below; cleared right after.
        selection: withMask ? sel : null,
      };
      state.execute(new LoadDocumentCommand(newDoc));
      if (withMask) {
        state.execute(new AddLayerMaskCommand(copy.id, "from-selection"));
        state.execute(new SetSelectionCommand(null));
      }
      fitDoc();
      break;
    }
  }
  state.requestRender();
}

// â”€â”€ View menu helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

let guidesLocked = false;

function zoomTo200(): void {
  if (!state.doc) return;
  const r = viewportEl.getBoundingClientRect();
  state.setViewport({
    zoom: 2,
    panX: r.width / 2 - state.doc.width,
    panY: r.height / 2 - state.doc.height,
  });
}

async function newGuide(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;

  const result = await filterDialog.open("New Guide", [
    { key: "orientation", label: "Orientation", type: "dropdown", default: "vertical", options: [
      { value: "horizontal", label: "Horizontal" },
      { value: "vertical", label: "Vertical" },
    ]},
    { key: "position", label: "Position", type: "number", default: 0, min: 0, max: Math.max(doc.width, doc.height), unit: "px" },
  ]);
  if (!result) return;

  const kind = result.orientation as "horizontal" | "vertical";
  const position = result.position as number;
  guides.push({ kind, position });
  state.requestRender();
}

function toggleLockGuides(): void {
  guidesLocked = !guidesLocked;
}

// â”€â”€ Workspace management â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
let currentWorkspaceName = "Essentials";

function getCurrentWorkspace(): string {
  return currentWorkspaceName;
}

function setCurrentWorkspace(name: string): void {
  currentWorkspaceName = name;
  const workspaceEl = document.querySelector(".menubar__workspace span:last-child");
  if (workspaceEl) workspaceEl.textContent = name;
  const ws = state.loadWorkspace(name);
  if (ws) {
    applyWorkspacePanels(ws.panels);
  }
  showTip(`Workspace "${name}" loaded.`);
}

function applyWorkspacePanels(panelIds: string[]): void {
  const panelMap: Record<string, () => void> = {
    layers: () => {
      const panelsEl = document.querySelector(".panels") as HTMLElement;
      if (panelsEl) panelsEl.style.display = "";
    },
    color: () => {},
    swatches: () => openSwatchesPanel(),
    properties: () => openPropertiesPanel(),
    styles: () => openStylesPanel(),
    adjustments: () => openAdjustmentsPanel(),
    navigator: () => openNavigatorPanel(),
    info: () => openInfoPanel(),
    histogram: () => openHistogramPanel(),
    "brush-settings": () => openBrushSettingsPanel(),
  };
  for (const id of panelIds) {
    const fn = panelMap[id];
    if (fn) fn();
  }
}

function buildWorkspaceMenuItems(anchor: HTMLElement): Array<{ label: string; shortcut?: string; disabled?: boolean; action?: () => void; separator?: boolean }> {
  const items: Array<{ label: string; shortcut?: string; disabled?: boolean; action?: () => void; separator?: boolean }> = [];
  const defaults = ["Essentials", "Photography", "Painting", "Web"];
  for (const name of defaults) {
    items.push({
      label: `${name === currentWorkspaceName ? "âœ“ " : ""}${name}`,
      action: () => setCurrentWorkspace(name),
    });
  }
  items.push({ separator: true, label: "" });

  const customWorkspaces = Array.from(state.workspaces.keys()).filter(k => !defaults.includes(k));
  for (const name of customWorkspaces) {
    items.push({
      label: `${name === currentWorkspaceName ? "âœ“ " : ""}${name}`,
      action: () => setCurrentWorkspace(name),
    });
  }
  if (customWorkspaces.length > 0) {
    items.push({ separator: true, label: "" });
  }
  items.push({ label: "New Workspaceâ€¦", action: () => newWorkspace(anchor) });
  items.push({ label: "Reset Workspace", action: () => { state.resetWorkspaces(); setCurrentWorkspace("Essentials"); } });
  return items;
}

function newWorkspace(anchor: HTMLElement): void {
  const name = prompt("Workspace name:");
  if (!name) return;
  const panels = ["layers"];
  state.saveWorkspace(name, panels);
  setCurrentWorkspace(name);
}

// â”€â”€ Snap-to system â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
let snapToLayers = false;
let snapToDocumentBounds = false;
let snapEnabled = true;
let snapTolerance = 5;

function toggleSnap(): void {
  snapEnabled = !snapEnabled;
  showTip(`Snap ${snapEnabled ? "enabled" : "disabled"}.`);
}

function toggleSnapToLayers(): void {
  snapToLayers = !snapToLayers;
}

function toggleSnapToDocumentBounds(): void {
  snapToDocumentBounds = !snapToDocumentBounds;
}

/**
 * Collect left/right/center-x and top/bottom/center-y edges of every visible
 * raster layer NOT in `excludeIds`, so a dragged layer/selection can snap to
 * them — mirrors Photoshop smart guides, which only treat non-moving layers
 * as snap references (audit i26). `excludeIds` is normally the set of layers
 * currently being dragged (ctx.selectedLayerIds), so a layer never snaps to
 * its own stale pre-drag bounds.
 */
function collectOtherLayerRects(
  doc: Document,
  excludeIds: ReadonlySet<string>,
): { x: number; y: number; w: number; h: number }[] {
  const rects: { x: number; y: number; w: number; h: number }[] = [];
  const walk = (layers: readonly Layer[]) => {
    for (const l of layers) {
      if (!l.visible || l.opacity <= 0) continue;
      if (excludeIds.has(l.id)) continue;
      if (l.kind === "raster") {
        rects.push({ x: l.x, y: l.y, w: l.pixels.width, h: l.pixels.height });
      } else if (l.kind === "group") {
        walk(l.children);
      }
    }
  };
  walk(doc.layers);
  return rects;
}

/**
 * THE single consolidated snap function (audit i26): snaps a doc-space point
 * to the nearest active guide, grid line, other-layer edge/center, or
 * document bound, honoring the View ▸ Snap + Snap To toggles. Delegates to
 * snapPosition() in src/app/ui/SnapTo.ts. Returns the input unchanged when
 * snapping is off, or when nothing is within the (zoom-adjusted) threshold.
 * Wired onto ToolContext.snapPoint in toolCtx() below.
 */
function snapDocPoint(
  docX: number,
  docY: number,
  excludeIds: ReadonlySet<string> = new Set(),
): { x: number; y: number } {
  if (!snapEnabled || !state.doc) return { x: docX, y: docY };

  const guideLists = {
    vertical: guides.filter((g) => g.kind === "vertical").map((g) => g.position),
    horizontal: guides.filter((g) => g.kind === "horizontal").map((g) => g.position),
  };
  const layerEdges = snapToLayers
    ? buildLayerEdgeTargets(collectOtherLayerRects(state.doc, excludeIds))
    : { vertical: [], horizontal: [] };
  const targets = buildSnapTargets(state.doc.width, state.doc.height, gridSpacing, guideLists, layerEdges);

  const settings: SnapSettings = {
    enabled: snapEnabled,
    snapToGrid,
    snapToGuides,
    snapToDocBounds: snapToDocumentBounds,
    snapToLayers,
    snapToCenter: false,
    snapToThirds: false,
  };

  // Screen-space threshold converted to doc-space so the pull feels the same
  // size regardless of zoom (matches the old per-target helpers' behaviour).
  const threshold = snapTolerance / state.viewport.zoom;
  const result = snapPosition(docX, docY, targets, settings, threshold);
  return { x: result.x, y: result.y };
}

// â”€â”€ Grid configuration â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
let gridMajorEvery = 5;
let gridColor = "#444444";
let gridMajorColor = "#666666";

// â”€â”€ New filter prompts â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function promptAverageBlur(): Promise<void> { return FD.promptAverageBlur(filterDeps()); }
async function promptSurfaceBlur(): Promise<void> { return FD.promptSurfaceBlur(filterDeps()); }
async function promptSmartBlur(): Promise<void> { return FD.promptSmartBlur(filterDeps()); }
async function promptLensBlur(): Promise<void> { return FD.promptLensBlur(filterDeps()); }
async function promptRadialBlur(): Promise<void> { return FD.promptRadialBlur(filterDeps()); }
async function promptShapeBlur(): Promise<void> { return FD.promptShapeBlur(filterDeps()); }
async function promptFieldBlur(): Promise<void> { return FD.promptFieldBlur(filterDeps()); }
async function promptIrisBlur(): Promise<void> { return FD.promptIrisBlur(filterDeps()); }
async function promptTiltShift(): Promise<void> { return FD.promptTiltShift(filterDeps()); }
async function promptDisplace(): Promise<void> { return FD.promptDisplace(filterDeps()); }
async function promptRipple(): Promise<void> { return FD.promptRipple(filterDeps()); }
async function promptShear(): Promise<void> { return FD.promptShear(filterDeps()); }
async function promptWave(): Promise<void> { return FD.promptWave(filterDeps()); }
async function promptZigZag(): Promise<void> { return FD.promptZigZag(filterDeps()); }
async function promptPolarCoordinates(): Promise<void> { return FD.promptPolarCoordinates(filterDeps()); }
async function promptCrystallize(): Promise<void> { return FD.promptCrystallize(filterDeps()); }
async function promptFacet(): Promise<void> { return FD.promptFacet(filterDeps()); }
async function promptFragment(): Promise<void> { return FD.promptFragment(filterDeps()); }
async function promptMezzotint(): Promise<void> { return FD.promptMezzotint(filterDeps()); }
async function promptPointillize(): Promise<void> { return FD.promptPointillize(filterDeps()); }
async function promptDiffuse(): Promise<void> { return FD.promptDiffuse(filterDeps()); }
async function promptExtrude(): Promise<void> { return FD.promptExtrude(filterDeps()); }
async function promptSolarize(): Promise<void> { return FD.promptSolarize(filterDeps()); }
async function promptTiles(): Promise<void> { return FD.promptTiles(filterDeps()); }
async function promptTraceContour(): Promise<void> { return FD.promptTraceContour(filterDeps()); }
async function promptWind(): Promise<void> { return FD.promptWind(filterDeps()); }
async function promptGlowingEdges(): Promise<void> { return FD.promptGlowingEdges(filterDeps()); }
async function promptFibers(): Promise<void> { return FD.promptFibers(filterDeps()); }
async function promptLensFlare(): Promise<void> { return FD.promptLensFlare(filterDeps()); }
async function promptLightingEffects(): Promise<void> { return FD.promptLightingEffects(filterDeps()); }
async function promptPictureFrame(): Promise<void> { return FD.promptPictureFrame(filterDeps()); }

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c));
}

// Layers panel â€” rebuilt on every state notify.
const layerListEl = document.querySelector<HTMLUListElement>(".layer-list");
let layerDropIndicator: HTMLElement | null = null;

// i28: which group layers are collapsed (children hidden) in the panel.
// UI-only presentation state — deliberately NOT part of Document/EditorState
// since it has no undo/redo semantics and isn't part of the saved file.
// Kept as a module-level Set (mirrors layerDropIndicator above) rather than
// threaded through EditorState, to keep this fix's surface to the rendering
// layer only. Groups default to expanded.
let collapsedGroupIds = new Set<string>();

function toggleGroupCollapsed(groupId: string): void {
  if (collapsedGroupIds.has(groupId)) collapsedGroupIds.delete(groupId);
  else collapsedGroupIds.add(groupId);
  renderLayersPanel();
}

// Change which layer is "active" WITHOUT clearing the Layers-panel
// multi-selection (state.selectedLayerIds lives outside `doc`, so a Command
// that only touches `doc.activeLayerId` can't disturb it — same idiom
// MoveTool.ts already uses for its auto-select "set-active-layer" command).
//
// Fixes a real bug: `state.setActiveLayer()` unconditionally clears
// `_selectedLayerIds` (see EditorState.setActiveLayer). The Ctrl+click and
// Shift+click handlers below used to build up a multi-selection with
// `toggleLayerSelection`/`setSelectedLayerIds` and then immediately call
// `state.setActiveLayer(id)`, which wiped the selection right back out —
// so Ctrl/Shift+click could never produce more than a 1-layer selection.
// That silently broke every multi-layer op gated on selectedLayerIds
// (Align/Distribute among them — audit i2/i3), not just this feature.
function setActiveLayerPreservingSelection(id: string): void {
  const doc = state.doc;
  if (!doc) return;
  const prevId = doc.activeLayerId;
  const setActiveLayerCommand = (targetId: string | null, restoreId: string | null): Command => ({
    kind: "set-active-layer",
    apply(d: Document) { return { ...d, activeLayerId: targetId }; },
    invert() { return setActiveLayerCommand(restoreId, targetId); },
  });
  state.execute(setActiveLayerCommand(id, prevId));
}

// i121: Alt+click the eye icon "solos" a layer (Photoshop convention) — hides
// every sibling at the same nesting level and shows only the clicked layer;
// a second Alt+click on the same layer restores everyone's prior visibility.
// Tracked per sibling-list (keyed by parentId) so solo state in one group
// doesn't leak into another.
let layerSoloState: { parentKey: string; soloId: string; prevVisibility: Map<string, boolean> } | null = null;

/** Finds the sibling array (and its parentId) that contains layer `id`. */
function findSiblingLayers(layers: readonly Layer[], id: string, parentId: string | null = null): { siblings: readonly Layer[]; parentId: string | null } | null {
  for (const l of layers) {
    if (l.id === id) return { siblings: layers, parentId };
    if (l.kind === "group") {
      const inner = findSiblingLayers(l.children, id, l.id);
      if (inner) return inner;
    }
  }
  return null;
}

function toggleLayerSolo(id: string): void {
  const doc = state.doc;
  if (!doc) return;
  const found = findSiblingLayers(doc.layers, id);
  if (!found) return;
  const { siblings, parentId } = found;
  const parentKey = parentId ?? "__root__";

  if (layerSoloState && layerSoloState.parentKey === parentKey) {
    if (layerSoloState.soloId === id) {
      // Alt+click the SAME soloed layer — exit solo, restore previous visibility.
      for (const sib of siblings) {
        const prevVis = layerSoloState.prevVisibility.get(sib.id);
        if (prevVis !== undefined && sib.visible !== prevVis) {
          state.execute(new SetLayerPropertyCommand(sib.id, { visible: prevVis }));
        }
      }
      layerSoloState = null;
      return;
    }
    // Alt+click a DIFFERENT layer in the same group — re-target the solo to it
    // (Photoshop UX) rather than exiting. Keep the ORIGINAL pre-solo snapshot so
    // a later exit still restores the true pre-solo visibility.
    for (const sib of siblings) {
      const shouldBeVisible = sib.id === id;
      if (sib.visible !== shouldBeVisible) {
        state.execute(new SetLayerPropertyCommand(sib.id, { visible: shouldBeVisible }));
      }
    }
    layerSoloState.soloId = id;
    return;
  }

  const prevVisibility = new Map<string, boolean>();
  for (const sib of siblings) prevVisibility.set(sib.id, sib.visible);
  layerSoloState = { parentKey, soloId: id, prevVisibility };
  for (const sib of siblings) {
    const shouldBeVisible = sib.id === id;
    if (sib.visible !== shouldBeVisible) {
      state.execute(new SetLayerPropertyCommand(sib.id, { visible: shouldBeVisible }));
    }
  }
}

function renderLayersPanel(): void {
  if (!layerListEl) return;
  const doc = state.doc;
  if (!doc || doc.layers.length === 0) {
    layerListEl.innerHTML = "";
    return;
  }

  const filter = state.layerFilter.toLowerCase();
  // i28: flatten the full layer tree (top-first per level), descending into
  // expanded groups only. `entries` also carries parentId/bottomIndex, used
  // below by the drag-reorder wiring to target the right list + position.
  const entries = flattenLayerRows(doc.layers, collapsedGroupIds);
  const selectedIds = state.selectedLayerIds;
  const entryById = new Map<string, LayerRowEntry>();
  for (const entry of entries) entryById.set(entry.layer.id, entry);

  // Build rows with clipping + nesting awareness.
  const rows: string[] = [];
  for (const entry of entries) {
    const l = entry.layer;
    // Apply search filter
    if (filter) {
      const nameMatch = l.name.toLowerCase().includes(filter);
      const kindMatch = l.kind.toLowerCase().includes(filter);
      const blendMatch = l.blendMode.toLowerCase().includes(filter);
      if (!nameMatch && !kindMatch && !blendMatch) continue;
    }
    const isClipped = l.clippedToBelow;
    const isSelected = selectedIds.has(l.id);
    const isCollapsed = l.kind === "group" && collapsedGroupIds.has(l.id);
    rows.push(layerRow(l, doc.activeLayerId, isSelected, entry.depth, isClipped, isCollapsed));
  }
  layerListEl.innerHTML = rows.join("");

  // Wire up layer search input
  const searchInput = document.querySelector<HTMLInputElement>(".layer-search-input");
  if (searchInput) {
    searchInput.value = state.layerFilter;
    searchInput.oninput = () => { state.layerFilter = searchInput!.value; };
  }

  layerListEl.querySelectorAll<HTMLLIElement>(".layer").forEach((row) => {
    const id = row.getAttribute("data-id");
    if (!id) return;

    // i28: disclosure triangle toggles group expand/collapse. stopPropagation
    // so it doesn't also fall through to the row-select click handler below.
    row.querySelector(".layer__disclosure")?.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      toggleGroupCollapsed(id);
    });

    // Click handling with multi-select support
    row.addEventListener("click", (ev) => {
      // Alt+click on the gap between layers toggles clipping.
      if (ev.altKey) {
        const rect = row.getBoundingClientRect();
        const clickY = ev.clientY - rect.top;
        if (clickY < 8) {
          ev.preventDefault();
          ev.stopPropagation();
          toggleClippingForLayer(id);
          return;
        }
      }

      // Ctrl/Cmd+click: toggle multi-select
      if (ev.ctrlKey || ev.metaKey) {
        ev.preventDefault();
        state.toggleLayerSelection(id);
        // Not state.setActiveLayer() — that unconditionally clears
        // selectedLayerIds, which would immediately undo the toggle above.
        setActiveLayerPreservingSelection(id);
        return;
      }

      // Shift+click: range select. Uses the currently-rendered tree order
      // (respects collapsed groups) — same as the previous top-level-only
      // behavior, which likewise ignored the search filter for range calc.
      if (ev.shiftKey && doc.activeLayerId) {
        ev.preventDefault();
        const allIds = entries.map((e) => e.layer.id);
        const fromIdx = allIds.indexOf(doc.activeLayerId);
        const toIdx = allIds.indexOf(id);
        if (fromIdx >= 0 && toIdx >= 0) {
          const start = Math.min(fromIdx, toIdx);
          const end = Math.max(fromIdx, toIdx);
          const range = new Set<string>();
          for (let i = start; i <= end; i++) range.add(allIds[i]!);
          state.setSelectedLayerIds(range);
          // Not state.setActiveLayer() — see the Ctrl+click branch above.
          setActiveLayerPreservingSelection(id);
        }
        return;
      }

      // Normal click: single select
      state.setActiveLayer(id);
    });

    // Double-click: adjustment layers open edit dialog, smart objects open for
    // editing, type layers enter text editing (Photoshop parity, audit i16),
    // others rename.
    row.addEventListener("dblclick", (ev) => {
      ev.stopPropagation();
      const layer = findLayer(state.doc?.layers ?? [], id);
      if (layer && layer.kind === "adjustment") {
        editAdjustmentLayer(id);
      } else if (layer && layer.kind === "smart-object") {
        editSmartObject();
      } else if (layer && layer.kind === "type") {
        state.setActiveTool(typeTool);
        highlightToolByShortcut("T");
        typeTool.beginEditingLayerById(toolCtx(), id, true);
      } else {
        startRenameLayer(id);
      }
    });

    // i123: Shift+click the mask thumbnail toggles the mask's enabled state
    // (EnableLayerMaskCommand). Plain click (select-mask-as-edit-target) and
    // Alt+click (isolate mask view) would need paint-pipeline / renderer
    // support that doesn't exist yet (BrushTool et al. only ever paint the
    // layer's own raster buffer) — out of scope for this pass.
    row.querySelector<HTMLElement>(`[data-mask-thumb="${CSS.escape(id)}"]`)?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      const layer = findLayer(state.doc?.layers ?? [], id) as { mask?: import("./core/document/Layer").LayerMask } | null;
      if (!layer?.mask) return;
      if ((ev as MouseEvent).shiftKey) {
        state.execute(new EnableLayerMaskCommand(id, !layer.mask.enabled));
      }
    });

    // i122: right-click context menu, reusing the same showMenuDropdown()
    // infrastructure the toolbar flyouts and canvas tool context menus (i129)
    // already use. Acts on THIS row's layer (making it active first if it
    // isn't already), not whatever was previously the active layer.
    row.addEventListener("contextmenu", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const rowLayer = findLayer(state.doc?.layers ?? [], id);
      if (!rowLayer) return;
      const ensureActive = () => { if (state.doc?.activeLayerId !== id) state.setActiveLayer(id); };
      const canMergeDown = entryById.get(id) ? entries.some((en) => en.parentId === entryById.get(id)!.parentId && en.bottomIndex < entryById.get(id)!.bottomIndex) : false;
      showMenuDropdown(row, [
        { label: "Duplicate Layer", action: () => { ensureActive(); duplicateActiveLayer(); } },
        { label: "Delete Layer", action: () => { ensureActive(); deleteActiveLayer(); } },
        { separator: true, label: "" },
        { label: "Rasterize Layer", disabled: rowLayer.kind === "raster", action: () => { rasterizeLayerById(id); } },
        { label: "Group from Layer", action: () => { ensureActive(); groupFromLayers(); } },
        { label: "Merge Down", disabled: !canMergeDown, action: () => { ensureActive(); mergeDownActive(); } },
        { separator: true, label: "" },
        { label: rowLayer.clippedToBelow ? "Release Clipping Mask" : "Create Clipping Mask", action: () => toggleClippingForLayer(id) },
      ]);
    });

    // Drag-drop reordering with visual indicator
    row.draggable = true;
    row.addEventListener("dragstart", (e) => {
      e.dataTransfer?.setData("text/plain", id);
      row.style.opacity = "0.5";
      // Also include selected layers
      if (selectedIds.size > 1 && selectedIds.has(id)) {
        e.dataTransfer?.setData("application/x-pixelpa-layers", JSON.stringify([...selectedIds]));
      }
    });
    row.addEventListener("dragend", () => {
      row.style.opacity = "";
      removeDropIndicator();
    });
    row.addEventListener("dragover", (e) => {
      e.preventDefault();
      showDropIndicator(row, e.clientY);
    });
    row.addEventListener("dragleave", () => {
      removeDropIndicator();
    });
    row.addEventListener("drop", (e) => {
      e.preventDefault();
      removeDropIndicator();
      const draggedId = e.dataTransfer?.getData("text/plain");
      if (!draggedId || draggedId === id) return;
      // i28: target the dropped-on row's own parent + position (root list,
      // or the group it belongs to) instead of always the root list, so
      // reordering keeps working once nested rows are rendered/draggable.
      // Dropping onto one of a group's already-visible children naturally
      // lands the dragged layer inside that group (ReorderLayerCommand
      // supports an arbitrary targetParentId); dropping directly onto a
      // (possibly collapsed) group's own header row still reorders it as a
      // sibling — full drag-*into*-a-collapsed-group is a follow-up.
      const targetEntry = entryById.get(id);
      if (!targetEntry) return;
      // i117: also consume the multi-select payload dragstart wrote
      // ("application/x-pixelpa-layers") so dropping a multi-selected group
      // of rows reorders all of them together, not just the single row the
      // native HTML5 drag happened to originate from.
      let draggedIds: string[] = [draggedId];
      const multiPayload = e.dataTransfer?.getData("application/x-pixelpa-layers");
      if (multiPayload) {
        try {
          const parsed = JSON.parse(multiPayload) as unknown;
          if (Array.isArray(parsed) && parsed.every((v) => typeof v === "string")) {
            draggedIds = parsed as string[];
          }
        } catch { /* malformed payload — fall back to the single draggedId */ }
      }
      const orderedDraggedIds = draggedIds
        .filter((did) => did !== id && entryById.has(did))
        .sort((a, b) => entryById.get(a)!.bottomIndex - entryById.get(b)!.bottomIndex);
      if (orderedDraggedIds.length === 1) {
        // Single-layer drag — unchanged, well-tested path: insert at the
        // target's live bottom-first index.
        const liveTarget = flattenLayerRows(state.doc?.layers ?? [], collapsedGroupIds).find((en) => en.layer.id === id);
        if (liveTarget) state.execute(new ReorderLayerCommand(orderedDraggedIds[0]!, liveTarget.parentId, liveTarget.bottomIndex));
      } else if (orderedDraggedIds.length > 1) {
        // i117: move the whole multi-selection as one atomic block that
        // preserves the dragged layers' relative order. The prior per-layer
        // loop (re-resolving the target each iteration) inverted their order
        // when dropping toward the top and cost N undo steps.
        state.execute(new ReorderLayersCommand(orderedDraggedIds, id));
      }
    });

    row.querySelector(".layer__vis")?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      const layer = findLayer(state.doc?.layers ?? [], id);
      if (!layer) return;
      // i121: Alt+click solos the layer instead of a plain visibility toggle.
      if ((ev as MouseEvent).altKey) {
        toggleLayerSolo(id);
        return;
      }
      state.execute(new SetLayerPropertyCommand(id, { visible: !layer.visible }));
    });
  });
}

function showDropIndicator(row: HTMLElement, clientY: number): void {
  removeDropIndicator();
  const rect = row.getBoundingClientRect();
  const midY = rect.top + rect.height / 2;
  const indicator = document.createElement("div");
  indicator.className = "layer-drop-indicator";
  indicator.style.cssText = `position:absolute;left:0;right:0;height:2px;background:var(--accent);pointer-events:none;z-index:10;`;
  // Position relative to the scrollable list (.layer-list is position:relative,
  // so it is `row`'s offsetParent). offsetTop is content-relative and scrolls
  // with the list, so the indicator stays aligned with the row boundary at any
  // scroll position. (audit F4: the old `getBoundingClientRect().top -
  // scrollTop` was viewport-relative math against a viewport-anchored element —
  // it double-counted scroll and, with left:0;right:0, spanned the whole window.)
  indicator.style.top = (clientY < midY ? row.offsetTop : row.offsetTop + row.offsetHeight) + "px";
  row.parentElement!.appendChild(indicator);
  layerDropIndicator = indicator;
}

function removeDropIndicator(): void {
  if (layerDropIndicator) {
    layerDropIndicator.remove();
    layerDropIndicator = null;
  }
}

/** Toggle clipping mask for a layer. If clipped, release it; if not, clip to layer below. */
function toggleClippingForLayer(layerId: string): void {
  const doc = state.doc;
  if (!doc) return;
  const layer = findLayer(doc.layers, layerId);
  if (!layer) return;

  if (layer.clippedToBelow) {
    // Release clipping
    state.execute(new SetLayerPropertyCommand(layerId, { clippedToBelow: false }));
  } else {
    // Find the layer below (in doc.layers, which is bottom-first, the layer
    // below is at index-1). But we need to check within the same group.
    const idx = findLayerIndex(doc.layers, layerId);
    if (idx === null || idx === 0) return; // no layer below, or at bottom

    // Check if the layer below is in a different group (clipping doesn't cross groups)
    // For simplicity at root level, just clip to the layer below
    state.execute(new SetLayerPropertyCommand(layerId, { clippedToBelow: true }));
  }
}

/** Find the index of a layer in a layer list (bottom-first). Returns null if not found. */
function findLayerIndex(layers: readonly Layer[], id: string): number | null {
  for (let i = 0; i < layers.length; i++) {
    if (layers[i]!.id === id) return i;
    if (layers[i]!.kind === "group") {
      const inner = findLayerIndex((layers[i]! as import("./core/document/Layer").GroupLayer).children, id);
      if (inner !== null) return inner;
    }
  }
  return null;
}

// Inline rename for a layer.
function startRenameLayer(id: string): void {
  const row = layerListEl?.querySelector<HTMLLIElement>(`.layer[data-id="${id}"]`);
  if (!row) return;
  const nameEl = row.querySelector<HTMLDivElement>(".layer__name");
  if (!nameEl) return;
  const layer = findLayer(state.doc?.layers ?? [], id);
  if (!layer) return;

  const input = document.createElement("input");
  input.type = "text";
  input.value = layer.name;
  input.style.width = "100%";
  input.style.background = "var(--bg-input)";
  input.style.border = "1px solid var(--accent)";
  input.style.color = "var(--text)";
  input.style.fontSize = "11px";
  input.style.padding = "0 4px";
  nameEl.textContent = "";
  nameEl.appendChild(input);
  input.focus();
  input.select();

  const commit = () => {
    const newName = input.value.trim() || layer.name;
    if (newName !== layer.name) {
      state.execute(new RenameLayerCommand(id, newName));
    }
    renderLayersPanel();
  };

  input.addEventListener("blur", commit);
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") { e.preventDefault(); input.blur(); }
    if (e.key === "Escape") { input.value = layer.name; input.blur(); }
  });
}

function layerRow(l: Layer, activeId: string | null, isSelected = false, depth = 0, isClipped = false, isCollapsed = false): string {
  const isActive = l.id === activeId;
  const isGroup = l.kind === "group";
  const colorLabel = l.colorLabel && l.colorLabel !== "none" ? ` data-color="${l.colorLabel}"` : "";
  const thumb = (l.kind === "raster" || l.kind === "background")
    // i165: a document's bottom layer may be kind:"background" (opaque
    // locked Background) rather than "raster" -- it's structurally
    // compatible with RasterLayer for thumbnail rendering (same `pixels`
    // buffer), so give it a real pixel thumbnail instead of falling through
    // to the blank placeholder below.
    ? `<div class="layer__thumb" style="background:${thumbDataUrl(l as import("./core/document/Layer").RasterLayer)}"></div>`
    : l.kind === "type"
      ? `<div class="layer__thumb" style="background:var(--bg-input);display:flex;align-items:center;justify-content:center"><svg class="ico-xs"><use href="#i-type"/></svg></div>`
      : l.kind === "adjustment"
        ? `<div class="layer__thumb layer__thumb--adjustment" style="background:var(--bg-input);display:flex;align-items:center;justify-content:center"><svg class="ico-xs"><use href="#i-adjust"/></svg></div>`
        : l.kind === "smart-object"
          ? `<div class="layer__thumb layer__thumb--smartobject" style="background:var(--bg-input);display:flex;align-items:center;justify-content:center" title="Smart Object"><svg class="ico-xs"><use href="#i-smartobject"/></svg></div>`
          : l.kind === "shape"
            ? `<div class="layer__thumb layer__thumb--shape" style="background:var(--bg-input);display:flex;align-items:center;justify-content:center" title="Shape"><svg class="ico-xs"><use href="#i-shape"/></svg></div>`
            : isGroup
              ? `<div class="layer__thumb layer__thumb--group" style="background:var(--bg-input);display:flex;align-items:center;justify-content:center" title="Group"><svg class="ico-xs"><use href="#i-folder"/></svg></div>`
              : `<div class="layer__thumb"></div>`;
  const clipIndicator = isClipped
    ? `<span class="layer__clip-arrow" title="Clipped to layer below">&#x21B5;</span>`
    : "";
  // i123: a second thumbnail for the layer's mask (if any), rendered as a
  // grayscale swatch of the mask's own alpha channel.
  const mask = (l as { mask?: import("./core/document/Layer").LayerMask }).mask;
  const maskThumb = mask
    ? `<div class="layer__mask-thumb" data-mask-thumb="${l.id}" title="${mask.enabled ? "Layer mask (Shift+click to disable)" : "Layer mask (disabled — Shift+click to enable)"}" style="width:32px;height:32px;border:1px solid var(--border);flex-shrink:0;cursor:pointer;margin-left:2px;background:${maskThumbDataUrl(l.id, mask)};opacity:${mask.enabled ? 1 : 0.4}"></div>`
    : "";
  // i28: indentation stacks per nesting depth (12px/level — the same unit the
  // clip indent already used), plus the pre-existing clip-indent bump. A
  // top-level clipped layer still gets exactly the old single-level 12px.
  const indentLevels = depth + (isClipped ? 1 : 0);
  const indentStyle = indentLevels > 0 ? `style="padding-left:${indentLevels * 12}px"` : "";
  const selectedClass = isSelected ? " is-selected" : "";
  // i28: disclosure triangle for group rows; a same-width blank spacer for
  // every other row so thumbnails stay column-aligned within a depth level.
  const disclosure = isGroup
    ? `<button class="layer__disclosure" title="${isCollapsed ? "Expand group" : "Collapse group"}" style="width:14px;height:14px;flex-shrink:0;display:flex;align-items:center;justify-content:center;background:none;border:none;padding:0;color:var(--text-mute);cursor:pointer;">
        <svg class="ico-xs" style="transform:rotate(${isCollapsed ? -90 : 0}deg)"><use href="#i-chevron"/></svg>
      </button>`
    : `<span style="width:14px;flex-shrink:0;display:inline-block;"></span>`;
  return `
    <li class="layer ${isActive ? "is-active" : ""}${isClipped ? " is-clipped" : ""}${selectedClass}${isGroup ? " is-group" : ""}" data-id="${l.id}" data-depth="${depth}"${indentStyle}${colorLabel}>
      ${disclosure}
      <button class="layer__vis" title="${l.visible ? "Hide" : "Show"}">
        ${l.visible ? `<svg class="ico-xs"><use href="#i-eye"/></svg>` : ""}
      </button>
      ${thumb}
      ${maskThumb}
      ${clipIndicator}
      <div class="layer__name">${escapeHtml(l.name)}</div>
    </li>
  `;
}

// i123: mask thumbnails render the mask's own alpha channel as grayscale
// (white = fully revealed, black = fully hidden — masks store their
// coverage in the alpha channel with R/G/B always 0, see LayerMaskCommands.ts).
const maskThumbCache = new Map<string, { url: string; version: number }>();
function maskThumbDataUrl(layerId: string, mask: { pixels: PixelBuffer }): string {
  const pb = mask.pixels;
  const cached = maskThumbCache.get(layerId);
  if (cached && cached.version === pb.version) return `url(${cached.url}) center/cover`;
  const off = document.createElement("canvas");
  const sz = 22;
  off.width = sz;
  off.height = sz;
  const ctx = off.getContext("2d");
  if (!ctx) return "#000";
  const src = document.createElement("canvas");
  src.width = pb.width;
  src.height = pb.height;
  const sctx = src.getContext("2d");
  if (!sctx) return "#000";
  const id = sctx.createImageData(pb.width, pb.height);
  for (let i = 0; i < pb.data.length; i += 4) {
    const gray = pb.data[i + 3] ?? 0;
    id.data[i] = gray;
    id.data[i + 1] = gray;
    id.data[i + 2] = gray;
    id.data[i + 3] = 255;
  }
  sctx.putImageData(id, 0, 0);
  ctx.drawImage(src, 0, 0, sz, sz);
  const url = off.toDataURL();
  maskThumbCache.set(layerId, { url, version: pb.version });
  return `url(${url}) center/cover`;
}

const thumbCache = new Map<string, { url: string; version: number }>();
function thumbDataUrl(layer: RasterLayer): string {
  const cached = thumbCache.get(layer.id);
  if (cached && cached.version === layer.pixels.version) return `url(${cached.url}) center/cover`;
  const off = document.createElement("canvas");
  const sz = 22;
  off.width = sz;
  off.height = sz;
  const ctx = off.getContext("2d");
  if (!ctx) return "#444";
  const src = document.createElement("canvas");
  src.width = layer.pixels.width;
  src.height = layer.pixels.height;
  const sctx = src.getContext("2d");
  if (!sctx) return "#444";
  const id = sctx.createImageData(layer.pixels.width, layer.pixels.height);
  id.data.set(layer.pixels.data);
  sctx.putImageData(id, 0, 0);
  ctx.drawImage(src, 0, 0, sz, sz);
  const url = off.toDataURL();
  thumbCache.set(layer.id, { url, version: layer.pixels.version });
  return `url(${url}) center/cover`;
}

function showError(msg: string): void {
  modShowError(msg);
}

function showTip(msg: string): void {
  modShowTip(msg);
}

// ===================================================================
// #249: Global error handler — surface unhandled JS errors as a toast.
// ===================================================================
window.addEventListener('error', (event) => {
  const msg = event.message || 'An unexpected error occurred.';
  console.error('[Pixelpa] Unhandled error:', event.error ?? msg);
  showError('Unexpected error: ' + msg);
});

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const msg = reason instanceof Error ? reason.message : String(reason ?? 'Unknown rejection');
  console.error('[Pixelpa] Unhandled promise rejection:', reason);
  showError('Unexpected error: ' + msg);
});

// â”€â”€ Path rasterization helpers (used by PenTool & ShapeTool) â”€â”€â”€â”€â”€
// These live in main.ts (app layer) so they can use DOM APIs and
// go through the Command system for undo/redo.

function rasterizePathOnLayer(
  points: ReadonlyArray<{ x: number; y: number; leftDirection?: { x: number; y: number }; rightDirection?: { x: number; y: number } }>,
  closed: boolean,
  color: { r: number; g: number; b: number },
  lineWidth: number,
): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findRaster(doc.layers, doc.activeLayerId);
  if (!layer || !layer.visible) return;

  const canvas = document.createElement("canvas");
  canvas.width = doc.width;
  canvas.height = doc.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.strokeStyle = `rgb(${color.r},${color.g},${color.b})`;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(points[0]!.x, points[0]!.y);
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]!;
    const curr = points[i]!;
    if (prev.rightDirection && curr.leftDirection) {
      ctx.bezierCurveTo(
        prev.rightDirection.x, prev.rightDirection.y,
        curr.leftDirection.x, curr.leftDirection.y,
        curr.x, curr.y,
      );
    } else {
      ctx.lineTo(curr.x, curr.y);
    }
  }
  if (closed && points.length > 2) {
    const last = points[points.length - 1]!;
    const first = points[0]!;
    if (last.rightDirection && first.leftDirection) {
      ctx.bezierCurveTo(
        last.rightDirection.x, last.rightDirection.y,
        first.leftDirection.x, first.leftDirection.y,
        first.x, first.y,
      );
    } else {
      ctx.lineTo(first.x, first.y);
    }
    ctx.closePath();
  }
  ctx.stroke();

  // Compute the bounding box of the painted area.
  let minX = doc.width, minY = doc.height, maxX = 0, maxY = 0;
  const imgData = ctx.getImageData(0, 0, doc.width, doc.height);
  for (let y = 0; y < doc.height; y++) {
    for (let x = 0; x < doc.width; x++) {
      const a = imgData.data[(y * doc.width + x) * 4 + 3];
      if (a && a > 0) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < minX || maxY < minY) return;

  // Capture before snapshot, apply, capture after, emit command.
  const bbox = { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
  const before = snapshotRegion(layer, bbox);

  const layerData = layer.pixels.data;
  const sel = (doc as any).selection as { mask?: Uint8Array } | null;
  const selMask = sel?.mask;
  for (let i = 0; i < imgData.data.length; i += 4) {
    if (imgData.data[i + 3]! > 0) {
      // Selection masking
      if (selMask) {
        const px = (i / 4) % doc.width;
        const py = Math.floor((i / 4) / doc.width);
        if (selMask[py * doc.width + px] === 0) continue;
      }
      layerData[i] = imgData.data[i]!;
      layerData[i + 1] = imgData.data[i + 1]!;
      layerData[i + 2] = imgData.data[i + 2]!;
      layerData[i + 3] = imgData.data[i + 3]!;
    }
  }
  layer.pixels.markDirty();

  const after = snapshotRegion(layer, bbox);
  state.execute(new PaintStrokeCommand(layer.id, bbox, before, after));
}

function rasterizeShapeOnLayer(
  path: any,
  _mode: string,
  color: { r: number; g: number; b: number },
  lineWidth: number,
): void {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findRaster(doc.layers, doc.activeLayerId);
  if (!layer || !layer.visible) return;

  const canvas = document.createElement("canvas");
  canvas.width = doc.width;
  canvas.height = doc.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.fillStyle = `rgb(${color.r},${color.g},${color.b})`;
  ctx.strokeStyle = `rgb(${color.r},${color.g},${color.b})`;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  for (const subpath of path.subpaths) {
    const pts = subpath.points;
    if (pts.length < 2) continue;
    ctx.beginPath();
    ctx.moveTo(pts[0]!.x, pts[0]!.y);
    for (let i = 1; i < pts.length; i++) {
      const prev = pts[i - 1]!;
      const pt = pts[i]!;
      if (prev.rightDirection && pt.leftDirection) {
        ctx.bezierCurveTo(
          prev.rightDirection.x, prev.rightDirection.y,
          pt.leftDirection.x, pt.leftDirection.y,
          pt.x, pt.y
        );
      } else {
        ctx.lineTo(pt.x, pt.y);
      }
    }
    if (subpath.closed) ctx.closePath();
    if (_mode !== "line") ctx.fill();
    ctx.stroke();
  }

  // Compute bbox
  let minX = doc.width, minY = doc.height, maxX = 0, maxY = 0;
  const imgData = ctx.getImageData(0, 0, doc.width, doc.height);
  for (let y = 0; y < doc.height; y++) {
    for (let x = 0; x < doc.width; x++) {
      const a = imgData.data[(y * doc.width + x) * 4 + 3];
      if (a && a > 0) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < minX || maxY < minY) return;

  const bbox = { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
  const before = snapshotRegion(layer, bbox);

  const layerData = layer.pixels.data;
  for (let i = 0; i < imgData.data.length; i += 4) {
    if (imgData.data[i + 3]! > 0) {
      layerData[i] = imgData.data[i]!;
      layerData[i + 1] = imgData.data[i + 1]!;
      layerData[i + 2] = imgData.data[i + 2]!;
      layerData[i + 3] = imgData.data[i + 3]!;
    }
  }
  layer.pixels.markDirty();

  const after = snapshotRegion(layer, bbox);
  state.execute(new PaintStrokeCommand(layer.id, bbox, before, after));
}

updateStatusBar();
updateSwatches();
updateDropHint();
updateCursor();
// Startup tip
showTip("Select a tool (B=Brush) and paint on the canvas. Pick colors from the swatch.");
// Load system fonts for Character panel
import("./app/ui/CharacterPanel").then(({ loadSystemFonts }) => {
  loadSystemFonts().then((fonts) => {
    if (charPanel) charPanel.setFonts(fonts);
  });
});

// â”€â”€ Layer Comps Panel â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
let layerCompsPanelOpen = false;

function showLayerCompsPanel(): void {
  if (layerCompsPanelOpen) return;
  layerCompsPanelOpen = true;

  const overlay = document.createElement("div");
  overlay.className = "filter-dialog";
  overlay.id = "layer-comps-panel";
  overlay.innerHTML = `
    <div class="filter-dialog__sheet" style="max-width:360px">
      <div class="filter-dialog__title">Layer Comps</div>
      <div class="filter-dialog__body" style="padding:8px 0;max-height:400px;overflow-y:auto">
        <div id="layer-comps-list"></div>
      </div>
      <div class="filter-dialog__actions">
        <button class="filter-dialog__btn" id="comps-new">New</button>
        <button class="filter-dialog__btn" id="comps-update" title="Update selected comp">Update</button>
        <button class="filter-dialog__btn" id="comps-apply" title="Apply selected comp">Apply</button>
        <button class="filter-dialog__btn" id="comps-delete" title="Delete selected comp">Delete</button>
        <button class="filter-dialog__btn filter-dialog__btn--primary" id="comps-close">Close</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  let selectedCompId: string | null = null;

  function renderList(): void {
    const list = document.getElementById("layer-comps-list");
    if (!list) return;
    const comps = state.layerComps;
    if (comps.length === 0) {
      list.innerHTML = '<div class="muted" style="padding:12px;text-align:center">No layer comps. Click New to create one.</div>';
      return;
    }
    list.innerHTML = comps.map((c) => `
      <div class="layer-comp-row${c.id === selectedCompId ? " is-selected" : ""}" data-id="${escapeHtml(c.id)}" style="padding:6px 8px;cursor:pointer;border-bottom:1px solid var(--bg-secondary);font-size:12px;display:flex;align-items:center;gap:8px">
        <span style="flex:1">${escapeHtml(c.name)}</span>
        <span class="muted" style="font-size:10px">${c.visibility.size} layers</span>
      </div>
    `).join("");

    list.querySelectorAll(".layer-comp-row").forEach((row) => {
      row.addEventListener("click", () => {
        selectedCompId = row.getAttribute("data-id");
        renderList();
      });
      row.addEventListener("dblclick", () => {
        const id = row.getAttribute("data-id");
        if (id) { state.applyLayerComp(id); showTip("Layer comp applied."); }
      });
    });
  }

  renderList();

  document.getElementById("comps-new")?.addEventListener("click", async () => {
    const name = prompt("Layer Comp name:", `Comp ${state.layerComps.length + 1}`);
    if (!name) return;
    state.createLayerComp(name);
    selectedCompId = state.layerComps[state.layerComps.length - 1]?.id ?? null;
    renderList();
  });

  document.getElementById("comps-update")?.addEventListener("click", () => {
    if (!selectedCompId) { showTip("Select a comp to update."); return; }
    state.updateLayerComp(selectedCompId);
    showTip("Layer comp updated.");
    renderList();
  });

  document.getElementById("comps-apply")?.addEventListener("click", () => {
    if (!selectedCompId) { showTip("Select a comp to apply."); return; }
    state.applyLayerComp(selectedCompId);
    showTip("Layer comp applied.");
  });

  document.getElementById("comps-delete")?.addEventListener("click", () => {
    if (!selectedCompId) { showTip("Select a comp to delete."); return; }
    state.deleteLayerComp(selectedCompId);
    selectedCompId = null;
    renderList();
  });

  document.getElementById("comps-close")?.addEventListener("click", () => {
    overlay.remove();
    layerCompsPanelOpen = false;
  });

  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) { overlay.remove(); layerCompsPanelOpen = false; }
  });
}

// â”€â”€ BATCH MMM: Help Menu â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function showAbout(): void {
  const overlay = document.createElement("div");
  overlay.className = "filter-dialog";
  overlay.innerHTML = `
    <div class="filter-dialog__sheet" style="max-width:420px">
      <div class="filter-dialog__title">About Pixelpa</div>
      <div class="filter-dialog__body" style="padding:12px 0;text-align:center">
        <div style="font-size:24px;font-weight:700;margin-bottom:4px">Pixelpa</div>
        <div style="font-size:12px;color:var(--text-dim);margin-bottom:16px">Version 0.0.1 (2026-05-16)</div>
        <div style="font-size:12px;color:var(--text-dim);margin-bottom:16px;line-height:1.6">
          A web-based image editor inspired by Photoshop.<br>
          Built with TypeScript, Canvas 2D, and Vite.
        </div>
        <div style="font-size:11px;color:var(--text-dim);border-top:1px solid var(--bg-secondary);padding-top:12px">
          <strong>Credits</strong><br>
          PSD parser, filter pipeline, selection model,<br>
          tool framework, layer system, and UI shell<br>
          developed by the Pixelpa team.
        </div>
        <div style="font-size:10px;color:var(--text-dim);margin-top:12px">
          Â© 2026 Pixelpa. All rights reserved.
        </div>
      </div>
      <div class="filter-dialog__actions">
        <button class="filter-dialog__btn filter-dialog__btn--primary" data-action="ok">OK</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.querySelector('[data-action="ok"]')?.addEventListener("click", () => overlay.remove());
  overlay.addEventListener("click", (e) => { if (e.target === overlay) overlay.remove(); });
}

function openTutorials(): void {
  const overlay = document.createElement("div");
  overlay.className = "filter-dialog";
  overlay.innerHTML = `
    <div class="filter-dialog__sheet" style="max-width:480px">
      <div class="filter-dialog__title">Tutorials & Documentation</div>
      <div class="filter-dialog__body" style="padding:12px 0">
        <div style="font-size:12px;color:var(--text-dim);margin-bottom:12px">Getting started with Pixelpa:</div>
        <div style="font-size:12px;line-height:2">
          <div>ðŸ“– <strong>Getting Started</strong> â€” Open images, use tools, save files</div>
          <div>ðŸŽ¨ <strong>Layers & Masks</strong> â€” Non-destructive editing workflow</div>
          <div>ðŸ”§ <strong>Filters & Adjustments</strong> â€” 60+ filters and adjustment layers</div>
          <div>âœ‚ï¸ <strong>Selections</strong> â€” Marquee, Lasso, Magic Wand, Quick Selection</div>
          <div>ðŸ–Œï¸ <strong>Brushes & Painting</strong> â€” Brush settings, dynamics, presets</div>
          <div>ðŸ“ <strong>Transform & Crop</strong> â€” Free Transform, Crop, Image Size</div>
          <div>ðŸŽ­ <strong>Screen Modes</strong> â€” Press F to cycle display modes</div>
          <div>âŒ¨ï¸ <strong>Keyboard Shortcuts</strong> â€” Ctrl+Alt+Shift+K to customize</div>
        </div>
        <div style="font-size:11px;color:var(--text-dim);margin-top:12px;padding-top:8px;border-top:1px solid var(--bg-secondary)">
          Tip: Most menu items have keyboard shortcuts shown in the menu.
        </div>
      </div>
      <div class="filter-dialog__actions">
        <button class="filter-dialog__btn filter-dialog__btn--primary" data-action="close">Close</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.querySelector('[data-action="close"]')?.addEventListener("click", () => overlay.remove());
  overlay.addEventListener("click", (e) => { if (e.target === overlay) overlay.remove(); });
}

function showWhatsNew(): void {
  const overlay = document.createElement("div");
  overlay.className = "filter-dialog";
  overlay.innerHTML = `
    <div class="filter-dialog__sheet" style="max-width:500px;max-height:500px">
      <div class="filter-dialog__title">What's New in Pixelpa 0.0.1</div>
      <div class="filter-dialog__body" style="padding:12px 0;max-height:400px;overflow-y:auto;font-size:12px;line-height:1.8">
        <div style="margin-bottom:8px"><strong>Tools (30+)</strong></div>
        <div>Move, Marquee, Lasso, Polygonal Lasso, Magic Wand, Quick Selection,</div>
        <div>Crop, Eyedropper, Brush, Eraser, Clone Stamp, Healing Brush, Spot Healing,</div>
        <div>Patch, Red Eye, Gradient, Paint Bucket, Blur/Sharpen/Smudge,</div>
        <div>Dodge/Burn/Sponge, Pen, Freeform Pen, Curvature Pen, Type,</div>
        <div>Rectangle, Ellipse, Polygon, Rounded Rectangle shapes,</div>
        <div>Hand, Zoom, Pencil, Mixer Brush, Color Replacement,</div>
        <div>Background Eraser, Magic Eraser, History Brush, Pattern Stamp,</div>
        <div>Color Sampler, Ruler, Note, Count, Direct Selection</div>

        <div style="margin-top:8px"><strong>Filters (60+)</strong></div>
        <div>Blur: Gaussian, Motion, Box, Average, Surface, Smart, Lens, Radial, Shape, Field, Iris, Tilt-Shift</div>
        <div>Sharpen: Sharpen, Sharpen More, Unsharp Mask</div>
        <div>Noise: Add Noise, Median, Dust & Scratches</div>
        <div>Distort: Twirl, Pinch, Spherize, Displace, Ripple, Shear, Wave, ZigZag, Polar Coordinates</div>
        <div>Pixelate: Mosaic, Crystallize, Facet, Fragment, Mezzotint, Pointillize</div>
        <div>Stylize: Find Edges, Emboss, Solarize, Tiles, Trace Contour, Wind, Glowing Edges</div>
        <div>Render: Clouds, Difference Clouds, Fibers, Lens Flare, Lighting Effects, Picture Frame</div>
        <div>Other: High Pass, Diffuse, Extrude, Offset</div>
        <div>Adjustments: Levels, Curves, Brightness/Contrast, Hue/Saturation, Color Balance,</div>
        <div>Black & White, Vibrance, Exposure, Photo Filter, Channel Mixer, Color Lookup,</div>
        <div>Selective Color, Shadows/Highlights, Auto Tone/Contrast/Color, Invert, Posterize, Threshold, Gradient Map</div>

        <div style="margin-top:8px"><strong>Layer Features</strong></div>
        <div>Smart Objects, Smart Filters, Adjustment Layers, Layer Groups,</div>
        <div>Blend Modes (27), Opacity/Fill, Lock options, Clipping Masks,</div>
        <div>Blend If, Layer Comps, Merge Down/Visible, Flatten</div>

        <div style="margin-top:8px"><strong>Selection</strong></div>
        <div>Quick Mask mode, Select All/Deselect/Inverse/Reselect,</div>
        <div>Border/Smooth/Expand/Contract/Feather, Grow, Similar, Color Range, Select and Mask</div>

        <div style="margin-top:8px"><strong>File I/O</strong></div>
        <div>Open: PNG, JPEG, WebP, AVIF, GIF, BMP, ICO, PSD/PSB</div>
        <div>Save/Export: PNG, JPEG, WebP, BMP, GIF, ICO, PSD</div>

        <div style="margin-top:8px"><strong>UI</strong></div>
        <div>Dark/Light theme, Workspaces, Floating panels, Rulers, Guides, Grid,</div>
        <div>Snap-to system, Screen modes (Standard/Full Menu/Full Screen),</div>
        <div>Keyboard shortcuts editor, History panel, Navigator, Info, Histogram</div>
      </div>
      <div class="filter-dialog__actions">
        <button class="filter-dialog__btn filter-dialog__btn--primary" data-action="close">Close</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.querySelector('[data-action="close"]')?.addEventListener("click", () => overlay.remove());
  overlay.addEventListener("click", (e) => { if (e.target === overlay) overlay.remove(); });
}

// â”€â”€ BATCH NNN: Arrange Menu (Window > Arrange) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface OpenDocument {
  id: string;
  name: string;
  doc: Document;
  viewport: { zoom: number; panX: number; panY: number };
}

let openDocuments: OpenDocument[] = [];
let arrangeMode: "tabs" | "tile" | "cascade" | "float" = "tabs";

// i44: keyed by the document's stable `id`, not its display `name` -- two
// open documents that happen to share a name (two "Untitled-1"s, a
// duplicate, a file re-opened after being renamed to match another open
// document) must never be conflated at the tab layer, mirroring
// EditorState's per-document History (#209), which is already keyed by
// `id`. `name` is kept purely for display (the tab label) and is refreshed
// on rename below.
function registerOpenDocument(doc: Document): void {
  // Stamp a fallback id in place (same technique EditorState._keyFor uses,
  // Document.id being only `readonly` at the type level) for any
  // construction site that still omits one, so this registry never has to
  // fall back to name-based matching.
  let id = doc.id;
  if (!id) {
    id = crypto.randomUUID();
    (doc as { id?: string }).id = id;
  }
  const existing = openDocuments.findIndex(d => d.id === id);
  if (existing >= 0) {
    openDocuments[existing] = { id, name: doc.name, doc, viewport: { ...state.viewport } };
  } else {
    openDocuments.push({ id, name: doc.name, doc, viewport: { ...state.viewport } });
  }
}

function removeClosedDocument(id: string): void {
  openDocuments = openDocuments.filter(d => d.id !== id);
  // #183: free the per-document history so it can be GC'd.
  state.dropDocumentHistory(id);
}

function tileDocuments(): void {
  if (openDocuments.length === 0) return;
  arrangeMode = "tile";
  showTip("Tile mode: Use document tabs to switch between open documents.");
  updateDocTabs();
}

function cascadeDocuments(): void {
  if (openDocuments.length === 0) return;
  arrangeMode = "cascade";
  showTip("Cascade mode: Documents are offset for visibility.");
  updateDocTabs();
}

function floatDocument(): void {
  if (!state.doc) return;
  arrangeMode = "float";
  showTip(`"${state.doc.name}" floated in its own window.`);
}

function consolidateTabs(): void {
  arrangeMode = "tabs";
  showTip("All documents consolidated to tabbed view.");
  updateDocTabs();
}

function updateDocTabs(): void {
  const tabs = document.querySelector(".doc-tabs");
  if (!tabs || !state.doc) return;

  if (openDocuments.length <= 1) {
    updateDocTab();
    return;
  }

  let html = "";
  for (const od of openDocuments) {
    // i44: compare by id, not name -- two tabs can legitimately share a
    // display name without being the same open document.
    const isActive = od.id === state.doc.id;
    html += `<div class="doc-tab${isActive ? " is-active" : ""}" data-doc="${escapeHtml(od.id)}">
      ${escapeHtml(od.name)}
      <span class="doc-tab__close" title="Close">Ã—</span>
    </div>`;
  }
  tabs.innerHTML = html;

  tabs.querySelectorAll(".doc-tab").forEach((tab) => {
    tab.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).classList.contains("doc-tab__close")) {
        const docId = tab.getAttribute("data-doc");
        if (docId && docId === state.doc?.id) {
          closeDoc();
        } else {
          removeClosedDocument(docId || "");
          updateDocTabs();
        }
        return;
      }
      const docId = tab.getAttribute("data-doc");
      if (docId) {
        const od = openDocuments.find(d => d.id === docId);
        if (od && od.doc !== state.doc) {
          // #183: use switchDocument (not state.execute) so the tab-switch
          // is never recorded in either document's undo history.
          state.switchDocument(od.doc);
          state.setViewport(od.viewport);
          // i149: tell the autosave manager which document is now active
          // (keyed the same way as History/openDocuments — by doc name) so
          // its debounced writes land under the newly-active document's own
          // IDB snapshot instead of the one just switched away from.
          _autosaveManager?.setActiveDocId(od.name);
          fitDoc();
        }
      }
    });
  });
}

// â”€â”€ BATCH OOO: Remaining Filter Dialogs â€” delegated to filterDialogs module â”€â”€

async function promptBrightnessContrast(): Promise<void> { return FD.promptBrightnessContrast(filterDeps()); }
async function promptOilPaint(): Promise<void> { return FD.promptOilPaint(filterDeps()); }
async function promptOffset(): Promise<void> { return FD.promptOffset(filterDeps()); }
async function promptMaximum(): Promise<void> { return FD.promptMaximum(filterDeps()); }
async function promptMinimum(): Promise<void> { return FD.promptMinimum(filterDeps()); }
async function promptHSBHSL(): Promise<void> { return FD.promptHSBHSL(filterDeps()); }
function promptReduceNoise(): void { FD.promptReduceNoise(filterDeps()); }
async function promptSmartSharpen(): Promise<void> { return FD.promptSmartSharpen(filterDeps()); }
// New filter features (closes #31)
function promptSharpenEdges(): void { FD.promptSharpenEdges(filterDeps()); }
async function promptCameraRawFilter(): Promise<void> { return FD.promptCameraRawFilter(filterDeps()); }
async function promptLiquify(): Promise<void> { return FD.promptLiquify(filterDeps()); }

async function promptLensBlurGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Lens Blur", [
    { key: "radius", label: "Radius", type: "slider", default: 15, min: 0, max: 100, unit: "px" },
    { key: "bladeCurvature", label: "Blade Curvature", type: "slider", default: 50, min: 0, max: 100, unit: "%" },
    { key: "rotation", label: "Rotation", type: "slider", default: 0, min: 0, max: 360, unit: "Â°" },
  ], (r) => {
    applyFilter(LensBlur, { radius: r.radius as number, bladeCurvature: r.bladeCurvature as number, rotation: r.rotation as number });
  });
}

async function promptRadialBlurGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Radial Blur", [
    { key: "amount", label: "Amount", type: "slider", default: 10, min: 1, max: 100 },
    { key: "method", label: "Method", type: "dropdown", default: "spin", options: [
      { value: "spin", label: "Spin" },
      { value: "zoom", label: "Zoom" },
    ]},
    { key: "quality", label: "Quality", type: "dropdown", default: "good", options: [
      { value: "draft", label: "Draft" },
      { value: "good", label: "Good" },
      { value: "best", label: "Best" },
    ]},
  ], (r) => {
    applyFilter(RadialBlur, { amount: r.amount as number, mode: r.method as any });
  });
}

async function promptWaveGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Wave", [
    { key: "numGenerators", label: "Number of Generators", type: "slider", default: 1, min: 1, max: 999 },
    { key: "minWavelength", label: "Min Wavelength", type: "slider", default: 10, min: 1, max: 999, unit: "px" },
    { key: "maxWavelength", label: "Max Wavelength", type: "slider", default: 100, min: 1, max: 999, unit: "px" },
    { key: "minAmplitude", label: "Min Amplitude", type: "slider", default: 5, min: 1, max: 999, unit: "px" },
    { key: "maxAmplitude", label: "Max Amplitude", type: "slider", default: 50, min: 1, max: 999, unit: "px" },
    { key: "type", label: "Type", type: "dropdown", default: "sine", options: [
      { value: "sine", label: "Sine" },
      { value: "triangle", label: "Triangle" },
      { value: "square", label: "Square" },
    ]},
  ], (r) => {
    applyFilter(Wave, {
      numGenerators: r.numGenerators as number,
      wavelength: r.maxWavelength as number,
      amplitude: r.maxAmplitude as number,
      scale: 100,
    });
  });
}

async function promptShearGallery(): Promise<void> {
  if (!state.doc) return;
  showTip("Shear: Drag points on the graph to define the curve. (Simplified: applying default shear)");
  applyFilter(Shear, { points: [{ x: 0, y: 0 }, { x: 128, y: 64 }, { x: 255, y: 0 }], undefinedAreas: "wrap" });
}

async function promptCrystallizeGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Crystallize", [
    { key: "cellSize", label: "Cell Size", type: "slider", default: 15, min: 3, max: 300, unit: "px" },
  ], (r) => {
    applyFilter(Crystallize, { cellSize: r.cellSize as number });
  });
}

async function promptFacetGallery(): Promise<void> {
  if (!state.doc) return;
  applyFilter(Facet, undefined);
}

async function promptFragmentGallery(): Promise<void> {
  if (!state.doc) return;
  applyFilter(Fragment, undefined);
}

async function promptMezzotintGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Mezzotint", [
    { key: "type", label: "Type", type: "dropdown", default: "fine", options: [
      { value: "fine", label: "Fine Dots" },
      { value: "medium", label: "Medium Dots" },
      { value: "coarse", label: "Coarse Dots" },
      { value: "stipple", label: "Stipple" },
      { value: "fineLines", label: "Fine Lines" },
      { value: "mediumLines", label: "Medium Lines" },
      { value: "coarseLines", label: "Coarse Lines" },
      { value: "shortLines", label: "Short Strokes" },
      { value: "longLines", label: "Long Strokes" },
      { value: "shortLines2", label: "Short Lines" },
    ]},
  ], (r) => {
    applyFilter(Mezzotint, { type: r.type as any });
  });
}

async function promptPointillizeGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Pointillize", [
    { key: "dotSize", label: "Dot Size", type: "slider", default: 6, min: 3, max: 50, unit: "px" },
  ], (r) => {
    applyFilter(Pointillize, { dotSize: r.dotSize as number });
  });
}

async function promptDiffuseGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Diffuse", [
    { key: "mode", label: "Mode", type: "dropdown", default: "normal", options: [
      { value: "normal", label: "Normal" },
      { value: "anisotropic", label: "Anisotropic" },
    ]},
  ], (r) => {
    applyFilter(Diffuse, { mode: r.mode as any, intensity: 50 });
  });
}

async function promptExtrudeGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Extrude", [
    { key: "type", label: "Type", type: "dropdown", default: "blocks", options: [
      { value: "blocks", label: "Blocks" },
      { value: "pyramids", label: "Pyramids" },
    ]},
    { key: "size", label: "Size", type: "slider", default: 16, min: 2, max: 255, unit: "px" },
    { key: "depth", label: "Depth", type: "slider", default: 3, min: 1, max: 255, unit: "" },
  ], (r) => {
    const typeVal = String(r.type);
    applyFilter(Extrude, { type: typeVal === "blocks" ? "block" as any : "pyramid" as any, size: r.size as number, depth: r.depth as number, random: false });
  });
}

async function promptSolarizeGallery(): Promise<void> {
  if (!state.doc) return;
  applyFilter(Solarize, undefined);
}

async function promptTilesGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Tiles", [
    { key: "numTiles", label: "Number of Tiles", type: "slider", default: 6, min: 1, max: 50 },
    { key: "maximumOffset", label: "Maximum Offset", type: "slider", default: 25, min: 0, max: 255, unit: "%" },
  ], (r) => {
    applyFilter(Tiles, { tileSize: r.numTiles as number, offset: r.maximumOffset as number, fill: "wrap" });
  });
}

async function promptTraceContourGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Trace Contour", [
    { key: "edge", label: "Edge", type: "slider", default: 10, min: 1, max: 100 },
    { key: "simplify", label: "Simplify", type: "slider", default: 1, min: 1, max: 10 },
  ], (r) => {
    applyFilter(TraceContour, { level: r.edge as number, direction: "lighter" });
  });
}

async function promptWindGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Wind", [
    { key: "method", label: "Method", type: "dropdown", default: "wind", options: [
      { value: "wind", label: "Wind" },
      { value: "blast", label: "Blast" },
      { value: "stagger", label: "Stagger" },
    ]},
    { key: "direction", label: "Direction", type: "dropdown", default: "from-right", options: [
      { value: "from-right", label: "From the Right" },
      { value: "from-left", label: "From the Left" },
    ]},
  ], (r) => {
    const methodVal = String(r.method);
    const dirVal = String(r.direction);
    applyFilter(Wind, { method: methodVal === "stagger" ? "staggered" as any : methodVal as any, direction: dirVal === "from-right" ? "right" : "left", intensity: 50 });
  });
}

async function promptGlowingEdgesGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Glowing Edges", [
    { key: "edgeWidth", label: "Edge Width", type: "slider", default: 3, min: 1, max: 20 },
    { key: "edgeBrightness", label: "Edge Brightness", type: "slider", default: 10, min: 1, max: 20 },
    { key: "smoothness", label: "Smoothness", type: "slider", default: 5, min: 1, max: 15 },
  ], (r) => {
    applyFilter(GlowingEdges, { edgeWidth: r.edgeWidth as number, edgeBrightness: r.edgeBrightness as number, smoothness: r.smoothness as number });
  });
}

async function promptFibersGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Fibers", [
    { key: "variance", label: "Variance", type: "slider", default: 16, min: 1, max: 100 },
    { key: "strength", label: "Strength", type: "slider", default: 4, min: 1, max: 100 },
  ], (r) => {
    applyFilter(Fibers, { variance: r.variance as number, strength: r.strength as number });
  });
}

async function promptLensFlareGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Lens Flare", [
    { key: "brightness", label: "Brightness", type: "slider", default: 100, min: 0, max: 300, unit: "%" },
    { key: "lensType", label: "Lens Type", type: "dropdown", default: "50-300mm", options: [
      { value: "50-300mm", label: "50-300mm Zoom" },
      { value: "35mm", label: "35mm Prime" },
      { value: "105mm", label: "105mm Prime" },
      { value: "movie", label: "Movie Prime" },
    ]},
  ], (r) => {
    const lensTypeMap: Record<string, number> = { "50-300mm": 1, "35mm": 2, "105mm": 3, "movie": 4 };
    const lensTypeVal = String(r.lensType);
    applyFilter(LensFlare, { brightness: r.brightness as number, lensType: lensTypeMap[lensTypeVal] ?? 1, positionX: 50, positionY: 50 });
  });
}

async function promptLightingEffectsGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Lighting Effects", [
    { key: "style", label: "Style", type: "dropdown", default: "default", options: [
      { value: "default", label: "Default" },
      { value: "blue", label: "Blue" },
      { value: "crossing", label: "Crossing" },
      { value: "down", label: "Down" },
      { value: "left", label: "Left" },
      { value: "right", label: "Right" },
      { value: "sunrise", label: "Sunrise" },
      { value: "top", label: "Top" },
      { value: "three-point", label: "Three-Point" },
      { value: "two-o-clock", label: "Two O'Clock" },
    ]},
    { key: "lightType", label: "Light Type", type: "dropdown", default: "spot", options: [
      { value: "spot", label: "Spot" },
      { value: "point", label: "Point" },
      { value: "infinite", label: "Infinite" },
    ]},
    { key: "intensity", label: "Intensity", type: "slider", default: 50, min: 0, max: 100 },
    { key: "ambient", label: "Ambient", type: "slider", default: 30, min: 0, max: 100 },
  ], (r) => {
    applyFilter(LightingEffects, { lightType: r.lightType as any, color: { r: 255, g: 255, b: 255 }, intensity: r.intensity as number, ambient: r.ambient as number, positionX: 50, positionY: 50, height: 50 });
  });
}

async function promptPictureFrameGallery(): Promise<void> {
  if (!state.doc) return;
  openFilterDialog("Picture Frame", [
    { key: "frameStyle", label: "Style", type: "slider", default: 1, min: 1, max: 3, unit: "" },
    { key: "frameWidth", label: "Width", type: "slider", default: 20, min: 1, max: 100, unit: "px" },
  ], (r) => {
    applyFilter(PictureFrame, { frameStyle: r.frameStyle as number, frameWidth: r.frameWidth as number, frameColor: { r: 139, g: 90, b: 43 } });
  });
}

// â”€â”€ BATCH PPP: Quick Mask Mode (verify and enhance) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Quick Mask is already implemented in the Q key handler and
// state.setQuickMask. Double-click the quick mask indicator for options.

let quickMaskOptions = {
  color: { r: 255, g: 0, b: 0 },
  opacity: 50,
  indicate: "selected" as "selected" | "masked",
};

async function promptQuickMaskOptions(): Promise<void> {
  const result = await filterDialog.open("Quick Mask Options", [
    { key: "indicate", label: "Color Indicates", type: "dropdown", default: quickMaskOptions.indicate, options: [
      { value: "masked", label: "Masked Areas" },
      { value: "selected", label: "Selected Areas" },
    ]},
    { key: "opacity", label: "Opacity", type: "slider", default: quickMaskOptions.opacity, min: 0, max: 100, unit: "%" },
  ]);
  if (!result) return;
  quickMaskOptions.indicate = result.indicate as any;
  quickMaskOptions.opacity = result.opacity as number;
  renderer.setQuickMaskOptions(quickMaskOptions);
  showTip("Quick Mask options updated.");
}

// â”€â”€ BATCH QQQ: Screen Modes (enhance) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Screen mode cycling is already wired to F key.
// The View > Screen Mode menu items are already wired.
// Enhance: update menu checkmarks based on current mode.

// â”€â”€ BATCH RRR: File Info / XMP â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface FileInfo {
  description: string;
  author: string;
  copyright: string;
  keywords: string[];
  headline: string;
  instructions: string;
  credit: string;
  source: string;
  creationDate: string;
  modificationDate: string;
  xmpRaw: string;
}

let currentFileInfo: FileInfo | null = null;

async function showFileInfo(): Promise<void> {
  if (!state.doc) return;

  const info = currentFileInfo || {
    description: "", author: "", copyright: "", keywords: [],
    headline: "", instructions: "", credit: "", source: "",
    creationDate: new Date().toISOString(),
    modificationDate: new Date().toISOString(),
    xmpRaw: "",
  };

  const overlay = document.createElement("div");
  overlay.className = "filter-dialog";
  overlay.innerHTML = `
    <div class="filter-dialog__sheet" style="max-width:560px;max-height:600px">
      <div class="filter-dialog__title">File Info â€” ${escapeHtml(state.doc.name)}</div>
      <div class="filter-dialog__body" style="padding:8px 0;max-height:480px;overflow-y:auto">
        <div style="margin-bottom:12px">
          <div style="font-size:11px;font-weight:600;color:var(--text-dim);text-transform:uppercase;margin-bottom:6px">Description</div>
          <div style="font-size:12px;display:flex;flex-direction:column;gap:6px">
            <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Document Title
              <input id="fi-title" type="text" value="${escapeHtml(info.description)}" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px" />
            </label>
            <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Author
              <input id="fi-author" type="text" value="${escapeHtml(info.author)}" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px" />
            </label>
            <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Copyright
              <input id="fi-copyright" type="text" value="${escapeHtml(info.copyright)}" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px" />
            </label>
            <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Keywords (comma-separated)
              <input id="fi-keywords" type="text" value="${escapeHtml(info.keywords.join(", "))}" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px" />
            </label>
          </div>
        </div>
        <div style="margin-bottom:12px">
          <div style="font-size:11px;font-weight:600;color:var(--text-dim);text-transform:uppercase;margin-bottom:6px">Origin</div>
          <div style="font-size:12px;color:var(--text-dim)">
            <div>Created: ${info.creationDate}</div>
            <div>Modified: ${info.modificationDate}</div>
            <div>Dimensions: ${state.doc.width} Ã— ${state.doc.height} px</div>
          </div>
        </div>
        <div style="margin-bottom:12px">
          <div style="font-size:11px;font-weight:600;color:var(--text-dim);text-transform:uppercase;margin-bottom:6px">IPTC</div>
          <div style="font-size:12px;display:flex;flex-direction:column;gap:6px">
            <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Headline
              <input id="fi-headline" type="text" value="${escapeHtml(info.headline)}" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px" />
            </label>
            <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Instructions
              <input id="fi-instructions" type="text" value="${escapeHtml(info.instructions)}" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px" />
            </label>
            <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Credit
              <input id="fi-credit" type="text" value="${escapeHtml(info.credit)}" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px" />
            </label>
            <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Source
              <input id="fi-source" type="text" value="${escapeHtml(info.source)}" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px" />
            </label>
          </div>
        </div>
        <div>
          <div style="font-size:11px;font-weight:600;color:var(--text-dim);text-transform:uppercase;margin-bottom:6px">Raw XMP</div>
          <textarea id="fi-xmp" style="width:100%;min-height:80px;background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:11px;font-family:monospace;resize:vertical" placeholder="<x:xmpmeta>...</x:xmpmeta>">${escapeHtml(info.xmpRaw)}</textarea>
        </div>
      </div>
      <div class="filter-dialog__actions">
        <button class="filter-dialog__btn" data-action="cancel">Cancel</button>
        <button class="filter-dialog__btn filter-dialog__btn--primary" data-action="ok">OK</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  overlay.querySelector('[data-action="ok"]')?.addEventListener("click", () => {
    currentFileInfo = {
      description: (overlay.querySelector("#fi-title") as HTMLInputElement)?.value || "",
      author: (overlay.querySelector("#fi-author") as HTMLInputElement)?.value || "",
      copyright: (overlay.querySelector("#fi-copyright") as HTMLInputElement)?.value || "",
      keywords: ((overlay.querySelector("#fi-keywords") as HTMLInputElement)?.value || "").split(",").map(s => s.trim()).filter(Boolean),
      headline: (overlay.querySelector("#fi-headline") as HTMLInputElement)?.value || "",
      instructions: (overlay.querySelector("#fi-instructions") as HTMLInputElement)?.value || "",
      credit: (overlay.querySelector("#fi-credit") as HTMLInputElement)?.value || "",
      source: (overlay.querySelector("#fi-source") as HTMLInputElement)?.value || "",
      creationDate: info.creationDate,
      modificationDate: new Date().toISOString(),
      xmpRaw: (overlay.querySelector("#fi-xmp") as HTMLTextAreaElement)?.value || "",
    };
    overlay.remove();
    showTip("File Info saved.");
  });

  overlay.querySelector('[data-action="cancel"]')?.addEventListener("click", () => overlay.remove());
  overlay.addEventListener("click", (e) => { if (e.target === overlay) overlay.remove(); });
}

// â”€â”€ BATCH SSS: Print â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function showPrintDialog(): Promise<void> {
  if (!state.doc) return;

  const overlay = document.createElement("div");
  overlay.className = "filter-dialog";
  overlay.innerHTML = `
    <div class="filter-dialog__sheet" style="max-width:500px">
      <div class="filter-dialog__title">Print</div>
      <div class="filter-dialog__body" style="padding:12px 0">
        <div style="font-size:12px;display:flex;flex-direction:column;gap:8px">
          <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Page Size
            <select id="print-page-size" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px">
              <option value="letter">Letter (8.5 Ã— 11 in)</option>
              <option value="a4">A4 (210 Ã— 297 mm)</option>
              <option value="legal">Legal (8.5 Ã— 14 in)</option>
              <option value="tabloid">Tabloid (11 Ã— 17 in)</option>
            </select>
          </label>
          <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Orientation
            <select id="print-orientation" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px">
              <option value="portrait">Portrait</option>
              <option value="landscape">Landscape</option>
            </select>
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:12px">
            <input id="print-scale" type="checkbox" checked /> Scale to Fit Media
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:12px">
            <input id="print-layer-names" type="checkbox" /> Print Layer Names
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:12px">
            <input id="print-grid" type="checkbox" /> Print Grid
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:12px">
            <input id="print-guides" type="checkbox" /> Print Guides
          </label>
        </div>
        <div style="margin-top:12px;padding:8px;background:var(--bg-input);border:1px solid var(--bg-secondary);text-align:center">
          <div style="font-size:10px;color:var(--text-dim)">Preview</div>
          <canvas id="print-preview" style="max-width:100%;max-height:150px;margin-top:4px;border:1px solid var(--bg-secondary)"></canvas>
        </div>
      </div>
      <div class="filter-dialog__actions">
        <button class="filter-dialog__btn" data-action="cancel">Cancel</button>
        <button class="filter-dialog__btn filter-dialog__btn--primary" data-action="print">Print</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  // Draw preview
  const previewCanvas = overlay.querySelector("#print-preview") as HTMLCanvasElement;
  if (previewCanvas) {
    previewCanvas.width = 200;
    previewCanvas.height = 150;
    const ctx = previewCanvas.getContext("2d");
    if (ctx) {
      ctx.drawImage(canvas, 0, 0, 200, 150);
    }
  }

  overlay.querySelector('[data-action="print"]')?.addEventListener("click", () => {
    const scaleToFit = (overlay.querySelector("#print-scale") as HTMLInputElement)?.checked;
    const layerNames = (overlay.querySelector("#print-layer-names") as HTMLInputElement)?.checked;
    const printGrid = (overlay.querySelector("#print-grid") as HTMLInputElement)?.checked;
    const printGuides = (overlay.querySelector("#print-guides") as HTMLInputElement)?.checked;

    // Create a print window
    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (!printWindow) { showError("Pop-up blocked. Allow pop-ups to print."); overlay.remove(); return; }

    const doc = state.doc;
    if (!doc) return;

    const printCanvas = document.createElement("canvas");
    printCanvas.width = doc.width;
    printCanvas.height = doc.height;
    const pCtx = printCanvas.getContext("2d");
    if (pCtx) {
      pCtx.drawImage(canvas, 0, 0);
      if (printGrid) {
        pCtx.strokeStyle = "rgba(0,0,0,0.2)";
        pCtx.lineWidth = 1;
        for (let x = 0; x < doc.width; x += gridSpacing) {
          pCtx.beginPath(); pCtx.moveTo(x, 0); pCtx.lineTo(x, doc.height); pCtx.stroke();
        }
        for (let y = 0; y < doc.height; y += gridSpacing) {
          pCtx.beginPath(); pCtx.moveTo(0, y); pCtx.lineTo(doc.width, y); pCtx.stroke();
        }
      }
      if (printGuides) {
        pCtx.strokeStyle = "rgba(0,120,215,0.5)";
        for (const g of guides) {
          if (g.kind === "horizontal") {
            pCtx.beginPath(); pCtx.moveTo(0, g.position); pCtx.lineTo(doc.width, g.position); pCtx.stroke();
          } else {
            pCtx.beginPath(); pCtx.moveTo(g.position, 0); pCtx.lineTo(g.position, doc.height); pCtx.stroke();
          }
        }
      }
    }

    const dataUrl = printCanvas.toDataURL("image/png");
    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>Print â€” ${escapeHtml(doc.name)}</title>
      <style>body{margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh}img{max-width:100%;max-height:100%}</style>
      </head><body><img src="${dataUrl}" />${layerNames ? `<div style="position:fixed;bottom:10px;left:10px;font-size:10px">Layers: ${doc.layers.map(l => l.name).join(", ")}</div>` : ""}</body></html>
    `);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);

    overlay.remove();
  });

  overlay.querySelector('[data-action="cancel"]')?.addEventListener("click", () => overlay.remove());
  overlay.addEventListener("click", (e) => { if (e.target === overlay) overlay.remove(); });
}

// â”€â”€ BATCH TTT: Automation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface ActionStep {
  id: string;
  name: string;
  command: string;
  params: Record<string, unknown>;
  recordable?: boolean;
}

interface ActionSet {
  name: string;
  actions: Action[];
}

interface Action {
  id: string;
  name: string;
  steps: ActionStep[];
}

let actionSets: ActionSet[] = [];
let currentActionSet: ActionSet | null = null;
let isRecording = false;
let recordingSteps: ActionStep[] = [];

function initDefaultActions(): void {
  if (actionSets.length > 0) return;
  actionSets = [
    {
      name: "Default Actions",
      actions: [
        {
          id: crypto.randomUUID(),
          name: "Frame Photo (50px)",
          steps: [
            { id: crypto.randomUUID(), name: "Canvas Size", command: "canvas-size", params: { width: 0, height: 0, anchor: "center", addWidth: 100, addHeight: 100 } },
            { id: crypto.randomUUID(), name: "Fill Background", command: "fill", params: { contents: "background", opacity: 100 } },
          ],
        },
        {
          id: crypto.randomUUID(),
          name: "Resize for Web (1200px)",
          steps: [
            { id: crypto.randomUUID(), name: "Image Size", command: "image-size", params: { width: 1200, method: "bilinear" } },
            { id: crypto.randomUUID(), name: "Sharpen", command: "sharpen", params: { amount: 100 } },
          ],
        },
        {
          id: crypto.randomUUID(),
          name: "Watermark",
          steps: [
            { id: crypto.randomUUID(), name: "Add Text Layer", command: "add-text", params: { text: "Â© 2026", size: 48, opacity: 0.3 } },
          ],
        },
      ],
    },
  ];
  currentActionSet = actionSets[0]!;
}

function showActionsPanel(): void {
  initDefaultActions();

  const overlay = document.createElement("div");
  overlay.className = "filter-dialog";
  overlay.id = "actions-panel";
  overlay.innerHTML = `
    <div class="filter-dialog__sheet" style="max-width:360px;max-height:500px">
      <div class="filter-dialog__title">
        Actions
        <button class="filter-dialog__close" style="margin-left:auto;width:20px;height:20px;display:flex;align-items:center;justify-content:center;font-size:14px">Ã—</button>
      </div>
      <div class="filter-dialog__body" style="padding:8px 0;max-height:400px;overflow-y:auto">
        <div style="display:flex;gap:4px;margin-bottom:8px">
          <button class="filter-dialog__btn" id="act-record" style="flex:1;font-size:11px">âº Record</button>
          <button class="filter-dialog__btn" id="act-play" style="flex:1;font-size:11px">â–¶ Play</button>
          <button class="filter-dialog__btn" id="act-stop" style="flex:1;font-size:11px">â¹ Stop</button>
        </div>
        <div id="actions-list"></div>
      </div>
      <div class="filter-dialog__actions">
        <button class="filter-dialog__btn" id="act-new-action">New Action</button>
        <button class="filter-dialog__btn" id="act-delete-action">Delete</button>
        <button class="filter-dialog__btn filter-dialog__btn--primary" id="act-close">Close</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  overlay.querySelector(".filter-dialog__close")?.addEventListener("click", () => overlay.remove());
  document.getElementById("act-close")?.addEventListener("click", () => overlay.remove());
  overlay.addEventListener("click", (e) => { if (e.target === overlay) overlay.remove(); });

  let selectedActionId: string | null = null;

  function renderActions(): void {
    const list = document.getElementById("actions-list");
    if (!list || !currentActionSet) return;
    if (currentActionSet.actions.length === 0) {
      list.innerHTML = '<div class="muted" style="padding:12px;text-align:center">No actions. Click New Action to create one.</div>';
      return;
    }
    list.innerHTML = currentActionSet.actions.map((a) => `
      <div class="action-row${a.id === selectedActionId ? " is-selected" : ""}" data-id="${escapeHtml(a.id)}" style="padding:6px 8px;cursor:pointer;border-bottom:1px solid var(--bg-secondary);font-size:12px">
        <div style="font-weight:${a.id === selectedActionId ? "600" : "400"}">${escapeHtml(a.name)}</div>
        <div style="font-size:10px;color:var(--text-dim)">${a.steps.length} step(s)</div>
      </div>
    `).join("");

    list.querySelectorAll(".action-row").forEach((row) => {
      row.addEventListener("click", () => {
        selectedActionId = row.getAttribute("data-id");
        renderActions();
      });
      row.addEventListener("dblclick", () => {
        const id = row.getAttribute("data-id");
        if (id) playAction(id);
      });
    });
  }

  renderActions();

  document.getElementById("act-record")?.addEventListener("click", () => {
    if (!isRecording) {
      isRecording = true;
      recordingSteps = [];
      // Attach the recorder so every non-transient Command executed while
      // recording appends a step to recordingSteps.
      state.setActionRecorder((step) => {
        recordingSteps.push(step);
        if (document.getElementById("actions-panel")) renderActions();
      });
      showTip("Recording started. Perform actions to record.");
      (document.getElementById("act-record") as HTMLElement).style.background = "var(--accent)";
    }
  });

  document.getElementById("act-play")?.addEventListener("click", () => {
    if (selectedActionId) playAction(selectedActionId);
    else showTip("Select an action to play.");
  });

  document.getElementById("act-stop")?.addEventListener("click", () => {
    if (isRecording) {
      isRecording = false;
      state.clearActionRecorder();
      (document.getElementById("act-record") as HTMLElement).style.background = "";
      // Flush recorded steps into the selected action (or the last action).
      if (recordingSteps.length > 0) {
        const target = selectedActionId
          ? currentActionSet!.actions.find(a => a.id === selectedActionId)
          : currentActionSet!.actions[currentActionSet!.actions.length - 1];
        if (target) {
          target.steps.push(...recordingSteps);
          renderActions();
        }
      }
      recordingSteps = [];
      showTip("Recording stopped.");
    }
  });

  document.getElementById("act-new-action")?.addEventListener("click", () => {
    const name = prompt("Action name:", `Action ${currentActionSet!.actions.length + 1}`);
    if (!name) return;
    currentActionSet!.actions.push({ id: crypto.randomUUID(), name, steps: [] });
    selectedActionId = currentActionSet!.actions[currentActionSet!.actions.length - 1]!.id;
    renderActions();
  });

  document.getElementById("act-delete-action")?.addEventListener("click", () => {
    if (!selectedActionId) { showTip("Select an action to delete."); return; }
    currentActionSet!.actions = currentActionSet!.actions.filter(a => a.id !== selectedActionId);
    selectedActionId = null;
    renderActions();
  });
}

function playAction(actionId: string): void {
  if (!currentActionSet || !state.doc) return;
  const action = currentActionSet.actions.find(a => a.id === actionId);
  if (!action) return;

  showTip(`Playing: ${action.name}`);
  for (const step of action.steps) {
    executeActionStep(step);
  }
  showTip(`Action "${action.name}" completed.`);
}

function executeActionStep(step: ActionStep): void {
  // Steps with recordable:false cannot be replayed automatically.
  if (step.recordable === false) {
      showTip(`Step "${step.name}" cannot be replayed automatically.`);
    return;
  }

  const p = step.params;

  switch (step.command) {
    // Commands recorded via toActionStep() -- replay with captured params
    case "image-size": {
      if (!state.doc) break;
      const rawConstraint = p["constrainProportions"];
      const imgOpts: ResizeOptions = {
        width: (p["width"] as number) ?? state.doc.width,
        height: (p["height"] as number) ?? state.doc.height,
        method: (p["method"] as "nearest" | "bilinear" | "bicubic") ?? "bilinear",
        ...(rawConstraint !== undefined && { constrainProportions: rawConstraint as boolean }),
      };
      state.execute(new ResizeImageCommand(imgOpts));
      break;
    }
    case "fill": {
      if (!state.doc?.activeLayerId) break;
      const rawColor = p["color"];
      const fillOpts: FillOptions = {
        source: (p["source"] as FillOptions["source"]) ?? "foreground",
        ...(rawColor !== undefined && { color: rawColor as { r: number; g: number; b: number; a?: number } }),
        opacity: (p["opacity"] as number) ?? 1,
        blendMode: (p["blendMode"] as FillOptions["blendMode"]) ?? "normal",
        respectLock: (p["respectLock"] as boolean) ?? false,
      };
      state.execute(new FillCommand(state.doc.activeLayerId, fillOpts));
      break;
    }
    // Legacy/hardcoded steps without recorded params -- show dialog
    case "canvas-size":
      promptCanvasSize();
      break;
    case "sharpen":
      promptSharpen();
      break;
    case "gaussian-blur":
      promptGaussianBlur();
      break;
    default:
        showTip(`Step "${step.name}" executed (${step.command}).`);
  }
}
async function showBatchDialog(): Promise<void> {
  initDefaultActions();

  const overlay = document.createElement("div");
  overlay.className = "filter-dialog";
  overlay.innerHTML = `
    <div class="filter-dialog__sheet" style="max-width:480px">
      <div class="filter-dialog__title">Batch</div>
      <div class="filter-dialog__body" style="padding:12px 0">
        <div style="font-size:12px;display:flex;flex-direction:column;gap:8px">
          <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Action Set
            <select id="batch-set" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px">
              ${actionSets.map(s => `<option value="${escapeHtml(s.name)}">${escapeHtml(s.name)}</option>`).join("")}
            </select>
          </label>
          <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Action
            <select id="batch-action" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px">
              ${currentActionSet ? currentActionSet.actions.map(a => `<option value="${escapeHtml(a.id)}">${escapeHtml(a.name)}</option>`).join("") : ""}
            </select>
          </label>
          <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Source
            <select id="batch-source" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px">
              <option value="folder">Folder</option>
              <option value="opened">Opened Files</option>
            </select>
          </label>
          <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Destination
            <select id="batch-dest" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px">
              <option value="none">None</option>
              <option value="folder">Save and Close</option>
            </select>
          </label>
          <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Output Format
            <select id="batch-format" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px">
              <option value="png">PNG</option>
              <option value="jpeg">JPEG</option>
              <option value="webp">WebP</option>
            </select>
          </label>
        </div>
        <div style="margin-top:12px;font-size:11px;color:var(--text-dim)">
          Batch processing applies an action to multiple files. Select a folder of images to process.
        </div>
      </div>
      <div class="filter-dialog__actions">
        <button class="filter-dialog__btn" data-action="cancel">Cancel</button>
        <button class="filter-dialog__btn filter-dialog__btn--primary" data-action="run">Run</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  overlay.querySelector('[data-action="run"]')?.addEventListener("click", async () => {
    const actionId = (overlay.querySelector("#batch-action") as HTMLSelectElement)?.value;
    if (!actionId) { showError("Select an action."); return; }

    // Open folder picker
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "image/*";
    input.style.display = "none";
    document.body.appendChild(input);

    input.addEventListener("change", async () => {
      const files = input.files;
      if (!files || files.length === 0) { input.remove(); overlay.remove(); return; }

      showTip(`Processing ${files.length} file(s)...`);
      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        try {
          await openFile(file);
          playAction(actionId);
          const format = (overlay.querySelector("#batch-format") as HTMLSelectElement)?.value || "png";
          if (state.doc) {
            const mimeMap: Record<string, ExportFormat> = { png: "image/png", jpeg: "image/jpeg", webp: "image/webp" };
            const blob = await exportDocument(state.doc, mimeMap[format] || "image/png");
            const ext = format === "jpeg" ? "jpg" : format;
            downloadBlob(blob, `${file.name.replace(/\.[^.]+$/, "")}_processed.${ext}`);
          }
        } catch (err) {
          showError(`Failed to process ${file.name}: ${err}`);
        }
      }
      showTip("Batch processing complete.");
      input.remove();
      overlay.remove();
    });

    input.click();
  });

  overlay.querySelector('[data-action="cancel"]')?.addEventListener("click", () => overlay.remove());
  overlay.addEventListener("click", (e) => { if (e.target === overlay) overlay.remove(); });
}

async function showImageProcessor(): Promise<void> {
  const overlay = document.createElement("div");
  overlay.className = "filter-dialog";
  overlay.innerHTML = `
    <div class="filter-dialog__sheet" style="max-width:480px">
      <div class="filter-dialog__title">Image Processor</div>
      <div class="filter-dialog__body" style="padding:12px 0">
        <div style="font-size:12px;display:flex;flex-direction:column;gap:8px">
          <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Select Files
            <button id="ip-select-files" class="filter-dialog__btn" style="width:100%;font-size:12px">Choose Files...</button>
          </label>
          <div style="font-size:11px;color:var(--text-dim)">
            <label style="display:flex;align-items:center;gap:8px">
              <input id="ip-jpeg" type="checkbox" checked /> Save as JPEG
            </label>
            <label style="display:flex;align-items:center;gap:8px;margin-left:16px;font-size:11px">
              Quality: <input id="ip-jpeg-quality" type="range" min="1" max="12" value="8" style="width:80px" /> <span id="ip-jpeg-quality-val">8</span>
            </label>
          </div>
          <div style="font-size:11px">
            <label style="display:flex;align-items:center;gap:8px">
              <input id="ip-png" type="checkbox" /> Save as PNG
            </label>
          </div>
          <div style="font-size:11px">
            <label style="display:flex;align-items:center;gap:8px">
              <input id="ip-psd" type="checkbox" /> Save as PSD
            </label>
          </div>
          <label style="display:flex;align-items:center;gap:8px;font-size:12px">
            <input id="ip-resize" type="checkbox" /> Resize to Fit
          </label>
          <label style="display:flex;gap:4px;font-size:11px;margin-left:16px">
            W: <input id="ip-width" type="number" value="1200" style="width:60px;background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:2px 4px;font-size:11px" />
            H: <input id="ip-height" type="number" value="800" style="width:60px;background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:2px 4px;font-size:11px" />
          </label>
        </div>
      </div>
      <div class="filter-dialog__actions">
        <button class="filter-dialog__btn" data-action="cancel">Cancel</button>
        <button class="filter-dialog__btn filter-dialog__btn--primary" id="ip-run">Run</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const jpegQualitySlider = overlay.querySelector("#ip-jpeg-quality") as HTMLInputElement;
  const jpegQualityVal = overlay.querySelector("#ip-jpeg-quality-val") as HTMLSpanElement;
  jpegQualitySlider?.addEventListener("input", () => {
    jpegQualityVal.textContent = jpegQualitySlider.value;
  });

  document.getElementById("ip-select-files")?.addEventListener("click", () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "image/*";
    input.style.display = "none";
    document.body.appendChild(input);
    input.addEventListener("change", () => {
      if (input.files && input.files.length > 0) {
        (document.getElementById("ip-select-files") as HTMLElement).textContent = `${input.files.length} file(s) selected`;
        (document.getElementById("ip-select-files") as HTMLElement).dataset.files = JSON.stringify([...input.files].map(f => f.name));
      }
      input.remove();
    });
    input.click();
  });

  document.getElementById("ip-run")?.addEventListener("click", async () => {
    const filesBtn = document.getElementById("ip-select-files") as HTMLElement;
    const filesData = filesBtn.dataset.files;
    if (!filesData) { showError("Select files first."); return; }

    const saveJpeg = (overlay.querySelector("#ip-jpeg") as HTMLInputElement)?.checked;
    const savePng = (overlay.querySelector("#ip-png") as HTMLInputElement)?.checked;
    const savePsd = (overlay.querySelector("#ip-psd") as HTMLInputElement)?.checked;
    const resize = (overlay.querySelector("#ip-resize") as HTMLInputElement)?.checked;
    const maxWidth = parseInt((overlay.querySelector("#ip-width") as HTMLInputElement)?.value || "1200");
    const maxHeight = parseInt((overlay.querySelector("#ip-height") as HTMLInputElement)?.value || "800");
    const jpegQuality = parseInt((overlay.querySelector("#ip-jpeg-quality") as HTMLInputElement)?.value || "8");

    showTip("Image Processor started...");

    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "image/*";
    input.style.display = "none";
    document.body.appendChild(input);

    input.addEventListener("change", async () => {
      const files = input.files;
      if (!files || files.length === 0) { input.remove(); overlay.remove(); return; }

      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        try {
          await openFile(file);
          if (resize && state.doc) {
            const scale = Math.min(maxWidth / state.doc.width, maxHeight / state.doc.height, 1);
            if (scale < 1) {
              state.execute(new ResizeImageCommand({
                width: Math.round(state.doc.width * scale),
                height: Math.round(state.doc.height * scale),
                method: "bilinear",
                constrainProportions: true,
              }));
            }
          }
          const baseName = file.name.replace(/\.[^.]+$/, "");
          if (saveJpeg && state.doc) {
            const blob = await exportDocument(state.doc, "image/jpeg", jpegQuality / 12);
            downloadBlob(blob, `${baseName}.jpg`);
          }
          if (savePng && state.doc) {
            const blob = await exportDocument(state.doc, "image/png");
            downloadBlob(blob, `${baseName}.png`);
          }
          if (savePsd && state.doc) {
            const { encodePsd } = await import("./app/io/encodeImage");
            const bytes = await encodePsd(state.doc, {
              guides,
              ...(currentIccProfile ? { iccProfile: currentIccProfile } : {}),
            });
            const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "application/x-psd" });
            downloadBlob(blob, `${baseName}.psd`);
          }
        } catch (err) {
          showError(`Failed: ${file.name}`);
        }
      }
      showTip("Image Processor complete.");
      input.remove();
      overlay.remove();
    });

    input.click();
  });

  overlay.querySelector('[data-action="cancel"]')?.addEventListener("click", () => overlay.remove());
  overlay.addEventListener("click", (e) => { if (e.target === overlay) overlay.remove(); });
}

// â”€â”€ Script Runner sandbox â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
//
// Scripts run inside a sandboxed iframe (sandbox="allow-scripts", NO
// allow-same-origin). Without allow-same-origin the iframe has an opaque
// origin â€” it cannot reach parent.window, localStorage, indexedDB, or any
// DOM outside its own document. Communication is strictly via postMessage
// with a narrow, serializable API.
//
// Narrow API: getDocInfo(), runNamedCommand(id, params), showTip(), showError()
// Script length cap: 64 KB. Execution timeout: 5 s.
// Confirmation gate: user must approve before each run.

const SCRIPT_MAX_BYTES = 64 * 1024; // 64 KB hard cap

// Commands the sandboxed script is allowed to invoke via runNamedCommand().
export const SCRIPT_COMMAND_ALLOWLIST = new Set([
  "new-document",
  "add-layer",
  "fit-doc",
  "export-png",
  "image-size",
]);

// Session-scoped confirmation flag.
let _scriptRunConfirmedThisSession = false;

/** Show a confirmation dialog before running script code. */
function confirmScriptRun(): Promise<boolean> {
  if (_scriptRunConfirmedThisSession) return Promise.resolve(true);
  return new Promise<boolean>((resolve) => {
    const dlg = document.createElement("div");
    dlg.className = "filter-dialog";
    dlg.style.zIndex = "10001";
    dlg.innerHTML = `
      <div class="filter-dialog__sheet" style="max-width:400px">
        <div class="filter-dialog__title">Run Script?</div>
        <div class="filter-dialog__body" style="padding:12px;font-size:12px;line-height:1.6">
          <p style="margin:0 0 10px">This script will execute JavaScript in a sandbox with access to your document via a restricted API.</p>
          <p style="margin:0 0 10px;color:var(--text-dim)">Only run scripts from sources you trust.</p>
          <label style="display:flex;align-items:center;gap:6px;font-size:11px">
            <input type="checkbox" id="script-dontask" />
            Don't ask again this session
          </label>
        </div>
        <div class="filter-dialog__actions">
          <button class="filter-dialog__btn" id="script-confirm-cancel">Cancel</button>
          <button class="filter-dialog__btn filter-dialog__btn--primary" id="script-confirm-run">Run</button>
        </div>
      </div>
    `;
    document.body.appendChild(dlg);
    dlg.querySelector("#script-confirm-run")?.addEventListener("click", () => {
      if ((dlg.querySelector("#script-dontask") as HTMLInputElement)?.checked) {
        _scriptRunConfirmedThisSession = true;
      }
      dlg.remove();
      resolve(true);
    });
    dlg.querySelector("#script-confirm-cancel")?.addEventListener("click", () => { dlg.remove(); resolve(false); });
    dlg.addEventListener("click", (e) => { if (e.target === dlg) { dlg.remove(); resolve(false); } });
  });
}

/** Execute code inside a sandboxed iframe communicating via postMessage. */
function runScriptInSandbox(code: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const TIMEOUT_MS = 5000;
    const channelId = crypto.randomUUID();

    // Build the iframe HTML with the sandbox runtime inline.
    // The closing </script> tag is split to avoid prematurely ending the TS template.
    const scriptClose = "<\/script>";
    const iframeHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body><script>
(function() {
  'use strict';
  var _cid = ${JSON.stringify(channelId)};
  var pixelpa = {
    getDocInfo: function() {
      return new Promise(function(res, rej) {
        var rid = Math.random().toString(36).slice(2);
        var h = function(e) { if (!e.data || e.data._cid !== _cid || e.data._rid !== rid) return; window.removeEventListener('message', h); if (e.data.error) rej(new Error(e.data.error)); else res(e.data.result); };
        window.addEventListener('message', h);
        parent.postMessage({ _cid: _cid, _rid: rid, method: 'getDocInfo', params: {} }, '*');
      });
    },
    runNamedCommand: function(id, params) {
      return new Promise(function(res, rej) {
        var rid = Math.random().toString(36).slice(2);
        var h = function(e) { if (!e.data || e.data._cid !== _cid || e.data._rid !== rid) return; window.removeEventListener('message', h); if (e.data.error) rej(new Error(e.data.error)); else res(e.data.result); };
        window.addEventListener('message', h);
        parent.postMessage({ _cid: _cid, _rid: rid, method: 'runNamedCommand', params: { id: id, args: params || {} } }, '*');
      });
    },
    showTip: function(msg) { parent.postMessage({ _cid: _cid, method: 'showTip', params: { msg: String(msg) } }, '*'); },
    showError: function(msg) { parent.postMessage({ _cid: _cid, method: 'showError', params: { msg: String(msg) } }, '*'); },
  };
  Promise.resolve().then(function() { return (async function(pixelpa) {\n${code}\n})(pixelpa); })
    .then(function() { parent.postMessage({ _cid: _cid, method: 'done' }, '*'); })
    .catch(function(err) { parent.postMessage({ _cid: _cid, method: 'error', params: { msg: err && err.message ? err.message : String(err) } }, '*'); });
})();
${scriptClose}</body></html>`;

    const iframe = document.createElement("iframe");
    iframe.setAttribute("sandbox", "allow-scripts");
    iframe.style.cssText = "position:absolute;width:0;height:0;border:0;visibility:hidden";
    iframe.srcdoc = iframeHtml;
    document.body.appendChild(iframe);

    let settled = false;
    const cleanup = (): void => { if (settled) return; settled = true; window.removeEventListener("message", onMessage); iframe.remove(); };
    const timer = setTimeout(() => { cleanup(); reject(new Error("Script timed out after 5 seconds")); }, TIMEOUT_MS);

    function onMessage(e: MessageEvent): void {
      if (!e.data || e.data._cid !== channelId) return;
      const { method, params, _rid } = e.data as { method: string; params?: Record<string, unknown>; _rid?: string };
      switch (method) {
        case "getDocInfo": {
          const doc = state.doc;
          const result = doc ? { name: doc.name, width: doc.width, height: doc.height, dpi: doc.dpi, layerCount: doc.layers.length, activeLayerName: doc.layers.find((l) => l.id === doc.activeLayerId)?.name ?? null } : null;
          iframe.contentWindow?.postMessage({ _cid: channelId, _rid, result }, "*");
          break;
        }
        case "runNamedCommand": {
          const cmdId = params?.id as string | undefined;
          if (!cmdId || !SCRIPT_COMMAND_ALLOWLIST.has(cmdId)) {
            iframe.contentWindow?.postMessage({ _cid: channelId, _rid, error: `Unknown or disallowed command: "${cmdId ?? ""}"` }, "*");
            break;
          }
          try {
            switch (cmdId) {
              case "new-document": newDocument(); break;
              case "add-layer": addNewLayer(); break;
              case "fit-doc": fitDoc(); break;
              case "export-png": quickExportPng(); break;
              case "image-size": {
                const targetW = Number((params?.args as Record<string, unknown>)?.width ?? 1024);
                if (state.doc && isFinite(targetW) && targetW > 0) {
                  const newH = Math.round(state.doc.height * (targetW / state.doc.width));
                  state.execute(new ResizeImageCommand({ width: targetW, height: newH, method: "bilinear", constrainProportions: true }));
                  fitDoc();
                }
                break;
              }
              default: break;
            }
            iframe.contentWindow?.postMessage({ _cid: channelId, _rid, result: null }, "*");
          } catch (err) {
            iframe.contentWindow?.postMessage({ _cid: channelId, _rid, error: err instanceof Error ? err.message : String(err) }, "*");
          }
          break;
        }
        case "showTip": showTip(String(params?.msg ?? "")); break;
        case "showError": showError(String(params?.msg ?? "")); break;
        case "done": clearTimeout(timer); cleanup(); resolve(); break;
        case "error": clearTimeout(timer); cleanup(); reject(new Error(String(params?.msg ?? "Script error"))); break;
        default: break;
      }
    }
    window.addEventListener("message", onMessage);
  });
}

async function showScriptsDialog(): Promise<void> {
  const overlay = document.createElement("div");
  overlay.className = "filter-dialog";
  overlay.innerHTML = `
    <div class="filter-dialog__sheet" style="max-width:500px;max-height:500px">
      <div class="filter-dialog__title">Scripts</div>
      <div class="filter-dialog__body" style="padding:12px 0;max-height:400px;overflow-y:auto">
        <div style="font-size:12px;display:flex;flex-direction:column;gap:8px">
          <label style="display:flex;flex-direction:column;gap:2px;font-size:11px">Script
            <select id="script-select" style="background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:4px 6px;font-size:12px">
              <option value="new-doc">Create New Document</option>
              <option value="add-layers">Add 5 Blank Layers</option>
              <option value="resize-all">Resize to 1024px</option>
              <option value="export-png">Export as PNG</option>
              <option value="custom">Custom JavaScript...</option>
            </select>
          </label>
          <div id="script-editor" style="display:none">
            <textarea id="script-code" style="width:100%;min-height:150px;background:var(--bg-input);border:1px solid var(--bg-secondary);color:var(--text);padding:8px;font-size:11px;font-family:monospace;resize:vertical" placeholder="// Custom JavaScript (sandboxed)"></textarea>
          </div>
        </div>
        <div style="margin-top:12px;font-size:11px;color:var(--text-dim)">
          <strong>Sandboxed API:</strong><br>
          <code>await pixelpa.getDocInfo()</code> â€” document metadata (read-only)<br>
          <code>await pixelpa.runNamedCommand(id, params)</code> â€” run a safe command<br>
          <em>Allowed commands:</em> new-document, add-layer, fit-doc, export-png, image-size<br>
          <code>pixelpa.showTip(msg)</code> / <code>pixelpa.showError(msg)</code><br>
          <span style="color:var(--text-warn,#f90)">Scripts run sandboxed â€” no access to window, DOM, or localStorage.</span>
        </div>
      </div>
      <div class="filter-dialog__actions">
        <button class="filter-dialog__btn" id="script-install">Install Script</button>
        <button class="filter-dialog__btn" data-action="cancel">Cancel</button>
        <button class="filter-dialog__btn filter-dialog__btn--primary" id="script-run">Run</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const scriptSelect = overlay.querySelector("#script-select") as HTMLSelectElement;
  const scriptEditor = overlay.querySelector("#script-editor") as HTMLDivElement;
  scriptSelect?.addEventListener("change", () => {
    scriptEditor.style.display = scriptSelect.value === "custom" ? "" : "none";
  });

  function getScriptCode(): string {
    const val = scriptSelect?.value;
    switch (val) {
      case "new-doc":
        return `await pixelpa.runNamedCommand('new-document', {});`;
      case "add-layers":
        return `for (let i = 0; i < 5; i++) { await pixelpa.runNamedCommand('add-layer', {}); }`;
      case "resize-all":
        return `const d = await pixelpa.getDocInfo(); if (d) { await pixelpa.runNamedCommand('image-size', { width: 1024 }); await pixelpa.runNamedCommand('fit-doc', {}); }`;
      case "export-png":
        return `const d = await pixelpa.getDocInfo(); if (d) { await pixelpa.runNamedCommand('export-png', {}); }`;
      case "custom":
        return (overlay.querySelector("#script-code") as HTMLTextAreaElement)?.value || "";
      default:
        return "";
    }
  }

  document.getElementById("script-run")?.addEventListener("click", async () => {
    const code = getScriptCode();
    if (!code) { showError("No script code."); return; }
    if (code.length > SCRIPT_MAX_BYTES) {
      showError(`Script exceeds ${SCRIPT_MAX_BYTES / 1024} KB limit (${code.length} bytes).`);
      return;
    }
    const confirmed = await confirmScriptRun();
    if (!confirmed) return;
    try {
      await runScriptInSandbox(code);
      showTip("Script executed successfully.");
    } catch (err) {
      showError(`Script error: ${err instanceof Error ? err.message : String(err)}`);
    }
  });

  document.getElementById("script-install")?.addEventListener("click", () => {
    const name = prompt("Script name:");
    if (!name) return;
    const code = getScriptCode();
    try {
      const scripts = JSON.parse(localStorage.getItem("pixelpa-scripts") || "{}");
      scripts[name] = code;
      localStorage.setItem("pixelpa-scripts", JSON.stringify(scripts));
      showTip(`Script "${name}" installed.`);
    } catch {
      showError("Failed to install script.");
    }
  });

  overlay.querySelector('[data-action="cancel"]')?.addEventListener("click", () => overlay.remove());
  overlay.addEventListener("click", (e) => { if (e.target === overlay) overlay.remove(); });
}

// â”€â”€ BATCH C: Enhanced Color Range dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function colorRangeEnhanced(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;
  const layer = getActiveRaster();
  if (!layer) { showError("No active raster layer."); return; }

  const overlay = document.createElement("div");
  overlay.className = "filter-dialog";
  overlay.innerHTML = `
    <div class="filter-dialog__sheet" style="max-width:520px">
      <div class="filter-dialog__title">Color Range</div>
      <div class="filter-dialog__body" style="padding:12px 0;display:flex;gap:12px;align-items:flex-start">
        <div style="flex:1">
          <div style="margin-bottom:8px">
            <label style="font-size:11px;color:var(--text-dim)">Selection:</label>
            <select id="cr-selection" style="width:100%;background:var(--bg-input);border:1px solid var(--border);color:var(--text);padding:2px 4px;font-size:12px">
              <option value="sampled">Sampled Colors</option>
              <option value="highlights">Highlights</option>
              <option value="midtones">Midtones</option>
              <option value="shadows">Shadows</option>
            </select>
          </div>
          <div style="margin-bottom:8px">
            <label style="font-size:11px;color:var(--text-dim)">Fuzziness: <span id="cr-fuzz-val">40</span></label>
            <input id="cr-fuzziness" type="range" min="0" max="200" value="40" style="width:100%">
          </div>
          <div style="margin-bottom:8px">
            <label style="font-size:11px;color:var(--text-dim)">
              <input id="cr-localized" type="checkbox"> Localized Color Clusters
            </label>
          </div>
          <div id="cr-color-info" style="font-size:11px;color:var(--text-dim);min-height:16px">Click image to sample color</div>
        </div>
        <div>
          <div style="font-size:11px;color:var(--text-dim);margin-bottom:4px">Preview:</div>
          <canvas id="cr-preview" width="200" height="200" style="border:1px solid var(--border);image-rendering:pixelated"></canvas>
        </div>
      </div>
      <div class="filter-dialog__actions">
        <button class="filter-dialog__btn" data-action="cancel">Cancel</button>
        <button class="filter-dialog__btn filter-dialog__btn--primary" data-action="ok">OK</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const fuzzSlider = overlay.querySelector<HTMLInputElement>("#cr-fuzziness")!;
  const fuzzVal = overlay.querySelector<HTMLSpanElement>("#cr-fuzz-val")!;
  const previewCanvas = overlay.querySelector<HTMLCanvasElement>("#cr-preview")!;
  const pctx = previewCanvas.getContext("2d")!;
  const colorInfo = overlay.querySelector<HTMLDivElement>("#cr-color-info")!;
  let sampleColor: RGB | null = null;

  const updatePreview = () => {
    if (!sampleColor || !layer) return;
    const fuzz = parseInt(fuzzSlider.value, 10);
    const lw = layer.pixels.width, lh = layer.pixels.height;
    const data = layer.pixels.data;
    const tR = sampleColor.r, tG = sampleColor.g, tB = sampleColor.b;
    const maxDist = Math.sqrt(3 * 255 * 255);
    const pw = Math.min(200, lw), ph = Math.min(200, lh);
    const preview = pctx.createImageData(pw, ph);
    const sx = lw / pw, sy = lh / ph;
    for (let py = 0; py < ph; py++) {
      for (let px = 0; px < pw; px++) {
        const lx = Math.floor(px * sx), ly = Math.floor(py * sy);
        const pi = (ly * lw + lx) * 4;
        const dR = data[pi]! - tR, dG = data[pi + 1]! - tG, dB = data[pi + 2]! - tB;
        const dist = Math.sqrt(dR * dR + dG * dG + dB * dB);
        const normalized = dist / maxDist * 100;
        const sel = normalized <= fuzz ? Math.round((1 - normalized / (fuzz || 1)) * 255) : 0;
        const di = (py * pw + px) * 4;
        preview.data[di] = sel; preview.data[di + 1] = sel; preview.data[di + 2] = sel; preview.data[di + 3] = 255;
      }
    }
    pctx.putImageData(preview, 0, 0);
  };

  fuzzSlider.addEventListener("input", () => { fuzzVal.textContent = fuzzSlider.value; updatePreview(); });

  const sampleHandler = (e: PointerEvent) => {
    const r = canvas.getBoundingClientRect();
    const docPos = screenToDoc(state.viewport, e.clientX - r.left, e.clientY - r.top);
    const lx = Math.floor(docPos.x - layer.x), ly = Math.floor(docPos.y - layer.y);
    if (lx >= 0 && ly >= 0 && lx < layer.pixels.width && ly < layer.pixels.height) {
      const pi = (ly * layer.pixels.width + lx) * 4;
      sampleColor = { r: layer.pixels.data[pi]!, g: layer.pixels.data[pi + 1]!, b: layer.pixels.data[pi + 2]! };
      colorInfo.textContent = `Sampled: RGB(${sampleColor.r}, ${sampleColor.g}, ${sampleColor.b})`;
      updatePreview();
    }
  };
  canvas.addEventListener("click", sampleHandler);

  overlay.querySelector('[data-action="ok"]')!.addEventListener("click", () => {
    canvas.removeEventListener("click", sampleHandler);
    if (!sampleColor || !doc) { overlay.remove(); return; }
    const fuzz = parseInt(fuzzSlider.value, 10);
    const lw = layer.pixels.width, lh = layer.pixels.height;
    const data = layer.pixels.data;
    const tR = sampleColor.r, tG = sampleColor.g, tB = sampleColor.b;
    const maxDist = Math.sqrt(3 * 255 * 255);
    const mask = new Uint8Array(doc.width * doc.height);
    for (let ly = 0; ly < lh; ly++) {
      for (let lx = 0; lx < lw; lx++) {
        const pi = (ly * lw + lx) * 4;
        const dR = data[pi]! - tR, dG = data[pi + 1]! - tG, dB = data[pi + 2]! - tB;
        const dist = Math.sqrt(dR * dR + dG * dG + dB * dB);
        const normalized = dist / maxDist * 100;
        if (normalized <= fuzz) {
          const dx = lx + layer.x, dy = ly + layer.y;
          if (dx >= 0 && dx < doc.width && dy >= 0 && dy < doc.height) {
            mask[dy * doc.width + dx] = Math.round((1 - normalized / (fuzz || 1)) * 255);
          }
        }
      }
    }
    _previousSelection = doc.selection;
    state.execute(new SetSelectionCommand(new Selection(doc.width, doc.height, mask)));
    state.requestRender();
    overlay.remove();
  });

  overlay.querySelector('[data-action="cancel"]')!.addEventListener("click", () => {
    canvas.removeEventListener("click", sampleHandler);
    overlay.remove();
  });
  overlay.addEventListener("click", (e) => { if (e.target === overlay) { canvas.removeEventListener("click", sampleHandler); overlay.remove(); } });
}

// â”€â”€ BATCH C: Make Work Path from selection â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function makeWorkPath(): Promise<void> {
  const doc = state.doc;
  if (!doc || !doc.selection || doc.selection.isEmpty()) { showError("No selection."); return; }
  const result = await filterDialog.open("Make Work Path", [
    { key: "tolerance", label: "Tolerance", type: "slider", default: 2, min: 0.5, max: 10, step: 0.5, unit: "px" },
  ]);
  if (!result) return;
  const tolerance = result.tolerance as number;
  const sel = doc.selection;
  const b = sel.bounds();
  if (!b) return;
  const boundary = extractSelectionBoundary(sel, b);
  if (boundary.length < 3) { showError("Could not extract boundary."); return; }
  const simplified = douglasPeucker(boundary, tolerance);
  const pathModel = boundaryToPath(simplified);
  currentPathModel = pathModel;
  showTip(`Work path created with ${simplified.length} points.`);
  state.requestRender();
}

function extractSelectionBoundary(sel: Selection, bounds: { x: number; y: number; w: number; h: number }): Array<{ x: number; y: number }> {
  const mask = sel.mask, w = sel.docW;
  const points: Array<{ x: number; y: number }> = [];
  for (let y = bounds.y; y < bounds.y + bounds.h; y++) {
    for (let x = bounds.x; x < bounds.x + bounds.w; x++) {
      const idx = y * w + x;
      if ((mask[idx] ?? 0) > 128) {
        const isEdge = (x > 0 && (mask[idx - 1] ?? 0) <= 128) || (x < w - 1 && (mask[idx + 1] ?? 0) <= 128) || (y > 0 && (mask[idx - w] ?? 0) <= 128) || (y < sel.docH - 1 && (mask[idx + w] ?? 0) <= 128);
        if (isEdge) points.push({ x, y });
      }
    }
  }
  if (points.length < 3) return points;
  const sorted: Array<{ x: number; y: number }> = [points[0]!];
  const remaining = new Set(points.slice(1).map((_, i) => i));
  let current = points[0]!;
  while (remaining.size > 0) {
    let nearestIdx = -1, nearestDist = Infinity;
    for (const idx of remaining) {
      const p = points[idx + 1]!;
      const d = Math.hypot(p.x - current.x, p.y - current.y);
      if (d < nearestDist) { nearestDist = d; nearestIdx = idx; }
    }
    if (nearestIdx >= 0) { current = points[nearestIdx + 1]!; sorted.push(current); remaining.delete(nearestIdx); } else break;
  }
  return sorted;
}

function douglasPeucker(points: Array<{ x: number; y: number }>, tolerance: number): Array<{ x: number; y: number }> {
  if (points.length <= 2) return points;
  let maxDist = 0, maxIdx = 0;
  const first = points[0]!, last = points[points.length - 1]!;
  for (let i = 1; i < points.length - 1; i++) {
    const d = pointToLineDistance(points[i]!, first, last);
    if (d > maxDist) { maxDist = d; maxIdx = i; }
  }
  if (maxDist > tolerance) {
    const left = douglasPeucker(points.slice(0, maxIdx + 1), tolerance);
    const right = douglasPeucker(points.slice(maxIdx), tolerance);
    return [...left.slice(0, -1), ...right];
  }
  return [first, last];
}

function pointToLineDistance(p: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = b.x - a.x, dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

function boundaryToPath(points: Array<{ x: number; y: number }>): import("./core/document/PathModel").PathModel {
  const subpathPoints: Array<{ x: number; y: number; leftDirection?: { x: number; y: number }; rightDirection?: { x: number; y: number }; type: "corner" | "smooth" | "symmetric" }> = [];
  for (let i = 0; i < points.length; i++) {
    const prev = points[(i - 1 + points.length) % points.length]!;
    const curr = points[i]!;
    const next = points[(i + 1) % points.length]!;
    const t = 0.3;
    subpathPoints.push({ x: curr.x, y: curr.y, leftDirection: { x: curr.x - (curr.x - prev.x) * t, y: curr.y - (curr.y - prev.y) * t }, rightDirection: { x: curr.x + (next.x - curr.x) * t, y: curr.y + (next.y - curr.y) * t }, type: "smooth" as const });
  }
  return { subpaths: [{ points: subpathPoints as import("./core/document/PathModel").AnchorPoint[], closed: true }] };
}

// â”€â”€ BATCH D: Layer Properties dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function layerProperties(): Promise<void> {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (!layer) return;
  const colorLabels: Array<{ value: string; label: string }> = [
    { value: "none", label: "None" }, { value: "red", label: "Red" }, { value: "orange", label: "Orange" },
    { value: "yellow", label: "Yellow" }, { value: "green", label: "Green" }, { value: "blue", label: "Blue" },
    { value: "violet", label: "Violet" }, { value: "gray", label: "Gray" },
  ];
  const result = await filterDialog.open("Layer Properties", [
    { key: "name", label: "Name", type: "slider", default: 0, min: 0, max: 1, unit: "" },
    { key: "color", label: "Color", type: "dropdown", default: (layer as any).colorLabel || "none", options: colorLabels },
    { key: "opacity", label: "Opacity", type: "slider", default: Math.round((layer.opacity ?? 1) * 100), min: 0, max: 100, unit: "%" },
    { key: "fillOpacity", label: "Fill Opacity", type: "slider", default: Math.round((layer.fillOpacity ?? 1) * 100), min: 0, max: 100, unit: "%" },
  ]);
  if (!result) return;
  const name = prompt("Layer name:", layer.name);
  if (name === null) return;
  const patches: Record<string, unknown> = { name: name || layer.name };
  if (result.color !== "none") patches.colorLabel = result.color as import("./core/document/Layer").LayerColorLabel;
  patches.opacity = (result.opacity as number) / 100;
  patches.fillOpacity = (result.fillOpacity as number) / 100;
  state.execute(new SetLayerPropertyCommand(doc.activeLayerId, patches));
  showTip("Layer properties updated.");
}

// â”€â”€ BATCH D: Blending Options dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function blendingOptions(): Promise<void> {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (!layer) return;
  const existingStyles = (layer as any).styles ?? {};
  const existingBlendIf = (layer as any).blendIf;
  const overlay = document.createElement("div");
  overlay.className = "filter-dialog";
  overlay.innerHTML = `
    <div class="filter-dialog__sheet" style="max-width:500px;max-height:85vh">
      <div class="filter-dialog__title">Layer Style</div>
      <div class="filter-dialog__body" style="padding:12px 0">
        <div style="margin-bottom:8px">
          <label style="font-size:11px">Blend Mode:</label>
          <select id="bl-blend-mode" style="width:100%;background:var(--bg-input);border:1px solid var(--border);color:var(--text);padding:2px 4px;font-size:12px">
            ${BLEND_OPTIONS.filter(([v]) => v !== "").map(([v, l]) => `<option value="${v}" ${v === layer.blendMode ? "selected" : ""}>${escapeHtml(l)}</option>`).join("")}
          </select>
        </div>
        <div style="margin-bottom:8px">
          <label style="font-size:11px">Opacity: <span id="bl-opacity-val">${Math.round((layer.opacity ?? 1) * 100)}</span>%</label>
          <input id="bl-opacity" type="range" min="0" max="100" value="${Math.round((layer.opacity ?? 1) * 100)}" style="width:100%">
        </div>
        <div style="margin-bottom:8px">
          <label style="font-size:11px">Fill Opacity: <span id="bl-fill-val">${Math.round((layer.fillOpacity ?? 1) * 100)}</span>%</label>
          <input id="bl-fill" type="range" min="0" max="100" value="${Math.round((layer.fillOpacity ?? 1) * 100)}" style="width:100%">
        </div>
        <div style="margin-bottom:8px">
          <label style="font-size:11px">Blend If â€” Gray:</label>
          <div style="display:flex;gap:8px;margin-top:4px">
            <div style="flex:1">
              <label style="font-size:10px">This Layer:</label>
              <div style="display:flex;gap:4px">
                <input id="bl-this-black" type="range" min="0" max="255" value="${existingBlendIf?.channels?.gray?.thisLayer?.[0] ?? 0}" style="flex:1">
                <input id="bl-this-white" type="range" min="0" max="255" value="${existingBlendIf?.channels?.gray?.thisLayer?.[1] ?? 255}" style="flex:1">
              </div>
            </div>
            <div style="flex:1">
              <label style="font-size:10px">Underlying:</label>
              <div style="display:flex;gap:4px">
                <input id="bl-under-black" type="range" min="0" max="255" value="${existingBlendIf?.channels?.gray?.underlyingLayer?.[0] ?? 0}" style="flex:1">
                <input id="bl-under-white" type="range" min="0" max="255" value="${existingBlendIf?.channels?.gray?.underlyingLayer?.[1] ?? 255}" style="flex:1">
              </div>
            </div>
          </div>
        </div>
        <div style="font-size:11px;font-weight:600;color:var(--text-dim);text-transform:uppercase;margin-bottom:4px">Effects</div>
        <div id="bl-effects-list" style="font-size:12px"></div>
      </div>
      <div class="filter-dialog__actions">
        <button class="filter-dialog__btn" data-action="cancel">Cancel</button>
        <button class="filter-dialog__btn filter-dialog__btn--primary" data-action="ok">OK</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  const effectsList = overlay.querySelector<HTMLDivElement>("#bl-effects-list")!;
  const effectNames = ["dropShadow", "innerShadow", "outerGlow", "innerGlow", "bevelEmboss", "satin", "colorOverlay", "gradientOverlay", "patternOverlay", "stroke"];
  const effectLabels: Record<string, string> = {
    dropShadow: "Drop Shadow", innerShadow: "Inner Shadow", outerGlow: "Outer Glow",
    innerGlow: "Inner Glow", bevelEmboss: "Bevel & Emboss", satin: "Satin",
    colorOverlay: "Color Overlay", gradientOverlay: "Gradient Overlay", patternOverlay: "Pattern Overlay", stroke: "Stroke",
  };
  const renderEffectsList = () => {
    effectsList.innerHTML = effectNames.map(name => {
      const enabled = existingStyles[name]?.enabled;
      return `<div style="padding:2px 0"><label style="font-size:12px"><input type="checkbox" ${enabled ? "checked" : ""} data-effect="${name}"> ${effectLabels[name]}</label></div>`;
    }).join("");
    effectsList.querySelectorAll("input[type=checkbox]").forEach(cb => {
      cb.addEventListener("change", () => {
        const name = (cb as HTMLInputElement).dataset.effect!;
        if (!existingStyles[name]) existingStyles[name] = { enabled: true, blendMode: "normal", opacity: 1 };
        existingStyles[name].enabled = (cb as HTMLInputElement).checked;
      });
    });
  };
  renderEffectsList();
  const opacitySlider = overlay.querySelector<HTMLInputElement>("#bl-opacity")!;
  const opacityVal = overlay.querySelector<HTMLSpanElement>("#bl-opacity-val")!;
  opacitySlider.addEventListener("input", () => { opacityVal.textContent = opacitySlider.value; });
  const fillSlider = overlay.querySelector<HTMLInputElement>("#bl-fill")!;
  const fillVal = overlay.querySelector<HTMLSpanElement>("#bl-fill-val")!;
  fillSlider.addEventListener("input", () => { fillVal.textContent = fillSlider.value; });
  overlay.querySelector('[data-action="ok"]')!.addEventListener("click", () => {
    const patches: Record<string, unknown> = {
      blendMode: (overlay.querySelector<HTMLSelectElement>("#bl-blend-mode")!).value as BlendMode,
      opacity: parseInt(opacitySlider.value, 10) / 100,
      fillOpacity: parseInt(fillSlider.value, 10) / 100,
      styles: existingStyles,
    };
    const blendIf: import("./core/document/Layer").BlendIfConfig = {
      channels: {
        gray: {
          thisLayer: [parseInt((overlay.querySelector<HTMLInputElement>("#bl-this-black")!).value, 10), parseInt((overlay.querySelector<HTMLInputElement>("#bl-this-white")!).value, 10)],
          underlyingLayer: [parseInt((overlay.querySelector<HTMLInputElement>("#bl-under-black")!).value, 10), parseInt((overlay.querySelector<HTMLInputElement>("#bl-under-white")!).value, 10)],
        },
      },
    };
    patches.blendIf = blendIf;
    state.execute(new SetLayerPropertyCommand(doc.activeLayerId!, patches));
    state.requestRender();
    overlay.remove();
  });
  overlay.querySelector('[data-action="cancel"]')!.addEventListener("click", () => overlay.remove());
  overlay.addEventListener("click", (e) => { if (e.target === overlay) overlay.remove(); });
}

// â”€â”€ BATCH D: Scale Layer Effects â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function scaleLayerEffects(): Promise<void> {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (!layer) return;
  const styles = (layer as any).styles;
  if (!styles) { showError("No layer styles to scale."); return; }
  const result = await filterDialog.open("Scale Layer Effects", [
    { key: "scale", label: "Scale", type: "slider", default: 100, min: 1, max: 500, unit: "%" },
  ]);
  if (!result) return;
  const scale = (result.scale as number) / 100;
  const scaleEffect = (effect: any, keys: string[]) => {
    if (!effect) return;
    for (const key of keys) { if (typeof effect[key] === "number" && effect[key] > 0) effect[key] = Math.round(effect[key] * scale); }
  };
  scaleEffect(styles.dropShadow, ["distance", "size", "spread"]);
  scaleEffect(styles.innerShadow, ["distance", "size", "choke"]);
  scaleEffect(styles.outerGlow, ["size", "spread"]);
  scaleEffect(styles.innerGlow, ["size", "choke"]);
  scaleEffect(styles.bevelEmboss, ["size", "soften", "altitude"]);
  scaleEffect(styles.satin, ["distance", "size"]);
  scaleEffect(styles.stroke, ["size"]);
  state.execute(new SetLayerPropertyCommand(doc.activeLayerId, { styles }));
  showTip(`Layer effects scaled to ${Math.round(scale * 100)}%.`);
}

// â”€â”€ BATCH D: Global Light â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

let globalLightAngle = 120;
let globalLightAltitude = 30;

async function setGlobalLight(): Promise<void> {
  const result = await filterDialog.open("Global Light", [
    { key: "angle", label: "Angle", type: "slider", default: globalLightAngle, min: 0, max: 360, unit: "Â°" },
    { key: "altitude", label: "Altitude", type: "slider", default: globalLightAltitude, min: 0, max: 90, unit: "Â°" },
  ]);
  if (!result) return;
  globalLightAngle = result.angle as number;
  globalLightAltitude = result.altitude as number;
  const doc = state.doc;
  if (!doc) return;
  const applyGlobalLight = (layers: readonly Layer[]) => {
    for (const layer of layers) {
      const styles = (layer as any).styles;
      if (!styles) { if (layer.kind === "group") applyGlobalLight(layer.children); continue; }
      if (styles.dropShadow?.useGlobalLight) styles.dropShadow.angle = globalLightAngle;
      if (styles.innerShadow?.useGlobalLight) styles.innerShadow.angle = globalLightAngle;
      if (styles.bevelEmboss?.useGlobalLight) { styles.bevelEmboss.angle = globalLightAngle; styles.bevelEmboss.altitude = globalLightAltitude; }
      if (layer.kind === "group") applyGlobalLight(layer.children);
    }
  };
  applyGlobalLight(doc.layers);
  state.requestRender();
  showTip(`Global light set to angle ${globalLightAngle}Â°, altitude ${globalLightAltitude}Â°.`);
}

// â”€â”€ BATCH E: New Guide Layout â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function newGuideLayout(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;
  const result = await filterDialog.open("New Guide Layout", [
    { key: "cols", label: "Columns", type: "slider", default: 3, min: 1, max: 20, unit: "" },
    { key: "rows", label: "Rows", type: "slider", default: 0, min: 0, max: 20, unit: "" },
    { key: "gutter", label: "Gutter", type: "slider", default: 10, min: 0, max: 100, unit: "px" },
    { key: "margin", label: "Margin", type: "slider", default: 0, min: 0, max: 200, unit: "px" },
  ]);
  if (!result) return;
  const cols = result.cols as number, rows = result.rows as number, gutter = result.gutter as number, margin = result.margin as number;
  guides.length = 0;
  // i207: a nonzero gutter should produce a visibly bounded gutter CHANNEL
  // (two guide lines, one at the end of the column before the gap and one at
  // the start of the column after it) rather than a single line at the
  // midpoint convention. Outer edges (margin / doc size - margin) still get
  // one line each.
  if (cols > 1) {
    const colWidth = (doc.width - margin * 2 - gutter * (cols - 1)) / cols;
    guides.push({ kind: "vertical", position: margin });
    for (let i = 1; i < cols; i++) {
      const gapStart = margin + i * colWidth + (i - 1) * gutter;
      const gapEnd = margin + i * colWidth + i * gutter;
      if (gapStart >= 0 && gapStart <= doc.width) guides.push({ kind: "vertical", position: gapStart });
      if (gutter > 0 && gapEnd >= 0 && gapEnd <= doc.width) guides.push({ kind: "vertical", position: gapEnd });
    }
    const rightEdge = doc.width - margin;
    if (rightEdge >= 0 && rightEdge <= doc.width) guides.push({ kind: "vertical", position: rightEdge });
  }
  if (rows > 1) {
    const rowHeight = (doc.height - margin * 2 - gutter * (rows - 1)) / rows;
    guides.push({ kind: "horizontal", position: margin });
    for (let i = 1; i < rows; i++) {
      const gapStart = margin + i * rowHeight + (i - 1) * gutter;
      const gapEnd = margin + i * rowHeight + i * gutter;
      if (gapStart >= 0 && gapStart <= doc.height) guides.push({ kind: "horizontal", position: gapStart });
      if (gutter > 0 && gapEnd >= 0 && gapEnd <= doc.height) guides.push({ kind: "horizontal", position: gapEnd });
    }
    const bottomEdge = doc.height - margin;
    if (bottomEdge >= 0 && bottomEdge <= doc.height) guides.push({ kind: "horizontal", position: bottomEdge });
  }
  if (margin > 0) {
    guides.push({ kind: "vertical", position: margin }, { kind: "vertical", position: doc.width - margin }, { kind: "horizontal", position: margin }, { kind: "horizontal", position: doc.height - margin });
  }
  state.requestRender();
  showTip(`Guide layout created: ${cols} columns, ${rows} rows.`);
}

// â”€â”€ BATCH E: Clear Guides â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function clearGuides(): void {
  guides.length = 0;
  state.requestRender();
  showTip("All guides cleared.");
}

// â”€â”€ BATCH E: Pixel Grid toggle â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function togglePixelGrid(): void {
  showPixelGrid = !showPixelGrid;
  state.requestRender();
  showTip(`Pixel grid ${showPixelGrid ? "enabled" : "disabled"}.`);
}

// â”€â”€ BATCH E: Layer Edges toggle â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function toggleLayerEdges(): void {
  showLayerEdges = !showLayerEdges;
  state.requestRender();
  showTip(`Layer edges ${showLayerEdges ? "enabled" : "disabled"}.`);
}

// â”€â”€ BATCH F: Paste Outside â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function pasteOutside(): void {
  const doc = state.doc;
  if (!_clipboard || !doc || !doc.selection) { showError("Nothing to paste or no selection."); return; }
  const n = doc.layers.filter(l => l.kind === "raster").length + 1;
  const pixels = new PixelBuffer(_clipboard.width, _clipboard.height, "rgba8");
  pixels.data.set(_clipboard.pixels);
  pixels.markDirty();
  const invMask = new Uint8Array(doc.selection.mask.length);
  for (let i = 0; i < doc.selection.mask.length; i++) invMask[i] = 255 - (doc.selection.mask[i] ?? 0);
  const newLayer: RasterLayer = {
    id: crypto.randomUUID(), kind: "raster", name: `Layer ${n}`,
    visible: true, opacity: 1, fillOpacity: 1, blendMode: "normal",
    x: 0, y: 0, lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false, clippedToBelow: false,
    pixels,
    mask: { pixels: new PixelBuffer(doc.width, doc.height, "rgba8"), x: 0, y: 0, enabled: true, density: 1, feather: 0, linked: true },
  };
  const maskData = newLayer.mask!.pixels.data;
  for (let i = 0; i < invMask.length; i++) maskData[i * 4 + 3] = invMask[i] ?? 0;
  newLayer.mask!.pixels.markDirty();
  state.execute(new NewLayerCommand(newLayer));
  state.requestRender();
}

// â”€â”€ BATCH F: Transform Again â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

// `lastTransform` used to be declared but NEVER assigned anywhere, so
// transformAgain() always hit the "no transform" guard below â€” Shift+Ctrl+T
// had nothing to repeat (audit i25). It's now set by
// transformLayerOrientation() (Edit > Transform flip/rotate) and by the
// Free Transform commit handler further down. The actual replay decision
// is `planTransformAgain()` (core/commands/TransformLayerOrientationCommand.ts)
// so it's unit-testable; this function just wires that decision to
// state.execute()/showError().
let lastTransform: LastTransformRecord | null = null;

function transformAgain(): void {
  const doc = state.doc;
  if (!doc) { showError("No transform to repeat."); return; }
  const plan = planTransformAgain(lastTransform, transformLayerOrientationIds());
  if (!plan) { showError("No transform to repeat."); return; }
  if (plan.kind === "error") { showError(plan.message); return; }
  state.execute(plan.command);
  state.requestRender();
  showTip("Transform applied again.");
}

// â”€â”€ BATCH G: Filter Gallery framework â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const filterGalleryDialog = new FilterGalleryDialog();

async function filterGallery(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;
  const layer = getActiveRaster();
  if (!layer) { showError("No active raster layer."); return; }

  // Create a small preview sample (max 256x256)
  const maxPreview = 256;
  const sw = layer.pixels.width, sh = layer.pixels.height;
  const pw = Math.min(maxPreview, sw), ph = Math.min(maxPreview, sh);
  const previewPixels = new Uint8ClampedArray(pw * ph * 4);
  const src = layer.pixels.data;
  const sx = sw / pw, sy = sh / ph;
  for (let dy = 0; dy < ph; dy++) {
    for (let dx = 0; dx < pw; dx++) {
      const lx = Math.floor(dx * sx), ly = Math.floor(dy * sy);
      const si = (ly * sw + lx) * 4;
      const di = (dy * pw + dx) * 4;
      previewPixels[di] = src[si]!;
      previewPixels[di + 1] = src[si + 1]!;
      previewPixels[di + 2] = src[si + 2]!;
      previewPixels[di + 3] = src[si + 3]!;
    }
  }

  const result = await filterGalleryDialog.open(previewPixels, pw, ph);
  if (!result || result.filters.length === 0) return;

  // Apply each filter in sequence to the active layer
  for (const inst of result.filters) {
    if (!inst.enabled) continue;
    const { getGalleryFilterById } = await import("./core/filters/GalleryFilters");
    const entry = getGalleryFilterById(inst.entryId);
    if (!entry) continue;
    applyFilter(entry.filter, inst.params);
  }
}

// â”€â”€ BATCH G: Lens Correction filter â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function lensCorrection(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;
  const layer = getActiveRaster();
  if (!layer) { showError("No active raster layer."); return; }
  const result = await filterDialog.open("Lens Correction", [
    { key: "distortion", label: "Distortion", type: "slider", default: 0, min: -100, max: 100, unit: "" },
    { key: "chromaticR", label: "Chromatic R", type: "slider", default: 0, min: -50, max: 50, unit: "" },
    { key: "chromaticB", label: "Chromatic B", type: "slider", default: 0, min: -50, max: 50, unit: "" },
    { key: "vignette", label: "Vignette Amount", type: "slider", default: 0, min: -100, max: 100, unit: "" },
    { key: "vignetteMid", label: "Vignette Midpoint", type: "slider", default: 50, min: 0, max: 100, unit: "" },
  ]);
  if (!result) return;
  const distortion = (result.distortion as number) / 100;
  const chromaticR = (result.chromaticR as number) / 100;
  const chromaticB = (result.chromaticB as number) / 100;
  const vignette = (result.vignette as number) / 100;
  const vignetteMid = (result.vignetteMid as number) / 100;
  const w = layer.pixels.width, h = layer.pixels.height;
  const data = layer.pixels.data;
  const copy = new Uint8ClampedArray(data);
  const cx = w / 2, cy = h / 2;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (x - cx) / cx, dy = (y - cy) / cy;
      const r = Math.sqrt(dx * dx + dy * dy);
      const dr = r * (1 + distortion * r * r);
      const sx = Math.round(cx + dr * dx * cx), sy = Math.round(cy + dr * dy * cy);
      const rShift = chromaticR * r, bShift = chromaticB * r;
      const rSrcX = Math.round(sx + rShift * cx), bSrcX = Math.round(sx - bShift * cx);
      const di = (y * w + x) * 4;
      if (rSrcX >= 0 && rSrcX < w && sy >= 0 && sy < h) { const si = (sy * w + rSrcX) * 4; data[di] = copy[si]!; }
      if (sx >= 0 && sx < w && sy >= 0 && sy < h) { const si = (sy * w + sx) * 4; data[di + 1] = copy[si + 1]!; }
      if (bSrcX >= 0 && bSrcX < w && sy >= 0 && sy < h) { const si = (sy * w + bSrcX) * 4; data[di + 2] = copy[si + 2]!; }
      if (vignette !== 0) {
        const normR = r / (vignetteMid || 0.01);
        const vig = 1 - vignette * Math.max(0, normR - 1) * Math.max(0, normR - 1);
        data[di] = Math.min(255, Math.max(0, Math.round(data[di]! * vig)));
        data[di + 1] = Math.min(255, Math.max(0, Math.round(data[di + 1]! * vig)));
        data[di + 2] = Math.min(255, Math.max(0, Math.round(data[di + 2]! * vig)));
      }
    }
  }
  layer.pixels.markDirty();
  state.requestRender();
  showTip("Lens correction applied.");
}

// â”€â”€ BATCH G: Adaptive Wide Angle filter â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function adaptiveWideAngle(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;
  const layer = getActiveRaster();
  if (!layer) { showError("No active raster layer."); return; }
  const result = await filterDialog.open("Adaptive Wide Angle", [
    { key: "correction", label: "Correction", type: "dropdown", default: "auto", options: [
      { value: "auto", label: "Auto" }, { value: "fisheye", label: "Fisheye" },
      { value: "perspective", label: "Perspective" }, { value: "full-cylinder", label: "Full Cylinder" },
    ]},
    { key: "scale", label: "Scale", type: "slider", default: 100, min: 50, max: 200, unit: "%" },
  ]);
  if (!result) return;
  const correction = result.correction as string;
  const scale = (result.scale as number) / 100;
  const w = layer.pixels.width, h = layer.pixels.height;
  const data = layer.pixels.data;
  const copy = new Uint8ClampedArray(data);
  const cx = w / 2, cy = h / 2;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (x - cx) / cx, dy = (y - cy) / cy;
      const r = Math.sqrt(dx * dx + dy * dy);
      let sx = x, sy = y;
      if (correction === "fisheye" || correction === "auto") {
        const theta = Math.atan2(dy, dx);
        const newR = Math.sin(r * Math.PI / 2) / (Math.PI / 2);
        sx = Math.round(cx + newR * Math.cos(theta) * cx * scale);
        sy = Math.round(cy + newR * Math.sin(theta) * cy * scale);
      } else if (correction === "perspective") {
        const factor = 1 / (1 + r * 0.3);
        sx = Math.round(cx + dx * cx * factor * scale);
        sy = Math.round(cy + dy * cy * factor * scale);
      } else if (correction === "full-cylinder") {
        const theta = Math.asin(Math.min(1, Math.max(-1, dx)));
        sx = Math.round(cx + theta * cx * scale);
        sy = y;
      }
      const di = (y * w + x) * 4;
      if (sx >= 0 && sx < w && sy >= 0 && sy < h) {
        const si = (sy * w + sx) * 4;
        data[di] = copy[si]!; data[di + 1] = copy[si + 1]!; data[di + 2] = copy[si + 2]!; data[di + 3] = copy[si + 3]!;
      } else { data[di] = 0; data[di + 1] = 0; data[di + 2] = 0; data[di + 3] = 0; }
    }
  }
  layer.pixels.markDirty();
  state.requestRender();
  showTip("Adaptive Wide Angle correction applied.");
}

// â”€â”€ Place Linked â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function placeLinked(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/png,image/jpeg,image/webp,image/avif,image/gif,image/bmp,.psd,.svg";
  input.style.display = "none";
  document.body.appendChild(input);
  input.addEventListener("change", async () => {
    const f = input.files?.[0];
    if (!f) { input.remove(); return; }
    try {
      const { decodeImage } = await import("./app/io/decodeImage");
      const { pixels, width, height } = await decodeImage(f);
      const n = doc.layers.filter(l => l.kind === "raster").length + 1;
      const layer: RasterLayer = {
        id: crypto.randomUUID(), kind: "raster", name: f.name.replace(/\.[^.]+$/, "") || `Linked ${n}`,
        visible: true, opacity: 1, fillOpacity: 1, blendMode: "normal",
        x: Math.round((doc.width - width) / 2), y: Math.round((doc.height - height) / 2),
        lockTransparency: false, lockImage: false, lockPosition: false, lockAll: false, clippedToBelow: false,
        pixels,
      };
      // Store external file reference
      (layer as any)._linkedFile = f.name;
      (layer as any)._linkedPath = (f as any).path || f.name;
      state.execute(new NewLayerCommand(layer));
      state.requestRender();
      showTip(`Placed "${f.name}" as linked Smart Object.`);
    } catch (err) {
      console.error("Place Linked failed:", err);
      showError("Failed to place linked file.");
    }
    input.remove();
  });
  input.click();
}

// â”€â”€ Export Artboards to Files â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function exportArtboardsToFiles(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;
  const artboards = doc.layers.filter((l): l is ArtboardLayer => l.kind === "artboard" && l.visible);
  if (artboards.length === 0) { showError("No artboards found."); return; }

  const result = await filterDialog.open("Export Artboards to Files", [
    { key: "format", label: "Format", type: "dropdown", default: "png", options: [
      { value: "png", label: "PNG" }, { value: "jpg", label: "JPEG" }, { value: "pdf", label: "PDF" },
    ]},
  ]);
  if (!result) return;

  const format = result.format as string;
  let count = 0;

  for (const artboard of artboards) {
    const canvas = document.createElement("canvas");
    canvas.width = artboard.artboardWidth;
    canvas.height = artboard.artboardHeight;
    const ctx = canvas.getContext("2d")!;

    // Fill background
    const bg = artboard.backgroundColor;
    ctx.fillStyle = `rgba(${bg.r},${bg.g},${bg.b},${bg.a})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Composite children
    const renderChild = (layer: Layer) => {
      if (!layer.visible) return;
      if (layer.kind === "raster" || layer.kind === "background") {
        const px = layer.kind === "background" ? (layer as BackgroundLayer).pixels : (layer as RasterLayer).pixels;
        const tmp = document.createElement("canvas");
        tmp.width = px.width; tmp.height = px.height;
        const tc = tmp.getContext("2d")!;
        const id = tc.createImageData(px.width, px.height);
        id.data.set(px.data); tc.putImageData(id, 0, 0);
        ctx.globalAlpha = layer.opacity;
        ctx.drawImage(tmp, layer.x - artboard.artboardX, layer.y - artboard.artboardY);
        ctx.globalAlpha = 1;
      } else if (layer.kind === "group") {
        for (const c of layer.children) renderChild(c);
      }
    };
    for (const child of artboard.children) renderChild(child);

    const mimeType = format === "jpg" ? "image/jpeg" : "image/png";
    const ext = format === "jpg" ? "jpg" : "png";
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mimeType, 0.92));
    if (blob) {
      downloadBlob(blob, `${artboard.name}.${ext}`);
      count++;
    }
  }

  showTip(`Exported ${count} artboard(s) to files.`);
}

// â”€â”€ Export Artboards to PDF â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function exportArtboardsToPdf(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;
  const artboards = doc.layers.filter((l): l is ArtboardLayer => l.kind === "artboard" && l.visible);
  if (artboards.length === 0) { showError("No artboards found."); return; }

  // Export each artboard as a separate PDF
  let count = 0;
  for (const artboard of artboards) {
    // Create a temporary doc for this artboard
    const tempDoc: Document = {
      name: `${doc.name}_${artboard.name}`,
      width: artboard.artboardWidth,
      height: artboard.artboardHeight,
      dpi: doc.dpi,
      layers: artboard.children,
      activeLayerId: artboard.children[0]?.id ?? null,
      selection: null,
    };

    const pdfBlob = await exportPdf(tempDoc);
    downloadBlob(pdfBlob, `${artboard.name}.pdf`);
    count++;
  }

  showTip(`Exported ${count} artboard(s) to PDF.`);
}

// â”€â”€ Export Paths to Illustrator â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function exportPathsToIllustrator(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;

  // Collect all paths from shape layers and path items
  const allPaths: PathModel[] = [];
  const collectPaths = (layers: readonly Layer[]) => {
    for (const layer of layers) {
      if (layer.kind === "shape") {
        allPaths.push((layer as any).path);
        if ((layer as any).vectorMask) allPaths.push((layer as any).vectorMask);
      }
      if (layer.kind === "group") collectPaths(layer.children);
      if (layer.kind === "artboard") collectPaths((layer as ArtboardLayer).children);
    }
  };
  collectPaths(doc.layers);

  if (allPaths.length === 0) { showError("No paths to export."); return; }

  const { exportPathsToAI } = await import("./core/io/ExportUtils");
  const aiContent = exportPathsToAI(allPaths, doc.width, doc.height);
  const blob = new Blob([aiContent], { type: "application/postscript" });
  downloadBlob(blob, `${doc.name.replace(/\.[^.]+$/, "")}_paths.ai`);
  showTip(`Exported ${allPaths.length} path(s) to Illustrator.`);
}

// â”€â”€ Export Color Lookup Tables â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function exportColorLookupTables(): Promise<void> {
  const result = await filterDialog.open("Export Color Lookup Tables", [
    { key: "size", label: "LUT Size", type: "dropdown", default: "17", options: [
      { value: "17", label: "17Ã—17Ã—17" }, { value: "33", label: "33Ã—33Ã—33" }, { value: "65", label: "65Ã—65Ã—65" },
    ]},
    { key: "format", label: "Format", type: "dropdown", default: "cube", options: [
      { value: "cube", label: ".cube" }, { value: "3dl", label: ".3dl" },
    ]},
  ]);
  if (!result) return;

  const size = parseInt(result.size as string, 10);
  const format = result.format as string;

  const { generateLUT, exportLUTAsCube, exportLUTAs3dl } = await import("./core/io/ExportUtils");
  const lut = generateLUT(size);

  let content: string;
  let ext: string;
  if (format === "cube") {
    content = exportLUTAsCube(lut, "Pixelpa LUT");
    ext = "cube";
  } else {
    content = exportLUTAs3dl(lut, "Pixelpa LUT");
    ext = "3dl";
  }

  const blob = new Blob([content], { type: "text/plain" });
  downloadBlob(blob, `pixelpa_lut_${size}.${ext}`);
  showTip(`Exported ${size}Â³ LUT as .${ext}`);
}

// â”€â”€ Conditional Mode Change â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function conditionalModeChange(): Promise<void> {
  const doc = state.doc;
  if (!doc) return;
  const layer = getActiveRaster();
  if (!layer) { showError("No active raster layer."); return; }

  const result = await filterDialog.open("Conditional Mode Change", [
    { key: "sourceMode", label: "Source Mode", type: "dropdown", default: "rgb", options: [
      { value: "rgb", label: "RGB Color" }, { value: "grayscale", label: "Grayscale" },
    ]},
    { key: "targetMode", label: "Target Mode", type: "dropdown", default: "grayscale", options: [
      { value: "grayscale", label: "Grayscale" }, { value: "rgb", label: "RGB Color" },
    ]},
  ]);
  if (!result) return;

  const { conditionalModeChange: doModeChange } = await import("./core/io/ExportUtils");
  const newPixels = doModeChange(result.sourceMode as string, result.targetMode as string, layer.pixels);
  layer.pixels.data.set(newPixels.data);
  layer.pixels.markDirty();
  state.requestRender();
  showTip(`Mode changed from ${result.sourceMode} to ${result.targetMode}.`);
}

// â”€â”€ Create Droplet â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

async function createDroplet(): Promise<void> {
  const actionsInput = prompt("Actions (comma-separated):", "Default Actions");
  if (actionsInput === null) return;

  const result = await filterDialog.open("Create Droplet", [
    { key: "format", label: "Output Format", type: "dropdown", default: "png", options: [
      { value: "png", label: "PNG" }, { value: "jpg", label: "JPEG" }, { value: "webp", label: "WebP" },
    ]},
  ]);
  if (!result) return;

  const actions = actionsInput.split(",").map(s => s.trim()).filter(Boolean);
  const format = result.format as string;

  const { createDropletScript } = await import("./core/io/ExportUtils");
  const script = createDropletScript(actions, format);
  const blob = new Blob([script], { type: "application/javascript" });
  downloadBlob(blob, `droplet_${Date.now()}.js`);
  showTip(`Droplet created with ${actions.length} action(s).`);
}

// â”€â”€ Print Size View Mode â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function viewPrintSize(): void {
  const doc = state.doc;
  if (!doc) return;
  const dpi = doc.dpi || 72;
  // At print size: 1 document pixel = 1 screen pixel at 72 DPI
  // Scale = 72 / DPI
  const printZoom = 72 / dpi;
  // i114: center the document in the viewport (matching resetTo100's
  // formula) instead of pinning it to the top-left corner.
  const r = viewportEl.getBoundingClientRect();
  state.setViewport({
    zoom: printZoom,
    panX: (r.width - doc.width * printZoom) / 2,
    panY: (r.height - doc.height * printZoom) / 2,
  });
  updateDocTab();
  scheduleFrame();
  showTip(`Print size: ${printZoom.toFixed(0)}% (${dpi} DPI)`);
}

// â”€â”€ Show/Hide Slices â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

let showSlices = true;

function toggleSlices(): void {
  showSlices = !showSlices;
  state.requestRender();
  showTip(`Slices ${showSlices ? "shown" : "hidden"}.`);
}

// â”€â”€ Layer Style Dialog (Full Multi-Tab) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Implementation lives in src/app/ui/LayerStyleDialog.ts.

async function showLayerStyleDialog(): Promise<void> {
  const doc = state.doc;
  if (!doc?.activeLayerId) return;
  const layer = findLayer(doc.layers, doc.activeLayerId);
  if (!layer) return;

  const initialStyles = (layer as any).styles || {};

  const result = await openLayerStyleDialog(initialStyles, {
    openColorPicker: (initial) => colorPicker.open(initial),
    get globalLightAngle() { return globalLightAngle; },
    onGlobalLightAngleChange: (angle: number) => { globalLightAngle = angle; },
  });

  if (!result) return;
  state.execute(new SetLayerPropertyCommand(doc.activeLayerId!, { styles: result }));
  state.requestRender();
  showTip("Layer style updated.");
}

// ── Warp Text Dialog (i14) ──────────────────────────────────────────────
// Implementation lives in src/app/ui/WarpTextDialog.ts. The renderer
// (Canvas2DRenderer.drawTypeLayer / meshWarpCanvas) already honors
// TypeLayer.warp — this is pure wiring: seed the dialog with the active
// type layer's current warp and apply changes via SetLayerPropertyCommand
// so they're undoable and repaint. No-ops when there is no active type
// layer (guarded by getActiveTypeLayerWarp). Also reachable from the Type
// options bar's Warp button (see ToolUX.ts's TypeToolOptions).
function showWarpTextDialog(): void {
  openWarpTextDialogForActiveLayer({
    getActiveTypeLayer: () => getActiveTypeLayerWarp(state),
    onApply: (layerId, warp) => {
      state.execute(new SetLayerPropertyCommand(layerId, { warp }));
      state.requestRender();
      showTip("Warp Text applied.");
    },
  });
}

// ===================================================================
// Bug #114: beforeunload guard â€” closing tab with unsaved work
// ===================================================================
window.addEventListener("beforeunload", (e: BeforeUnloadEvent) => {
  if (state.dirty) {
    e.preventDefault();
    e.returnValue = "";
  }
});

// ===================================================================
// #176: Autosave + crash recovery — mount the autosave module at boot
// ===================================================================
// Module-level reference so Discard can call discardRecoverySnapshot() (#249).
import type { AutosaveManager } from './app/autosave';
let _autosaveManager: AutosaveManager | null = null;

function showRecoveryPrompt(snap: AutosaveSnapshot): void {
  const bar = document.createElement('div');
  bar.className = 'recovery-toast';
  bar.style.cssText =
    'position:fixed;top:0;left:0;right:0;z-index:9999;background:#d97706;color:#fff;' +
    'padding:10px 16px;display:flex;align-items:center;gap:12px;font-size:13px;';
  const savedAt = new Date(snap.savedAt).toLocaleString();
  bar.innerHTML =
    '<span>Unsaved document found from ' + savedAt + '. Recover?</span>' +
    '<button id="recovery-yes" style="padding:4px 12px;border:none;border-radius:3px;background:#fff;color:#d97706;cursor:pointer;font-weight:bold;">Recover</button>' +
    '<button id="recovery-no" style="padding:4px 12px;border:none;border-radius:3px;background:transparent;color:#fff;cursor:pointer;">Discard</button>';
  document.body.prepend(bar);

  bar.querySelector<HTMLButtonElement>('#recovery-yes')?.addEventListener('click', async () => {
    bar.remove();
    try {
      const { openPxa } = await import('./app/io/pxa');
      const blob = new Blob([snap.bytes]);
      const doc = await openPxa(blob);
      state.setDocument(doc);
      renderLayersPanel();
      renderer.render(doc, state.viewport);
      fitDoc();
      showTip('Document recovered from autosave.');
    } catch (err) {
      showError('Recovery failed: ' + (err instanceof Error ? err.message : String(err)));
    }
  });
  bar.querySelector<HTMLButtonElement>('#recovery-no')?.addEventListener('click', () => {
    bar.remove();
    // #249: Discard must delete the IDB snapshot so the banner does not reappear.
    _autosaveManager?.discardRecoverySnapshot().catch(() => { /* ignore IDB errors */ });
  });
}

// #249: Per-tab/session key to prevent multi-tab autosave conflicts.
// sessionStorage is tab-scoped: two tabs in the same browser get different keys.
function getOrCreateTabSessionId(): string {
  const KEY = 'pixelpa-tab-session-id';
  try {
    let id = sessionStorage.getItem(KEY);
    if (!id) {
      id = 'tab-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
      sessionStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return 'tab-' + crypto.randomUUID();
  }
}

// Install autosave after page load so IndexedDB is available.
// Use a per-tab session id to prevent multi-tab autosave conflicts (#249).
const _tabSessionDocId = getOrCreateTabSessionId();
installAutosaveWithDefaults(state, showRecoveryPrompt, 30_000, _tabSessionDocId).then(({ manager }) => {
  _autosaveManager = manager;
}).catch(() => {
  // IDB unavailable (private browsing, etc.) — fail silently.
});

// ===================================================================
// Bug #116: visibilitychange â€” pause rAF loop when tab is hidden
// ===================================================================
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    _rafPaused = true;
  } else if (document.visibilityState === "visible") {
    _rafPaused = false;
    if (state.doc) {
      // audit i104 follow-up: same updateOverlay()-before-render() ordering
      // as scheduleFrame() — see the comment there.
      updateOverlay();
      renderer.render(state.doc, state.viewport);
      updateStatusBar();
      updateDocTab();
      updateSwatches();
      syncColorPanel();
      renderLayersPanel();
      syncLayerControls();
      syncCharPanel();
      syncPropertiesPanel();
      renderChannelsPanel();
      updateDropHint();
      updateCursor();
      updateNavigatorPanel();
      updateHistogramPanel();
      if (showRulers) updateRulers();
    }
  }
});

// ===================================================================
// Bug #115: devicePixelRatio change — re-render on multi-monitor drag
// #249: Re-register matchMedia after each DPR change so further changes
//       (e.g. dragging across three monitors) are not missed.
// ===================================================================
let _lastDpr = window.devicePixelRatio || 1;
let _dprMq: MediaQueryList | null = null;

function registerDprMq(): void {
  if (_dprMq) { _dprMq.removeEventListener('change', handleDprChange); }
  _dprMq = window.matchMedia('(resolution: ' + (window.devicePixelRatio || 1) + 'dppx)');
  _dprMq.addEventListener('change', handleDprChange);
}

function handleDprChange() {
  const newDpr = window.devicePixelRatio || 1;
  if (newDpr !== _lastDpr) {
    _lastDpr = newDpr;
    resize();
    // Re-register for the next DPR change (#249).
    registerDprMq();
  }
}

window.addEventListener('resize', handleDprChange);
registerDprMq();

